// Canonical ingredient registry.
//
// ╭──────────────────────────────────────────────────────────────────────╮
// │ WHAT IS A CANONICAL? — THE CONCEPTUAL DEFINITION                    │
// │                                                                      │
// │ A canonical is what you'd buy at the store, expressed as the         │
// │ RAWEST VERSION OF ITSELF. Branding, marketing claims, packaging      │
// │ adjectives — all stripped. It's what a common person would call      │
// │ the thing if they had to point at it on a shelf.                     │
// │                                                                      │
// │ Examples:                                                            │
// │   • "tortilla" is a canonical. It doesn't matter if the bag says     │
// │     "Mission Burrito Size Flour Tortillas" or "Tía Rosa Yellow Corn  │
// │     Tortillas"; the canonical underneath both is "tortilla". The     │
// │     CUT axis (corn vs. flour) and STATE / SIZE may differ — those    │
// │     are separate axes per CLAUDE.md identity hierarchy — but the     │
// │     canonical itself is the common name.                             │
// │   • "tortilla_chips" is a canonical. "Scoops" the brand is NOT.      │
// │     Scoops is just a Frito-Lay shape. The system needs to know       │
// │     Scoops and "Tostitos Bite Size" both reduce to tortilla_chips    │
// │     so the recipe maker can pair "salsa needs a vehicle" with        │
// │     either bag without getting confused.                             │
// │   • "soda" / "cola" are canonicals. "Pepsi" and "Coke" are brands.   │
// │     Same canonical, different brand axis — so the recipe maker       │
// │     calling for "cola" doesn't fight a Pepsi-only pantry.            │
// │                                                                      │
// │ Why the abstraction exists:                                          │
// │   The canonical is the LAYER OF COMMON LANGUAGE every part of the   │
// │   app — pantry, recipes, shopping list, AI suggestions — speaks to  │
// │   each other in. Without it the recipe maker would have to know     │
// │   every possible brand/variant the user might have on hand.         │
// │   With it, recipes reference a canonical id ("tortilla", "soda")   │
// │   and the pantry resolves which specific brand/cut/state row        │
// │   satisfies the recipe.                                              │
// │                                                                      │
// │ Authority:                                                           │
// │   The canonical name is up for user interpretation — what does the  │
// │   common person call this thing? When a household disagrees, admin  │
// │   curation steps in to pick the prevailing name. New canonicals     │
// │   start as user-created slugs (pantry_items.canonical_id, migration │
// │   0039) and graduate into the bundled INGREDIENTS registry below    │
// │   once they earn a slot.                                             │
// ╰──────────────────────────────────────────────────────────────────────╯
//
// ╭──────────────────────────────────────────────────────────────────────╮
// │ NAMING NOTE — IMPORTANT FOR FUTURE READERS                          │
// │                                                                      │
// │ "Ingredient" and "Canonical" mean the SAME THING in this codebase.   │
// │ The terms are interchangeable; we use both for historical reasons.   │
// │                                                                      │
// │   • Older code (this file, findIngredient, INGREDIENTS,              │
// │     statesForIngredient, defaultStateFor) uses "ingredient".         │
// │   • Newer code (per CLAUDE.md identity hierarchy, migration 0039+)   │
// │     uses "canonical" / "canonicalId" — the same id string,           │
// │     surfaced via inferCanonicalFromName, resolveCanonicalIdentity,   │
// │     pantry_items.canonical_id, etc.                                  │
// │                                                                      │
// │ Both refer to entries in the INGREDIENTS array below, keyed by `id`. │
// │ When you see `findIngredient(canonicalId)` it's literally fetching   │
// │ the same registry entry the new code calls a "canonical".            │
// │                                                                      │
// │ NEW CODE: prefer "canonical" phrasing. Don't add new "ingredient"-   │
// │ named exports. The 80+ existing callsites are kept for compat; mass- │
// │ renaming would be a large diff for low payoff.                       │
// ╰──────────────────────────────────────────────────────────────────────╯
//
// ╭──────────────────────────────────────────────────────────────────────╮
// │ TWO-MAP ARCHITECTURE — ALSO IMPORTANT                                │
// │                                                                      │
// │ This file ships TWO separate maps because they serve two different   │
// │ load profiles:                                                       │
// │                                                                      │
// │ 1. INGREDIENTS (exported, on-deck)                                   │
// │    Tight identity + structural data: id, name, emoji, parentId,      │
// │    units, defaultUnit, density_g_per_ml, preferredUnit, measuredIn,  │
// │    estCentsPerBase, nutrition. ~400 entries.                         │
// │                                                                      │
// │    MUST be available synchronously at module load — unit math,       │
// │    recipe matching, classification, and pricing run on every render. │
// │    Lean on purpose so ALL ~400 canonicals load without dragging      │
// │    rich prose along.                                                 │
// │                                                                      │
// │ 2. INGREDIENT_INFO (module-private, JS map)                          │
// │    Editorial / contextual / storage prose: description,              │
// │    flavorProfile, prepTips, recipes, winePairings, culturalNotes,    │
// │    storage (incl. shelfLife / shelfLifeOpened), substitutions,       │
// │    pairs, allergens, diet, origin, sourcing, seasonality.            │
// │                                                                      │
// │    Mirrors the schema of the Supabase `ingredient_info` table        │
// │    (which IS the production source of truth — admin-curated, AI-     │
// │    enriched, lazily fetched at app boot via useIngredientInfo).      │
// │    INGREDIENT_INFO acts as an OFFLINE FALLBACK so pre-DB-fetch       │
// │    flows + onboarding seeding still have something to read.          │
// │                                                                      │
// │ HOW TO READ STORAGE / SUBSTITUTIONS / DESCRIPTION:                   │
// │   ALWAYS go through getIngredientInfo(ingredient, dbOverride):       │
// │     const info = getIngredientInfo(findIngredient(id), getDbInfo(id))│
// │     info.storage?.shelfLife?.fridge   ← correct                      │
// │                                                                      │
// │   NEVER read .storage / .description / .substitutions directly off   │
// │   findIngredient(id) — those fields don't live there. The bundled    │
// │   INGREDIENTS entries don't carry storage/prose; they're in          │
// │   INGREDIENT_INFO. You'll silently get undefined and the feature     │
// │   will look broken (this bit us once already, see commit a725138).   │
// │                                                                      │
// │ HOW TO READ NUTRITION / UNITS / CATEGORY:                            │
// │   findIngredient(id) directly — those fields ARE on INGREDIENTS.     │
// │                                                                      │
// │ HOW TO ADD STORAGE DATA TO A CANONICAL:                              │
// │   Edit INGREDIENT_INFO[id].storage in this file (offline fallback)   │
// │   AND/OR upsert a row into the Supabase ingredient_info table        │
// │   (production source of truth). Admin Panel handles the latter.      │
// ╰──────────────────────────────────────────────────────────────────────╯
//
// Every pantry item and every recipe ingredient that needs pantry tracking
// points at an `id` in this list. That gives us:
//   - one source of truth for name + emoji + category
//   - a defined set of units per ingredient (so the user isn't guessing "is
//     sticks spelled 'stick' or 'sticks'?")
//   - unit conversion (`toBase` factor) so the recipe's "2 tbsp butter" can
//     be checked against the pantry's "1.5 sticks butter".
//
// Base units by convention:
//   - mass:    grams (g)
//   - volume:  milliliters (ml)
//   - count:   units (eggs, cloves, slices, lemons)
//
// Add a new ingredient here and it's automatically available in the AddItem
// modal and can be referenced from any recipe.
//
// ─────────────────────────────────────────────────────────────────────────
// Rich display metadata lives in INGREDIENT_INFO (further down in the
// file). Each ingredient can optionally carry:
//
//   ── Cooking-centric ──────────────────────────────────────────────────
//   description:   short prose ("Italian hard cheese aged 12–36 months…")
//   flavorProfile: compact taste descriptor ("Nutty, savory, umami…")
//   prepTips:      one-line kitchen advice ("Grate just before use")
//   storage: {
//     location:     "fridge" | "pantry" | "freezer"      (primary / default)
//     shelfLifeDays:number (approximate; use the loosely-safe number)
//     shelfLife: {                                        (v2 — optional)
//       fridge:  number | null,
//       freezer: number | null,
//       pantry:  number | null,
//     }
//     tips:         "…"    (optional, e.g. "Wrap in parchment, not plastic")
//     spoilageSigns:"Slimy, sour smell, mold on top — toss." (v2, optional)
//     freezable:    boolean                                (v2, optional)
//     freezeNotes:  "In portioned zip-top bags, 6 months." (v2, optional)
//     prepYield: {                                         (v2, optional)
//       whole:  "1 medium onion",
//       yields: "~1 cup diced",
//     }
//   }
//   substitutions: [
//     // tier is optional; defaults to "direct" when omitted. (v2)
//     { id: "pecorino", tier: "direct"|"emergency"|"dietary"|"pro", note: "…" }, …
//   ]
//   irreplaceable:     boolean    (v2 — true when no real sub exists)
//   irreplaceableNote: "Saffron is saffron. No sub works."   (v2, optional)
//   pairs:         [otherIngredientId, …]  — classic complements
//   clashes:       [otherIngredientId, …]  — classic conflicts (rare)
//
//   ── Flavor (structured, v2) ─────────────────────────────────────────
//   // flavorProfile (the one-line string) stays as-is. This object is for
//   // filtering, pairing suggestions, and "how it tastes when cooked".
//   flavor: {
//     primary:   [ "sweet" | "sour" | "salt" | "bitter" | "umami" | "fat" | "heat", … ]
//     intensity: "mild" | "moderate" | "strong" | "aggressive"
//     heatChange: {
//       raw:     "grassy, sharp",
//       cooked:  "nutty, caramelized",
//       charred: "bitter with smoky depth",
//     }
//   }
//
//   ── Nutrition (approximate, per reference serving) ───────────────────
//   nutrition: {
//     per:       "100g" | "serving" | "count"
//     kcal, protein_g, fat_g, carb_g
//     fiber_g, sodium_mg  (both optional)
//   }
//
//   ── Social / cultural ────────────────────────────────────────────────
//   origin:        "Lazio, Italy"
//   culturalNotes: "…"          — the story, history, tradition
//   winePairings:  ["Chardonnay", …]
//   recipes:       ["Cacio e Pepe", …]  — classic preparations
//
//   ── Sourcing / allergens / seasonality ───────────────────────────────
//   allergens:     ["dairy","gluten","treenut","peanut","egg","shellfish",
//                   "soy","sesame","sulfites","mustard","fish"]
//   // v2: optional specificity — which tree nuts, which shellfish, etc.
//   allergenDetail:{ treenut: ["almond","hazelnut"], shellfish: ["shrimp"] }
//   seasonality: {
//     peakMonthsN:        [5,6,7,8]    — northern-hemisphere peak (1..12)
//     peakMonthsS:        [11,12,1,2]  — southern-hemisphere peak  (v2)
//     yearRound:          boolean       (v2)
//     preservedAvailable: boolean       (v2 — canned/frozen works year-round)
//   }
//   sourcing:      "Look for grass-fed / wild-caught / …"
//
//   ── Dietary / lifestyle flags (v2) ───────────────────────────────────
//   diet: {
//     vegan:      boolean,
//     vegetarian: boolean,     (vegan implies vegetarian)
//     keto:       boolean,     (<5g net carbs per reference serving)
//     halal:      boolean,     (default true; false for pork/alcohol/…)
//     kosher:     "meat" | "dairy" | "pareve" | "nonkosher",
//     fodmap:     "low" | "moderate" | "high",
//     nightshade: boolean,     (tomato, pepper, eggplant, potato)
//     allium:     boolean,     (onion/garlic family)
//   }
//
//   ── Market intelligence (structured, v2) ─────────────────────────────
//   // sourcing (prose) stays as-is. `market` is for filters and chips.
//   market: {
//     priceTier:     "budget" | "moderate" | "premium" | "luxury",
//     availability:  "supermarket" | "specialty" | "online" | "seasonal",
//     organicCommon: boolean,
//     qualityMatters:boolean,   (cheap version ruins the dish)
//     qualityNote:   "Generic parmesan doesn't behave like Parmigiano.",
//   }
//
//   ── Skill + course links (v2) ────────────────────────────────────────
//   skillDev: {
//     skills:              [skillId, …]   — references src/data/index.js skills
//     difficulty:          "easy" | "moderate" | "technical" | "expert",
//     proFromScratch:      boolean,       — can be made at home by a pro
//     fromScratchRecipeId: "homemade_stock" | null,
//   }
//
// Every field is optional — populated fields render their own section on
// the IngredientCard; empty fields fall back to SUBCATEGORY_INFO defaults
// (cheese subcategories especially), then to nothing.
// ─────────────────────────────────────────────────────────────────────────

// Shared unit ladder for the long tail of cheese varieties. Specialty
// cheeses we track individually (parmesan wedges, mozz balls, feta blocks,
// cream cheese bricks, goat cheese logs) keep their bespoke units above —
// this preset is just the "here's a hunk of cheese, price it by weight"
// shape used by everything in `cheeseMembers(…)` below.
const CHEESE_STANDARD_UNITS = [
  { id: "oz",    label: "oz",     toBase: 28.35 },
  { id: "lb",    label: "lb",     toBase: 453.6 },
  { id: "wedge", label: "wedges", toBase: 226 },  // ~8oz wedge
  { id: "g",     label: "g",      toBase: 1 },
];

// Hard-aged cheeses (Parmigiano-Reggiano, Pecorino, Asiago, Grana
// Padano, aged Gouda, etc.) almost always show up in recipes as
// GRATED amounts ("1/2 cup grated parm", "2 tbsp grated pecorino").
// Soft cheeses don't grate, so this variant is scoped to the
// "Hard / Aged" subcategory only — bleu / brie / fresh members stay
// on CHEESE_STANDARD_UNITS so a "1 cup grated brie" prompt doesn't
// look reasonable in the picker.
const HARD_CHEESE_UNITS = [
  ...CHEESE_STANDARD_UNITS,
  { id: "cup",  label: "cups grated", toBase: 90 },  // ~90g loosely grated
  { id: "tbsp", label: "tbsp grated", toBase: 5 },
  { id: "tsp",  label: "tsp grated",  toBase: 1.7 },
];

// Compact builder for the cheese hub. Tuples are:
//   [id, name, shortName, defaultUnit, estCentsPerGram]
// The `subcategory` label is threaded onto every member so the Cheese
// drill-down can group them by tradition (Fresh / Soft Ripened / Blue /…).
// estCentsPerGram lets the pantry estimate a $ total when the user adds
// without a receipt — a rough retail-per-lb ÷ 453.6.
function cheeseMembers(subcategory, rows) {
  // Hard-aged members get the grated-units variant so recipes can
  // pair "1/2 cup grated parmigiano" without falling back to the hub.
  const units = subcategory === "Hard / Aged" ? HARD_CHEESE_UNITS : CHEESE_STANDARD_UNITS;
  return rows.map((row) => {
    // Tuple shape: [id, name, shortName, defaultUnit, estCentsPerGram, searchAliases?]
    // searchAliases is an OPTIONAL 6th element — added so gjetost
    // can be findable as "brunost" / "geitost", aged_gouda as
    // "old amsterdam", etc. Without aliases, niche-name canonicals
    // are invisible to users who only know one of the common names.
    const [id, name, shortName, defaultUnit, estCentsPerBase, searchAliases] = row;
    const member = {
      id, name, shortName, subcategory,
      parentId: "cheese_hub", emoji: "🧀", category: "dairy",
      units,
      defaultUnit,
      estCentsPerBase,
    };
    if (Array.isArray(searchAliases) && searchAliases.length > 0) {
      member.searchAliases = searchAliases;
    }
    return member;
  });
}

export const INGREDIENTS = [
  // ── dairy / eggs ────────────────────────────────────────────────────────
  {
    id: "eggs", name: "Eggs", emoji: "🥚", parentId: "egg_hub", category: "dairy",
    // Singular "egg" — recipe rows often say "1 egg" rather than "1 eggs".
    searchAliases: ["egg"],
    units: [
      { id: "count", label: "eggs",   toBase: 1 },
      { id: "dozen", label: "dozen",  toBase: 12 },
    ],
    defaultUnit: "count",
    estCentsPerBase: 45, // ~$5.40/dozen
    // Per one large egg (~50g). Count-based: scaleFactor multiplies by
    // the number of eggs, which is the natural recipe unit.
    nutrition: { per: "count", kcal: 72, protein_g: 6, fat_g: 5, carb_g: 0.4, sodium_mg: 71 },
  },
  {
    // Stick equivalence (US supermarket butter — "quarter-pound stick"):
    //   1 stick = 8 tbsp = 4 oz = 113.4 g = 1/2 cup
    // The ladder factors are anchored on those exact identities so
    // cross-unit conversions inside the ladder stay integer-clean —
    // e.g. 2 sticks → 1 cup (not 0.997 cups). `aliases` attaches the
    // plural form at the ladder entry instead of in the global alias
    // map, since "stick" as a unit is butter-specific — parmesan
    // sticks, cinnamon sticks, and celery sticks carry different
    // weights and must not collide.
    id: "butter", name: "Unsalted Butter", emoji: "🧈", category: "dairy", subtype: "butter",
    // Bare-token alias: AI recipe rows commonly emit `item: "butter"`
    // without the "Unsalted" qualifier. Without this, inferCanonicalFromName
    // returns null for the bare token, and surfaces that fall back to
    // name-inference (UnitPicker, pairing, autofill) get a null
    // canonical and silently degrade — UnitPicker drops to a one-option
    // sheet showing only the parsed currentUnit.
    searchAliases: ["butter"],
    units: [
      { id: "stick", label: "sticks", toBase: 113.4, aliases: ["sticks"] },
      { id: "tbsp",  label: "tbsp",   toBase: 14.175 },
      { id: "tsp",   label: "tsp",    toBase: 4.725 },  // 1 tbsp / 3
      { id: "cup",   label: "cups",   toBase: 226.8 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "lb",    label: "lb",     toBase: 453.592 },
      { id: "block", label: "blocks", toBase: 453.592, aliases: ["blocks"] },
      { id: "tub",   label: "tubs",   toBase: 454,     aliases: ["tubs"] },
      { id: "g",     label: "g",      toBase: 1 },
    ],
    defaultUnit: "stick",
    preferredUnit: { us: "tbsp", metric: "g" },
    measuredIn:    { us: "oz",   metric: "g" },
    estCentsPerBase: 1.5, // ~$6.80/lb
    nutrition: { per: "100g", kcal: 717, protein_g: 0.9, fat_g: 81, carb_g: 0.1, sodium_mg: 11 },
  },
  {
    id: "milk", name: "Whole Milk", shortName: "Whole",
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    // Bare-token alias: recipes saying "1 cup milk" should resolve to
    // whole milk by default, not fail name-inference. Subtype-specific
    // milks (2%, skim, buttermilk) carry their own qualifying tokens.
    searchAliases: ["milk"],
    units: [
      { id: "gallon",      label: "gallons",      toBase: 3785 },
      { id: "half_gallon", label: "half gallons", toBase: 1893 },
      { id: "quart",       label: "quarts",       toBase: 946 },
      { id: "pint",        label: "pints",        toBase: 473 },
      { id: "cup",         label: "cups",         toBase: 240 },
      { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
      { id: "tbsp",        label: "tbsp",         toBase: 15 },
      { id: "ml",          label: "ml",           toBase: 1 },
    ],
    defaultUnit: "gallon",
    preferredUnit: { us: "cup", metric: "ml" },
    estCentsPerBase: 0.12, // ~$4.50/gallon
  },
  {
    id: "milk_2pct", name: "2% Milk", shortName: "2%",
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    units: [
      { id: "gallon",      label: "gallons",      toBase: 3785 },
      { id: "half_gallon", label: "half gallons", toBase: 1893 },
      { id: "quart",       label: "quarts",       toBase: 946 },
      { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
      { id: "cup",         label: "cups",         toBase: 240 },
    ],
    defaultUnit: "gallon",
    preferredUnit: { us: "cup", metric: "ml" },
    estCentsPerBase: 0.12,
  },
  {
    id: "milk_skim", name: "Skim Milk", shortName: "Skim",
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    units: [
      { id: "gallon",      label: "gallons",      toBase: 3785 },
      { id: "half_gallon", label: "half gallons", toBase: 1893 },
      { id: "quart",       label: "quarts",       toBase: 946 },
      { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
    ],
    defaultUnit: "gallon",
    preferredUnit: { us: "cup", metric: "ml" },
    estCentsPerBase: 0.12,
  },
  {
    id: "buttermilk", name: "Buttermilk", shortName: "Buttermilk",
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    // Ladder must include `{id: "ml", toBase: 1}` so the family
    // detector (getUnitPickerOptions, isMassLadder, etc.) can read
    // the volume anchor. Without it, the picker falls into the
    // "couldn't detect a mass or volume family" path.
    units: [
      { id: "gallon",      label: "gallons",      toBase: 3785 },
      { id: "half_gallon", label: "half gallons", toBase: 1893 },
      { id: "quart",       label: "quarts",       toBase: 946 },
      { id: "pint",        label: "pints",        toBase: 473 },
      { id: "cup",         label: "cups",         toBase: 240 },
      { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
      { id: "tbsp",        label: "tbsp",         toBase: 15 },
      { id: "tsp",         label: "tsp",          toBase: 5 },
      { id: "ml",          label: "ml",           toBase: 1 },
      { id: "l",           label: "l",            toBase: 1000 },
    ],
    defaultUnit: "quart",
    preferredUnit: { us: "cup", metric: "ml" },
  },
  {
    id: "heavy_cream", name: "Heavy Cream", shortName: "Heavy",
    aliases: ["heavy_whipping_cream"],
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    units: [
      { id: "gallon",      label: "gallons",      toBase: 3785 },
      { id: "half_gallon", label: "half gallons", toBase: 1893 },
      { id: "quart",       label: "quarts",       toBase: 946 },
      { id: "pint",        label: "pints",        toBase: 473 },
      { id: "cup",         label: "cups",         toBase: 240 },
      { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
      { id: "tbsp",        label: "tbsp",         toBase: 15 },
      { id: "tsp",         label: "tsp",          toBase: 5 },
      { id: "ml",          label: "ml",           toBase: 1 },
      { id: "l",           label: "l",            toBase: 1000 },
    ],
    defaultUnit: "pint",
    preferredUnit: { us: "cup", metric: "ml" },
  },
  {
    id: "parmesan", name: "Parmesan", shortName: "Parmesan",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy", subtype: "cheese",
    units: [
      { id: "oz",    label: "oz",          toBase: 28.35 },
      { id: "wedge", label: "wedges",      toBase: 226 }, // ~8oz wedge
      { id: "cup",   label: "cups grated", toBase: 90 },
      { id: "g",     label: "g",           toBase: 1 },
      { id: "lb",    label: "lb",          toBase: 453.6 },
    ],
    defaultUnit: "oz",
    estCentsPerBase: 4.8, // ~$22/lb
  },
  {
    id: "pecorino", name: "Pecorino Romano", shortName: "Pecorino",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy",
    units: [
      { id: "oz",    label: "oz",          toBase: 28.35 },
      { id: "wedge", label: "wedges",      toBase: 226 },
      { id: "cup",   label: "cups grated", toBase: 90 },
      { id: "g",     label: "g",           toBase: 1 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "gruyere", name: "Gruyère", shortName: "Gruyère",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy",
    units: [
      { id: "oz",    label: "oz",          toBase: 28.35 },
      { id: "block", label: "blocks",      toBase: 226 },
      { id: "cup",   label: "cups grated", toBase: 90 },
      { id: "g",     label: "g",           toBase: 1 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "mozzarella", name: "Fresh Mozzarella", shortName: "Mozzarella",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy", subtype: "cheese",
    units: [
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "ball",  label: "balls", toBase: 226 }, // 8oz ball
      { id: "g",     label: "g",     toBase: 1 },
      { id: "lb",    label: "lb",    toBase: 453.6 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "cheddar", name: "Cheddar", shortName: "Cheddar",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy", subtype: "cheese",
    units: [
      { id: "oz",    label: "oz",          toBase: 28.35 },
      { id: "block", label: "blocks",      toBase: 226 },
      { id: "cup",   label: "cups grated", toBase: 113 },
      { id: "slice", label: "slices",      toBase: 21 },
      { id: "lb",    label: "lb",          toBase: 453.6 },
      { id: "g",     label: "g",           toBase: 1 },
    ],
    defaultUnit: "oz",
    nutrition: { per: "100g", kcal: 404, protein_g: 23, fat_g: 33, carb_g: 1.3, sodium_mg: 621 },
  },
  {
    id: "feta", name: "Feta", shortName: "Feta",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy",
    units: [
      { id: "oz",    label: "oz",   toBase: 28.35 },
      { id: "block", label: "blocks", toBase: 170 }, // 6oz tub
      { id: "g",     label: "g",    toBase: 1 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "goat_cheese", name: "Goat Cheese", shortName: "Goat",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy",
    units: [
      { id: "oz",  label: "oz",  toBase: 28.35 },
      { id: "log", label: "logs", toBase: 113 }, // 4oz log
      { id: "g",   label: "g",   toBase: 1 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "cream_cheese", name: "Cream Cheese", shortName: "Cream",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy", subtype: "cheese",
    units: [
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "block", label: "blocks", toBase: 226 }, // 8oz brick
      { id: "tub",   label: "tubs",  toBase: 226 },
      { id: "tbsp",  label: "tbsp",  toBase: 14.5 },
      { id: "g",     label: "g",     toBase: 1 },
    ],
    defaultUnit: "oz",
    preferredUnit: { us: "oz", metric: "g" },
    nutrition: { per: "100g", kcal: 342, protein_g: 6, fat_g: 34, carb_g: 4.1, sodium_mg: 321 },
  },
  {
    id: "brie", name: "Brie", shortName: "Brie",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy",
    units: [
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "wheel", label: "wheels", toBase: 226 },
      { id: "wedge", label: "wedges", toBase: 85 },
    ],
    defaultUnit: "oz",
  },
  {
    // Catch-all for Alouette, Boursin, Rondelé, etc. — the stuff sold in a
    // small tub that doesn't fit the hard/semi-hard cheese buckets.
    id: "spreadable_cheese", name: "Spreadable Cheese", shortName: "Spreadable",
    parentId: "cheese_hub", emoji: "🧀", category: "dairy",
    units: [
      { id: "oz",   label: "oz",   toBase: 28.35 },
      { id: "tub",  label: "tubs", toBase: 198 }, // typical 6.5oz tub
      { id: "tbsp", label: "tbsp", toBase: 14 },
    ],
    defaultUnit: "oz",
    estCentsPerBase: 3.3, // ~$15/lb — fancy spreadables (Boursin, Alouette)
  },
  // Additional cheese_hub members — shared units preset, defined via helpers
  // below so we don't repeat the same oz/lb/wedge/g ladder 60 times. Keep
  // individual specialties (ricotta tubs, mozz balls) as-is above.
  // estCentsPerBase below is cents-per-gram. Rough retail math:
  //   $/lb ÷ 453.6 ≈ cents/g  (e.g. $10/lb ≈ 2.2, $20/lb ≈ 4.4, $30/lb ≈ 6.6)
  ...cheeseMembers("Fresh / Unaged", [
    ["burrata",         "Burrata",         "Burrata",         "oz",   3.1],
    ["ricotta",         "Ricotta",         "Ricotta",         "cup",  0.9],
    ["mascarpone",      "Mascarpone",      "Mascarpone",      "oz",   2.2],
    ["queso_fresco",    "Queso Fresco",    "Queso Fresco",    "oz",   1.3],
    ["paneer",          "Paneer",          "Paneer",          "oz",   1.5],
    ["fromage_blanc",   "Fromage Blanc",   "Fromage Blanc",   "oz",   1.8],
    ["quark",           "Quark",           "Quark",           "oz",   1.1],
    ["stracchino",      "Stracchino",      "Stracchino",      "oz",   3.3],
  ]),
  ...cheeseMembers("Soft Ripened", [
    ["camembert",       "Camembert",       "Camembert",       "oz",   2.6],
    ["coulommiers",     "Coulommiers",     "Coulommiers",     "oz",   3.5],
    ["brillat_savarin", "Brillat-Savarin", "Brillat-Savarin", "oz",   4.8],
    ["saint_andre",     "Saint-André",     "Saint-André",     "oz",   4.4],
    ["explorateur",     "Explorateur",     "Explorateur",     "oz",   5.3],
    ["humboldt_fog",    "Humboldt Fog",    "Humboldt Fog",    "oz",   6.2],
  ]),
  ...cheeseMembers("Semi-Soft", [
    ["havarti",         "Havarti",         "Havarti",         "oz",   1.8],
    ["fontina",         "Fontina",         "Fontina",         "oz",   2.4],
    ["muenster",        "Muenster",        "Muenster",        "oz",   1.3],
    ["taleggio",        "Taleggio",        "Taleggio",        "oz",   4.4],
    ["port_salut",      "Port Salut",      "Port Salut",      "oz",   2.4],
    ["raclette",        "Raclette",        "Raclette",        "oz",   4.0],
    ["butterkase",      "Butterkäse",      "Butterkäse",      "oz",   2.0],
    ["tomme_savoie",    "Tomme de Savoie", "Tomme de Savoie", "oz",   3.5],
  ]),
  ...cheeseMembers("Washed Rind", [
    ["limburger",       "Limburger",       "Limburger",       "oz",   2.4],
    ["epoisses",        "Époisses",        "Époisses",        "oz",   6.6],
    ["langres",         "Langres",         "Langres",         "oz",   5.7],
    ["vacherin_mdor",   "Vacherin Mont d'Or","Vacherin MdO",   "oz",   7.7],
    ["stinking_bishop", "Stinking Bishop", "Stinking Bishop", "oz",   6.2],
  ]),
  ...cheeseMembers("Semi-Hard", [
    ["gouda",           "Gouda",           "Gouda",           "oz",   1.8],
    ["edam",            "Edam",            "Edam",            "oz",   2.0],
    ["comte",           "Comté",           "Comté",           "oz",   4.8],
    ["jarlsberg",       "Jarlsberg",       "Jarlsberg",       "oz",   2.6],
    ["manchego",        "Manchego",        "Manchego",        "oz",   4.0],
    ["provolone",       "Provolone",       "Provolone",       "oz",   1.8],
    ["colby",           "Colby",           "Colby",           "oz",   1.3],
    ["monterey_jack",   "Monterey Jack",   "Monterey Jack",   "oz",   1.3],
    ["emmental",        "Emmental",        "Emmental",        "oz",   2.9],
  ]),
  ...cheeseMembers("Hard / Aged", [
    ["parmigiano",      "Parmigiano-Reggiano", "Parm-Reggiano","oz",  4.8],
    ["aged_cheddar",    "Aged Cheddar",    "Aged Cheddar",    "oz",   3.1],
    ["aged_gouda",      "Aged Gouda",      "Aged Gouda",      "oz",   4.4],
    ["asiago",          "Asiago",          "Asiago",          "oz",   3.1],
    ["grana_padano",    "Grana Padano",    "Grana Padano",    "oz",   4.0],
    ["mimolette",       "Mimolette",       "Mimolette",       "oz",   4.4],
    ["piave",           "Piave",           "Piave",           "oz",   4.8],
    ["sbrinz",          "Sbrinz",          "Sbrinz",          "oz",   5.3],
    // Cotija / Queso Añejo are aged, dry, crumbly Mexican cheeses —
    // physically the same grate-and-sprinkle role as parmigiano. They
    // need the Hard/Aged unit ladder (cup/tbsp/tsp grated) so recipes
    // like "1/4 cup crumbled cotija" resolve cleanly.
    ["cotija",          "Cotija",          "Cotija",          "oz",   1.6,
      ["queso cotija", "mexican parmesan", "anejo"]],
    ["queso_anejo",     "Queso Añejo",     "Queso Añejo",     "oz",   1.6,
      ["queso anejo", "anejo enchilado", "aged mexican cheese"]],
  ]).map((ing) => {
    // Per-cheese density overrides for the volume↔mass bridge. Used
    // when a recipe asks for a volume amount and the ladder doesn't
    // have that exact unit. Values are the GRATED-loose equivalent
    // (the realistic state when a recipe writes "30 ml of parm")
    // since hard-aged cheese is almost never asked for in volume in
    // wedge form. Sources: USDA FoodData Central + retail nutrition
    // panels. Whole-wedge density is ~1.10 g/ml — using grated here
    // because the ladder's `cup` entry already encodes 90g per cup
    // (= 0.375 g/ml grated) and the bridge should agree with the
    // ladder.
    const grated = {
      parmigiano:    0.375,  // 1 cup loose grated ≈ 90g
      grana_padano:  0.375,  // sister cheese, same physical structure
      pecorino:      0.375,  // sheep-milk hard, similar grate behavior
      asiago:        0.36,   // slightly less dense when grated
      aged_cheddar:  0.45,   // softer crumb, denser grate
      aged_gouda:    0.42,
      cotija:        0.40,   // crumbled, drier than parm; 1 cup ≈ 96g
      queso_anejo:   0.40,
    };
    if (grated[ing.id] != null) return { ...ing, density_g_per_ml: grated[ing.id] };
    return ing;
  }),
  ...cheeseMembers("Blue", [
    ["gorgonzola",      "Gorgonzola",      "Gorgonzola",      "oz",   3.1],
    ["roquefort",       "Roquefort",       "Roquefort",       "oz",   5.3],
    ["stilton",         "Stilton",         "Stilton",         "oz",   4.4],
    ["maytag_blue",     "Maytag Blue",     "Maytag Blue",     "oz",   4.8],
    ["cabrales",        "Cabrales",        "Cabrales",        "oz",   6.2],
    ["bleu_dauvergne",  "Bleu d'Auvergne", "Bleu d'Auvergne", "oz",   4.0],
    ["cambozola",       "Cambozola",       "Cambozola",       "oz",   3.1],
    ["cashel_blue",     "Cashel Blue",     "Cashel Blue",     "oz",   4.4],
  ]),
  ...cheeseMembers("Smoked", [
    ["smoked_gouda",    "Smoked Gouda",    "Smoked Gouda",    "oz",   2.2],
    ["smoked_mozz",     "Smoked Mozzarella","Smoked Mozz",    "oz",   2.2],
    ["scamorza",        "Scamorza",        "Scamorza",        "oz",   2.4],
    ["smoked_cheddar",  "Smoked Cheddar",  "Smoked Cheddar",  "oz",   2.2],
    ["idiazabal",       "Idiazábal",       "Idiazábal",       "oz",   4.8],
  ]),
  ...cheeseMembers("Alpine", [
    ["appenzeller",     "Appenzeller",     "Appenzeller",     "oz",   4.4],
    ["beaufort",        "Beaufort",        "Beaufort",        "oz",   6.2],
    ["abondance",       "Abondance",       "Abondance",       "oz",   4.8],
    ["vacherin_frib",   "Vacherin Fribourgeois","Vacherin Frib","oz",  4.8],
  ]),
  ...cheeseMembers("American Originals", [
    ["pepper_jack",     "Pepper Jack",     "Pepper Jack",     "oz",   1.3,
      ["pepperjack", "pepper-jack", "jalapeno jack", "jalapeño jack", "monterey jack pepper", "spicy jack"]],
    ["monterey_jack",   "Monterey Jack",   "Monterey Jack",   "oz",   1.4,
      ["jack cheese", "jack", "monterey", "white jack"]],
    ["colby",           "Colby",           "Colby",           "oz",   1.4,
      ["colby cheese"]],
    ["colby_jack",      "Colby Jack",      "Colby Jack",      "oz",   1.4,
      ["cojack", "co-jack", "colby-jack", "marbled cheese"]],
    ["brick_cheese",    "Brick",           "Brick",           "oz",   1.8],
    ["teleme",          "Teleme",          "Teleme",          "oz",   3.3],
    ["cougar_gold",     "Cougar Gold",     "Cougar Gold",     "oz",   6.2],
  ]),
  ...cheeseMembers("Mexican / Latin American", [
    // Cotija and Queso Añejo live in "Hard / Aged" above — they're
    // grateable Mexican-parm style cheeses and need the grated unit
    // ladder. The melting/fresh styles stay here.
    ["oaxaca",          "Oaxaca",          "Oaxaca",          "oz",   1.4,
      ["queso oaxaca", "quesillo", "mexican mozzarella", "string cheese mexican"]],
    ["queso_blanco",    "Queso Blanco",    "Queso Blanco",    "oz",   1.3,
      ["queso fresco blanco", "white cheese mexican"]],
    ["queso_quesadilla","Queso Quesadilla","Quesadilla",      "oz",   1.4,
      ["quesadilla cheese", "melting cheese mexican"]],
    ["asadero",         "Asadero",         "Asadero",         "oz",   1.4,
      ["queso asadero", "chihuahua cheese", "mexican melting"]],
    ["panela",          "Panela",          "Panela",          "oz",   1.3,
      ["queso panela", "mexican basket cheese"]],
    ["chihuahua_cheese","Chihuahua",       "Chihuahua",       "oz",   1.6,
      ["queso chihuahua", "queso menonita"]],
  ]),
  ...cheeseMembers("Scandinavian / Specialty", [
    // Gjetost (also Brunost / Geitost) — Norwegian whey cheese,
    // brown, slightly sweet, dense. Sold in waxed bricks.
    // searchAliases let users find it via any of its alternate
    // names; without these the picker is blind to common Norwegian
    // / English variants.
    ["gjetost",         "Gjetost",         "Gjetost",         "oz",   4.0,
      ["brunost", "geitost", "norwegian_brown_cheese", "brown_cheese"]],
    // Other Scandinavian specialties — add here as observed.
    // ["nokkelost", "Nøkkelost", "Nøkkelost", "oz", 3.5],
    // ["danbo",     "Danbo",     "Danbo",     "oz", 2.6],
    // ["fontina",   "Fontina",   "Fontina",   "oz", 3.1],  // (Italian, not Scandinavian — already elsewhere)
  ]),
  {
    id: "yogurt", name: "Plain Yogurt", shortName: "Plain",
    parentId: "yogurt_hub", emoji: "🥛", category: "dairy", subtype: "yogurt",
    units: [
      { id: "oz",    label: "oz",      toBase: 28.35 },
      { id: "cup",   label: "cups",    toBase: 245 },
      { id: "tub",   label: "tubs",    toBase: 907 }, // 32oz tub
      { id: "cup_pk",label: "cups (6oz)", toBase: 170 },
      { id: "g",     label: "g",       toBase: 1 },
    ],
    defaultUnit: "oz",
    preferredUnit: { us: "cup", metric: "g" },
    nutrition: { per: "100g", kcal: 59, protein_g: 3.5, fat_g: 3.3, carb_g: 4.7, sodium_mg: 46 },
  },
  {
    id: "greek_yogurt", name: "Greek Yogurt", shortName: "Greek",
    parentId: "yogurt_hub", emoji: "🥛", category: "dairy", subtype: "yogurt",
    units: [
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "cup",   label: "cups",   toBase: 245 },
      { id: "tub",   label: "tubs",   toBase: 907 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "sour_cream", name: "Sour Cream", emoji: "🥛", category: "dairy", subtype: "sour_cream",
    parentId: "cream_hub",
    units: [
      { id: "oz",   label: "oz",   toBase: 28.35 },
      { id: "tub",  label: "tubs", toBase: 454 }, // 16oz tub
      { id: "tbsp", label: "tbsp", toBase: 14.5 },
      { id: "cup",  label: "cups", toBase: 230 },
    ],
    defaultUnit: "oz",
    preferredUnit: { us: "tbsp", metric: "g" },
  },
  {
    id: "cottage_cheese", name: "Cottage Cheese", emoji: "🥛", parentId: "cheese_hub", category: "dairy",
    units: [
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "tub", label: "tubs", toBase: 454 },
      { id: "cup", label: "cups", toBase: 225 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "half_and_half", name: "Half & Half", shortName: "Half & Half",
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    units: [
      { id: "quart", label: "quarts", toBase: 946 },
      { id: "pint",  label: "pints",  toBase: 473 },
      { id: "cup",   label: "cups",   toBase: 240 },
      { id: "fl_oz", label: "fl oz",  toBase: 29.57 },
      { id: "tbsp",  label: "tbsp",   toBase: 15 },
    ],
    defaultUnit: "quart",
    preferredUnit: { us: "cup", metric: "ml" },
  },
  {
    id: "oat_milk", name: "Oat Milk", shortName: "Oat",
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    units: [
      { id: "half_gallon", label: "half gallons", toBase: 1893 },
      { id: "quart",       label: "quarts",       toBase: 946 },
      { id: "carton",      label: "cartons",      toBase: 1420 }, // 48oz
      { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
      { id: "cup",         label: "cups",         toBase: 240 },
    ],
    defaultUnit: "half_gallon",
  },
  {
    id: "almond_milk", name: "Almond Milk", shortName: "Almond",
    parentId: "milk_hub", emoji: "🥛", category: "dairy",
    units: [
      { id: "half_gallon", label: "half gallons", toBase: 1893 },
      { id: "quart",       label: "quarts",       toBase: 946 },
      { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
      { id: "cup",         label: "cups",         toBase: 240 },
    ],
    defaultUnit: "half_gallon",
  },

  // ── meat / pork / seafood ───────────────────────────────────────────────
  {
    // `chicken` is the BASE canonical (the species). Whether a
    // specific pantry row is "whole," "ground," "diced," etc. is
    // carried on pantry_items.state (a separate axis per CLAUDE.md
    // identity hierarchy). Previously the display read "Chicken
    // (whole)" which baked state into the name; shortName is now
    // empty so the registry isn't lying about its role.
    id: "chicken", name: "Chicken", shortName: null,
    parentId: "chicken_hub", emoji: "🍗", category: "meat", subtype: "chicken_cut",
    // Full mass ladder with a count entry — count defaults to 200g
    // (middle of USDA 170–225g boneless-skinless breast range) but
    // cut-specific weights from CUT_WEIGHTS_G override this via
    // effectiveCountWeightG when the pantry row carries a cut value
    // (thigh ≈ 120g, wing ≈ 80g, tenderloin ≈ 45g). g:1 anchor
    // makes this a proper mass ladder so isMassLadder-gated paths
    // light up (scaleFactor, countWeightG override).
    units: [
      { id: "count", label: "pieces", toBase: 200 },
      { id: "g",     label: "g",      toBase: 1 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "kg",    label: "kg",     toBase: 1000 },
    ],
    defaultUnit: "lb",
    // Americans think "2 chicken breasts" but buy by weight.
    // Europeans weigh and cook by gram. Each side keeps its idiom.
    preferredUnit: { us: "count", metric: "g" },
  },
  {
    // Cut-based units feel more natural for recipes ("4 breasts") than weight.
    // Average boneless/skinless breast ~200g, but we expose lb/oz too for the
    // weight-minded and for items the user bought by weight (family packs).
    id: "chicken_breast", name: "Chicken Breast", shortName: "Breast",
    parentId: "chicken_hub", emoji: "🍗", category: "meat",
    units: [
      { id: "count", label: "breasts", toBase: 200 },
      { id: "lb",    label: "lb",      toBase: 453.6 },
      { id: "oz",    label: "oz",      toBase: 28.35 },
      { id: "kg",    label: "kg",      toBase: 1000 },
      { id: "g",     label: "g",       toBase: 1 },
    ],
    defaultUnit: "count",
    // Per 100g raw boneless/skinless breast.
    nutrition: { per: "100g", kcal: 165, protein_g: 31, fat_g: 3.6, carb_g: 0, sodium_mg: 74 },
  },
  {
    id: "chicken_thigh", name: "Chicken Thighs", shortName: "Thighs",
    aliases: ["chicken_thighs"],
    parentId: "chicken_hub", emoji: "🍗", category: "meat",
    units: [
      { id: "count", label: "thighs", toBase: 120 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
    ],
    defaultUnit: "count",
  },
  {
    id: "chicken_leg", name: "Chicken Legs", shortName: "Legs",
    parentId: "chicken_hub", emoji: "🍗", category: "meat",
    units: [
      { id: "count", label: "legs", toBase: 150 },
      { id: "lb",    label: "lb",   toBase: 453.6 },
      { id: "oz",    label: "oz",   toBase: 28.35 },
    ],
    defaultUnit: "count",
  },
  {
    id: "chicken_wing", name: "Chicken Wings", shortName: "Wings",
    aliases: ["chicken_wings"],
    parentId: "chicken_hub", emoji: "🍗", category: "meat",
    units: [
      { id: "count", label: "wings", toBase: 50 },
      { id: "lb",    label: "lb",    toBase: 453.6 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
    ],
    defaultUnit: "count",
  },
  {
    // Tenderloin is the strip of muscle attached to the underside of
    // the breast. Sold separately (and named differently — "chicken
    // tenders") often enough that it earns its own canonical rather
    // than riding on chicken_breast with a state modifier. Pre-fix,
    // "tenderloin" lived in MEAT_STATES which conflated cut (anatomy)
    // with state (preparation); this is the cut half of that split.
    id: "chicken_tenderloin", name: "Chicken Tenderloins", shortName: "Tenderloins",
    parentId: "chicken_hub", emoji: "🍗", category: "meat",
    units: [
      { id: "count", label: "tenders", toBase: 50 }, // typical strip ~50g
      { id: "lb",    label: "lb",      toBase: 453.6 },
      { id: "oz",    label: "oz",      toBase: 28.35 },
      { id: "g",     label: "g",       toBase: 1 },
    ],
    defaultUnit: "lb",
    // Tenderloin has the same macro profile as breast — same muscle
    // group, just a different cut. USDA figures for raw
    // boneless/skinless chicken tenderloin.
    nutrition: { per: "100g", kcal: 119, protein_g: 25, fat_g: 1.3, carb_g: 0, sodium_mg: 58 },
  },
  {
    // Ground chicken — separate canonical because it's a distinct
    // grocery SKU (ground chicken breast packs vs ground thigh packs
    // exist, but the generic "ground chicken" is its own product).
    // Recipes routinely call for it specifically (chicken meatballs,
    // Thai larb, weeknight skillet dishes).
    id: "ground_chicken", name: "Ground Chicken", shortName: "Ground",
    parentId: "chicken_hub", emoji: "🍗", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
      { id: "g",  label: "g",  toBase: 1 },
    ],
    defaultUnit: "lb",
    nutrition: { per: "100g", kcal: 143, protein_g: 17, fat_g: 8, carb_g: 0, sodium_mg: 60 },
  },
  // Beef ───────────────────
  {
    id: "steak", name: "Steak", shortName: "Steak",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb",    label: "lb",       toBase: 453.6 },
      { id: "oz",    label: "oz",       toBase: 28.35 },
      { id: "count", label: "steaks",   toBase: 340 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "ribeye", name: "Ribeye", shortName: "Ribeye",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "count", label: "steaks", toBase: 340 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
    ],
    defaultUnit: "count",
  },
  {
    id: "ny_strip", name: "NY Strip", shortName: "NY Strip",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "count", label: "steaks", toBase: 340 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
    ],
    defaultUnit: "count",
  },
  {
    id: "sirloin", name: "Sirloin", shortName: "Sirloin",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "brisket", name: "Brisket", shortName: "Brisket",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
      { id: "kg", label: "kg", toBase: 1000 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "chuck_roast", name: "Chuck Roast", shortName: "Chuck",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },
  {
    // BASE canonical for all beef cuts / forms. Pantry rows carry
    // state (ground, whole, cubed, etc.) on the state column —
    // NOT as a separate canonical. Legacy `ground_beef` slug is
    // aliased below to this canonical + state='ground'.
    id: "beef", name: "Beef", shortName: "Beef",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    // Full mass ladder + count. Count default 340g = ~12 oz (typical
    // steak portion). Cut-specific weights in CUT_WEIGHTS_G override
    // per pantry row (brisket ≈ 2.3 kg, chuck roast ≈ 1.4 kg, etc.).
    units: [
      { id: "count", label: "pieces", toBase: 340 },
      { id: "g",     label: "g",      toBase: 1 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "kg",    label: "kg",     toBase: 1000 },
    ],
    defaultUnit: "lb",
    preferredUnit: { us: "lb", metric: "g" },
  },
  {
    // DEPRECATED — state ("ground") baked into canonical id. Kept
    // in the registry for back-compat so findIngredient continues
    // to resolve old pantry_items rows; CANONICAL_ALIASES routes
    // this slug to { base: "beef", state: "ground" }. Migration
    // 0060 rewrites existing rows to the base slug. New code
    // should NOT reference this id.
    id: "ground_beef", name: "Ground Beef", shortName: "Ground",
    parentId: "beef_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
      { id: "g",  label: "g",  toBase: 1 },
    ],
    defaultUnit: "lb",
    // 80/20 ground beef, raw. Leaner grinds drop kcal ~15% for 90/10.
    nutrition: { per: "100g", kcal: 254, protein_g: 26, fat_g: 15, carb_g: 0, sodium_mg: 78 },
  },

  // Lamb ───────────────────
  {
    // BASE canonical for all lamb cuts/forms — mirrors beef/pork. Lives
    // under its own lamb_hub (the old wweia_lamb→beef_hub stopgap is
    // retired now that this hub exists). Cuts are first-class canonicals
    // per the cuts-are-canonicals rule (chops/shank/leg/ground).
    id: "lamb", name: "Lamb", shortName: "Lamb",
    parentId: "lamb_hub", emoji: "🥩", category: "meat",
    searchAliases: ["lamb"],
    units: [
      { id: "count", label: "pieces", toBase: 340 },
      { id: "g",     label: "g",      toBase: 1 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "kg",    label: "kg",     toBase: 1000 },
    ],
    defaultUnit: "lb",
    preferredUnit: { us: "lb", metric: "g" },
    // Raw lamb, average of retail cuts (USDA reference).
    nutrition: { per: "100g", kcal: 282, protein_g: 25, fat_g: 21, carb_g: 0, sodium_mg: 72 },
  },
  {
    id: "ground_lamb", name: "Ground Lamb", shortName: "Ground",
    parentId: "lamb_hub", emoji: "🥩", category: "meat",
    searchAliases: ["ground lamb", "lamb mince", "minced lamb"],
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
      { id: "g",  label: "g",  toBase: 1 },
    ],
    defaultUnit: "lb",
    nutrition: { per: "100g", kcal: 282, protein_g: 25, fat_g: 20, carb_g: 0, sodium_mg: 75 },
  },
  {
    id: "lamb_chop", name: "Lamb Chops", shortName: "Chops",
    parentId: "lamb_hub", emoji: "🥩", category: "meat",
    searchAliases: ["lamb chop", "lamb chops", "rack of lamb", "lamb loin chop", "lamb rib chop"],
    units: [
      { id: "count", label: "chops", toBase: 110 },   // ~1 loin/rib chop
      { id: "g",     label: "g",     toBase: 1 },
      { id: "lb",    label: "lb",    toBase: 453.6 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
    ],
    defaultUnit: "count",
    nutrition: { per: "100g", kcal: 282, protein_g: 25, fat_g: 20, carb_g: 0, sodium_mg: 72 },
  },
  {
    id: "lamb_shank", name: "Lamb Shank", shortName: "Shank",
    parentId: "lamb_hub", emoji: "🥩", category: "meat",
    searchAliases: ["lamb shank", "lamb shanks"],
    units: [
      { id: "count", label: "shanks", toBase: 350 },
      { id: "g",     label: "g",      toBase: 1 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
    ],
    defaultUnit: "count",
    nutrition: { per: "100g", kcal: 201, protein_g: 27, fat_g: 10, carb_g: 0, sodium_mg: 70 },
  },
  {
    id: "lamb_leg", name: "Leg of Lamb", shortName: "Leg",
    parentId: "lamb_hub", emoji: "🥩", category: "meat",
    searchAliases: ["leg of lamb", "lamb leg", "lamb roast"],
    units: [
      { id: "g",  label: "g",  toBase: 1 },
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
      { id: "kg", label: "kg", toBase: 1000 },
    ],
    defaultUnit: "lb",
    nutrition: { per: "100g", kcal: 230, protein_g: 26, fat_g: 14, carb_g: 0, sodium_mg: 70 },
  },

  // Pork ───────────────────
  {
    // BASE canonical for all pork cuts / forms. See beef comment
    // above — same pattern. Aliased slugs: ground_pork.
    id: "pork", name: "Pork", shortName: "Pork",
    parentId: "pork_hub", emoji: "🥩", category: "meat",
    // Full mass ladder + count. Count default 170g = 6oz (bone-in
    // chop, common portion). CUT_WEIGHTS_G overrides per cut —
    // loin ≈ 1.4 kg, shoulder ≈ 1.8 kg, tenderloin ≈ 340 g, etc.
    units: [
      { id: "count", label: "pieces", toBase: 170 },
      { id: "g",     label: "g",      toBase: 1 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "kg",    label: "kg",     toBase: 1000 },
    ],
    defaultUnit: "lb",
    preferredUnit: { us: "lb", metric: "g" },
  },
  {
    id: "pork_chop", name: "Pork Chops", shortName: "Chops",
    parentId: "pork_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "count", label: "chops", toBase: 200 },
      { id: "lb",    label: "lb",    toBase: 453.6 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
    ],
    defaultUnit: "count",
  },
  {
    id: "pork_loin", name: "Pork Loin", shortName: "Loin",
    parentId: "pork_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "pork_shoulder", name: "Pork Shoulder", shortName: "Shoulder",
    parentId: "pork_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },
  {
    // DEPRECATED — see ground_beef. Aliased to { base: "pork",
    // state: "ground" }. Migration 0060 rewrites existing rows.
    id: "ground_pork", name: "Ground Pork", shortName: "Ground",
    parentId: "pork_hub", emoji: "🥩", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "sausage", name: "Sausage", shortName: "Sausage",
    parentId: "pork_hub", emoji: "🌭", category: "meat", subtype: "sausage",
    units: [
      { id: "count", label: "links", toBase: 85 },
      { id: "lb",    label: "lb",    toBase: 453.6 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
    ],
    defaultUnit: "count",
  },
  {
    id: "hot_dog", name: "Hot Dog", shortName: "Hot Dog",
    aliases: ["hot_dogs", "frank", "franks"],
    parentId: "pork_hub", emoji: "🌭", category: "meat",
    units: [
      { id: "count", label: "dogs", toBase: 50 },
      { id: "pack",  label: "packs", toBase: 400 }, // ~8 per pack
      { id: "lb",    label: "lb",    toBase: 453.6 },
    ],
    defaultUnit: "count",
  },
  {
    id: "bacon", name: "Bacon / Pancetta", shortName: "Bacon",
    parentId: "pork_hub", emoji: "🥓", category: "meat", subtype: "sausage",
    units: [
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "slice", label: "slices", toBase: 14 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "guanciale", name: "Guanciale", shortName: "Guanciale",
    parentId: "pork_hub", emoji: "🥓", category: "meat",
    units: [
      { id: "oz", label: "oz", toBase: 28.35 },
      { id: "g",  label: "g",  toBase: 1 },
      { id: "lb", label: "lb", toBase: 453.6 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "ham", name: "Ham", shortName: "Ham",
    parentId: "pork_hub", emoji: "🥩", category: "meat", subtype: "deli",
    units: [
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "slice", label: "slices", toBase: 15 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "prosciutto", name: "Prosciutto", shortName: "Prosciutto",
    parentId: "pork_hub", emoji: "🥓", category: "meat", subtype: "deli",
    units: [
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "slice", label: "slices", toBase: 10 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "salami", name: "Salami", shortName: "Salami",
    parentId: "pork_hub", emoji: "🥩", category: "meat", subtype: "deli",
    units: [
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "slice", label: "slices", toBase: 7 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
    ],
    defaultUnit: "oz",
  },

  // Turkey ───────────────────
  {
    // BASE canonical for all turkey cuts / forms. See beef comment
    // above — same pattern. Aliased slugs: ground_turkey.
    id: "turkey", name: "Turkey", shortName: "Turkey",
    parentId: "turkey_hub", emoji: "🦃", category: "meat", subtype: "deli",
    // Full mass ladder + count. Count default 3175g = ~7 lb (turkey
    // breast is the most common countable portion). CUT_WEIGHTS_G
    // overrides for thigh / leg / wing / whole bird.
    units: [
      { id: "count", label: "pieces", toBase: 3175 },
      { id: "g",     label: "g",      toBase: 1 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "kg",    label: "kg",     toBase: 1000 },
    ],
    defaultUnit: "lb",
    preferredUnit: { us: "lb", metric: "g" },
  },
  {
    // DEPRECATED — see ground_beef. Aliased to { base: "turkey",
    // state: "ground" }. Migration 0060 rewrites existing rows.
    id: "ground_turkey", name: "Ground Turkey", shortName: "Ground",
    parentId: "turkey_hub", emoji: "🦃", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "deli_turkey", name: "Sliced Turkey (deli)", shortName: "Sliced (deli)",
    parentId: "turkey_hub", emoji: "🦃", category: "meat",
    units: [
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "slice", label: "slices", toBase: 14 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "turkey_breast", name: "Turkey Breast", shortName: "Breast",
    parentId: "turkey_hub", emoji: "🦃", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },

  // Seafood ───────────────────
  {
    id: "salmon", name: "Salmon", shortName: "Salmon",
    parentId: "seafood_hub", emoji: "🐟", category: "meat",
    units: [
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "fillet",label: "fillets",toBase: 170 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "tuna", name: "Tuna", shortName: "Tuna",
    parentId: "seafood_hub", emoji: "🐟", category: "meat",
    units: [
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "steak", label: "steaks", toBase: 170 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "cod", name: "Cod", shortName: "Cod",
    parentId: "seafood_hub", emoji: "🐟", category: "meat",
    units: [
      { id: "fillet",label: "fillets",toBase: 170 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
    ],
    defaultUnit: "fillet",
  },
  {
    id: "tilapia", name: "Tilapia", shortName: "Tilapia",
    parentId: "seafood_hub", emoji: "🐟", category: "meat",
    units: [
      { id: "fillet",label: "fillets",toBase: 140 },
      { id: "lb",    label: "lb",     toBase: 453.6 },
    ],
    defaultUnit: "fillet",
  },
  {
    id: "shrimp", name: "Shrimp", shortName: "Shrimp",
    parentId: "seafood_hub", emoji: "🍤", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "scallops", name: "Scallops", shortName: "Scallops",
    parentId: "seafood_hub", emoji: "🦪", category: "meat",
    units: [
      { id: "lb", label: "lb", toBase: 453.6 },
      { id: "oz", label: "oz", toBase: 28.35 },
    ],
    defaultUnit: "lb",
  },

  // ── produce ─────────────────────────────────────────────────────────────
  {
    id: "garlic", name: "Garlic", emoji: "🧄", parentId: "alliums_hub", category: "produce",
    // Unit ladder encodes average weights so a count-only user and a
    // weight-only user both get accurate conversions. 1 clove ≈ 4g
    // (the 3–5g range from USDA peeled fresh); 1 head ≈ 10 cloves.
    // tbsp / tsp sized against minced garlic density (~9g / tbsp,
    // ~3g / tsp) so a recipe "2 tbsp minced garlic" against a jar of
    // minced garlic in oz or a head of whole cloves both convert
    // cleanly. Full mass ladder (g:1 anchor) makes this a mass
    // canonical, so isMassLadder-gated paths (scaleFactor, count
    // override via effectiveCountWeightG) light up.
    units: [
      { id: "clove", label: "cloves", toBase: 4 },
      { id: "head",  label: "heads",  toBase: 40 },
      { id: "g",     label: "g",      toBase: 1 },
      { id: "oz",    label: "oz",     toBase: 28.35 },
      { id: "tbsp",  label: "tbsp",   toBase: 9 },
      { id: "tsp",   label: "tsp",    toBase: 3 },
    ],
    defaultUnit: "clove",
    // Per 4g clove.
    nutrition: { per: "100g", kcal: 149, protein_g: 6.4, fat_g: 0.5, carb_g: 33, fiber_g: 2.1, sodium_mg: 17 },
  },
  {
    id: "yellow_onion", name: "Yellow Onion", emoji: "🧅", parentId: "alliums_hub", category: "produce",
    units: [{ id: "count", label: "onions", toBase: 1 }],
    defaultUnit: "count",
    // Per medium onion (~110g).
    nutrition: { per: "count", kcal: 44, protein_g: 1.2, fat_g: 0.1, carb_g: 10, fiber_g: 1.9, sodium_mg: 4 },
  },
  {
    id: "shallot", name: "Shallot", emoji: "🧅", parentId: "alliums_hub", category: "produce",
    units: [{ id: "count", label: "shallots", toBase: 1 }],
    defaultUnit: "count",
  },
  {
    id: "pearl_onion", name: "Pearl Onions", emoji: "🧅", parentId: "alliums_hub", category: "produce",
    units: [{ id: "count", label: "onions", toBase: 1 }],
    defaultUnit: "count",
  },
  {
    id: "green_onion", name: "Green Onion", shortName: "Green Onion",
    emoji: "🌱", parentId: "alliums_hub", category: "produce",
    units: [
      { id: "count", label: "stalks",  toBase: 1 },
      { id: "bunch", label: "bunches", toBase: 8 },
    ],
    defaultUnit: "count",
  },
  {
    id: "carrot", name: "Carrot", emoji: "🥕", parentId: "root_vegetable_hub", category: "produce",
    units: [{ id: "count", label: "carrots", toBase: 1 }],
    defaultUnit: "count",
    // Per medium carrot (~61g).
    nutrition: { per: "count", kcal: 25, protein_g: 0.6, fat_g: 0.1, carb_g: 6, fiber_g: 1.7, sodium_mg: 42 },
  },
  {
    id: "tomato", name: "Tomato", emoji: "🍅", parentId: "fruiting_vegetable_hub", category: "produce",
    units: [
      { id: "count", label: "tomatoes", toBase: 1 },
      { id: "lb",    label: "lb",       toBase: 4 }, // roughly 4 tomatoes / lb
    ],
    defaultUnit: "count",
    // Per medium tomato (~123g).
    nutrition: { per: "count", kcal: 22, protein_g: 1.1, fat_g: 0.2, carb_g: 4.8, fiber_g: 1.5, sodium_mg: 6 },
  },
  {
    id: "lemon", name: "Lemon", emoji: "🍋", parentId: "fruit_hub", category: "produce",
    units: [{ id: "count", label: "lemons", toBase: 1 }],
    defaultUnit: "count",
    // Per medium lemon (~58g).
    nutrition: { per: "count", kcal: 17, protein_g: 0.6, fat_g: 0.2, carb_g: 5.4, fiber_g: 1.6, sodium_mg: 1 },
  },
  {
    id: "spinach", name: "Baby Spinach", emoji: "🥬", parentId: "leafy_green_hub", category: "produce",
    units: [
      { id: "cup", label: "cups", toBase: 30 },
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "g",   label: "g",    toBase: 1 },
    ],
    defaultUnit: "cup",
    nutrition: { per: "100g", kcal: 23, protein_g: 2.9, fat_g: 0.4, carb_g: 3.6, fiber_g: 2.2, sodium_mg: 79 },
  },
  {
    id: "basil", name: "Fresh Basil", emoji: "🌿", parentId: "fresh_herb_hub", category: "produce",
    units: [
      { id: "bunch", label: "bunches",     toBase: 30 },
      { id: "cup",   label: "cups leaves", toBase: 15 },
      { id: "leaf",  label: "leaves",      toBase: 0.3 },
    ],
    defaultUnit: "bunch",
  },
  {
    id: "parsley", name: "Parsley", emoji: "🌿", parentId: "fresh_herb_hub", category: "produce",
    units: [
      { id: "bunch", label: "bunches", toBase: 30 },
      { id: "cup",   label: "cups",    toBase: 15 },
    ],
    defaultUnit: "bunch",
  },
  {
    id: "mushroom", name: "Cremini Mushrooms", emoji: "🍄", parentId: "fungi_hub", category: "produce",
    aliases: ["cremini_mushrooms"],
    searchAliases: ["mushrooms", "button mushrooms", "white mushrooms"],
    units: [
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "cup", label: "cups", toBase: 70 },
    ],
    defaultUnit: "oz",
  },
  {
    // Generic catch-all for "mixed mushrooms" / "wild mushrooms" /
    // "mushroom medley" — the umbrella term Claude reaches for when
    // a recipe doesn't specify variety. Without this entry the AI
    // had no roster match and was leaving canonicalId null on every
    // mushroom-medley dish.
    id: "mixed_mushrooms", name: "Mixed Mushrooms", emoji: "🍄", parentId: "fungi_hub", category: "produce",
    aliases: ["mixed-mushrooms"],
    searchAliases: ["wild mushrooms", "mushroom medley", "assorted mushrooms"],
    units: [
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "cup", label: "cups", toBase: 70 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "shiitake", name: "Shiitake Mushrooms", emoji: "🍄", parentId: "fungi_hub", category: "produce",
    searchAliases: ["shiitake mushrooms"],
    units: [
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "cup", label: "cups", toBase: 70 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "portobello", name: "Portobello Mushrooms", emoji: "🍄", parentId: "fungi_hub", category: "produce",
    searchAliases: ["portobello mushrooms", "portabella"],
    units: [
      { id: "count", label: "caps", toBase: 1 },
      { id: "oz",    label: "oz",   toBase: 28.35 },
      { id: "lb",    label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "count",
    count_weight_g: 100,
  },
  {
    id: "oyster_mushroom", name: "Oyster Mushrooms", emoji: "🍄", parentId: "fungi_hub", category: "produce",
    searchAliases: ["oyster mushrooms", "king oyster"],
    units: [
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "cup", label: "cups", toBase: 70 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "bell_pepper", name: "Bell Pepper", emoji: "🫑", parentId: "pepper_hub", category: "produce",
    units: [{ id: "count", label: "peppers", toBase: 1 }],
    defaultUnit: "count",
  },
  {
    id: "cucumber", name: "Cucumber", emoji: "🥒", parentId: "fruiting_vegetable_hub", category: "produce",
    units: [{ id: "count", label: "cucumbers", toBase: 1 }],
    defaultUnit: "count",
  },
  {
    id: "avocado", name: "Avocado", emoji: "🥑", parentId: "fruit_hub", category: "produce",
    units: [{ id: "count", label: "avocados", toBase: 1 }],
    defaultUnit: "count",
  },
  {
    id: "potato", name: "Potato", emoji: "🥔", parentId: "root_vegetable_hub", category: "produce",
    units: [
      { id: "count", label: "potatoes", toBase: 1 },
      { id: "lb",    label: "lb",       toBase: 3 }, // ~3 mediums per lb
      { id: "bag",   label: "bags",     toBase: 15 }, // 5lb bag
    ],
    defaultUnit: "lb",
  },
  {
    id: "sweet_potato", name: "Sweet Potato", emoji: "🍠", parentId: "root_vegetable_hub", category: "produce",
    units: [
      { id: "count", label: "potatoes", toBase: 1 },
      { id: "lb",    label: "lb",       toBase: 2.5 },
    ],
    defaultUnit: "count",
  },
  {
    id: "broccoli", name: "Broccoli", emoji: "🥦", parentId: "cruciferous_hub", category: "produce",
    units: [
      { id: "head", label: "heads", toBase: 450 },
      { id: "lb",   label: "lb",    toBase: 453.6 },
      { id: "cup",  label: "cups florets", toBase: 71 },
    ],
    defaultUnit: "head",
    nutrition: { per: "100g", kcal: 34, protein_g: 2.8, fat_g: 0.4, carb_g: 6.6, fiber_g: 2.6, sodium_mg: 33 },
  },
  {
    id: "cauliflower", name: "Cauliflower", emoji: "🥦", parentId: "cruciferous_hub", category: "produce",
    units: [
      { id: "head", label: "heads", toBase: 700 },
      { id: "lb",   label: "lb",    toBase: 453.6 },
    ],
    defaultUnit: "head",
  },
  {
    id: "lettuce", name: "Lettuce", emoji: "🥬", parentId: "leafy_green_hub", category: "produce",
    units: [
      { id: "head", label: "heads", toBase: 500 },
      { id: "cup",  label: "cups",  toBase: 55 },
      { id: "g",    label: "g",     toBase: 1 },
    ],
    defaultUnit: "head",
    nutrition: { per: "100g", kcal: 15, protein_g: 1.4, fat_g: 0.2, carb_g: 2.9, fiber_g: 1.3, sodium_mg: 28 },
  },
  {
    id: "arugula", name: "Arugula", emoji: "🥬", parentId: "leafy_green_hub", category: "produce",
    units: [
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "cup", label: "cups", toBase: 20 },
    ],
    defaultUnit: "oz",
  },
  {
    id: "kale", name: "Kale", emoji: "🥬", parentId: "leafy_green_hub", category: "produce",
    units: [
      { id: "bunch", label: "bunches", toBase: 200 },
      { id: "oz",    label: "oz",      toBase: 28.35 },
      { id: "cup",   label: "cups",    toBase: 67 },
    ],
    defaultUnit: "bunch",
  },
  {
    id: "zucchini", name: "Zucchini", emoji: "🥒", parentId: "fruiting_vegetable_hub", category: "produce",
    units: [{ id: "count", label: "zucchini", toBase: 1 }],
    defaultUnit: "count",
  },
  {
    id: "apple", name: "Apple", emoji: "🍎", parentId: "fruit_hub", category: "produce",
    units: [
      { id: "count", label: "apples", toBase: 1 },
      { id: "lb",    label: "lb",     toBase: 3 },
      { id: "bag",   label: "bags",   toBase: 9 }, // 3lb bag
    ],
    defaultUnit: "count",
    // Per one medium apple (~182g). Recipes that say "1 cup chopped
    // apple" can't convert against a count ladder; recipes keep using
    // "count" for honest resolution. cup↔count would need density.
    nutrition: { per: "count", kcal: 95, protein_g: 0.5, fat_g: 0.3, carb_g: 25, fiber_g: 4.4, sodium_mg: 2 },
  },
  {
    id: "banana", name: "Banana", emoji: "🍌", parentId: "fruit_hub", category: "produce",
    units: [
      { id: "count", label: "bananas", toBase: 1 },
      { id: "bunch", label: "bunches", toBase: 5 },
      { id: "lb",    label: "lb",      toBase: 3 },
    ],
    defaultUnit: "count",
    nutrition: { per: "count", kcal: 105, protein_g: 1.3, fat_g: 0.4, carb_g: 27, fiber_g: 3.1, sodium_mg: 1 },
  },
  {
    id: "orange", name: "Orange", emoji: "🍊", parentId: "fruit_hub", category: "produce",
    units: [
      { id: "count", label: "oranges", toBase: 1 },
      { id: "lb",    label: "lb",      toBase: 3 },
      { id: "bag",   label: "bags",    toBase: 12 },
    ],
    defaultUnit: "count",
  },
  {
    id: "lime", name: "Lime", emoji: "🍋", parentId: "fruit_hub", category: "produce",
    units: [{ id: "count", label: "limes", toBase: 1 }],
    defaultUnit: "count",
    // Per medium lime (~67g).
    nutrition: { per: "count", kcal: 20, protein_g: 0.5, fat_g: 0.1, carb_g: 7.1, fiber_g: 1.9, sodium_mg: 1 },
  },
  {
    id: "strawberry", name: "Strawberries", emoji: "🍓", parentId: "fruit_hub", category: "produce",
    units: [
      { id: "oz",        label: "oz",        toBase: 28.35 },
      { id: "container", label: "containers",toBase: 454 }, // 16oz clamshell
      { id: "cup",       label: "cups",      toBase: 144 },
    ],
    defaultUnit: "container",
  },
  {
    id: "blueberry", name: "Blueberries", emoji: "🫐", parentId: "fruit_hub", category: "produce",
    units: [
      { id: "oz",        label: "oz",         toBase: 28.35 },
      { id: "container", label: "containers", toBase: 170 }, // 6oz pint
      { id: "cup",       label: "cups",       toBase: 148 },
    ],
    defaultUnit: "container",
  },
  {
    id: "cilantro", name: "Cilantro", emoji: "🌿", parentId: "fresh_herb_hub", category: "produce",
    units: [
      { id: "bunch", label: "bunches", toBase: 30 },
      { id: "cup",   label: "cups",    toBase: 15 },
    ],
    defaultUnit: "bunch",
  },
  {
    id: "ginger", name: "Ginger", emoji: "🫚", parentId: "root_vegetable_hub", category: "produce",
    units: [
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "piece", label: "pieces", toBase: 40 },
    ],
    defaultUnit: "piece",
  },

  // ── pantry / dry goods ──────────────────────────────────────────────────
  // Flours — hub member registry. Protein content varies dramatically
  // across types and drives gluten behavior, so they are NOT
  // interchangeable in baking. The hub groups them for the picker
  // while keeping each as a first-class canonical for recipe matching.
  {
    id: "flour", name: "All-Purpose Flour", shortName: "All-Purpose",
    parentId: "flour_hub", emoji: "🌾", category: "pantry", subtype: "baking",
    // Bare-token alias: recipes saying "2 cups flour" should resolve
    // to AP flour by default. Specialty flours (bread, cake, whole_wheat)
    // carry their own qualifying tokens that match more specifically.
    searchAliases: ["flour"],
    units: [
      { id: "cup",  label: "cups", toBase: 120 },
      { id: "tbsp", label: "tbsp", toBase: 7.5 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
    measuredIn:    { us: "lb",  metric: "kg" },
    nutrition: { per: "100g", kcal: 364, protein_g: 10, fat_g: 1, carb_g: 76, fiber_g: 2.7, sodium_mg: 2 },
  },
  {
    id: "bread_flour", name: "Bread Flour", shortName: "Bread",
    parentId: "flour_hub", emoji: "🌾", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 120 },
      { id: "tbsp", label: "tbsp", toBase: 7.5 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
    nutrition: { per: "100g", kcal: 361, protein_g: 12, fat_g: 1.7, carb_g: 72, fiber_g: 2.4, sodium_mg: 2 },
  },
  {
    id: "whole_wheat_flour", name: "Whole Wheat Flour", shortName: "Whole Wheat", aliases: ["wheat"],
    parentId: "flour_hub", emoji: "🌾", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 120 },
      { id: "tbsp", label: "tbsp", toBase: 7.5 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
  },
  {
    id: "pastry_flour", name: "Pastry Flour", shortName: "Pastry",
    parentId: "flour_hub", emoji: "🌾", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 120 },
      { id: "tbsp", label: "tbsp", toBase: 7.5 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
  },
  {
    id: "cake_flour", name: "Cake Flour", shortName: "Cake",
    parentId: "flour_hub", emoji: "🌾", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 120 },
      { id: "tbsp", label: "tbsp", toBase: 7.5 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
  },
  {
    id: "zero_zero_flour", name: "00 Flour", shortName: "00",
    parentId: "flour_hub", emoji: "🌾", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 120 },
      { id: "tbsp", label: "tbsp", toBase: 7.5 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "kg",   label: "kg",   toBase: 1000 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
  },
  {
    id: "semolina", name: "Semolina Flour", shortName: "Semolina",
    parentId: "flour_hub", emoji: "🌾", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 160 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
  },
  {
    id: "rice_flour", name: "Rice Flour", shortName: "Rice",
    parentId: "flour_hub", emoji: "🌾", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 160 },
      { id: "tbsp", label: "tbsp", toBase: 10 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
  },
  {
    id: "almond_flour", name: "Almond Flour", shortName: "Almond",
    parentId: "flour_hub", emoji: "🌰", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 96 },
      { id: "tbsp", label: "tbsp", toBase: 6 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
  },
  {
    id: "coconut_flour", name: "Coconut Flour", shortName: "Coconut",
    parentId: "flour_hub", emoji: "🥥", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 112 },
      { id: "tbsp", label: "tbsp", toBase: 7 },
      { id: "g",    label: "g",    toBase: 1 },
    ],
    defaultUnit: "cup",
  },
  {
    id: "cornmeal", name: "Cornmeal", shortName: "Cornmeal",
    parentId: "flour_hub", emoji: "🌽", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 140 },
      { id: "tbsp", label: "tbsp", toBase: 9 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
  },
  {
    id: "masa_harina", name: "Masa Harina", shortName: "Masa",
    parentId: "flour_hub", emoji: "🌽", category: "pantry",
    units: [
      { id: "cup",  label: "cups", toBase: 120 },
      { id: "g",    label: "g",    toBase: 1 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "cup",
  },
  {
    // Cane sugar — the canonical for granulated white sugar. The
    // prior generic `sugar` slug was retired because the registry
    // needs to surface specific sugar types (brown, powdered, raw,
    // coconut, etc.) rather than collapse them all into a catch-all.
    // `sugar`, `white_sugar`, `granulated_sugar` all redirect here
    // via the aliases array (matched by inferCanonicalFromName) +
    // CANONICAL_ALIASES (matched by findIngredient on legacy slugs).
    id: "cane_sugar", name: "Cane Sugar", emoji: "🍬", parentId: "sweetener_hub", category: "pantry",
    aliases: ["sugar", "white_sugar", "granulated_sugar"],
    units: [
      { id: "cup",  label: "cups", toBase: 200 },
      { id: "tbsp", label: "tbsp", toBase: 12.5 },
      { id: "tsp",  label: "tsp",  toBase: 4.2 },
      { id: "lb",   label: "lb",   toBase: 453.6 },
      { id: "g",    label: "g",    toBase: 1 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup", metric: "g" },
    measuredIn:    { us: "lb",  metric: "kg" },
  },
  {
    // Baking leavener — always in tsp in recipes, bought in small
    // canisters measured in oz on the label. Density 0.90 g/ml → 1 tsp
    // ≈ 4.4 g. Ladder covers the whole conversion surface (tsp/tbsp/
    // cup/oz/g/can) so the UnitPicker can offer any of them regardless
    // of which axis preferredUnit / measuredIn pick as the default.
    id: "baking_powder", name: "Baking Powder", emoji: "🥄", category: "pantry",
    units: [
      { id: "tsp",  label: "tsp",  toBase: 4.4 },
      { id: "tbsp", label: "tbsp", toBase: 13.2 },
      { id: "cup",  label: "cups", toBase: 211 },
      { id: "oz",   label: "oz",   toBase: 28.35 },
      { id: "can",  label: "cans", toBase: 227, aliases: ["cans", "canister", "canisters"] }, // 8oz Clabber Girl can
      { id: "g",    label: "g",    toBase: 1 },
    ],
    defaultUnit: "can",
    preferredUnit: { us: "tsp", metric: "g" },
    measuredIn:    { us: "oz",  metric: "g" },
    nutrition: { per: "100g", kcal: 53, protein_g: 0.1, fat_g: 0, carb_g: 28, sodium_mg: 10600 },
  },
  {
    // Baking leavener — tsp in recipes, boxes/oz on the shelf.
    // Density 1.10 g/ml → 1 tsp ≈ 5.5 g. Same ladder-coverage rule
    // as baking_powder — every idiomatic unit appears so nothing
    // vanishes from the UnitPicker regardless of system default.
    id: "baking_soda", name: "Baking Soda", emoji: "🥄", category: "pantry",
    units: [
      { id: "tsp",  label: "tsp",  toBase: 5.5 },
      { id: "tbsp", label: "tbsp", toBase: 16.5 },
      { id: "cup",  label: "cups", toBase: 264 },
      { id: "oz",   label: "oz",   toBase: 28.35 },
      { id: "box",  label: "boxes",toBase: 454, aliases: ["boxes"] }, // 1 lb Arm & Hammer
      { id: "g",    label: "g",    toBase: 1 },
    ],
    defaultUnit: "box",
    preferredUnit: { us: "tsp", metric: "g" },
    measuredIn:    { us: "oz",  metric: "g" },
    nutrition: { per: "100g", kcal: 0, protein_g: 0, fat_g: 0, carb_g: 0, sodium_mg: 27360 },
  },
  {
    id: "olive_oil", name: "Olive Oil", emoji: "🫒", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    // Per 100 ml (treated as 100g — olive oil density is ~0.92 g/ml,
    // so real kcal/ml is slightly lower; the 8% gap is within the
    // "close enough for home-cook tracking" band we accept for liquid
    // ingredients that lack per-liquid density correction).
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 2 },
  },
  // Other cooking fats — added so they can be canonical-bound on scan
  // and carry their own nutrition / smoke point / cooking metadata.
  // Same shape as olive_oil; per-100g kcal is identical for pure
  // fats (~884 kcal). Each oil's distinguishing feature (smoke point,
  // flavor, cuisine) lands via Phase 3 AI enrichment in
  // ingredient_info, not duplicated here.
  {
    id: "vegetable_oil", name: "Vegetable Oil", emoji: "🛢️", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "canola_oil", name: "Canola Oil", emoji: "🛢️", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "sesame_oil", name: "Sesame Oil", emoji: "🛢️", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "coconut_oil", name: "Coconut Oil", emoji: "🥥", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 14 },
      { id: "cup",   label: "cups",  toBase: 218 },
      { id: "tsp",   label: "tsp",   toBase: 4.5 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "g",     label: "g",     toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "g" },
    // Coconut oil is solid at room temp (below ~76°F) — measured by
    // weight more often than volume. kcal density is identical to
    // liquid oils (~884/100g pure fat).
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "avocado_oil", name: "Avocado Oil", emoji: "🥑", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "peanut_oil", name: "Peanut Oil", emoji: "🥜", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "grapeseed_oil", name: "Grapeseed Oil", emoji: "🍇", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "sunflower_oil", name: "Sunflower Oil", emoji: "🌻", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 15 },
      { id: "cup",   label: "cups",  toBase: 240 },
      { id: "tsp",   label: "tsp",   toBase: 5 },
      { id: "fl_oz", label: "fl oz", toBase: 29.57 },
      { id: "ml",    label: "ml",    toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "ghee", name: "Ghee", emoji: "🧈", parentId: "butter_hub", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 13 },
      { id: "cup",   label: "cups",  toBase: 205 },
      { id: "tsp",   label: "tsp",   toBase: 4.3 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "g",     label: "g",     toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "g" },
    // Ghee is clarified butter — milk solids removed, pure butterfat.
    // Higher smoke point than butter (~485°F vs 350°F), shelf-stable
    // at room temp because the water + casein that drive butter
    // spoilage are gone.
    nutrition: { per: "100g", kcal: 900, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 2 },
  },
  {
    id: "lard", name: "Lard", emoji: "🥓", parentId: "oil_hub", category: "pantry",
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 13 },
      { id: "cup",   label: "cups",  toBase: 205 },
      { id: "tsp",   label: "tsp",   toBase: 4.3 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "g",     label: "g",     toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "g" },
    // Rendered pork fat. Solid at room temp; kcal density same as
    // other fats (~900/100g, slightly higher than liquid oils on a
    // per-gram basis because lard's water content is near zero).
    nutrition: { per: "100g", kcal: 902, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  {
    id: "shortening", name: "Vegetable Shortening", emoji: "🥄", parentId: "oil_hub", category: "pantry", aliases: ["shortening_sticks"],
    units: [
      { id: "tbsp",  label: "tbsp",  toBase: 12.8 },
      { id: "cup",   label: "cups",  toBase: 205 },
      { id: "tsp",   label: "tsp",   toBase: 4.3 },
      { id: "oz",    label: "oz",    toBase: 28.35 },
      { id: "g",     label: "g",     toBase: 1 },
    ],
    defaultUnit: "tbsp",
    preferredUnit: { us: "tbsp", metric: "g" },
    // Crisco-style hydrogenated vegetable oil. Solid at room temp,
    // flaky pastry champion. Kcal density similar to lard.
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 0 },
  },
  // Pasta ───────────────────
  {
    id: "spaghetti", name: "Spaghetti", shortName: "Spaghetti",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
      { id: "g",   label: "g",     toBase: 1 },
    ],
    defaultUnit: "box",
  },
  {
    id: "penne", name: "Penne", shortName: "Penne",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "rigatoni", name: "Rigatoni", shortName: "Rigatoni",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "fettuccine", name: "Fettuccine", shortName: "Fettuccine",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "orzo", name: "Orzo", shortName: "Orzo",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
      { id: "cup", label: "cups",  toBase: 200 },
    ],
    defaultUnit: "box",
  },
  {
    id: "lasagna", name: "Lasagna Noodles", shortName: "Lasagna",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "box",   label: "boxes",   toBase: 454 },
      { id: "oz",    label: "oz",      toBase: 28.35 },
      { id: "lb",    label: "lb",      toBase: 453.6 },
      { id: "sheet", label: "sheets",  toBase: 20 },
    ],
    defaultUnit: "box",
  },
  {
    id: "cavatappi", name: "Cavatappi", shortName: "Cavatappi",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "fusilli", name: "Fusilli", shortName: "Fusilli",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "rotini", name: "Rotini", shortName: "Rotini",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "farfalle", name: "Farfalle", shortName: "Bow-Tie",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "macaroni", name: "Elbow Macaroni", shortName: "Macaroni",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
      { id: "cup", label: "cups",  toBase: 140 },
    ],
    defaultUnit: "box",
  },
  {
    id: "bucatini", name: "Bucatini", shortName: "Bucatini",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "linguine", name: "Linguine", shortName: "Linguine",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "angel_hair", name: "Angel Hair", shortName: "Angel Hair",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "ziti", name: "Ziti", shortName: "Ziti",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "orecchiette", name: "Orecchiette", shortName: "Orecchiette",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "box", label: "boxes", toBase: 454 },
    ],
    defaultUnit: "box",
  },
  {
    id: "tortellini", name: "Tortellini", shortName: "Tortellini",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "pkg", label: "pkgs",  toBase: 255 },  // ~9oz fresh pkg
    ],
    defaultUnit: "pkg",
  },
  {
    id: "ravioli", name: "Ravioli", shortName: "Ravioli",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "pkg", label: "pkgs",  toBase: 255 },
    ],
    defaultUnit: "pkg",
  },
  {
    id: "gnocchi", name: "Gnocchi", shortName: "Gnocchi",
    parentId: "pasta_hub", emoji: "🍝", category: "pantry",
    units: [
      { id: "oz",  label: "oz",    toBase: 28.35 },
      { id: "lb",  label: "lb",    toBase: 453.6 },
      { id: "pkg", label: "pkgs",  toBase: 500 },  // ~1.1lb shelf-stable pkg
    ],
    defaultUnit: "pkg",
  },

  // Bread ───────────────────
  {
    id: "bread", name: "Sandwich Bread", shortName: "Sandwich",
    parentId: "bread_hub", emoji: "🍞", category: "pantry", subtype: "bread",
    units: [
      { id: "slice", label: "slices", toBase: 1 },
      { id: "loaf",  label: "loaves", toBase: 20 },
    ],
    defaultUnit: "loaf",
    // Per slice (~28 g). Count-based because the ladder's base unit
    // is a slice, not grams — 1 count = 1 slice.
    nutrition: { per: "count", kcal: 74, protein_g: 2.6, fat_g: 0.9, carb_g: 14, fiber_g: 0.8, sodium_mg: 137 },
  },
  {
    id: "sourdough", name: "Sourdough", shortName: "Sourdough",
    parentId: "bread_hub", emoji: "🍞", category: "pantry",
    units: [
      { id: "slice", label: "slices", toBase: 1 },
      { id: "loaf",  label: "loaves", toBase: 16 },
    ],
    defaultUnit: "loaf",
  },
  {
    id: "rye_bread", name: "Rye Bread", shortName: "Rye",
    parentId: "bread_hub", emoji: "🍞", category: "pantry", subtype: "bread",
    units: [
      { id: "slice", label: "slices", toBase: 1 },
      { id: "loaf",  label: "loaves", toBase: 16 },
    ],
    defaultUnit: "loaf",
    nutrition: { per: "count", kcal: 83, protein_g: 2.7, fat_g: 1.1, carb_g: 15.5, fiber_g: 1.9, sodium_mg: 211 },
  },
  {
    id: "baguette", name: "Baguette", shortName: "Baguette",
    parentId: "bread_hub", emoji: "🥖", category: "pantry",
    units: [
      { id: "count", label: "whole",  toBase: 20 },
      { id: "slice", label: "slices", toBase: 1 },
    ],
    defaultUnit: "count",
  },
  {
    id: "ciabatta", name: "Ciabatta / Focaccia", shortName: "Ciabatta",
    parentId: "bread_hub", emoji: "🍞", category: "pantry",
    units: [
      { id: "count", label: "rolls",   toBase: 1 },
      { id: "loaf",  label: "loaves",  toBase: 6 },
    ],
    defaultUnit: "count",
  },
  {
    id: "bagel", name: "Bagels", shortName: "Bagel",
    aliases: ["bagels"],
    parentId: "bread_hub", emoji: "🥯", category: "pantry",
    units: [
      { id: "count", label: "bagels", toBase: 1 },
      { id: "pack",  label: "packs",  toBase: 6 },
    ],
    defaultUnit: "count",
  },
  {
    id: "english_muffin", name: "English Muffins", shortName: "English Muffin",
    aliases: ["english_muffins"],
    parentId: "bread_hub", emoji: "🍞", category: "pantry",
    units: [
      { id: "count", label: "muffins", toBase: 1 },
      { id: "pack",  label: "packs",   toBase: 6 },
    ],
    defaultUnit: "pack",
  },
  {
    id: "tomato_paste", name: "Tomato Paste", emoji: "🍅", category: "pantry",
    parentId: "tomato_hub",
    units: [
      { id: "tbsp", label: "tbsp", toBase: 15 },
      { id: "can",  label: "cans", toBase: 170 },
      { id: "oz",   label: "oz",   toBase: 28.35 },
    ],
    defaultUnit: "tbsp",
    // tbsp on this ladder is mass-keyed (toBase=15g), and oz is in the
    // ladder, so the cook/pantry pair stays inside the ladder — no
    // family bridge needed.
    preferredUnit: { us: "tbsp", metric: "g" },
    measuredIn:    { us: "oz",   metric: "g" },
  },
  {
    id: "chicken_stock", name: "Chicken Stock", emoji: "🍲", parentId: "stock_hub", category: "pantry",
    units: [
      { id: "cup",    label: "cups",   toBase: 240 },
      { id: "quart",  label: "quarts", toBase: 946 },
      { id: "ml",     label: "ml",     toBase: 1 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup",   metric: "ml" },
    measuredIn:    { us: "quart", metric: "ml" },
  },
  {
    id: "beef_stock", name: "Beef Stock", emoji: "🍲", parentId: "stock_hub", category: "pantry",
    units: [
      { id: "cup",   label: "cups",   toBase: 240 },
      { id: "quart", label: "quarts", toBase: 946 },
      { id: "ml",    label: "ml",     toBase: 1 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup",   metric: "ml" },
    measuredIn:    { us: "quart", metric: "ml" },
  },
  {
    id: "red_wine", name: "Red Wine", emoji: "🍷", parentId: "cooking_alcohol_hub", category: "pantry",
    units: [
      { id: "cup",    label: "cups",    toBase: 240 },
      { id: "bottle", label: "bottles", toBase: 750 },
      { id: "ml",     label: "ml",      toBase: 1 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup",    metric: "ml" },
    measuredIn:    { us: "bottle", metric: "ml" },
  },
  {
    id: "white_wine", name: "White Wine / Sherry", emoji: "🍷", parentId: "cooking_alcohol_hub", category: "pantry",
    units: [
      { id: "cup",    label: "cups",    toBase: 240 },
      { id: "bottle", label: "bottles", toBase: 750 },
      { id: "ml",     label: "ml",      toBase: 1 },
    ],
    defaultUnit: "cup",
    preferredUnit: { us: "cup",    metric: "ml" },
    measuredIn:    { us: "bottle", metric: "ml" },
  },
  // Rice ───────────────────
  {
    id: "rice", name: "White Rice", shortName: "White",
    parentId: "rice_hub", emoji: "🍚", category: "pantry",
    units: [
      { id: "cup", label: "cups", toBase: 185 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 2268 }, // 5lb bag
      { id: "g",   label: "g",    toBase: 1 },
    ],
    defaultUnit: "lb",
    preferredUnit: { us: "cup", metric: "g" },
    // Per 100g uncooked — cooked rice is ~130 kcal/100g but recipes
    // universally call for the dry amount so that's our reference.
    nutrition: { per: "100g", kcal: 365, protein_g: 7.1, fat_g: 0.7, carb_g: 80, fiber_g: 1.3, sodium_mg: 5 },
  },
  {
    id: "brown_rice", name: "Brown Rice", shortName: "Brown",
    parentId: "rice_hub", emoji: "🍚", category: "pantry",
    units: [
      { id: "cup", label: "cups", toBase: 190 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 908 }, // 2lb bag
    ],
    defaultUnit: "lb",
  },
  {
    id: "jasmine_rice", name: "Jasmine Rice", shortName: "Jasmine",
    parentId: "rice_hub", emoji: "🍚", category: "pantry",
    units: [
      { id: "cup", label: "cups", toBase: 185 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 2268 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "basmati_rice", name: "Basmati Rice", shortName: "Basmati",
    parentId: "rice_hub", emoji: "🍚", category: "pantry",
    units: [
      { id: "cup", label: "cups", toBase: 185 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 2268 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "arborio_rice", name: "Arborio Rice", shortName: "Arborio",
    parentId: "rice_hub", emoji: "🍚", category: "pantry",
    units: [
      { id: "cup", label: "cups", toBase: 200 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 454 }, // 1lb bag typical
    ],
    defaultUnit: "lb",
  },
  {
    // Tomato-and-spice seasoned rice side dish, sold both as a
    // packaged mix (Goya, Knorr, Rice-a-Roni) and made from scratch.
    // Sometimes labeled "Spanish rice" in US English — the
    // searchAlias keeps both phrasings resolving to this canonical.
    id: "mexican_rice", name: "Mexican Rice", shortName: "Mexican",
    parentId: "rice_hub", emoji: "🍚", category: "pantry",
    searchAliases: ["spanish rice", "arroz rojo", "red rice"],
    units: [
      { id: "cup", label: "cups", toBase: 185 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 226 }, // 8oz mix box typical
      { id: "g",   label: "g",    toBase: 1 },
    ],
    defaultUnit: "bag",
    preferredUnit: { us: "cup", metric: "g" },
    // Nutrition is per 100g uncooked / dry mix — matches how the
    // other rice canonicals reference dry weight. Cooked yields
    // ~3x by mass.
    nutrition: { per: "100g", kcal: 360, protein_g: 7, fat_g: 1.2, carb_g: 78, fiber_g: 1.5, sodium_mg: 480 },
  },
  {
    id: "quinoa", name: "Quinoa", emoji: "🌾", parentId: "rice_hub", category: "pantry",
    units: [
      { id: "cup", label: "cups", toBase: 170 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "g",   label: "g",    toBase: 1 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "oats", name: "Rolled Oats", emoji: "🌾", category: "pantry",
    parentId: "oats_hub",
    units: [
      { id: "cup",       label: "cups",       toBase: 80 },
      { id: "container", label: "containers", toBase: 1134 }, // 40oz canister
      { id: "lb",        label: "lb",         toBase: 453.6 },
      { id: "g",         label: "g",          toBase: 1 },
    ],
    defaultUnit: "container",
    preferredUnit: { us: "cup", metric: "g" },
    nutrition: { per: "100g", kcal: 379, protein_g: 13, fat_g: 7, carb_g: 68, fiber_g: 10, sodium_mg: 6 },
  },
  // Beans & legumes ───────────────────
  {
    id: "black_beans", name: "Black Beans", shortName: "Black",
    parentId: "bean_hub", emoji: "🫘", category: "pantry",
    units: [
      { id: "can", label: "cans", toBase: 425 }, // 15oz
      { id: "cup", label: "cups dry", toBase: 190 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "can",
  },
  {
    id: "chickpeas", name: "Chickpeas", shortName: "Chickpeas",
    parentId: "bean_hub", emoji: "🫘", category: "pantry",
    units: [
      { id: "can", label: "cans", toBase: 425 },
      { id: "cup", label: "cups dry", toBase: 200 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "can",
  },
  {
    id: "lentils", name: "Lentils", shortName: "Lentils",
    parentId: "bean_hub", emoji: "🫘", category: "pantry",
    units: [
      { id: "cup", label: "cups", toBase: 200 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 454 },
    ],
    defaultUnit: "lb",
  },
  {
    id: "kidney_beans", name: "Kidney Beans", shortName: "Kidney",
    parentId: "bean_hub", emoji: "🫘", category: "pantry",
    units: [
      { id: "can", label: "cans", toBase: 425 },
      { id: "cup", label: "cups dry", toBase: 190 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "can",
  },
  {
    id: "pinto_beans", name: "Pinto Beans", shortName: "Pinto",
    parentId: "bean_hub", emoji: "🫘", category: "pantry",
    units: [
      { id: "can", label: "cans", toBase: 425 },
      { id: "cup", label: "cups dry", toBase: 190 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "can",
  },
  {
    id: "cannellini_beans", name: "Cannellini Beans", shortName: "Cannellini",
    parentId: "bean_hub", emoji: "🫘", category: "pantry",
    units: [
      { id: "can", label: "cans", toBase: 425 },
      { id: "cup", label: "cups dry", toBase: 190 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
    ],
    defaultUnit: "can",
  },
  {
    // Pre-mashed seasoned bean spread — usually pinto-based, sometimes
    // black-bean. Sold canned (Old El Paso, Rosarita, La Costeña) and
    // fresh in some grocers. Lives in bean_hub alongside the dry/
    // canned bean variants so the hub-chain resolver groups it with
    // the rest of the legume family. Searchable by Spanish name too,
    // since "frijoles refritos" appears in plenty of recipes.
    id: "refried_beans", name: "Refried Beans", shortName: "Refried",
    parentId: "bean_hub", emoji: "🫘", category: "pantry",
    searchAliases: ["frijoles refritos", "refritos"],
    units: [
      { id: "can", label: "cans", toBase: 454 },   // 16oz can typical
      { id: "cup", label: "cups", toBase: 252 },
      { id: "tub", label: "tubs", toBase: 454 },   // fresh-deli tub
      { id: "g",   label: "g",    toBase: 1 },
    ],
    defaultUnit: "can",
    preferredUnit: { us: "cup", metric: "g" },
    // Per 100g cooked / canned (the form everyone actually uses).
    // Sodium runs high because canned refried beans are heavily
    // salted; brand-specific rows in ingredient_info can refine.
    nutrition: { per: "100g", kcal: 110, protein_g: 6, fat_g: 2.5, carb_g: 16, fiber_g: 5, sodium_mg: 460 },
  },
  {
    // Refrigerated avocado-based dip. Sold in tubs (Wholly Guacamole,
    // Good Foods), fresh-deli tubs at the grocer, and made from
    // scratch. Category=produce so the storage resolver places it in
    // the fridge tile by default — guacamole oxidizes within hours
    // at room temp. Sits without a hub because there's no "dips" hub
    // yet; if salsa / hummus / queso land later we can group them.
    id: "guacamole", name: "Guacamole", shortName: "Guac", parentId: "condiment_hub",
    emoji: "🥑", category: "produce",
    searchAliases: ["guac"],
    units: [
      { id: "tub",  label: "tubs",  toBase: 227 }, // 8oz tub typical
      { id: "cup",  label: "cups",  toBase: 230 },
      { id: "tbsp", label: "tbsp",  toBase: 14 },
      { id: "g",    label: "g",     toBase: 1 },
    ],
    defaultUnit: "tub",
    preferredUnit: { us: "cup", metric: "g" },
    // Per 100g — avocado-dominant macros, modest sodium since most
    // commercial guac is mildly seasoned. Brand-specific rows refine
    // via ingredient_info.
    nutrition: { per: "100g", kcal: 150, protein_g: 2, fat_g: 14, carb_g: 8, fiber_g: 5, sodium_mg: 220 },
  },
  {
    id: "canned_tomatoes", name: "Canned Tomatoes", emoji: "🥫", category: "pantry",
    parentId: "tomato_hub",
    units: [
      { id: "can", label: "cans", toBase: 794 }, // 28oz
      { id: "oz",  label: "oz",   toBase: 28.35 },
    ],
    defaultUnit: "can",
  },
  {
    id: "peanut_butter", name: "Peanut Butter", emoji: "🥜", category: "pantry", subtype: "spread",
    units: [
      { id: "jar",  label: "jars", toBase: 454 }, // 16oz jar
      { id: "tbsp", label: "tbsp", toBase: 16 },
      { id: "cup",  label: "cups", toBase: 258 },
      { id: "oz",   label: "oz",   toBase: 28.35 },
    ],
    defaultUnit: "jar",
  },
  {
    id: "jam", name: "Jam", emoji: "🍓", category: "pantry", subtype: "spread",
    searchAliases: ["preserves", "fruit spread", "marmalade"],
    units: [
      { id: "jar",  label: "jars", toBase: 340 },
      { id: "tbsp", label: "tbsp", toBase: 20 },
      { id: "oz",   label: "oz",   toBase: 28.35 },
    ],
    defaultUnit: "jar",
  },
  {
    id: "jelly", name: "Jelly", emoji: "🍇", category: "pantry", subtype: "spread",
    searchAliases: ["fruit jelly", "grape jelly"],
    units: [
      { id: "jar",  label: "jars", toBase: 340 },
      { id: "tbsp", label: "tbsp", toBase: 20 },
      { id: "oz",   label: "oz",   toBase: 28.35 },
    ],
    defaultUnit: "jar",
  },
  {
    id: "almonds", name: "Almonds", emoji: null, category: "pantry", subtype: "nut",
    searchAliases: ["almond", "raw almonds", "roasted almonds"],
    units: [
      { id: "package", label: "packages", toBase: 454 },
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "cup",     label: "cups",     toBase: 143 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "package",
  },
  {
    id: "walnuts", name: "Walnuts", emoji: null, category: "pantry", subtype: "nut",
    searchAliases: ["walnut", "walnut halves"],
    units: [
      { id: "package", label: "packages", toBase: 454 },
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "cup",     label: "cups",     toBase: 117 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "package",
  },
  {
    id: "cashews", name: "Cashews", emoji: null, category: "pantry", subtype: "nut",
    searchAliases: ["cashew", "raw cashews", "roasted cashews"],
    units: [
      { id: "package", label: "packages", toBase: 454 },
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "cup",     label: "cups",     toBase: 137 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "package",
  },
  {
    id: "pecans", name: "Pecans", emoji: null, category: "pantry", subtype: "nut",
    searchAliases: ["pecan", "pecan halves"],
    units: [
      { id: "package", label: "packages", toBase: 454 },
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "cup",     label: "cups",     toBase: 109 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "package",
  },
  {
    id: "honey", name: "Honey", emoji: "🍯", category: "pantry", subtype: "sweetener",
    units: [
      { id: "jar",  label: "jars", toBase: 340 }, // 12oz squeeze bottle
      { id: "tbsp", label: "tbsp", toBase: 21 },
      { id: "cup",  label: "cups", toBase: 340 },
      { id: "oz",   label: "oz",   toBase: 28.35 },
      { id: "g",    label: "g",    toBase: 1 },
    ],
    defaultUnit: "jar",
    preferredUnit: { us: "tbsp", metric: "g" },
    nutrition: { per: "100g", kcal: 304, protein_g: 0.3, fat_g: 0, carb_g: 82, fiber_g: 0.2, sodium_mg: 4 },
  },
  {
    id: "maple_syrup", name: "Maple Syrup", emoji: "🍁", category: "pantry",
    // Bare token "syrup" defaults here; corn_syrup is baking-specific
    // and recipes asking for it always say "corn syrup" explicitly.
    searchAliases: ["syrup"],
    units: [
      { id: "bottle", label: "bottles", toBase: 355 }, // 12oz
      { id: "tbsp",   label: "tbsp",    toBase: 20 },
      { id: "cup",    label: "cups",    toBase: 322 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
      { id: "ml",     label: "ml",      toBase: 1 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tbsp", metric: "ml" },
    nutrition: { per: "100g", kcal: 260, protein_g: 0, fat_g: 0.1, carb_g: 67, sodium_mg: 12 },
  },
  {
    id: "coffee", name: "Coffee (whole bean)", emoji: "☕", category: "pantry", subtype: "beverage",
    units: [
      { id: "oz",  label: "oz",   toBase: 28.35 },
      { id: "lb",  label: "lb",   toBase: 453.6 },
      { id: "bag", label: "bags", toBase: 340 }, // 12oz bag
    ],
    defaultUnit: "oz",
  },
  {
    // Carbonated soft drink — the cola/lemon-lime/root-beer family.
    // Recipes that call for "cola" don't fight a Pepsi-only pantry
    // because the canonical sits ABOVE brand: Pepsi, Coke, RC, store
    // brands all reduce to `soda` here. Common shelf forms are 12 fl oz
    // cans (often in 6-/12-/24-packs) and 2L / 20oz bottles, so the
    // unit ladder leads with can / bottle and includes fl_oz / ml for
    // recipe-side fractional usage. category="pantry" because no
    // "beverage" category exists in the registry yet — the LOCATION
    // / TILE axes route it correctly via tagHintsToAxes (categoryHints
    // tagged "carbonated-drinks" → category=beverage, tile=drinks).
    id: "soda", name: "Soda", emoji: "🥤", category: "pantry",
    aliases: ["soda_pop"],
    // Search aliases — every flavor family within "soda" routes to
    // this canonical. Without these, "Fitz's Premium Root Beer" or
    // "Coca-Cola" or "Sprite Lemon-Lime" never resolve to `soda` and
    // the brand+variant pipeline never fires (it needs canonical
    // bound first). Each entry is a substring the inferCanonicalFromName
    // walker checks against the input text. Lowercase, multi-word OK.
    searchAliases: [
      "soda", "soda pop", "soft drink", "carbonated drink",
      "cola", "diet cola",
      "root beer", "rootbeer", "diet root beer",
      "lemon lime", "lemon-lime", "lemon-lime soda",
      "cream soda", "vanilla cream",
      "ginger ale", "ginger beer",
      "orange soda", "grape soda", "strawberry soda", "fruit soda",
      "tonic water", "club soda", "seltzer",
      "energy drink",
      // Common brand-as-shorthand. Users type / receipts say "Coke"
      // and "Pepsi" interchangeably with the soda category. These
      // route to the canonical so the brand catalog can refine.
      "coke", "diet coke", "pepsi", "diet pepsi",
      "sprite", "fanta", "dr pepper", "mountain dew", "mtn dew",
      "7-up", "7up", "rc cola", "schweppes",
    ],
    units: [
      { id: "can",    label: "cans",    toBase: 355 }, // 12 fl oz can
      { id: "bottle", label: "bottles", toBase: 591 }, // 20 fl oz bottle
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
      { id: "ml",     label: "ml",      toBase: 1 },
      { id: "l",      label: "L",       toBase: 1000 },
    ],
    defaultUnit: "can",
    preferredUnit: { us: "fl_oz", metric: "ml" },
    nutrition: { per: "100g", kcal: 42, protein_g: 0, fat_g: 0, carb_g: 11, sodium_mg: 4 },
  },
  {
    id: "soy_sauce", name: "Soy Sauce", emoji: "🍶", category: "pantry",
    units: [
      { id: "bottle", label: "bottles", toBase: 296 }, // 10oz
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "tsp",    label: "tsp",     toBase: 5 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
      { id: "ml",     label: "ml",      toBase: 1 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tbsp", metric: "ml" },
    estCentsPerBase: 0.7, // ~$4 for a 15oz Kikkoman
  },
  {
    // Nori — dried edible seaweed sheets used for sushi rolls and as
    // a finishing garnish. Most common mapping from OFF taxonomy tag
    // `sushi-nori` / `seaweed-sheets` / `dried-seaweed`. Product lines
    // commonly blend in flavor variants (wasabi, sea-salt, smoked) —
    // those ride on pantry_items.attributes.flavor rather than forking
    // the canonical.
    id: "nori", name: "Nori", shortName: "Nori", emoji: "🍙", category: "pantry",
    parentId: "sea_vegetable_hub",
    units: [
      { id: "pack",  label: "packs",  toBase: 1 },        // full package (10-sheet standard)
      { id: "sheet", label: "sheets", toBase: 0.1 },      // individual sheet, 1/10 of a standard pack
      { id: "g",     label: "g",      toBase: 0.02 },     // ~50g per 10-sheet pack, ~5g per sheet
    ],
    defaultUnit: "pack",
    estCentsPerBase: 300, // ~$3 for a 10-sheet pack
  },
  {
    // Ramen — Japanese / Asian wheat noodle. Distinct from Italian
    // pasta (pasta_hub) despite OFF's taxonomy putting both under
    // the broader "pastas" parent tag. Without this canonical, any
    // scanned ramen product falls through to the "pasta" canonical
    // via OFF's tag cascade, which is technically accurate but
    // collapses the specificity the user cares about (protein
    // ramen != spaghetti). Covers fresh, instant, and dried forms —
    // the `state` axis can carry "dried" / "fresh" when it matters.
    id: "ramen", name: "Ramen", shortName: "Ramen", emoji: "🍜", parentId: "pasta_hub", category: "pantry", aliases: ["ramen_noodles"],
    units: [
      { id: "pack",    label: "packs",    toBase: 1 },          // instant-ramen "brick"
      { id: "serving", label: "servings", toBase: 0.5 },        // half a brick = one bowl
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "lb",      label: "lb",       toBase: 453.6 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "pack",
    estCentsPerBase: 120, // ~$1.20 per pack for standard instant
  },
  {
    // Udon — thick Japanese wheat noodle. Usually sold fresh-packed
    // (vacuum-sealed, refrigerated) or dried. Separate canonical
    // because the thicker bite changes how recipes treat it vs
    // ramen / soba.
    id: "udon", name: "Udon", shortName: "Udon", emoji: "🍜", parentId: "pasta_hub", category: "pantry", aliases: ["udon_noodles"],
    units: [
      { id: "pack",    label: "packs",    toBase: 1 },
      { id: "serving", label: "servings", toBase: 0.5 },
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "pack",
    estCentsPerBase: 180,
  },
  {
    // Soba — Japanese buckwheat noodle. Usually dried, sometimes
    // fresh. Buckwheat content varies 30-100% by brand; the
    // attribute axis can carry grade / % details when relevant.
    id: "soba", name: "Soba", shortName: "Soba", emoji: "🍜", parentId: "pasta_hub", category: "pantry", aliases: ["soba_noodles"],
    units: [
      { id: "pack",    label: "packs",    toBase: 1 },
      { id: "serving", label: "servings", toBase: 0.5 },
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "pack",
    estCentsPerBase: 220,
  },
  {
    // Rice noodles — base for pho, pad thai, drunken noodles. Wheat-
    // free (relevant for GF diets). Sold dried, thin / wide variants
    // differ by recipe need; attribute axis can carry the shape.
    // Lives in rice_hub so the hub-chain resolver pulls rice
    // properties (storage, GF semantics) without needing a separate
    // noodle hub for one-off entries.
    id: "rice_noodles", name: "Rice Noodles", shortName: "Rice Noodles", emoji: "🍜",
    parentId: "rice_hub", category: "pantry",
    units: [
      { id: "pack",    label: "packs",    toBase: 1 },
      { id: "serving", label: "servings", toBase: 0.5 },
      { id: "oz",      label: "oz",       toBase: 28.35 },
      { id: "g",       label: "g",        toBase: 1 },
    ],
    defaultUnit: "pack",
    estCentsPerBase: 200,
  },
  {
    // Compound ingredient — bottled sriracha is one product, homemade is
    // another with the same id. Links to the "sriracha" scratch recipe
    // via skillDev.fromScratchRecipeId (Phase 1 of the compound work).
    id: "sriracha", name: "Sriracha", emoji: "🌶️", category: "pantry",
    units: [
      { id: "bottle", label: "bottles", toBase: 482 }, // ~17 fl oz classic Huy Fong
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "tsp",    label: "tsp",     toBase: 5 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tsp",    metric: "ml" },
    measuredIn:    { us: "fl_oz",  metric: "ml" },
    estCentsPerBase: 2.0, // ~$8–10 for the 28oz rooster bottle
  },
  {
    // Compound ingredient — jarred pesto varies wildly by brand; the
    // scratch recipe produces a ~1 cup yield. Category "pantry" for
    // shelf placement purposes; physical location defaults to fridge
    // after opening (or after making).
    id: "pesto", name: "Pesto", emoji: "🌿", category: "pantry",
    units: [
      { id: "jar",    label: "jars",    toBase: 180 }, // ~6 oz supermarket jar
      { id: "cup",    label: "cups",    toBase: 240 },
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
    ],
    defaultUnit: "jar",
    preferredUnit: { us: "tbsp", metric: "ml" },
    measuredIn:    { us: "jar",  metric: "ml" },
    estCentsPerBase: 5.5, // ~$8–10 for a 6 oz jar of the decent stuff
  },
  {
    id: "vinegar", name: "Vinegar", emoji: "🍶", category: "pantry",
    units: [
      { id: "bottle", label: "bottles", toBase: 473 },
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "cup",    label: "cups",    toBase: 240 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tbsp",  metric: "ml" },
    measuredIn:    { us: "fl_oz", metric: "ml" },
  },
  {
    // Pickled vegetable mix (cauliflower, peppers, carrots, celery in
    // brine). Recipe-side: scooped by spoon/cup as a relish or
    // antipasto component, measured by volume. Pantry-side: jars
    // are labeled in fl oz / oz net weight. Brine + veg mix is
    // close to water density so the volume↔mass bridge is honest;
    // declared explicitly so convertStrict can take the density-
    // bridge path between cup (cook) and oz (pantry).
    id: "giardiniera", name: "Giardiniera", emoji: "🥒", parentId: "pickle_ferment_hub", category: "pantry",
    aliases: ["giardinera"],
    units: [
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "cup",    label: "cups",    toBase: 240 },
      { id: "jar",    label: "jars",    toBase: 473 }, // 16 fl oz standard
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
      { id: "oz",     label: "oz",      toBase: 28.35 },
      { id: "g",      label: "g",       toBase: 1 },
      { id: "ml",     label: "ml",      toBase: 1 },
    ],
    defaultUnit: "jar",
    density_g_per_ml: 1.0,  // brine-soaked vegetables ≈ water density
    preferredUnit: { us: "cup", metric: "ml" },
    measuredIn:    { us: "oz",  metric: "g"  },
  },
  {
    id: "tortillas", name: "Tortillas", emoji: "🌮", category: "pantry",
    units: [
      { id: "count", label: "tortillas", toBase: 1 },
      { id: "pack",  label: "packs",     toBase: 10 },
    ],
    defaultUnit: "pack",
    preferredUnit: { us: "count", metric: "count" },
    measuredIn:    { us: "pack",  metric: "pack"  },
  },
  // ── condiments batch 2: Asian staples ──────────────────────────────
  {
    id: "fish_sauce", name: "Fish Sauce", emoji: "🐟", category: "pantry",
    units: [
      { id: "bottle", label: "bottles", toBase: 700 }, // ~24oz glass
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "tsp",    label: "tsp",     toBase: 5 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tsp",    metric: "ml" },
    measuredIn:    { us: "fl_oz",  metric: "ml" },
    estCentsPerBase: 1.0, // ~$6–10 for a 24oz Red Boat
  },
  {
    id: "miso", name: "Miso Paste", emoji: "🍲", category: "pantry",
    units: [
      { id: "tub",    label: "tubs",    toBase: 500 }, // ~17oz standard
      { id: "cup",    label: "cups",    toBase: 250 },
      { id: "tbsp",   label: "tbsp",    toBase: 17 },
      { id: "tsp",    label: "tsp",     toBase: 5.7 },
    ],
    defaultUnit: "tub",
    preferredUnit: { us: "tbsp", metric: "ml" },
    measuredIn:    { us: "tub",  metric: "ml" },
    estCentsPerBase: 1.6, // ~$8–10 for the Hikari tub
  },
  {
    id: "mirin", name: "Mirin", emoji: "🍶", parentId: "cooking_alcohol_hub", category: "pantry",
    units: [
      { id: "bottle", label: "bottles", toBase: 300 }, // 10oz typical
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "tsp",    label: "tsp",     toBase: 5 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tbsp",   metric: "ml" },
    measuredIn:    { us: "fl_oz",  metric: "ml" },
    estCentsPerBase: 1.8, // ~$5–8 depending on real-hon-mirin vs. aji-mirin
  },
  {
    id: "oyster_sauce", name: "Oyster Sauce", emoji: "🦪", category: "pantry",
    units: [
      { id: "bottle", label: "bottles", toBase: 510 }, // ~18oz LKK Premium
      { id: "tbsp",   label: "tbsp",    toBase: 18 },
      { id: "tsp",    label: "tsp",     toBase: 6 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tbsp",   metric: "ml" },
    measuredIn:    { us: "bottle", metric: "ml" },
    estCentsPerBase: 0.9, // ~$5 LKK Premium
  },
  // ── condiments batch 1: American staples ────────────────────────────
  {
    id: "ketchup", name: "Ketchup", emoji: "🥫", category: "pantry", subtype: "sauce",
    units: [
      { id: "bottle", label: "bottles", toBase: 567 }, // 20oz squeeze bottle
      { id: "tbsp",   label: "tbsp",    toBase: 17 },
      { id: "tsp",    label: "tsp",     toBase: 5.7 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tbsp",   metric: "ml" },
    measuredIn:    { us: "fl_oz", metric: "ml" },
    estCentsPerBase: 0.6, // ~$3–4 for a 20oz Heinz
  },
  {
    id: "mustard", name: "Yellow Mustard", emoji: "🟡", category: "pantry",
    units: [
      { id: "bottle", label: "bottles", toBase: 397 }, // 14oz squeeze
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "tsp",    label: "tsp",     toBase: 5 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tsp",    metric: "ml" },
    measuredIn:    { us: "bottle", metric: "ml" },
    estCentsPerBase: 0.5, // ~$2–3 for a standard yellow mustard
  },
  {
    id: "hot_sauce", name: "Hot Sauce", emoji: "🌶️", category: "pantry", subtype: "sauce",
    units: [
      { id: "bottle", label: "bottles", toBase: 148 }, // ~5oz Tabasco-size bottle
      { id: "tbsp",   label: "tbsp",    toBase: 15 },
      { id: "tsp",    label: "tsp",     toBase: 5 },
      { id: "fl_oz",  label: "fl oz",   toBase: 29.57 },
    ],
    defaultUnit: "bottle",
    preferredUnit: { us: "tsp",    metric: "ml" },
    measuredIn:    { us: "fl_oz",  metric: "ml" },
    estCentsPerBase: 2.5, // ~$4–5 for a 5oz bottle; varies wildly by brand
  },
  // ── spices & dried herbs ────────────────────────────────────────────
  //
  // The home-cook spice rack. Every entry here is the DRIED / GROUND
  // shelf-stable form — fresh basil, fresh thyme, etc. live under
  // produce and route to the fridge's Fresh Herbs tile. Category is
  // "pantry" so the default-location heuristic doesn't try to stash
  // garlic powder in the fridge.
  //
  // ── Unit ladder convention (post-Phase-1 nutrition fix) ──
  // Every spice ladder's `toBase` is in GRAMS, not teaspoons. That way
  // `scaleFactor` in src/lib/nutrition.js can apply a `per=100g`
  // nutrition block with just `baseAmount / 100` regardless of whether
  // a recipe measured in tsp, tbsp, oz, or grams. The old tsp-as-base
  // ladder silently returned nonsense for any nutrition calculation
  // because "1 tsp cinnamon" is ~2.6g, not ~2.6g pretending to be 1g.
  //
  // Grams-per-tsp per spice is empirical (USDA + cooking refs). Unlisted
  // spices fall back to SPICE_DEFAULT_GPT (2.5 g/tsp — the average for
  // ground spices) so a new entry without a density still yields
  // plausible numbers rather than zero.

  // Empirical density (grams per level teaspoon) for each spice. Drives
  // the tsp + tbsp toBase values in the factory below. Salts are denser
  // than ground spices; dried herbs are much lighter (lots of air).
  // Whole seeds vs ground forms differ — we break those out where the
  // registry has separate ids.
  ...(() => {
    const SPICE_DEFAULT_GPT = 2.5;
    // Spice → sub-hub assignment (Phase 2 of the hub-tree migration —
    // tmp/hub-tree-design.md). Every spice falls under spice_hub, but
    // the wizard's drilldown reads as a 88-row dump unless we split
    // into sub-hubs (peppercorn_hub, dried_herbs_hub, asian_spices_hub,
    // etc.). Lookup-by-slug so the bracket-array shorthand below stays
    // compact — each .map() callback spreads `{ parentId: SPICE_PARENT_HUB[id] }`
    // into the standardSpice result.
    const SPICE_PARENT_HUB = {
      // salt_hub (pure salts)
      flaky_salt: "salt_hub", kosher_salt: "salt_hub",
      sea_salt: "salt_hub", table_salt: "salt_hub",
      // peppercorn_hub — Piper nigrum genus only. Cayenne /
      // red_pepper_flakes are Capsicum (chiles) and go in
      // paprika_chili_hub below.
      black_pepper: "peppercorn_hub", white_pepper: "peppercorn_hub",
      peppercorns: "peppercorn_hub",
      // paprika_chili_hub — Capsicum-derived spices (chiles, paprika,
      // cayenne, pepper flakes). Distinct from peppercorn_hub.
      paprika: "paprika_chili_hub", smoked_paprika: "paprika_chili_hub",
      sweet_paprika: "paprika_chili_hub", chili_powder: "paprika_chili_hub",
      cayenne: "paprika_chili_hub", red_pepper_flakes: "paprika_chili_hub",
      // indian_spices_hub — includes fenugreek (heavy Indian use,
      // also Middle Eastern).
      cardamom: "indian_spices_hub", coriander: "indian_spices_hub",
      cumin: "indian_spices_hub", cumin_seed: "indian_spices_hub",
      curry_powder: "indian_spices_hub", garam_masala: "indian_spices_hub",
      ground_coriander: "indian_spices_hub", ground_cumin: "indian_spices_hub",
      turmeric: "indian_spices_hub", fenugreek: "indian_spices_hub",
      // warm_spices_hub — aromatic baking-friendly spices. Mace
      // (the nutmeg-tree aril) and juniper berries (gin-style
      // conifer aromatic) belong here, not in baking_spices /
      // seeds_hub respectively.
      allspice: "warm_spices_hub", cinnamon: "warm_spices_hub",
      cloves: "warm_spices_hub", ground_cinnamon: "warm_spices_hub",
      nutmeg: "warm_spices_hub", star_anise: "warm_spices_hub",
      mace: "warm_spices_hub", juniper_berries: "warm_spices_hub",
      // seed_spices_hub
      fennel_seed: "seed_spices_hub", mustard_seed: "seed_spices_hub",
      // dried_herbs_hub
      bay_leaves: "dried_herbs_hub", dried_basil: "dried_herbs_hub",
      dried_chives: "dried_herbs_hub", dried_dill: "dried_herbs_hub",
      dried_marjoram: "dried_herbs_hub", dried_mint: "dried_herbs_hub",
      dried_oregano: "dried_herbs_hub", dried_parsley: "dried_herbs_hub",
      dried_rosemary: "dried_herbs_hub", dried_sage: "dried_herbs_hub",
      dried_tarragon: "dried_herbs_hub", dried_thyme: "dried_herbs_hub",
      herbs_de_provence: "dried_herbs_hub", italian_seasoning: "dried_herbs_hub",
      // powder_seasonings_hub
      garlic_powder: "powder_seasonings_hub",
      ginger_powder: "powder_seasonings_hub",
      onion_powder: "powder_seasonings_hub",
      // seasoning_salts_hub — flavored salts + MSG (umami amplifier,
      // salt-adjacent in usage).
      celery_salt: "seasoning_salts_hub", garlic_salt: "seasoning_salts_hub",
      onion_salt: "seasoning_salts_hub", seasoned_salt: "seasoning_salts_hub",
      smoked_salt: "seasoning_salts_hub", truffle_salt: "seasoning_salts_hub",
      msg: "seasoning_salts_hub",
      // seeds_hub — true seed-based spices/garnishes. Juniper
      // berries moved to warm_spices_hub (not actually seeds).
      annatto: "seeds_hub", black_sesame: "seeds_hub",
      caraway_seed: "seeds_hub", celery_seed: "seeds_hub",
      poppy_seed: "seeds_hub", white_sesame: "seeds_hub",
      // global_blends_hub — adds saffron (Mediterranean/Persian) and
      // sumac (Middle Eastern, NOT Asian).
      berbere: "global_blends_hub", dukkah: "global_blends_hub",
      five_spice: "global_blends_hub", furikake: "global_blends_hub",
      ras_el_hanout: "global_blends_hub", togarashi: "global_blends_hub",
      zaatar: "global_blends_hub", saffron: "global_blends_hub",
      sumac: "global_blends_hub",
      // american_seasonings_hub — Caribbean-style jerk packets are
      // Americanized blends; folded in here since dedicating a single-
      // member caribbean_seasonings_hub is friction without value.
      cajun_seasoning: "american_seasonings_hub",
      everything_bagel: "american_seasonings_hub",
      lemon_pepper: "american_seasonings_hub",
      old_bay: "american_seasonings_hub",
      ranch_seasoning: "american_seasonings_hub",
      taco_seasoning: "american_seasonings_hub",
      jerk_seasoning: "american_seasonings_hub",
      // baking_spices_hub — only true baking-context spices. Mace
      // moved to warm_spices, MSG to seasoning_salts, saffron to
      // global_blends, fenugreek to indian_spices. cream_of_tartar
      // (leavening acid) and ground_mustard (mustard powder /
      // mustard plaster) are the genuine "baking spice" members.
      cream_of_tartar: "baking_spices_hub",
      ground_mustard: "baking_spices_hub",
      // dried_chili_hub (Mexican dried chiles — keep the existing
      // hub which already routes to the dried_chilies tile)
      ancho: "dried_chili_hub", chipotle: "dried_chili_hub",
      guajillo: "dried_chili_hub", pasilla: "dried_chili_hub",
      // korean_spices_hub — renamed from asian_spices_hub. Sumac
      // moved out (Middle Eastern). Gochugaru sits here pending
      // future Korean additions (gochujang_powder, etc.).
      gochugaru: "korean_spices_hub",
      // salt (the bare generic) routes to salt_hub same as flaky/sea/kosher
      salt: "salt_hub",
    };
    const SPICE_DENSITY = {
      // salts (table/sea ~6; kosher is flakier, less dense)
      kosher_salt: 4.0, sea_salt: 5.9, table_salt: 5.9, flaky_salt: 3.0,
      smoked_salt: 5.0, truffle_salt: 5.0, celery_salt: 5.0,
      garlic_salt: 5.5, onion_salt: 5.5, seasoned_salt: 5.8, msg: 5.4,
      // peppers (whole peppercorns denser than ground)
      black_pepper: 2.3, white_pepper: 2.4, peppercorns: 3.5,
      // paprika family + chilis
      paprika: 2.3, smoked_paprika: 2.3, sweet_paprika: 2.3,
      cayenne: 1.8, chili_powder: 2.7, red_pepper_flakes: 1.0,
      // cumin / coriander / cinnamon / turmeric
      cumin: 2.1, ground_cumin: 2.1, cumin_seed: 2.4,
      coriander: 1.8, ground_coriander: 1.8,
      cinnamon: 2.6, ground_cinnamon: 2.6, turmeric: 3.0,
      // dried herbs (very light — mostly air)
      oregano: 1.0, dried_oregano: 1.0, dried_thyme: 1.0,
      dried_rosemary: 1.2, dried_sage: 0.7, dried_basil: 1.4,
      dried_parsley: 0.3, dried_dill: 1.0, dried_tarragon: 1.8,
      dried_marjoram: 0.6, dried_chives: 0.7, dried_mint: 0.7,
      // warm spices / seeds
      cardamom: 2.0, cloves: 2.1, nutmeg: 2.2, allspice: 1.9,
      fennel_seed: 2.0, mustard_seed: 3.3, ground_mustard: 2.0,
      curry_powder: 2.0, garam_masala: 2.5,
      // onion / garlic powders
      garlic_powder: 2.8, onion_powder: 2.4, ginger_powder: 1.8,
      // herb blends
      italian_seasoning: 1.5, herbs_de_provence: 1.5,
      // blends & specialty
      saffron: 0.7, five_spice: 2.3, taco_seasoning: 2.5,
      ranch_seasoning: 2.0, everything_bagel: 3.0, lemon_pepper: 2.5,
      cajun_seasoning: 2.5, jerk_seasoning: 2.3, ras_el_hanout: 2.3,
      berbere: 2.0, dukkah: 3.5, furikake: 2.8, togarashi: 2.3,
      cream_of_tartar: 3.1, old_bay: 2.3, zaatar: 2.5, sumac: 2.5,
      // seeds
      poppy_seed: 2.8, caraway_seed: 2.1, celery_seed: 2.1,
      white_sesame: 3.0, black_sesame: 3.0, mace: 1.7, fenugreek: 3.7,
      juniper_berries: 2.0, annatto: 2.5,
    };

    // Per-100g nutrition for each spice. Salts round to 0 kcal (sodium
    // drives their signal); dried herbs are in the 250-320 range per
    // 100g but recipes use ≤1 tsp so the contribution to a meal is
    // negligible — we still seed them so coverage stays honest. USDA
    // FoodData Central values where available. Spices without an
    // entry pick up the SPICE_DEFAULT_NUTRITION block below.
    const SPICE_NUTRITION = {
      kosher_salt:   { per: "100g", kcal: 0,   sodium_mg: 38400 },
      sea_salt:      { per: "100g", kcal: 0,   sodium_mg: 38400 },
      table_salt:    { per: "100g", kcal: 0,   sodium_mg: 38700 },
      flaky_salt:    { per: "100g", kcal: 0,   sodium_mg: 38400 },
      smoked_salt:   { per: "100g", kcal: 0,   sodium_mg: 38000 },
      truffle_salt:  { per: "100g", kcal: 0,   sodium_mg: 38000 },
      celery_salt:   { per: "100g", kcal: 4,   sodium_mg: 35100 },
      garlic_salt:   { per: "100g", kcal: 75,  sodium_mg: 27200 },
      onion_salt:    { per: "100g", kcal: 50,  sodium_mg: 26800 },
      seasoned_salt: { per: "100g", kcal: 4,   sodium_mg: 33000 },
      msg:           { per: "100g", kcal: 0,   sodium_mg: 12300 },
      black_pepper:  { per: "100g", kcal: 251, protein_g: 10, fat_g: 3.3, carb_g: 64, fiber_g: 26 },
      white_pepper:  { per: "100g", kcal: 296, protein_g: 10, fat_g: 2,   carb_g: 69 },
      peppercorns:   { per: "100g", kcal: 251, protein_g: 10, fat_g: 3.3, carb_g: 64 },
      paprika:        { per: "100g", kcal: 282, protein_g: 14, fat_g: 13, carb_g: 54, fiber_g: 35 },
      smoked_paprika: { per: "100g", kcal: 282, protein_g: 14, fat_g: 13, carb_g: 54, fiber_g: 35 },
      sweet_paprika:  { per: "100g", kcal: 282, protein_g: 14, fat_g: 13, carb_g: 54, fiber_g: 35 },
      cayenne:           { per: "100g", kcal: 318, protein_g: 12, fat_g: 17, carb_g: 57, fiber_g: 27 },
      chili_powder:      { per: "100g", kcal: 282, protein_g: 13, fat_g: 14, carb_g: 50, fiber_g: 34 },
      red_pepper_flakes: { per: "100g", kcal: 318, protein_g: 12, fat_g: 17, carb_g: 57, fiber_g: 27 },
      cumin:           { per: "100g", kcal: 375, protein_g: 18, fat_g: 22, carb_g: 44, fiber_g: 11 },
      ground_cumin:    { per: "100g", kcal: 375, protein_g: 18, fat_g: 22, carb_g: 44, fiber_g: 11 },
      cumin_seed:      { per: "100g", kcal: 375, protein_g: 18, fat_g: 22, carb_g: 44, fiber_g: 11 },
      coriander:       { per: "100g", kcal: 298, protein_g: 12, fat_g: 18, carb_g: 55, fiber_g: 42 },
      ground_coriander:{ per: "100g", kcal: 298, protein_g: 12, fat_g: 18, carb_g: 55, fiber_g: 42 },
      cinnamon:        { per: "100g", kcal: 247, protein_g: 4,  fat_g: 1.2, carb_g: 81, fiber_g: 53 },
      ground_cinnamon: { per: "100g", kcal: 247, protein_g: 4,  fat_g: 1.2, carb_g: 81, fiber_g: 53 },
      turmeric:        { per: "100g", kcal: 312, protein_g: 10, fat_g: 3,   carb_g: 67, fiber_g: 23 },
      oregano:         { per: "100g", kcal: 265, protein_g: 9,  fat_g: 4,   carb_g: 69, fiber_g: 43 },
      dried_oregano:   { per: "100g", kcal: 265, protein_g: 9,  fat_g: 4,   carb_g: 69, fiber_g: 43 },
      bay_leaves:      { per: "100g", kcal: 313, protein_g: 8,  fat_g: 8,   carb_g: 75, fiber_g: 26 },
      cardamom:     { per: "100g", kcal: 311, protein_g: 11, fat_g: 7,  carb_g: 68, fiber_g: 28 },
      cloves:       { per: "100g", kcal: 274, protein_g: 6,  fat_g: 13, carb_g: 66, fiber_g: 33 },
      nutmeg:       { per: "100g", kcal: 525, protein_g: 6,  fat_g: 36, carb_g: 49, fiber_g: 21 },
      allspice:     { per: "100g", kcal: 263, protein_g: 6,  fat_g: 9,  carb_g: 72, fiber_g: 22 },
      fennel_seed:  { per: "100g", kcal: 345, protein_g: 16, fat_g: 15, carb_g: 52, fiber_g: 40 },
      mustard_seed: { per: "100g", kcal: 508, protein_g: 26, fat_g: 36, carb_g: 28, fiber_g: 12 },
      ground_mustard:{ per: "100g", kcal: 508, protein_g: 26, fat_g: 36, carb_g: 28, fiber_g: 12 },
      curry_powder: { per: "100g", kcal: 325, protein_g: 14, fat_g: 14, carb_g: 56, fiber_g: 53 },
      garam_masala: { per: "100g", kcal: 379, protein_g: 13, fat_g: 15, carb_g: 48, fiber_g: 25 },
      garlic_powder: { per: "100g", kcal: 331, protein_g: 17, fat_g: 0.7, carb_g: 73, fiber_g: 9 },
      onion_powder:  { per: "100g", kcal: 341, protein_g: 10, fat_g: 1,   carb_g: 79, fiber_g: 15 },
      ginger_powder: { per: "100g", kcal: 335, protein_g: 9,  fat_g: 4,   carb_g: 72, fiber_g: 14 },
      italian_seasoning: { per: "100g", kcal: 259, protein_g: 10, fat_g: 10, carb_g: 43, fiber_g: 30 },
      herbs_de_provence: { per: "100g", kcal: 271, protein_g: 10, fat_g: 8,  carb_g: 49, fiber_g: 31 },
      dried_thyme:    { per: "100g", kcal: 276, protein_g: 9,  fat_g: 7, carb_g: 64, fiber_g: 37 },
      dried_rosemary: { per: "100g", kcal: 331, protein_g: 5,  fat_g: 15,carb_g: 64, fiber_g: 43 },
      dried_sage:     { per: "100g", kcal: 315, protein_g: 11, fat_g: 13,carb_g: 61, fiber_g: 40 },
      dried_basil:    { per: "100g", kcal: 233, protein_g: 23, fat_g: 4, carb_g: 48, fiber_g: 37 },
      dried_parsley:  { per: "100g", kcal: 292, protein_g: 26, fat_g: 5, carb_g: 51, fiber_g: 27 },
      dried_dill:     { per: "100g", kcal: 253, protein_g: 20, fat_g: 4, carb_g: 56, fiber_g: 12 },
      dried_tarragon: { per: "100g", kcal: 295, protein_g: 23, fat_g: 7, carb_g: 50, fiber_g: 7 },
      dried_chives:   { per: "100g", kcal: 311, protein_g: 21, fat_g: 10,carb_g: 44, fiber_g: 27 },
      dried_mint:     { per: "100g", kcal: 285, protein_g: 20, fat_g: 6, carb_g: 52, fiber_g: 30 },
      white_sesame:  { per: "100g", kcal: 573, protein_g: 18, fat_g: 50, carb_g: 23, fiber_g: 12 },
      black_sesame:  { per: "100g", kcal: 573, protein_g: 18, fat_g: 50, carb_g: 23, fiber_g: 12 },
      poppy_seed:    { per: "100g", kcal: 525, protein_g: 18, fat_g: 42, carb_g: 28, fiber_g: 20 },
      caraway_seed:  { per: "100g", kcal: 333, protein_g: 20, fat_g: 15, carb_g: 50, fiber_g: 38 },
      celery_seed:   { per: "100g", kcal: 392, protein_g: 18, fat_g: 25, carb_g: 41, fiber_g: 12 },
      fenugreek:     { per: "100g", kcal: 323, protein_g: 23, fat_g: 6,  carb_g: 58, fiber_g: 25 },
      saffron:         { per: "100g", kcal: 310, protein_g: 11, fat_g: 6, carb_g: 65, fiber_g: 4 },
      five_spice:      { per: "100g", kcal: 344, protein_g: 8,  fat_g: 11,carb_g: 61 },
      taco_seasoning:  { per: "100g", kcal: 252, protein_g: 9,  fat_g: 3, carb_g: 52, sodium_mg: 14200 },
      ranch_seasoning: { per: "100g", kcal: 440, protein_g: 7,  fat_g: 26,carb_g: 47, sodium_mg: 9800 },
      everything_bagel:{ per: "100g", kcal: 360, protein_g: 14, fat_g: 19,carb_g: 34, sodium_mg: 8900 },
      lemon_pepper:    { per: "100g", kcal: 190, protein_g: 7,  fat_g: 2, carb_g: 42, sodium_mg: 11600 },
      cajun_seasoning: { per: "100g", kcal: 265, protein_g: 10, fat_g: 9, carb_g: 45, sodium_mg: 9000 },
      jerk_seasoning:  { per: "100g", kcal: 265, protein_g: 9,  fat_g: 7, carb_g: 48, sodium_mg: 7500 },
      ras_el_hanout:   { per: "100g", kcal: 292, protein_g: 13, fat_g: 11,carb_g: 46, fiber_g: 23 },
      berbere:         { per: "100g", kcal: 310, protein_g: 13, fat_g: 13,carb_g: 50, fiber_g: 30 },
      dukkah:          { per: "100g", kcal: 480, protein_g: 18, fat_g: 36,carb_g: 23, fiber_g: 12 },
      furikake:        { per: "100g", kcal: 367, protein_g: 15, fat_g: 10,carb_g: 51, sodium_mg: 3800 },
      togarashi:       { per: "100g", kcal: 342, protein_g: 12, fat_g: 13,carb_g: 48, sodium_mg: 850 },
      cream_of_tartar: { per: "100g", kcal: 258, protein_g: 0,  fat_g: 0, carb_g: 62, sodium_mg: 52 },
      old_bay:         { per: "100g", kcal: 194, protein_g: 7,  fat_g: 4, carb_g: 35, sodium_mg: 18500 },
      zaatar:          { per: "100g", kcal: 315, protein_g: 9,  fat_g: 13,carb_g: 45, fiber_g: 22 },
      sumac:           { per: "100g", kcal: 320, protein_g: 7,  fat_g: 11,carb_g: 65, fiber_g: 18 },
      mace:            { per: "100g", kcal: 475, protein_g: 7,  fat_g: 33,carb_g: 50, fiber_g: 20 },
      juniper_berries: { per: "100g", kcal: 314, protein_g: 2,  fat_g: 2, carb_g: 81, fiber_g: 10 },
      annatto:         { per: "100g", kcal: 380, protein_g: 13, fat_g: 3, carb_g: 68, fiber_g: 44 },
      dried_marjoram:  { per: "100g", kcal: 271, protein_g: 13, fat_g: 7, carb_g: 60, fiber_g: 40 },
    };
    // Fallback for spices not individually listed — a mid-range ground-
    // spice profile. Close enough to keep the dashboard honest without
    // claiming knowledge we don't have.
    const SPICE_DEFAULT_NUTRITION = { per: "100g", kcal: 300, protein_g: 10, fat_g: 10, carb_g: 55, fiber_g: 25 };

    // Standard factory — all grams-based. Every spice gets the same
    // five-unit ladder so pantry / recipe conversion is uniform. A
    // typical spice jar is ~2 oz (57 g) across brands; we standardize
    // to that rather than pretending saffron and paprika share a jar
    // size (saffron is sold in 0.5g pinches; treat that as a manual
    // override if it matters — for now "1 jar" ≈ 2 oz is close enough).
    const standardSpice = (id, name, emoji, searchAliases = null) => {
      const gpt = SPICE_DENSITY[id] ?? SPICE_DEFAULT_GPT;
      const parentId = SPICE_PARENT_HUB[id] || undefined;
      const out = {
        id, name, emoji, category: "pantry",
        ...(parentId ? { parentId } : null),
        // Subtype:"spice" is what powers the brand picker's Tier-1.5
        // soft-fallback. Without it, spice canonicals resolved to
        // null subtype and brands declaring `subtypes: ["spice"]`
        // (Best Choice, McCormick, Tone's, Schnucks Selects, etc.)
        // never surfaced for cinnamon/nutmeg/paprika even though
        // those brands obviously make those products. This single
        // field unlocks the cold-start path for every spice in the
        // registry at once.
        subtype: "spice",
        units: [
          { id: "tsp",  label: "tsp",  toBase: gpt },
          { id: "tbsp", label: "tbsp", toBase: gpt * 3 },
          { id: "oz",   label: "oz",   toBase: 28.35 },
          { id: "jar",  label: "jars", toBase: 57 },
          { id: "g",    label: "g",    toBase: 1 },
        ],
        defaultUnit: "jar",
        // Recipes ALWAYS measure spices by spoon — Metric users want
        // grams, US users want teaspoons. Pantry/shopping is the jar
        // (US) or weight (metric). Both pairs stay inside this mass-
        // anchored ladder so no density bridge is needed.
        preferredUnit: { us: "tsp", metric: "g" },
        measuredIn:    { us: "jar", metric: "g" },
        nutrition: SPICE_NUTRITION[id] ?? SPICE_DEFAULT_NUTRITION,
      };
      if (Array.isArray(searchAliases) && searchAliases.length > 0) {
        out.searchAliases = searchAliases;
      }
      return out;
    };

    return [
      // ── TIER 1: the absolute basics ──
      // Every kitchen has these — the "salt pepper garlic" tier plus
      // the half-dozen spices that power 80% of home cooking.
      ...[
        // Salts
                                ["flaky_salt",      "Flaky Salt",             "🧂"],
        // Peppers
        ["black_pepper",    "Black Pepper",           "🫚"],
        ["white_pepper",    "White Pepper",           "⚪"],
        ["peppercorns",     "Peppercorns",            "⚫"],
        // Core ground spices
        ["paprika",         "Paprika",                "🟠"],
        ["smoked_paprika",  "Smoked Paprika",         "🟠"],
        ["sweet_paprika",   "Sweet Paprika",          "🟠"],
        ["cayenne",         "Cayenne Pepper",         "🌶️"],
        ["chili_powder",    "Chili Powder",           "🌶️"],
        ["red_pepper_flakes","Red Pepper Flakes",     "🌶️"],
        ["cumin",           "Cumin",                  "🟤"],
        ["ground_cumin",    "Ground Cumin",           "🟤"],
        ["cumin_seed",      "Cumin Seeds",            "🟤"],
        ["coriander",       "Ground Coriander",       "🟤"],
        ["ground_coriander","Ground Coriander Seeds", "🟤"],
        ["cinnamon",        "Cinnamon",               "🟤"],
        ["ground_cinnamon", "Ground Cinnamon",        "🟤"],
        ["turmeric",        "Turmeric",               "🟡"],
                ["dried_oregano",   "Dried Oregano",          "🌿"],
        ["bay_leaves",      "Bay Leaves",             "🍃"],
      ].map(([id, name, emoji]) => {
        if (id === "bay_leaves") {
          // Count-based — each dried leaf ~0.06 g; a pack is ~14 g (½ oz).
          return {
            id, name, emoji, category: "pantry",
            ...(SPICE_PARENT_HUB[id] ? { parentId: SPICE_PARENT_HUB[id] } : null),
            units: [
              { id: "count", label: "leaves", toBase: 0.06 },
              { id: "pack",  label: "packs",  toBase: 14 },
              { id: "g",     label: "g",      toBase: 1 },
            ],
            defaultUnit: "pack",
            nutrition: SPICE_NUTRITION.bay_leaves,
          };
        }
        return standardSpice(id, name, emoji);
      }),

      // Promoted from bracket-array map by apply_source_edits.mjs:
      // these spices need extras (aliases / parentId) that the
      // bracket shorthand can't carry, so they get full literals.
      {
        ...standardSpice("kosher_salt", "Kosher Salt", "🧂"),
        aliases: ["kosher-salt","salt_kosher"],
      },
      {
        ...standardSpice("sea_salt", "Sea Salt", "🧂"),
        aliases: ["salt_sea"],
      },
      {
        ...standardSpice("table_salt", "Table Salt", "🧂"),
        aliases: ["salt_table"],
      },
      {
        ...standardSpice("oregano", "Oregano", "🌿"),
        parentId: "mediterranean_herbs_hub",
      },

      // ── TIER 2: well-stocked home cook ──
      ...[
        ["cardamom",        "Cardamom",               "🟢"],
        ["cloves",          "Cloves",                 "🟤"],
        ["nutmeg",          "Nutmeg",                 "🟤"],
        ["allspice",        "Allspice",               "🟤"],
        ["star_anise",      "Star Anise",             "⭐"],
        ["fennel_seed",     "Fennel Seeds",           "🟢"],
        ["mustard_seed",    "Mustard Seeds",          "🟡"],
        ["curry_powder",    "Curry Powder",           "🟡"],
        ["garam_masala",    "Garam Masala",           "🟤"],
      ].map(([id, name, emoji]) => {
        if (id === "star_anise") {
          // Count-based — each pod ~1.5 g; tsp (crushed) ~2 g; jar ~14 g.
          return {
            id, name, emoji, category: "pantry",
            ...(SPICE_PARENT_HUB[id] ? { parentId: SPICE_PARENT_HUB[id] } : null),
            units: [
              { id: "count", label: "pods", toBase: 1.5 },
              { id: "tsp",   label: "tsp",  toBase: 2.0 },
              { id: "jar",   label: "jars", toBase: 14 },
              { id: "g",     label: "g",    toBase: 1 },
            ],
            defaultUnit: "jar",
            // Star anise has a strong flavor but tiny amounts — per-
            // 100g kcal is comparable to other whole spices.
            nutrition: { per: "100g", kcal: 337, protein_g: 18, fat_g: 16, carb_g: 50, fiber_g: 15 },
          };
        }
        return standardSpice(id, name, emoji);
      }),

      // ── TIER 3: powders, dried herbs, seasoning salts, seeds, blends ──
      // (garlic_powder, onion_powder, dried_thyme, italian_seasoning, etc.)
      ...[
        ["garlic_powder",       "Garlic Powder",          "🧄"],
        ["onion_powder",        "Onion Powder",           "🧅"],
        ["ginger_powder",       "Ground Ginger",          "🫚"],
        ["italian_seasoning",   "Italian Seasoning",      "🌿"],
        ["herbs_de_provence",   "Herbes de Provence",     "🌿"],
        ["dried_thyme",         "Dried Thyme",            "🌿"],
        ["dried_rosemary",      "Dried Rosemary",         "🌿"],
        ["dried_sage",          "Dried Sage",             "🌿"],
        ["dried_basil",         "Dried Basil",            "🌿"],
        ["dried_parsley",       "Dried Parsley",          "🌿"],
        ["dried_dill",          "Dried Dill",             "🌿"],
        ["dried_tarragon",      "Dried Tarragon",         "🌿"],
        ["dried_marjoram",      "Dried Marjoram",         "🌿"],
        ["celery_salt",         "Celery Salt",            "🧂"],
        ["garlic_salt",         "Garlic Salt",            "🧂"],
        ["onion_salt",          "Onion Salt",             "🧂"],
        ["seasoned_salt",       "Seasoned Salt",          "🧂"],
        ["msg",                 "MSG",                    "🧂"],
        ["saffron",             "Saffron",                "🌸"],
        ["five_spice",          "Chinese Five Spice",     "🧂"],
        ["taco_seasoning",      "Taco Seasoning",         "🌮"],
        ["ranch_seasoning",     "Ranch Seasoning",        "🥗"],
        ["everything_bagel",    "Everything Bagel Seasoning","🥯"],
        ["lemon_pepper",        "Lemon Pepper",           "🍋"],
        ["cajun_seasoning",     "Cajun Seasoning",        "🌶️"],
        ["jerk_seasoning",      "Jerk Seasoning",         "🌶️"],
        ["ras_el_hanout",       "Ras el Hanout",          "🧂"],
        ["berbere",             "Berbere",                "🌶️"],
        ["dukkah",              "Dukkah",                 "🥜"],
        ["furikake",            "Furikake",               "🍚"],
        ["togarashi",           "Shichimi Togarashi",     "🌶️"],
        ["ground_mustard",      "Ground Mustard",         "🟡"],
        ["cream_of_tartar",     "Cream of Tartar",        "🧂"],
        ["poppy_seed",          "Poppy Seeds",            "🌸"],
        ["caraway_seed",        "Caraway Seeds",          "🧂"],
        ["celery_seed",         "Celery Seed",            "🧂"],
        ["dried_chives",        "Dried Chives",           "🌿"],
        ["dried_mint",          "Dried Mint",             "🌿"],
        ["white_sesame",        "White Sesame Seeds",     "🫘"],
        ["black_sesame",        "Black Sesame Seeds",     "🫘"],
        ["mace",                "Mace",                   "🧂"],
        ["fenugreek",           "Fenugreek",              "🧂"],
        ["juniper_berries",     "Juniper Berries",        "🫐"],
        ["annatto",             "Annatto Seeds",          "🟠"],
        ["smoked_salt",         "Smoked Salt",            "🧂"],
        ["truffle_salt",        "Truffle Salt",           "🧂"],
        ["old_bay",             "Old Bay Seasoning",      "🦀"],
        ["zaatar",              "Za'atar",                "🌿"],
        ["sumac",               "Sumac",                  "🟣"],
      ].map(([id, name, emoji]) => standardSpice(id, name, emoji)),
    ];
  })(),

  // ─────────────────────────────────────────────────────────────────────────
  // BRAND-EXPERTISE SEED BATCH
  //
  // Compact canonical seeds for SKUs that brand-expertise (src/data/
  // brandExpertise.js) references — beef sticks, chicken wings,
  // cookies, ice cream, candy, deli meats, sliced cheeses, sauces.
  // These ensure findIngredient(id).category resolves correctly so
  // the brand picker scores hub/category-level matches even when a
  // canonical isn't yet a fully-fleshed-out pantry citizen with a
  // unit ladder, recipe data, or nutrition.
  //
  // Unit math is PLACEHOLDER (every entry has a generic
  // package/oz/g triple; toBase values reflect grams, but "package"
  // toBase is set to a typical SKU weight per category as a rough
  // default). Refine these when the canonical earns real recipe /
  // pantry / receipt-commit usage.
  //
  // parentId is set to a real hub (chicken_hub, beef_hub, pork_hub,
  // turkey_hub, cheese_hub, yogurt_hub) where the canonical clearly
  // belongs to one — gives hubForIngredient a meaningful answer for
  // picker scoring. Pantry-only canonicals (cookies, candy, sauces)
  // have no parentId since the registry doesn't have a hub for them.
  ...(() => {
    // Builders. Three unit kits: SOLID (oz/lb/g + package), FLUID
    // (fl_oz/cup/tbsp/bottle), and PIECES (piece/package + oz/g for
    // candy bars, cookies, sticks).
    const SOLID = (pkgGrams = 113) => [
      { id: "package", label: "package", toBase: pkgGrams },
      { id: "oz",      label: "oz",      toBase: 28.35 },
      { id: "lb",      label: "lb",      toBase: 453.6 },
      { id: "g",       label: "g",       toBase: 1 },
    ];
    const FLUID = (bottleMl = 355) => [
      { id: "bottle",  label: "bottle",  toBase: bottleMl },
      { id: "fl_oz",   label: "fl oz",   toBase: 29.57 },
      { id: "cup",     label: "cups",    toBase: 240 },
      { id: "tbsp",    label: "tbsp",    toBase: 15 },
      { id: "ml",      label: "ml",      toBase: 1 },
    ];
    const PIECES = (pieceGrams = 28, pkgGrams = 200) => [
      { id: "piece",   label: "pieces",  toBase: pieceGrams },
      { id: "package", label: "package", toBase: pkgGrams },
      { id: "oz",      label: "oz",      toBase: 28.35 },
      { id: "g",       label: "g",       toBase: 1 },
    ];

    // Subtype helper — `subtype` is the load-bearing finer-grained
    // axis for brand-expertise classification (see brandExpertise.js).
    // Without it, "pantry" is too coarse: cookies and candy both
    // route to "pantry" and the picker can't tell that M&M's on a
    // cookie is an inclusion, not a co-brand. With subtype, the
    // picker compares brand.subtypes vs canonical.subtype and demotes
    // mismatches to ingredient.
    const seed = (id, name, emoji, category, subtype, units, defaultUnit, extra = {}) => ({
      id, name, emoji, category, subtype, units, defaultUnit, ...extra,
    });

    return [
      // ── meat — snack meats / jerky ───────────────────────────────
      seed("beef_stick",     "Beef Stick",      "🥩", "meat", "snack_meat", PIECES(28, 28),  "piece", { parentId: "beef_hub" }),
      seed("beef_jerky",     "Beef Jerky",      "🥩", "meat", "snack_meat", SOLID(85),       "package", { parentId: "beef_hub" }),
      seed("snack_stick",    "Snack Stick",     "🥩", "meat", "snack_meat", PIECES(28, 28),  "piece", { parentId: "beef_hub" }),
      seed("pork_stick",     "Pork Stick",      "🥓", "meat", "snack_meat", PIECES(28, 28),  "piece", { parentId: "pork_hub" }),
      seed("pork_jerky",     "Pork Jerky",      "🥓", "meat", "snack_meat", SOLID(85),       "package", { parentId: "pork_hub" }),
      seed("turkey_jerky",   "Turkey Jerky",    "🦃", "meat", "snack_meat", SOLID(85),       "package", { parentId: "turkey_hub" }),

      // ── meat — chicken cuts ──────────────────────────────────────
      // chicken_breast / chicken_thigh (singular) / chicken_wing /
      // ground_chicken / chicken_leg / chicken_tenderloin all live as
      // INLINE canonicals around lines 689-792 above with full unit
      // ladders, nutrition, and parentId="chicken_hub" already set.
      // Seeding them again here previously created duplicate entries
      // in the hub's member list (the wizard rendered "Chicken Breast"
      // twice because INGREDIENTS.filter(parentId === "chicken_hub")
      // returned both copies). Plural slugs (chicken_wings,
      // chicken_thighs) are read-compat aliases handled by
      // CANONICAL_ALIASES, not first-class canonicals. Only
      // chicken_nuggets is seeded here because it's distinct from the
      // species/cuts and has no inline declaration.
      seed("chicken_nuggets", "Chicken Nuggets", "🍗", "meat", "nugget",       PIECES(20, 454), "package", { parentId: "chicken_hub" }),

      // ── meat — deli ──────────────────────────────────────────────
      seed("bologna",        "Bologna",         "🥩", "meat", "deli",    SOLID(454),      "lb", { parentId: "beef_hub" }),
      seed("pastrami",       "Pastrami",        "🥩", "meat", "deli",    SOLID(227),      "package", { parentId: "beef_hub" }),
      seed("roast_beef",     "Roast Beef",      "🥩", "meat", "deli",    SOLID(227),      "package", { parentId: "beef_hub" }),
      seed("mortadella",     "Mortadella",      "🥓", "meat", "deli",    SOLID(227),      "package", { parentId: "pork_hub" }),
      seed("capicola",       "Capicola",        "🥓", "meat", "deli",    SOLID(227),      "package", { parentId: "pork_hub" }),
      seed("kielbasa",       "Kielbasa",        "🥓", "meat", "sausage", SOLID(454),      "lb", { parentId: "pork_hub" }),
      seed("smoked_sausage", "Smoked Sausage",  "🥓", "meat", "sausage", SOLID(454),      "lb", { parentId: "pork_hub" }),      // Frank is its OWN canonical, NOT an alias of hot_dogs.
      // Franks are traditionally all-pork; hot dogs can be any meat.
      // The compositional-tier rule beats them BOTH against
      // primitives (cheese, sugar) — "Cheese Frank" → frank canonical
      // with cheese demoted to a claim chip.
      // ── dairy — sliced & shredded cheese ────────────────────────
      // Generic cheese (the building-block ingredient form). Specific
      // cheeses (cheddar, mozzarella, swiss, etc.) are separate
      // canonicals with subtype="cheese". Generic "cheese" exists so
      // a productName like "Bar-S Cheese Frank" matches both `frank`
      // (tier 3) and `cheese` (tier 2 default) — the tier-aware
      // resolver picks frank, demotes cheese to a claim chip.
      seed("american_cheese", "American Cheese", "🧀", "dairy", "cheese", PIECES(21, 340), "package", { parentId: "cheese_hub" }),
      seed("swiss",           "Swiss",           "🧀", "dairy", "cheese", SOLID(227),      "package", { parentId: "cheese_hub" }),
      // provolone, muenster have inline canonicals — see lines ~700-1500
      seed("string_cheese",   "String Cheese",   "🧀", "dairy", "cheese", PIECES(28, 340), "piece",   { parentId: "cheese_hub" }),
      seed("shredded_cheese", "Shredded Cheese", "🧀", "dairy", "cheese", SOLID(227),      "package", { parentId: "cheese_hub" }),
      seed("sliced_cheese",   "Sliced Cheese",   "🧀", "dairy", "cheese", PIECES(21, 227), "package", { parentId: "cheese_hub" }),
      seed("cheese_singles",  "Cheese Singles",  "🧀", "dairy", "cheese", PIECES(21, 340), "package", { parentId: "cheese_hub" }),
      seed("irish_cheese",    "Irish Cheese",    "🧀", "dairy", "cheese", SOLID(227),      "package", { parentId: "cheese_hub" }),

      // ── dairy — pre-mixed cheese blends (compound canonicals) ───
      // These are real shelf SKUs (Sargento, Kraft, Borden, etc.) sold
      // as pre-shredded mixes. Each gets its own canonical so a scan of
      // "Sargento Italian Blend Shredded" binds to italian_cheese_blend
      // (tier 3 — assembled product) instead of the generic
      // shredded_cheese (tier 2). The `compound: true` flag tells item
      // surfaces to render the constituents as small chips, and
      // `ingredients` lists the actual cheese canonicals in the mix
      // so search by any constituent ("mozzarella", "asiago") still
      // hits these blends. searchAliases catch the casual phrasings.
      seed("italian_cheese_blend", "Italian Cheese Blend", "🧀", "dairy", "cheese", SOLID(227), "package", {
        parentId: "cheese_hub",
        compound: true,
        ingredients: ["mozzarella", "provolone", "parmesan", "asiago", "romano"],
        searchAliases: ["italian blend", "italian shredded", "italian cheese", "italian 4 cheese", "italian five cheese"],
      }),
      seed("mexican_cheese_blend", "Mexican Cheese Blend", "🧀", "dairy", "cheese", SOLID(227), "package", {
        parentId: "cheese_hub",
        compound: true,
        ingredients: ["monterey_jack", "cheddar", "queso_quesadilla", "asadero"],
        searchAliases: ["mexican blend", "mexican shredded", "mexican cheese", "fiesta blend", "taco cheese", "queso shredded"],
      }),
      seed("four_cheese_blend", "Four Cheese Blend", "🧀", "dairy", "cheese", SOLID(227), "package", {
        parentId: "cheese_hub",
        compound: true,
        ingredients: ["mozzarella", "cheddar", "monterey_jack", "parmesan"],
        searchAliases: ["four cheese", "4 cheese", "quattro formaggi", "four cheese blend"],
      }),
      seed("pizza_cheese_blend", "Pizza Cheese Blend", "🧀", "dairy", "cheese", SOLID(227), "package", {
        parentId: "cheese_hub",
        compound: true,
        ingredients: ["mozzarella", "provolone", "parmesan"],
        searchAliases: ["pizza blend", "pizza cheese", "pizza shredded", "pizza mozzarella"],
      }),
      seed("nacho_cheese_blend", "Nacho Cheese Blend", "🧀", "dairy", "cheese", SOLID(227), "package", {
        parentId: "cheese_hub",
        compound: true,
        ingredients: ["cheddar", "monterey_jack"],
        searchAliases: ["nacho blend", "nacho cheese", "cheddar jack", "colby jack"],
      }),

      // ── dairy — yogurt / frozen ─────────────────────────────────
      seed("yogurt_drink",    "Yogurt Drink",    "🥤", "dairy", "yogurt",    FLUID(207),      "bottle",  { parentId: "yogurt_hub" }),
      seed("frozen_yogurt",   "Frozen Yogurt",   "🍦", "dairy", "ice_cream", FLUID(473),      "bottle",  { parentId: "yogurt_hub" }),
      seed("ice_cream",       "Ice Cream",       "🍨", "dairy", "ice_cream", FLUID(473),      "bottle", { storage: { location: "freezer" } }),
      seed("ice_cream_bar",   "Ice Cream Bar",   "🍡", "dairy", "ice_cream", PIECES(80, 480), "piece",  { storage: { location: "freezer" } }),

      // ── pantry — cookies / crackers ─────────────────────────────
      seed("cookie",                "Cookie",                "🍪", "pantry", "cookie",  PIECES(15, 340), "piece"),
      seed("chocolate_chip_cookie", "Chocolate Chip Cookie", "🍪", "pantry", "cookie",  PIECES(15, 340), "piece"),
      seed("sandwich_cookie",       "Sandwich Cookie",       "🍪", "pantry", "cookie",  PIECES(11, 432), "piece"),
      seed("cookies_and_cream",     "Cookies & Cream",       "🍪", "pantry", "cookie",  PIECES(15, 340), "piece"),
      seed("cracker",               "Cracker",               "🍘", "pantry", "cracker", PIECES(3, 200),  "package"),
      seed("goldfish",              "Goldfish",              "🐟", "pantry", "cracker", SOLID(187),      "package"),

      // ── pantry — candy / chocolate ──────────────────────────────
      seed("candy",                "Candy",                "🍬", "pantry", "candy",     PIECES(5,   200), "package"),
      seed("candy_bar",            "Candy Bar",            "🍫", "pantry", "candy",     PIECES(50,  50),  "piece"),
      seed("chocolate",            "Chocolate",            "🍫", "pantry", "chocolate", SOLID(100),       "package"),
      seed("chocolate_candy",      "Chocolate Candy",      "🍫", "pantry", "candy",     PIECES(5,   200), "package", { aliases: ["chocolate_candies"] }),
      seed("peanut_butter_candy",  "Peanut Butter Candy",  "🥜", "pantry", "candy",     PIECES(15,  200), "package"),
      seed("peanut_butter_cup",    "Peanut Butter Cup",    "🥜", "pantry", "candy",     PIECES(20,  40),  "piece"),
      seed("cocoa_powder",         "Cocoa Powder",         "🍫", "pantry", "chocolate", SOLID(227),       "package"),
      seed("chocolate_chips",      "Chocolate Chips",      "🍫", "pantry", "chocolate", SOLID(340),       "package"),
      seed("chocolate_spread",     "Chocolate Spread",     "🍫", "pantry", "spread",    SOLID(370),       "package"),
      seed("hazelnut_spread",      "Hazelnut Spread",      "🌰", "pantry", "spread",    SOLID(370),       "package"),

      // ── pantry — sauces / condiments / dressings ────────────────
      seed("hot_honey",          "Hot Honey",            "🍯", "pantry", "sweetener", FLUID(355), "bottle"),
      seed("buffalo_sauce",      "Buffalo Sauce",        "🌶️", "pantry", "sauce",     FLUID(355), "bottle"),
      seed("wing_sauce",         "Wing Sauce",           "🌶️", "pantry", "sauce",     FLUID(355), "bottle"),
      seed("bbq_sauce",          "BBQ Sauce",            "🍖", "pantry", "sauce",     FLUID(510), "bottle"),
      seed("mayonnaise",         "Mayonnaise",           "🥚", "pantry", "condiment", FLUID(887), "bottle", { aliases: ["mayo"] }),
      seed("yellow_mustard",     "Yellow Mustard",       "🌭", "pantry", "condiment", FLUID(383), "bottle"),
      seed("relish",             "Relish",               "🥒", "pantry", "condiment", FLUID(296), "bottle"),
      seed("worcestershire",     "Worcestershire",       "🥩", "pantry", "sauce",     FLUID(296), "bottle"),
      seed("ranch_dressing",     "Ranch Dressing",       "🥗", "pantry", "dressing",  FLUID(473), "bottle"),
      seed("blue_cheese_dressing","Blue Cheese Dressing","🥗", "pantry", "dressing",  FLUID(473), "bottle"),
      seed("italian_dressing",   "Italian Dressing",     "🥗", "pantry", "dressing",  FLUID(473), "bottle"),

      // ── pantry — boxed / shelf-stable ───────────────────────────
      seed("mac_and_cheese",  "Mac & Cheese", "🧀", "pantry", "boxed_meal", PIECES(206, 206), "package", { parentId: "pasta_hub", aliases: ["mac_cheese"] }),
      seed("cereal",          "Cereal",       "🥣", "pantry", "cereal",     SOLID(454),       "package", { parentId: "rice_hub" }),
      seed("water",           "Water",        "💧", "pantry", "beverage",   FLUID(500),       "bottle"),

      // ── 2026-04-30 batch — coverage expansion ──────────────────
      // Big-batch addition of common ingredients that were missing
      // from the registry. Minimum-viable canonicals (id, name,
      // category, subtype, unit ladder, default unit) so they exist
      // and can be tagged on rows immediately; full editorial
      // (nutrition, shelf life, allergens, prep tips) lands in
      // Phase 2/3 of the canonical-data-quality push.

      // ── herbs (fresh) — basil/cilantro/parsley already exist ────
      seed("thyme",         "Fresh Thyme",       "🌿", "produce", "herb", PIECES(1, 28),    "sprig"),
      seed("rosemary",      "Fresh Rosemary",    "🌿", "produce", "herb", PIECES(2, 28),    "sprig"),
      seed("sage",          "Fresh Sage",        "🌿", "produce", "herb", PIECES(1, 28),    "sprig"),
      seed("dill",          "Fresh Dill",        "🌿", "produce", "herb", PIECES(2, 28),    "sprig"),
      seed("mint",          "Fresh Mint",        "🌿", "produce", "herb", PIECES(2, 28),    "sprig"),
      seed("chives",        "Chives",            "🌿", "produce", "herb", PIECES(2, 28),    "tbsp"),
      seed("tarragon",      "Fresh Tarragon",    "🌿", "produce", "herb", PIECES(1, 28),    "sprig"),
      seed("marjoram",      "Fresh Marjoram",    "🌿", "produce", "herb", PIECES(1, 28),    "sprig"),

      // ── peppers / chiles — fresh ────────────────────────────────
      seed("jalapeno",      "Jalapeño",          "🌶️", "produce", "chile", PIECES(14, 100), "piece", {aliases: ["jalape_os", "jalapeños", "jalapenos"], parentId: "pepper_hub" }),
      seed("serrano",       "Serrano Pepper",    "🌶️", "produce", "chile", PIECES(7, 100),  "piece", { parentId: "pepper_hub" }),
      seed("habanero",      "Habanero",          "🌶️", "produce", "chile", PIECES(8, 100),  "piece", { parentId: "pepper_hub" }),
      seed("poblano",       "Poblano",           "🫑", "produce", "chile", PIECES(85, 200), "piece", { parentId: "pepper_hub" }),
      seed("anaheim",       "Anaheim Pepper",    "🌶️", "produce", "chile", PIECES(60, 200), "piece", { parentId: "pepper_hub" }),
      seed("bell_pepper_red",    "Red Bell Pepper",    "🫑", "produce", "pepper", PIECES(120, 200), "piece"),
      seed("bell_pepper_yellow", "Yellow Bell Pepper", "🫑", "produce", "pepper", PIECES(120, 200), "piece"),
      seed("bell_pepper_orange", "Orange Bell Pepper", "🫑", "produce", "pepper", PIECES(120, 200), "piece"),
      seed("scotch_bonnet", "Scotch Bonnet",     "🌶️", "produce", "chile", PIECES(8, 100),  "piece", { parentId: "pepper_hub" }),
      seed("thai_chile",    "Thai Chile",        "🌶️", "produce", "chile", PIECES(2, 50),   "piece", { parentId: "pepper_hub" }),
      // ── peppers / chiles — dried ────────────────────────────────
      seed("ancho",         "Ancho Chile",       "🌶️", "pantry", "dried_chile", SOLID(20),  "oz"),
      seed("chipotle",      "Chipotle (dried)",  "🌶️", "pantry", "dried_chile", SOLID(20),  "oz", { searchAliases: ["chipotle"] }),
      seed("guajillo",      "Guajillo Chile",    "🌶️", "pantry", "dried_chile", SOLID(20),  "oz"),
      seed("pasilla",       "Pasilla Chile",     "🌶️", "pantry", "dried_chile", SOLID(20),  "oz"),

      // ── vegetables — gap fills ──────────────────────────────────
      seed("asparagus",      "Asparagus",         "🥬", "produce", "vegetable", SOLID(454), "lb", { parentId: "stalk_vegetable_hub" }),
      seed("brussels_sprouts","Brussels Sprouts", "🥬", "produce", "vegetable", SOLID(454), "lb", { parentId: "cruciferous_hub" }),
      seed("leek",           "Leek",              "🧅", "produce", "vegetable", PIECES(150, 300), "piece", { parentId: "alliums_hub" }),
      seed("fennel_bulb",    "Fennel Bulb",       "🌿", "produce", "vegetable", PIECES(300, 600), "piece", { parentId: "stalk_vegetable_hub" }),
      seed("artichoke",      "Artichoke",         "🌿", "produce", "vegetable", PIECES(225, 450), "piece", { parentId: "stalk_vegetable_hub" }),
      seed("eggplant",       "Eggplant",          "🍆", "produce", "vegetable", PIECES(450, 900), "piece", { parentId: "fruiting_vegetable_hub" }),
      seed("celery",         "Celery",            "🌿", "produce", "vegetable", PIECES(40, 600),  "stalk", { parentId: "stalk_vegetable_hub" }),
      seed("cabbage",        "Green Cabbage",     "🥬", "produce", "vegetable", PIECES(900, 900), "piece", { parentId: "cruciferous_hub" }),
      seed("red_cabbage",    "Red Cabbage",       "🥬", "produce", "vegetable", PIECES(900, 900), "piece", { parentId: "cruciferous_hub" }),
      seed("napa_cabbage",   "Napa Cabbage",      "🥬", "produce", "vegetable", PIECES(900, 900), "piece", { parentId: "cruciferous_hub" }),
      seed("bok_choy",       "Bok Choy",          "🥬", "produce", "vegetable", PIECES(225, 450), "piece", { parentId: "cruciferous_hub" }),
      seed("green_beans",    "Green Beans",       "🥬", "produce", "vegetable", SOLID(454),       "lb", { parentId: "pea_pod_hub" }),
      seed("snap_peas",      "Sugar Snap Peas",   "🥬", "produce", "vegetable", SOLID(227),       "package", { parentId: "pea_pod_hub" }),
      seed("snow_peas",      "Snow Peas",         "🥬", "produce", "vegetable", SOLID(227),       "package", { parentId: "pea_pod_hub" }),
      seed("butternut_squash","Butternut Squash", "🎃", "produce", "vegetable", PIECES(900, 1800),"piece", { parentId: "squash_hub" }),
      seed("acorn_squash",   "Acorn Squash",      "🎃", "produce", "vegetable", PIECES(680, 1360),"piece", { parentId: "squash_hub" }),
      seed("spaghetti_squash","Spaghetti Squash", "🎃", "produce", "vegetable", PIECES(1360, 2270),"piece", { parentId: "squash_hub" }),
      seed("pumpkin",        "Pumpkin",           "🎃", "produce", "vegetable", PIECES(1800, 3600),"piece", { parentId: "squash_hub" }),
      seed("beet",           "Beet",              "🍠", "produce", "vegetable", PIECES(115, 450), "piece", { parentId: "root_vegetable_hub" }),
      seed("radish",         "Radish",            "🥕", "produce", "vegetable", PIECES(15, 200),  "piece", { parentId: "root_vegetable_hub" }),
      seed("turnip",         "Turnip",            "🥕", "produce", "vegetable", PIECES(120, 450), "piece", { parentId: "root_vegetable_hub" }),
      seed("parsnip",        "Parsnip",           "🥕", "produce", "vegetable", PIECES(110, 450), "piece", { parentId: "root_vegetable_hub" }),
      seed("daikon",         "Daikon Radish",     "🥕", "produce", "vegetable", PIECES(450, 900), "piece", { parentId: "root_vegetable_hub" }),
      seed("jicama",         "Jicama",            "🥔", "produce", "vegetable", PIECES(450, 900), "piece", { parentId: "root_vegetable_hub" }),
      seed("kohlrabi",       "Kohlrabi",          "🌿", "produce", "vegetable", PIECES(225, 450), "piece", { parentId: "cruciferous_hub" }),
      seed("romaine",        "Romaine",           "🥬", "produce", "lettuce",   PIECES(300, 600), "piece", { parentId: "leafy_green_hub" }),
      seed("watercress",     "Watercress",        "🥬", "produce", "leafy_green",SOLID(85),       "package", { parentId: "leafy_green_hub" }),
      seed("collard_greens", "Collard Greens",    "🥬", "produce", "leafy_green",SOLID(454),      "lb", { parentId: "leafy_green_hub" }),
      seed("swiss_chard",    "Swiss Chard",       "🥬", "produce", "leafy_green",SOLID(454),      "lb", { parentId: "leafy_green_hub" }),
      seed("mustard_greens", "Mustard Greens",    "🥬", "produce", "leafy_green",SOLID(454),      "lb", { parentId: "leafy_green_hub" }),
      seed("radicchio",      "Radicchio",         "🥬", "produce", "lettuce",   PIECES(225, 450),"piece", { parentId: "leafy_green_hub" }),
      seed("endive",         "Endive",            "🥬", "produce", "lettuce",   PIECES(115, 230),"piece", { parentId: "leafy_green_hub" }),
      // ── fruits — gap fills ──────────────────────────────────────
      seed("peach",          "Peach",             "🍑", "produce", "stone_fruit", PIECES(150, 450), "piece"),
      seed("plum",           "Plum",              "🍑", "produce", "stone_fruit", PIECES(65, 200),  "piece"),
      seed("apricot",        "Apricot",           "🍑", "produce", "stone_fruit", PIECES(35, 100),  "piece"),
      seed("cherry",         "Cherries",          "🍒", "produce", "stone_fruit", SOLID(454),       "lb"),
      seed("nectarine",      "Nectarine",         "🍑", "produce", "stone_fruit", PIECES(140, 450), "piece"),
      seed("fig",            "Fresh Figs",        "🌿", "produce", "fruit",       SOLID(227),       "package"),
      seed("date",           "Medjool Dates",     "🌿", "pantry",  "dried_fruit", SOLID(227),       "package"),
      seed("raisin",         "Raisins",           "🌿", "pantry",  "dried_fruit", SOLID(425),       "package"),
      seed("prune",          "Prunes",            "🌿", "pantry",  "dried_fruit", SOLID(340),       "package"),
      seed("dried_cranberry","Dried Cranberries", "🌿", "pantry",  "dried_fruit", SOLID(340),       "package"),
      seed("kiwi",           "Kiwi",              "🥝", "produce", "fruit",       PIECES(75, 150),  "piece"),
      seed("mango",          "Mango",             "🥭", "produce", "fruit",       PIECES(340, 680), "piece"),
      seed("papaya",         "Papaya",            "🍈", "produce", "fruit",       PIECES(450, 900), "piece"),
      seed("pineapple",      "Pineapple",         "🍍", "produce", "fruit",       PIECES(900, 1800),"piece"),
      seed("watermelon",     "Watermelon",        "🍉", "produce", "melon",       PIECES(2700, 5400),"piece"),
      seed("cantaloupe",     "Cantaloupe",        "🍈", "produce", "melon",       PIECES(900, 1800),"piece"),
      seed("honeydew",       "Honeydew",          "🍈", "produce", "melon",       PIECES(1800, 2700),"piece"),
      seed("grapefruit",     "Grapefruit",        "🍊", "produce", "citrus",      PIECES(225, 450), "piece"),
      seed("blackberry",     "Blackberries",      "🫐", "produce", "berry",       SOLID(170),       "package"),
      seed("raspberry",      "Raspberries",       "🫐", "produce", "berry",       SOLID(170),       "package"),
      seed("cranberry",      "Cranberries",       "🍒", "produce", "berry",       SOLID(340),       "package"),
      seed("pomegranate",    "Pomegranate",       "🍎", "produce", "fruit",       PIECES(280, 450), "piece"),
      seed("persimmon",      "Persimmon",         "🍊", "produce", "fruit",       PIECES(170, 340), "piece"),
      seed("passion_fruit",  "Passion Fruit",     "🌿", "produce", "fruit",       PIECES(40, 100),  "piece"),
      seed("grape",          "Grapes",            "🍇", "produce", "fruit",       SOLID(454),       "lb", { aliases: ["grapes", "green_grapes"] }),
      seed("pear",           "Pear",              "🍐", "produce", "fruit",       PIECES(180, 360), "piece"),

      // ── seafood — gap fills (we have salmon/tuna/cod/tilapia/shrimp/scallops) ──
      seed("clams",          "Clams",             "🦪", "meat", "shellfish", SOLID(454), "lb",   { parentId: "seafood_hub" }),
      seed("mussels",        "Mussels",           "🦪", "meat", "shellfish", SOLID(900), "lb",   { parentId: "seafood_hub" }),
      seed("oysters",        "Oysters",           "🦪", "meat", "shellfish", PIECES(75, 12), "piece", { parentId: "seafood_hub" }),
      seed("crab",           "Crab",              "🦀", "meat", "shellfish", SOLID(454), "lb",   { parentId: "seafood_hub" }),
      seed("lobster",        "Lobster",           "🦞", "meat", "shellfish", SOLID(680), "piece", { parentId: "seafood_hub" }),
      seed("anchovies",      "Anchovies",         "🐟", "pantry", "tinned_fish", SOLID(50), "tin"),
      seed("sardines",       "Sardines",          "🐟", "pantry", "tinned_fish", SOLID(106),"tin"),
      seed("mackerel",       "Mackerel",          "🐟", "meat", "fish",       SOLID(454), "lb",   { parentId: "seafood_hub" }),
      seed("halibut",        "Halibut",           "🐟", "meat", "fish",       SOLID(454), "lb",   { parentId: "seafood_hub" }),
      seed("trout",          "Trout",             "🐟", "meat", "fish",       SOLID(454), "lb",   { parentId: "seafood_hub" }),
      seed("snapper",        "Red Snapper",       "🐟", "meat", "fish",       SOLID(454), "lb",   { parentId: "seafood_hub" }),
      seed("sea_bass",       "Sea Bass",          "🐟", "meat", "fish",       SOLID(454), "lb",   { parentId: "seafood_hub" }),
      seed("branzino",       "Branzino",          "🐟", "meat", "fish",       PIECES(500, 500), "piece", { parentId: "seafood_hub" }),
      seed("octopus",        "Octopus",           "🐙", "meat", "shellfish",  SOLID(900), "lb",  { parentId: "seafood_hub" }),
      seed("squid",          "Squid (calamari)",  "🦑", "meat", "shellfish",  SOLID(454), "lb",  { parentId: "seafood_hub", searchAliases: ["squid", "calamari"] }),

      // ── beans / legumes — gap fills ─────────────────────────────
      seed("white_beans",     "White Beans",      "🫘", "pantry", "bean", SOLID(425), "can"),
      seed("navy_beans",      "Navy Beans",       "🫘", "pantry", "bean", SOLID(425), "can"),
      seed("lima_beans",      "Lima Beans",       "🫘", "pantry", "bean", SOLID(425), "can"),
      seed("fava_beans",      "Fava Beans",       "🫘", "pantry", "bean", SOLID(425), "can"),
      seed("split_peas",      "Split Peas",       "🫘", "pantry", "bean", SOLID(454), "package"),
      seed("black_eyed_peas", "Black-Eyed Peas",  "🫘", "pantry", "bean", SOLID(425), "can"),
      seed("mung_beans",      "Mung Beans",       "🫘", "pantry", "bean", SOLID(454), "package"),
      seed("red_lentils",     "Red Lentils",      "🫘", "pantry", "bean", SOLID(454), "package"),
      seed("green_lentils",   "Green Lentils",    "🫘", "pantry", "bean", SOLID(454), "package"),
      seed("french_lentils",  "French Lentils",   "🫘", "pantry", "bean", SOLID(454), "package"),
      seed("edamame",         "Edamame",          "🫛", "produce", "bean",  SOLID(454), "package"),

      // ── nuts / seeds ────────────────────────────────────────────
      seed("almonds",        "Almonds",           "🌰", "pantry", "nut",  SOLID(170), "package"),
      seed("walnuts",        "Walnuts",           "🌰", "pantry", "nut",  SOLID(170), "package"),
      seed("pecans",         "Pecans",            "🌰", "pantry", "nut",  SOLID(170), "package"),
      seed("cashews",        "Cashews",           "🌰", "pantry", "nut",  SOLID(170), "package"),
      seed("pistachios",     "Pistachios",        "🌰", "pantry", "nut",  SOLID(227), "package"),
      seed("hazelnuts",      "Hazelnuts",         "🌰", "pantry", "nut",  SOLID(170), "package"),
      seed("macadamia_nuts", "Macadamia Nuts",    "🌰", "pantry", "nut",  SOLID(170), "package"),
      seed("brazil_nuts",    "Brazil Nuts",       "🌰", "pantry", "nut",  SOLID(170), "package"),
      seed("peanuts",        "Peanuts",           "🥜", "pantry", "nut",  SOLID(454), "package"),
      seed("pine_nuts",      "Pine Nuts",         "🌰", "pantry", "nut",  SOLID(85),  "package"),
      seed("sesame_seeds",   "Sesame Seeds",      "🌿", "pantry", "seed", SOLID(85),  "package"),
      seed("chia_seeds",     "Chia Seeds",        "🌿", "pantry", "seed", SOLID(340), "package"),
      seed("flax_seeds",     "Flax Seeds",        "🌿", "pantry", "seed", SOLID(340), "package"),
      seed("hemp_seeds",     "Hemp Seeds",        "🌿", "pantry", "seed", SOLID(227), "package"),
      seed("pumpkin_seeds",  "Pumpkin Seeds",     "🌰", "pantry", "seed", SOLID(227), "package"),
      seed("sunflower_seeds","Sunflower Seeds",   "🌰", "pantry", "seed", SOLID(227), "package"),
      seed("poppy_seeds",    "Poppy Seeds",       "🌿", "pantry", "seed", SOLID(85),  "package"),

      // ── Asian ingredients ───────────────────────────────────────
      seed("tofu",           "Tofu",              "🥡", "produce", "soy",        SOLID(397), "package"),
      seed("silken_tofu",    "Silken Tofu",       "🥡", "produce", "soy",        SOLID(340), "package"),
      seed("firm_tofu",      "Firm Tofu",         "🥡", "produce", "soy",        SOLID(397), "package"),
      seed("extra_firm_tofu","Extra-Firm Tofu",   "🥡", "produce", "soy",        SOLID(397), "package"),
      seed("tempeh",         "Tempeh",            "🥡", "produce", "soy",        SOLID(227), "package"),
      seed("seitan",         "Seitan",            "🥡", "pantry",  "wheat_protein", SOLID(227), "package", { parentId: "flour_hub" }),
      seed("kimchi",         "Kimchi",            "🥬", "produce", "ferment",    SOLID(454), "jar", { parentId: "fermented_vegetables_hub" }),
      seed("sauerkraut",     "Sauerkraut",        "🥬", "produce", "ferment",    SOLID(454), "jar", { parentId: "fermented_vegetables_hub" }),
      seed("wakame",         "Wakame",            "🌿", "pantry",  "sea_vegetable", SOLID(57),  "package", { parentId: "sea_vegetable_hub" }),
      seed("kombu",          "Kombu",             "🌿", "pantry",  "sea_vegetable", SOLID(57),  "package", { parentId: "sea_vegetable_hub" }),
      seed("hijiki",         "Hijiki",            "🌿", "pantry",  "sea_vegetable", SOLID(57),  "package", { parentId: "sea_vegetable_hub" }),
      seed("dashi_powder",   "Dashi Powder",      "🌿", "pantry",  "stock",         SOLID(57),  "package"),
      // oyster_sauce + mirin have inline canonicals — keep only unique seeds.
      seed("hoisin_sauce",   "Hoisin Sauce",      "🥢", "pantry",  "sauce",         FLUID(220), "bottle", { aliases: ["hoisin"] }),
      seed("ponzu",          "Ponzu",             "🥢", "pantry",  "sauce",         FLUID(296), "bottle"),
      seed("sake",           "Sake (cooking)",    "🍶", "pantry",  "cooking_wine",  FLUID(750), "bottle", { searchAliases: ["sake"] }),
      seed("rice_wine_vinegar","Rice Wine Vinegar","🍶","pantry",  "vinegar",       FLUID(355), "bottle"),
      seed("gochugaru",      "Gochugaru",         "🌶️", "pantry",  "spice",         SOLID(227), "package", { parentId: "korean_spices_hub" }),
      seed("gochujang",      "Gochujang",         "🌶️", "pantry",  "ferment",       SOLID(500), "tub"),
      seed("doenjang",       "Doenjang",          "🥢", "pantry",  "ferment",       SOLID(454), "jar"),
      seed("ssamjang",       "Ssamjang",          "🥢", "pantry",  "sauce",         SOLID(454), "jar"),
      seed("lemongrass",     "Lemongrass",        "🌿", "produce", "aromatic",      PIECES(35, 100), "stalk", { parentId: "fresh_herb_hub" }),
      seed("galangal",       "Galangal",          "🌿", "produce", "aromatic",      SOLID(57),  "package", { parentId: "root_vegetable_hub" }),
      seed("kaffir_lime",    "Kaffir Lime Leaves","🌿", "produce", "aromatic",      SOLID(28),  "package", { parentId: "fresh_herb_hub" }),
      seed("thai_basil",     "Thai Basil",        "🌿", "produce", "herb",          SOLID(28),  "package"),
      seed("coconut_milk",   "Coconut Milk",      "🥥", "pantry",  "milk_alt",      FLUID(400), "can"),
      seed("coconut_cream",  "Coconut Cream",     "🥥", "pantry",  "milk_alt",      FLUID(400), "can"),
      seed("coconut_water",  "Coconut Water",     "🥥", "pantry",  "beverage",      FLUID(330), "bottle"),
      seed("curry_paste_red","Red Curry Paste",   "🥥", "pantry",  "sauce",         SOLID(113), "jar"),
      seed("curry_paste_green","Green Curry Paste","🥥","pantry",  "sauce",         SOLID(113), "jar"),
      seed("curry_paste_yellow","Yellow Curry Paste","🥥","pantry","sauce",         SOLID(113), "jar"),
      seed("curry_paste_massaman","Massaman Paste","🥥","pantry",  "sauce",         SOLID(113), "jar"),

      // ── Mediterranean / Middle Eastern ──────────────────────────
      seed("olives_kalamata","Kalamata Olives",   "🫒", "pantry",  "olive",     SOLID(170), "jar"),
      seed("olives_green",   "Green Olives",      "🫒", "pantry",  "olive",     SOLID(227), "jar"),
      seed("olives_black",   "Black Olives",      "🫒", "pantry",  "olive",     SOLID(170), "can"),
      seed("capers",         "Capers",            "🌿", "pantry",  "pickle",    SOLID(85),  "jar"),
      seed("tahini",         "Tahini",            "🌿", "pantry",  "sauce",     SOLID(454), "jar"),
      seed("hummus",         "Hummus",            "🌿", "produce", "dip",       SOLID(283), "tub"),
      seed("baba_ganoush",   "Baba Ganoush",      "🌿", "produce", "dip",       SOLID(227), "tub"),
      seed("pita_bread",     "Pita Bread",        "🍞", "pantry",  "bread",     PIECES(60, 360), "piece"),
      seed("phyllo_dough",   "Phyllo Dough",      "🍞", "pantry",  "dough",     SOLID(454), "package", { parentId: "laminated_dough_hub" }),
      seed("pomegranate_molasses","Pomegranate Molasses","🌿","pantry","sauce", FLUID(295), "bottle"),
      seed("rose_water",     "Rose Water",        "🌹", "pantry",  "extract",   FLUID(250), "bottle"),
      seed("orange_blossom_water","Orange Blossom Water","🌼","pantry","extract",FLUID(250),"bottle"),
      seed("preserved_lemon","Preserved Lemon",   "🍋", "pantry",  "preserve",  SOLID(227), "jar", { parentId: "pickle_ferment_hub" }),

      // ── pasta — gap fills ───────────────────────────────────────
      // ravioli / tortellini / gnocchi / orecchiette / bucatini /
      // cavatappi all have full inline canonicals up around lines
      // 1851-1972 with parentId="pasta_hub" already set. Seeding
      // them again here would duplicate the wizard's pasta_hub
      // member list (and double-bill any AI brand-population pass
      // since INGREDIENTS gets walked once per id × occurrence).
      // Only the seeds with no inline counterpart remain.
      seed("pappardelle",    "Pappardelle",       "🍝", "pantry", "pasta_long",   SOLID(454), "package", { parentId: "pasta_hub" }),
      seed("tagliatelle",    "Tagliatelle",       "🍝", "pantry", "pasta_long",   SOLID(454), "package", { parentId: "pasta_hub" }),
      seed("lasagna_sheets", "Lasagna Sheets",    "🍝", "pantry", "pasta_sheet",  SOLID(454), "package", { parentId: "pasta_hub" }),
      seed("conchiglie",     "Conchiglie (shells)","🍝","pantry", "pasta_short",  SOLID(454), "package", { parentId: "pasta_hub", searchAliases: ["conchiglie", "shells"] }),
      seed("egg_noodles",    "Egg Noodles",       "🍜", "pantry", "noodle",       SOLID(454), "package", { parentId: "pasta_hub" }),

      // ── grains / starches — gap fills ───────────────────────────
      seed("couscous",       "Couscous",          "🌾", "pantry", "grain",  SOLID(454), "package"),
      seed("bulgur",         "Bulgur",            "🌾", "pantry", "grain",  SOLID(454), "package"),
      seed("farro",          "Farro",             "🌾", "pantry", "grain",  SOLID(454), "package"),
      seed("barley",         "Pearl Barley",      "🌾", "pantry", "grain",  SOLID(454), "package"),
      seed("polenta",        "Polenta",           "🌽", "pantry", "grain",  SOLID(680), "package"),
      // cornmeal + masa_harina have inline canonicals.
      seed("steel_cut_oats", "Steel-Cut Oats",    "🌾", "pantry", "grain",  SOLID(680), "package"),
      seed("rolled_oats",    "Rolled Oats",       "🌾", "pantry", "grain",  SOLID(454), "package"),
      seed("millet",         "Millet",            "🌾", "pantry", "grain",  SOLID(454), "package"),

      // ── flours / starches ───────────────────────────────────────
      // coconut_flour has inline canonical.
      seed("tapioca_flour",  "Tapioca Flour",     "🌾", "pantry", "flour",  SOLID(454), "package"),
      seed("cornstarch",     "Cornstarch",        "🌽", "pantry", "starch", SOLID(454), "package", { aliases: ["corn_starch"] }),
      seed("arrowroot",      "Arrowroot",         "🌾", "pantry", "starch", SOLID(227), "package"),
      seed("rye_flour",      "Rye Flour",         "🌾", "pantry", "flour",  SOLID(680), "package"),
      seed("buckwheat_flour","Buckwheat Flour",   "🌾", "pantry", "flour",  SOLID(454), "package"),

      // ── sweeteners / baking ─────────────────────────────────────
      seed("brown_sugar",    "Brown Sugar",       "🍬", "pantry", "sweetener", SOLID(454), "package", { aliases: ["dark_brown_cane_sugar"] }),
      seed("powdered_sugar", "Powdered Sugar",    "🍬", "pantry", "sweetener", SOLID(454), "package"),
      // white_sugar retired — collapsed into cane_sugar (which is
      // defined inline above with sugar + white_sugar + granulated_sugar
      // as aliases). CANONICAL_ALIASES redirects legacy reads.
      seed("turbinado_sugar","Turbinado Sugar",   "🍬", "pantry", "sweetener", SOLID(680), "package"),
      seed("agave_nectar",   "Agave Nectar",      "🌿", "pantry", "sweetener", FLUID(355), "bottle"),
      seed("molasses",       "Molasses",          "🌿", "pantry", "sweetener", FLUID(355), "bottle"),
      seed("corn_syrup",     "Corn Syrup",        "🌽", "pantry", "sweetener", FLUID(454), "bottle"),
      seed("vanilla_extract","Vanilla Extract",   "🌿", "pantry", "extract",   FLUID(118), "bottle", { searchAliases: ["extract"] }),
      seed("almond_extract", "Almond Extract",    "🌿", "pantry", "extract",   FLUID(59),  "bottle"),
      // Yeast — recipes ALWAYS measure in tsp / packet / tbsp, never
      // in grams or oz. SOLID() ladders shipped without those units;
      // recipes asking for "1 tsp instant yeast" couldn't convert
      // against pantry rows in oz/package, leaving cooks looking at
      // "have N package" with no idea how many tsp that is. Custom
      // ladder + density gives the volume↔mass bridge what it needs.
      // Standard packet = 7g instant ≈ 2¼ tsp.
      // density_g_per_ml ≈ 0.62 (granular dry).
      {
        id: "active_dry_yeast", name: "Active Dry Yeast", emoji: "🌾",
        category: "pantry", subtype: "leavener",
        units: [
          { id: "tsp",     label: "tsp",     toBase: 3.1 },
          { id: "tbsp",    label: "tbsp",    toBase: 9.4 },
          { id: "packet",  label: "packet",  toBase: 7 },
          { id: "oz",      label: "oz",      toBase: 28.35 },
          { id: "g",       label: "g",       toBase: 1 },
          { id: "package", label: "package", toBase: 113 },
        ],
        defaultUnit: "packet",
        density_g_per_ml: 0.62,
      },
      {
        id: "instant_yeast", name: "Instant Yeast", emoji: "🌾",
        category: "pantry", subtype: "leavener",
        units: [
          { id: "tsp",     label: "tsp",     toBase: 3.1 },
          { id: "tbsp",    label: "tbsp",    toBase: 9.4 },
          { id: "packet",  label: "packet",  toBase: 7 },
          { id: "oz",      label: "oz",      toBase: 28.35 },
          { id: "g",       label: "g",       toBase: 1 },
          { id: "package", label: "package", toBase: 113 },
        ],
        defaultUnit: "packet",
        density_g_per_ml: 0.62,
      },
      // Salt seeds removed 2026-05-02 — `sea_salt` / `kosher_salt` /
      // `table_salt` already exist with proper tsp/tbsp/cup ladders +
      // density (see SPICE_DENSITY block at line ~2725). The legacy
      // `salt_*` slugs were duplicates with broken SOLID() ladders;
      // recipes asking for "1 tsp coarse sea salt" couldn't convert
      // against pantry rows on the broken slug. CANONICAL_ALIASES
      // entries below redirect any old `salt_sea` / `salt_kosher` /
      // `salt_table` pantry rows to the proper slugs so existing data
      // doesn't orphan.

      // ── vinegars ────────────────────────────────────────────────
      seed("balsamic_vinegar","Balsamic Vinegar", "🍷", "pantry", "vinegar", FLUID(355), "bottle", { aliases: ["balsamic"] }),
      seed("red_wine_vinegar","Red Wine Vinegar", "🍷", "pantry", "vinegar", FLUID(355), "bottle"),
      seed("white_wine_vinegar","White Wine Vinegar","🍷","pantry","vinegar", FLUID(355), "bottle"),
      seed("apple_cider_vinegar","Apple Cider Vinegar","🍎","pantry","vinegar",FLUID(473), "bottle"),
      seed("sherry_vinegar", "Sherry Vinegar",    "🍷", "pantry", "vinegar", FLUID(355), "bottle"),
      seed("white_vinegar",  "White Vinegar",     "🍷", "pantry", "vinegar", FLUID(946), "bottle"),
      seed("malt_vinegar",   "Malt Vinegar",      "🍷", "pantry", "vinegar", FLUID(355), "bottle"),

      // ── condiments / mustards / dressings ───────────────────────
      seed("dijon_mustard",  "Dijon Mustard",     "🌿", "pantry", "mustard",   SOLID(227), "jar", { aliases: ["dijon"] }),
      seed("whole_grain_mustard","Whole-Grain Mustard","🌿","pantry","mustard",SOLID(227), "jar"),
      // ketchup + hot_sauce have inline canonicals.
      seed("tabasco",        "Tabasco",           "🌶️", "pantry", "sauce",     FLUID(60),  "bottle"),
      seed("horseradish",    "Horseradish",       "🌿", "pantry", "condiment", SOLID(170), "jar"),

      // ── cured meats — gap fills ─────────────────────────────────
      seed("chorizo",        "Chorizo",           "🌶️", "meat", "sausage", SOLID(340), "package", { parentId: "pork_hub" }),
      seed("andouille",      "Andouille Sausage", "🌶️", "meat", "sausage", SOLID(340), "package", { parentId: "pork_hub" }),
      seed("bratwurst",      "Bratwurst",         "🥓", "meat", "sausage", SOLID(454), "package", { parentId: "pork_hub" }),
      seed("breakfast_sausage","Breakfast Sausage","🥓","meat", "sausage", SOLID(340), "package", { parentId: "pork_hub" }),
      seed("italian_sausage","Italian Sausage",   "🥓", "meat", "sausage", SOLID(454), "package", { parentId: "pork_hub" }),
      seed("soppressata",    "Soppressata",       "🥓", "meat", "salami",  SOLID(170), "package", { parentId: "pork_hub" }),
      seed("pepperoni",      "Pepperoni",         "🍕", "meat", "salami",  SOLID(170), "package", { parentId: "pork_hub" }),
      seed("speck",          "Speck",             "🥓", "meat", "cured",   SOLID(85),  "package", { parentId: "pork_hub" }),
      seed("bresaola",       "Bresaola",          "🥩", "meat", "cured",   SOLID(85),  "package", { parentId: "beef_hub" }),

      // ── beverages / liquids ─────────────────────────────────────
      seed("orange_juice",   "Orange Juice",      "🍊", "pantry", "juice",     FLUID(1893), "carton", { aliases: ["oj"] }),
      seed("apple_juice",    "Apple Juice",       "🍎", "pantry", "juice",     FLUID(1893), "carton"),
      seed("lemon_juice",    "Lemon Juice",       "🍋", "pantry", "juice",     FLUID(296),  "bottle"),
      seed("lime_juice",     "Lime Juice",        "🍋", "pantry", "juice",     FLUID(296),  "bottle"),
      // white_wine + red_wine + beef_stock + chicken_stock have inline canonicals.
      seed("vegetable_stock","Vegetable Stock",   "🌿", "pantry", "stock",     FLUID(946),  "carton"),
      seed("fish_stock",     "Fish Stock",        "🐟", "pantry", "stock",     FLUID(946),  "carton"),
      seed("dashi",          "Dashi",             "🌿", "pantry", "stock",     FLUID(946),  "carton"),

      // ── tomatoes — canned variants ──────────────────────────────
      seed("crushed_tomatoes","Crushed Tomatoes", "🍅", "pantry", "tomato_canned", SOLID(794), "can", { parentId: "tomato_hub" }),
      seed("diced_tomatoes", "Diced Tomatoes",    "🍅", "pantry", "tomato_canned", SOLID(411), "can", { parentId: "tomato_hub" }),
      seed("whole_tomatoes", "Whole Peeled Tomatoes","🍅","pantry","tomato_canned",SOLID(794), "can", { parentId: "tomato_hub" }),
      // tomato_paste has inline canonical.
      seed("tomato_sauce",   "Tomato Sauce",      "🍅", "pantry", "tomato_canned", SOLID(425), "can"),
      seed("sun_dried_tomatoes","Sun-Dried Tomatoes","🍅","pantry","tomato_dried", SOLID(227),"jar", { parentId: "tomato_hub" }),

      // ── breads / wraps — gap fills ──────────────────────────────
      // baguette + ciabatta + english_muffin + bagel have inline canonicals.
      seed("sourdough_bread","Sourdough Bread",   "🍞", "pantry", "bread",  PIECES(680, 680), "loaf",  { parentId: "bread_hub" }),
      seed("naan",           "Naan",              "🍞", "pantry", "bread",  PIECES(85, 510),  "piece", { parentId: "bread_hub" }),
      seed("flour_tortilla", "Flour Tortilla",    "🌮", "pantry", "tortilla",PIECES(45, 360), "piece"),
      seed("corn_tortilla",  "Corn Tortilla",     "🌮", "pantry", "tortilla",PIECES(25, 350), "piece"),

      // ── frozen / convenience ────────────────────────────────────
      seed("frozen_peas",    "Frozen Peas",       "🟢", "produce", "frozen_veg", SOLID(454), "package", { parentId: "frozen_vegetable_hub" }),
      seed("frozen_corn",    "Frozen Corn",       "🌽", "produce", "frozen_veg", SOLID(454), "package", { parentId: "frozen_vegetable_hub" }),
      seed("frozen_spinach", "Frozen Spinach",    "🥬", "produce", "frozen_veg", SOLID(283), "package", { parentId: "frozen_vegetable_hub" }),
      seed("frozen_berries", "Frozen Berries",    "🫐", "produce", "frozen_fruit",SOLID(454),"package",  { storage: { location: "freezer" } }),
      seed("puff_pastry",    "Puff Pastry",       "🥐", "pantry", "dough",      SOLID(454),  "package", { parentId: "laminated_dough_hub" }),
      seed("pie_crust",      "Pie Crust",         "🥧", "pantry", "dough",      SOLID(425),  "package", { parentId: "raw_dough_hub" }),
      seed("pizza_dough",    "Pizza Dough",       "🍕", "pantry", "dough",      SOLID(454),  "package", { parentId: "raw_dough_hub" }),
      seed("croissant", "Croissant", "🧂", "pantry", "spice", SOLID(454), "package", { parentId: "pastries_hub", aliases: ["croissants","croissant_rolls"] }),
      seed("salt", "Salt", "🧂", "pantry", "spice", SOLID(454), "package", { parentId: "salt_hub" }),
    ];
  })(),
];

// ─────────────────────────────────────────────────────────────────────────────
// Produce sub-hub assignment — split the produce shelf into intuitive
// families at READ time. Without this split, the Produce tile would dump
// 60+ items into a single drilldown. The wizard's group step renders a
// hub card per distinct parentId in a tile, so each set below becomes
// its own visual family inside Produce:
//
//   fruit_hub           — apples, berries, citrus, melons…
//   vegetable_hub       — leafy + cruciferous + alliums + everything else
//   pepper_hub          — bell peppers + chiles (mild → wild)
//   legume_hub          — tofu / tempeh / chickpeas / edamame
//   root_vegetable_hub  — carrots, beets, radishes (incl. daikon), turnips,
//                         parsnips, rutabaga, jicama, kohlrabi, potato/yam
//   frozen_fruit_hub    — frozen berries (storage.location set so it only
//                         shows up under freezer)
//
// Items not in any set fall through to vegetable_hub as the catch-all.
// Herbs (basil, thyme, marjoram, thai_basil, etc.) are NOT assigned a
// produce sub-hub here because the fresh_herbs TILE classifier routes
// them out of the Produce tile entirely.
//
// Run AFTER the array is built so it sweeps every entry whether seeded
// inline or via the seed() helper. Items already carrying a parentId
// (e.g. tofu siblings seeded with explicit parentId, ice_cream's storage
// override) are not overwritten.

const FRUIT_IDS = new Set([
  "apple", "banana", "orange", "lime", "lemon", "strawberry", "blueberry",
  "raspberry", "blackberry", "cranberry", "pomegranate", "grape", "watermelon",
  "cantaloupe", "honeydew", "pineapple", "mango", "papaya", "kiwi",
  "peach", "nectarine", "pear", "plum", "apricot", "cherry", "fig",
  "dragonfruit", "passionfruit", "lychee", "guava", "persimmon",
  "tangerine", "clementine", "grapefruit", "starfruit", "kumquat",
  "currant", "gooseberry", "elderberry", "boysenberry", "mulberry",
  "date", "coconut", "rhubarb",
]);

// Peppers & chiles — bell peppers (any color) and the chile family.
// Sweet bells live next to fiery scotch_bonnets so the user has one
// place to look when they're cooking with a pepper of any kind.
const PEPPER_IDS = new Set([
  "bell_pepper",
  "bell_pepper_red", "bell_pepper_yellow", "bell_pepper_orange",
  "jalapeno", "serrano", "habanero", "poblano", "anaheim",
  "scotch_bonnet", "thai_chile",
]);

// Legumes & soy — tofu siblings + tempeh + edamame.
// Chickpeas would conceptually fit here too, but it's already
// parentId="bean_hub" (pantry shelf) and substitution / aggregation
// logic keys off that. Leaving chickpeas in bean_hub for now; if the
// user wants it under Produce → Legumes specifically, swap below.
const LEGUME_IDS = new Set([
  "tofu", "silken_tofu", "firm_tofu", "extra_firm_tofu",
  "tempeh", "edamame",
]);

// Root vegetables — anything that grows underground and stores well.
// Includes potato/sweet_potato/yam since users mentally group them with
// other root vegetables when picking from the shelf.
const ROOT_VEGETABLE_IDS = new Set([
  "carrot", "beet", "radish", "daikon", "turnip", "parsnip", "rutabaga",
  "jicama", "kohlrabi",
  "potato", "sweet_potato", "yam",
  "ginger", "horseradish_root", "galangal", "lemongrass",
]);

// Leafy greens — cabbages, lettuces, spinach, kales, ferments
// (sauerkraut, kimchi). Nested under vegetable_hub.
const LEAFY_GREEN_IDS = new Set([
  "cabbage", "red_cabbage", "napa_cabbage", "bok_choy",
  "lettuce", "romaine", "iceberg", "butter_lettuce", "mesclun",
  "spinach", "kale", "arugula", "swiss_chard",
  "collard_greens", "mustard_greens", "turnip_greens", "dandelion_greens",
  "endive", "escarole", "frisee", "radicchio", "watercress",
  "sauerkraut", "kimchi",
]);

// Fungi — mushrooms of any variety. Top-level hub in Produce
// (NOT nested under Vegetables). Today the registry has a single
// catch-all "mushroom" canonical (Cremini); the set is pre-wired
// for cremini / portobello / shiitake / oyster / chanterelle /
// porcini / morel / enoki / maitake / king_trumpet so those auto-
// route to fungi_hub the moment they get minted.
const FUNGI_IDS = new Set([
  "mushroom",
  "cremini", "portobello", "white_button", "button_mushroom",
  "shiitake", "oyster_mushroom", "chanterelle", "morel",
  "porcini", "dried_porcini", "enoki", "maitake",
  "king_trumpet", "lions_mane", "trumpet_mushroom",
]);

// Frozen fruit — separate hub so the freezer drilldown groups them
// alongside frozen_veg. storage.location should already be set on
// these (or they'd show up in produce tile) — we just attach the hub
// id so they group visually.
const FROZEN_FRUIT_IDS = new Set([
  "frozen_berries", "frozen_strawberries", "frozen_blueberries",
  "frozen_raspberries", "frozen_mango", "frozen_pineapple",
  "frozen_mixed_berries",
]);

// Catch-all for vegetables — every produce canonical that isn't fruit,
// pepper, legume, root, leafy green, or frozen_fruit. Listed positively
// for clarity. Items pulled out into their own hubs (greens, peppers,
// roots, legumes) are NOT listed here so they go to those hubs first.
const VEGETABLE_IDS = new Set([
  "garlic", "yellow_onion", "shallot", "pearl_onion",
  "scallion", "green_onion", "leek",
  "tomato", "cucumber",
  "avocado", "broccoli", "cauliflower",
  "zucchini", "asparagus", "celery",
  "fennel", "brussels_sprouts", "eggplant", "okra",
  "artichoke",
  "butternut_squash", "acorn_squash", "spaghetti_squash", "kabocha",
  "delicata", "pumpkin", "yellow_squash",
  "snap_peas", "snow_peas", "green_beans", "sugar_snap_peas",
  "corn", "sprouts", "mung_bean_sprouts", "alfalfa_sprouts",
]);

for (const ing of INGREDIENTS) {
  // Frozen fruit can be either category="produce" (current shape) or
  // category="frozen". Hub-assign first by id since storage.location
  // is the gate that keeps them out of produce drilldowns.
  if (FROZEN_FRUIT_IDS.has(ing.id)) {
    if (!ing.parentId) ing.parentId = "frozen_fruit_hub";
    if (!ing.storage) ing.storage = { location: "freezer" };
    else if (!ing.storage.location) ing.storage.location = "freezer";
    continue;
  }
  if (ing.category !== "produce") continue;
  if (ing.parentId) continue; // respect any explicit assignment
  if (FRUIT_IDS.has(ing.id)) {
    ing.parentId = "fruit_hub";
  } else if (FUNGI_IDS.has(ing.id)) {
    ing.parentId = "fungi_hub";
  } else if (PEPPER_IDS.has(ing.id)) {
    ing.parentId = "pepper_hub";
  } else if (LEGUME_IDS.has(ing.id)) {
    ing.parentId = "legume_hub";
  } else if (ROOT_VEGETABLE_IDS.has(ing.id)) {
    ing.parentId = "root_vegetable_hub";
  } else if (LEAFY_GREEN_IDS.has(ing.id)) {
    ing.parentId = "leafy_green_hub";
  } else if (VEGETABLE_IDS.has(ing.id)) {
    ing.parentId = "vegetable_hub";
  }
  // Items not in any set (herbs, exotic produce we forgot) fall through
  // and remain standalone. The fresh_herbs tile classifier handles
  // herbs separately, so they show up in the Fresh Herbs tile rather
  // than next to the produce hub cards.
}


// ─────────────────────────────────────────────────────────────────────────────
// Lookups & helpers
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Price hints — rough retail cents-per-base-unit for each ingredient. Kept
// in one table rather than sprinkled through INGREDIENTS so we can tune
// prices without rewriting every ingredient entry. Mass items use cents/g,
// volume items cents/ml, count items cents/each.
//
// Anything an ingredient already carries inline (e.g. the cheese_hub
// members generated via cheeseMembers) wins — this map is a fallback.
// ─────────────────────────────────────────────────────────────────────────────
const PRICE_HINTS = {
  // ── meat ──────────────────────────────────────────
  chicken: 0.66, chicken_breast: 1.1, chicken_thigh: 0.77,
  chicken_leg: 0.55, chicken_wing: 0.88,
  steak: 2.2, ribeye: 4.0, ny_strip: 3.7, sirloin: 2.2,
  brisket: 1.8, chuck_roast: 1.5, ground_beef: 1.3,
  pork_chop: 1.1, pork_loin: 0.88, pork_shoulder: 0.88, ground_pork: 1.1,
  sausage: 1.3, bacon: 2.0, guanciale: 4.8, ham: 1.5,
  prosciutto: 6.6, salami: 3.1,
  ground_turkey: 1.1, deli_turkey: 2.2, turkey_breast: 1.5,
  salmon: 3.1, tuna: 4.0, cod: 2.6, tilapia: 1.8, shrimp: 2.4, scallops: 4.4,
  // ── dairy (non-cheese) ─────────────────────────────
  heavy_cream: 0.6, sour_cream: 0.5, cottage_cheese: 0.6,
  half_and_half: 0.4, buttermilk: 0.25,
  oat_milk: 0.2, almond_milk: 0.15, oj: 0.15,
  yogurt: 0.5, greek_yogurt: 0.9,
  // cheeses not generated by cheeseMembers()
  feta: 2.6, goat_cheese: 4.0, cream_cheese: 1.3, brie: 3.5,
  mozzarella: 2.6, cheddar: 1.8, gruyere: 4.4, pecorino: 4.8,
  // ── produce ───────────────────────────────────────
  // count-based: cents per each
  lemon: 75, lime: 50, apple: 100, banana: 35, orange: 125,
  avocado: 150, yellow_onion: 80, shallot: 100, pearl_onion: 10,
  carrot: 35, bell_pepper: 125, cucumber: 125, zucchini: 125,
  potato: 80, sweet_potato: 150, tomato: 100,
  // mass-based: cents per gram
  mushroom: 1.1, spinach: 1.3, arugula: 1.8, kale: 1.1,
  broccoli: 0.9, cauliflower: 0.8, lettuce: 0.7,
  basil: 2.0, parsley: 1.0, cilantro: 1.0, ginger: 1.1,
  strawberry: 1.1, blueberry: 2.2, garlic: 30,
  // ── pantry ────────────────────────────────────────
  flour: 0.3, sugar: 0.3, olive_oil: 2.5,
  spaghetti: 0.4, penne: 0.4, rigatoni: 0.4, fettuccine: 0.4,
  orzo: 0.5, lasagna: 0.5,
  rice: 0.3, brown_rice: 0.4, jasmine_rice: 0.4,
  basmati_rice: 0.5, arborio_rice: 0.9,
  quinoa: 1.1, oats: 0.3,
  black_beans: 0.5, chickpeas: 0.5, lentils: 0.5,
  kidney_beans: 0.5, pinto_beans: 0.5, cannellini_beans: 0.5,
  canned_tomatoes: 0.4, tomato_paste: 1.0,
  chicken_stock: 0.4, beef_stock: 0.4,
  red_wine: 1.3, white_wine: 1.3,
  peanut_butter: 1.1, honey: 2.0, maple_syrup: 3.5,
  coffee: 2.4, soy_sauce: 1.3, vinegar: 0.5, dijon: 1.5, balsamic: 2.5,
  // bread members — base is "slice" (1 slice = 1 base unit)
  bread: 15, sourdough: 50, rye_bread: 50, baguette: 15, ciabatta: 200,
  bagel: 75, english_muffin: 50,
  tortillas: 15,
};
for (const ing of INGREDIENTS) {
  if (PRICE_HINTS[ing.id] != null && ing.estCentsPerBase == null) {
    ing.estCentsPerBase = PRICE_HINTS[ing.id];
  }
}

// Sub-category tags for the legacy cheese entries (the ones declared inline
// above, which have bespoke unit ladders and weren't generated via
// cheeseMembers). Keeps the Cheese drill-down neatly grouped.
const CHEESE_LEGACY_SUBCATEGORY = {
  parmesan:          "Hard / Aged",
  pecorino:          "Hard / Aged",
  gruyere:           "Alpine",
  mozzarella:        "Fresh / Unaged",
  cheddar:           "Semi-Hard",
  feta:              "Fresh / Unaged",
  goat_cheese:       "Fresh / Unaged",
  cream_cheese:      "Fresh / Unaged",
  brie:              "Soft Ripened",
  spreadable_cheese: "Fresh / Unaged",
};
for (const ing of INGREDIENTS) {
  if (CHEESE_LEGACY_SUBCATEGORY[ing.id] && !ing.subcategory) {
    ing.subcategory = CHEESE_LEGACY_SUBCATEGORY[ing.id];
  }
}

const byId = new Map(INGREDIENTS.map(i => [i.id, i]));

// ── DB-backed canonicals registry (unified lookup) ───────────────────
//
// The bundled INGREDIENTS array is the 400-item hand-curated seed.
// User-created + admin-approved canonicals live in Supabase's
// `ingredient_info` table and previously only surfaced inside
// LinkIngredient's picker — fuzzyMatchIngredient / findIngredient /
// inferCanonicalFromName all ignored them, so typing "nori from the
// japanese store" into the typeahead or scanning its UPC found no
// match even when an admin had blessed it.
//
// This module-level map is populated by the React layer (see
// useIngredientInfo.js:IngredientInfoProvider) whenever `ingredient_info`
// fetches or updates. Keeping it as a plain Map rather than a React
// context lets pure helper functions (fuzzyMatchIngredient,
// inferCanonicalFromName) consult it without becoming hooks
// themselves — they run from inside event handlers, reducers, and
// non-React code paths (edge-function clients, the canonical resolver)
// where context isn't available.
//
// Shape per entry — mirrors LinkIngredient.syntheticCanonicalForSlug:
//   { id, name, shortName, emoji, category, parentId?, units[] }
// A minimal stub when info is sparse; fuller when admin enriched.
const dbCanonicals = new Map();

// Overlay map for BUNDLED canonicals — gap-fill from ingredient_info.info
// for the conversion-math fields the bundled record may not author yet:
//   density / count_weight_g / preferredUnit / measuredIn / prepTimings
//
// Why an overlay (not direct mutation): the bundled INGREDIENTS array
// is the source of truth that's authored by hand. Mutating entries in
// place would let DB info regress hand-curated values across server
// realtime updates. The overlay is GAP-FILL only — bundled wins when
// it has a value, overlay fills when bundled has null/undefined.
//
// Populated by registerCanonicalsFromDb (same call site that handles
// synthetic canonicals); applied at findIngredient read time.
const dbOverlay = new Map();

// Build a registry-compatible canonical object from an ingredient_info
// row. Kept here (rather than imported from LinkIngredient) so
// ingredients.js stays React-free.
function syntheticFromInfo(slug, info) {
  if (!slug) return null;
  const displayOverride = info && typeof info.display_name === "string"
    ? info.display_name.trim()
    : "";
  const name = displayOverride ||
    slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Build a gram-based unit ladder from whatever density metadata the
  // AI enrichment provided. `g` is always the base (toBase=1) so
  // `scaleFactor` in src/lib/nutrition.js can apply `per=100g`
  // nutrition without a special case. Pre-Phase-1 synthetics had a
  // permissive [unit, oz, g] ladder with every toBase=1, which
  // silently broke every tsp/tbsp/cup-unit recipe amount by failing
  // to parse the unit at all.
  //
  // Density shape the AI prompt produces (v3+):
  //   info.density = { g_per_tsp?, g_per_tbsp?, g_per_cup?, volume?, weight_g? }
  // `volume`/`weight_g` is the old legacy pair — we translate it to
  // the per-unit keys when present.
  // typeof null === "object" in JS — explicit null check or this
  // crashes the moment AI enrichment writes `density: null` (which
  // happens for ingredients with no meaningful density, e.g. raw
  // meats / countables).
  const d = (info && info.density && typeof info.density === "object") ? info.density : {};
  let gPerTsp  = typeof d.g_per_tsp  === "number" ? d.g_per_tsp  : null;
  let gPerTbsp = typeof d.g_per_tbsp === "number" ? d.g_per_tbsp : null;
  let gPerCup  = typeof d.g_per_cup  === "number" ? d.g_per_cup  : null;
  // Legacy { volume: "1 cup"|"1 tbsp"|"1 tsp", weight_g: N } → modern keys.
  if (typeof d.weight_g === "number" && typeof d.volume === "string") {
    const v = d.volume.toLowerCase();
    if (/tsp|teaspoon/.test(v)  && gPerTsp  == null) gPerTsp  = d.weight_g;
    if (/tbsp|tablespoon/.test(v) && gPerTbsp == null) gPerTbsp = d.weight_g;
    if (/cup/.test(v)             && gPerCup  == null) gPerCup  = d.weight_g;
  }
  // Fill derivable gaps: 1 tbsp = 3 tsp, 1 cup = 48 tsp.
  if (gPerTsp  == null && gPerTbsp != null) gPerTsp  = gPerTbsp / 3;
  if (gPerTsp  == null && gPerCup  != null) gPerTsp  = gPerCup / 48;
  if (gPerTbsp == null && gPerTsp  != null) gPerTbsp = gPerTsp * 3;
  if (gPerCup  == null && gPerTbsp != null) gPerCup  = gPerTbsp * 16;

  const units = [
    { id: "g",    label: "g",    toBase: 1 },
    { id: "oz",   label: "oz",   toBase: 28.35 },
  ];
  if (gPerTsp  != null) units.push({ id: "tsp",  label: "tsp",  toBase: gPerTsp });
  if (gPerTbsp != null) units.push({ id: "tbsp", label: "tbsp", toBase: gPerTbsp });
  if (gPerCup  != null) units.push({ id: "cup",  label: "cups", toBase: gPerCup });
  // `unit` is a permissive fallback so a pantry row stored with the
  // legacy `unit:"unit"` shape still parses. toBase=1 = 1 gram each —
  // effectively a no-op for nutrition since the UI normally never
  // writes this unit.
  units.push({ id: "unit", label: "unit", toBase: 1 });

  // ── preferredUnit / measuredIn / count_weight_g ──
  // The AI enrichment prompt (v4+) asks Claude to populate these
  // alongside density. Carry them through to the synthetic canonical
  // so the cook-display resolver picks them up the same way it does
  // for bundled entries — without this, every synthetic falls through
  // to the ladder pick, which on Metric is always "g" (the bug
  // giardiniera surfaced).
  //
  // Validation: both halves of the {us, metric} pair must be non-empty
  // strings. Anything else is ignored — the resolver's ladder/registry
  // fallback covers that case. count_weight_g must be a positive
  // finite number; otherwise omit (the resolver consults
  // COUNT_WEIGHTS_G / CUT_WEIGHTS_G / row override as a chain).
  const validUnitPair = (pair) =>
    pair && typeof pair === "object" &&
    typeof pair.us === "string" && pair.us.length > 0 &&
    typeof pair.metric === "string" && pair.metric.length > 0;

  // Hub linkage — info.hub (set by the scan-propose flow when Haiku
  // returns a parentHub / hubChain) lets findIngredient fall through
  // to the parent hub for the unit ladder, default placement, hub-
  // level metadata. Haiku writes lowercase slugs without the `_hub`
  // suffix ("cheese"); the bundled HUBS use the suffixed form
  // ("cheese_hub"). Normalize so either reads correctly.
  //
  // Fallback chain: explicit info.hub → info.hubChain[last] →
  // inferHubFromInfo (category / subtype / slug heuristics). The
  // inference layer is what makes existing synthetic canonicals like
  // `gochugaru` (minted before info.hub was being written) pick up a
  // parent on next read without needing a DB backfill — the
  // resolver derives "category=spice → spice_hub" at runtime.
  const rawHub = (info && typeof info.hub === "string" ? info.hub.trim() : "")
    || (info && Array.isArray(info.hubChain) && info.hubChain.length > 0
        ? String(info.hubChain[info.hubChain.length - 1]).trim()
        : "")
    || inferHubFromInfo(info, slug)
    || "";
  const parentId = rawHub
    ? (rawHub.endsWith("_hub") ? rawHub : `${rawHub}_hub`)
    : undefined;

  const out = {
    id: slug,
    name,
    shortName: null,
    emoji: (info && info.emoji) || "✨",
    category: (info && info.category) || "user",
    parentId,
    units,
    defaultUnit: gPerTsp != null ? "tsp" : "oz",
  };
  if (validUnitPair(info?.preferredUnit)) {
    out.preferredUnit = { us: info.preferredUnit.us, metric: info.preferredUnit.metric };
  }
  if (validUnitPair(info?.measuredIn)) {
    out.measuredIn = { us: info.measuredIn.us, metric: info.measuredIn.metric };
  }
  const cwg = Number(info?.count_weight_g);
  if (Number.isFinite(cwg) && cwg > 0) {
    out.count_weight_g = cwg;
  }
  // searchAliases — populated by the AI alias-generation deployment.
  // DB synthetics often have casual / informal names that benefit
  // from bare-token aliases the same way bundled canonicals do.
  if (Array.isArray(info?.searchAliases)) {
    const cleaned = info.searchAliases
      .filter(a => typeof a === "string")
      .map(a => a.trim().toLowerCase())
      .filter(a => a.length >= 3);
    if (cleaned.length > 0) out.searchAliases = cleaned;
  }
  return out;
}

// inferHubFromInfo — derive a parentId for a synthetic canonical when
// info.hub / info.hubChain weren't written at mint time. Reads
// info.category, info.subtype, and the slug, mapping each to a
// registered hub. Returns the bare slug (without "_hub" suffix); the
// caller appends the suffix.
//
// This is the runtime backfill that lets every pre-existing synthetic
// canonical with a category but no hub assignment (Gochugaru, every
// AI-minted spice / condiment / oil / etc. before mint flows wrote
// info.hub) pick up a parent automatically. New mints should still
// write info.hub explicitly so the inference is a safety net rather
// than the primary path.
const CATEGORY_HUB_MAP = {
  // ── pantry shelf-stables ──
  spice:              "spice",
  seasoning:          "spice",
  dried_herb:         "spice",
  condiment:          "condiment",
  sauce:              "condiment",
  oil:                "oil",
  fat:                "oil",
  vinegar:            "vinegar",
  baking:             "baking",
  sweetener:          "sweetener",
  nut:                "nut_seed",
  nuts:               "nut_seed",
  seed:               "nut_seed",
  seeds:              "nut_seed",
  snack:              "snack",
  candy:              "candy",
  chocolate:          "candy",
  jerky:              "jerky",
  snack_meat:         "jerky",
  pickle:             "pickle_ferment",
  ferment:            "pickle_ferment",
  canned:             "canned_jarred",
  jarred:             "canned_jarred",
  stock:              "stock",
  broth:              "stock",
  beverage:           "beverage",
  drink:              "beverage",
  drinks:             "beverage",
  juice:              "beverage",
  alcohol:            "cooking_alcohol",
  cooking_wine:       "cooking_alcohol",
  shelf_stable_dairy: "shelf_stable_dairy",
  milk_alt:           "shelf_stable_dairy",
  dried_chili:        "dried_chili",
  dried_fruit:        "dried_fruit",
  // ── dairy / fridge ──
  cheese:             "cheese",
  milk:               "milk",
  yogurt:             "yogurt",
  egg:                "egg",
  butter:             "butter",
  spread:             "butter",
  fresh_herb:         "fresh_herb",
  herb:               "fresh_herb",
  // ── meat hubs (subtype-driven; category="meat" without subtype is too coarse) ──
  chicken:            "chicken",
  beef:               "beef",
  pork:               "pork",
  turkey:             "turkey",
  seafood:            "seafood",
  fish:               "seafood",
  shellfish:          "seafood",
  // ── produce hubs ──
  vegetable:          "vegetable",
  vegetables:         "vegetable",
  fruit:              "fruit",
  fruits:             "fruit",
  legume:             "legume",
  legumes:            "legume",
  bean:               "bean",
  beans:              "bean",
  mushroom:           "fungi",
  mushrooms:          "fungi",
  fungi:              "fungi",
  pepper:             "pepper",
  peppers:            "pepper",
  chile:              "pepper",
  chiles:             "pepper",
  chili:              "pepper",
  root_vegetable:     "root_vegetable",
  root:               "root_vegetable",
  leafy_green:        "leafy_green",
  leafy_greens:       "leafy_green",
  greens:             "leafy_green",
  // ── pantry staples that have their own hubs ──
  pasta:              "pasta",
  rice:               "rice",
  grain:              "rice",
  grains:             "rice",
  flour:              "flour",
  bread:              "bread",
  // ── frozen ──
  frozen_meal:        "frozen_meal",
  frozen_dessert:     "frozen_dessert",
  ice_cream:          "frozen_dessert",
  frozen_fruit:       "frozen_fruit",
  // ── baking adjuncts (subtype-driven, all map to baking_hub) ──
  leavener:           "baking",
  leavening:          "baking",
  extract:            "baking",
  starch:             "baking",
  thickener:          "baking",
  cocoa:              "baking",
  // ── pantry assembled / canned / packaged ──
  dried_chile:        "dried_chili",  // singular variant
  tinned_fish:        "canned_jarred",
  canned_fish:        "canned_jarred",
  canned_meat:        "canned_jarred",
  canned_vegetable:   "canned_jarred",
  canned_bean:        "bean",
  paste:              "canned_jarred",   // tomato paste, curry paste, etc.
  // ── condiment-shaped subtypes ──
  dip:                "condiment",
  dressing:           "condiment",
  vinaigrette:        "condiment",
  glaze:              "condiment",
  marinade:           "condiment",
  salsa:              "condiment",
  hot_sauce:          "condiment",
  // ── snack family ──
  cookie:             "snack",
  cookies:            "snack",
  cracker:            "snack",
  crackers:           "snack",
  chip:               "snack",
  chips:              "snack",
  pretzel:            "snack",
  popcorn:            "snack",
  granola_bar:        "snack",
  // ── candy / chocolate family ──
  candy_bar:          "candy",
  // ── beverage family ──
  soda:               "beverage",
  tea:                "beverage",
  coffee:             "beverage",
  // ── dairy alt ──
  plant_milk:         "shelf_stable_dairy",
  // ── jerky / cured-meat aliases ──
  cured_meat:         "jerky",
};

// Tile id (from `tagHintsToAxes`) → hub slug. Higher-confidence than
// category/subtype because tile is itself a placement signal: an item
// already routed to `spices_dried_herbs` strongly anchors at spice_hub
// regardless of how its category landed.
const TILE_HUB_MAP = {
  // pantry tiles
  spices_dried_herbs: "spice",
  condiments_sauces:  "condiment",
  oils_fats:          "oil",
  sweeteners:         "sweetener",
  nuts_seeds:         "nut_seed",
  beans_legumes:      "bean",
  pasta_grains:       "pasta",
  baking:             "baking",
  bread:              "bread",
  cooking_alcohol:    "cooking_alcohol",
  pantry_drinks:      "beverage",
  candy_chocolate:    "candy",
  snacks:             "snack",
  jerky_snacks:       "jerky",
  pickles_ferments:   "pickle_ferment",
  dried_chilies:      "dried_chili",
  dried_fruit:        "dried_fruit",
  shelf_stable_dairy: "shelf_stable_dairy",
  canned_jarred:      "canned_jarred",
  // fridge tiles
  meat_poultry:       "chicken",       // generic fallback; category="meat" + subtype refines
  seafood:            "seafood",
  dairy:              "milk",          // generic; subtype="cheese"/"yogurt"/etc. refines
  produce:            "vegetable",
  fresh_herbs:        "fresh_herb",
  condiments:         "condiment",
  drinks:             "beverage",
  bread_baked:        "bread",
  // freezer tiles
  frozen_meat_poultry: "chicken",
  frozen_seafood:      "seafood",
  frozen_veg:          "vegetable",
  frozen_fruit:        "frozen_fruit",
  frozen_bread_dough:  "bread",
  frozen_desserts:     "frozen_dessert",
  frozen_meal_prep:    "frozen_meal",
  frozen_butter_dairy: "butter",
  frozen_herbs:        "fresh_herb",
  frozen_stocks_sauces:"stock",
};

export function inferHubFromInfo(info, slug) {
  if (!info && !slug) return null;
  // Tile match — highest-signal shortcut when `tagHintsToAxes` already
  // routed the synthetic to a tile. Subtype/category may have been
  // generic ("pantry") even when tileId is precise.
  const tile = ((info && info.tileId) || "").toString().toLowerCase().trim();
  if (TILE_HUB_MAP[tile]) return TILE_HUB_MAP[tile];
  // Category / subtype match — highest signal short of an explicit
  // info.hub. Read both so Haiku's frequent typeId/subtype tagging
  // works even when info.category landed on a generic "pantry".
  const cat = ((info && info.category) || "").toString().toLowerCase().trim();
  if (CATEGORY_HUB_MAP[cat]) return CATEGORY_HUB_MAP[cat];
  const subtype = ((info && info.subtype) || "").toString().toLowerCase().trim();
  if (CATEGORY_HUB_MAP[subtype]) return CATEGORY_HUB_MAP[subtype];
  // Slug-shape heuristics for the long tail. Conservative: only fires
  // on unambiguous tokens. A canonical with "oil" in its slug is an
  // oil; a canonical with "spice" / a known spice name is a spice.
  // Anything fuzzier returns null and lets the resolver fall through.
  const s = (slug || "").toString().toLowerCase();
  if (/(^|_)oil(_|$)/.test(s)) return "oil";
  if (/(^|_)vinegar(_|$)/.test(s)) return "vinegar";
  if (/(^|_)(balsamic)(_|$)/.test(s)) return "vinegar";
  if (/(^|_)(syrup|honey|molasses|agave|stevia|allulose|monk_fruit)(_|$)/.test(s)) return "sweetener";
  if (/(^|_)(sauce|ketchup|mustard|mayo|dressing|salsa|aioli|sriracha|gochujang|tahini|vinaigrette|chimichurri|harissa|pesto|tzatziki|hummus|baba_ganoush|relish|chutney|glaze)(_|$)/.test(s)) return "condiment";
  if (/(^|_)(soda|juice|tea|coffee|kombucha|seltzer|sparkling_water)(_|$)/.test(s)) return "beverage";
  if (/(^|_)(cookie|cookies|cracker|crackers|chip|chips|pretzel|pretzels|popcorn|granola_bar|trail_mix)(_|$)/.test(s)) return "snack";
  if (/(^|_)(candy|chocolate|gum|mints|caramel|toffee|fudge|marshmallow)(_|$)/.test(s)) return "candy";
  if (/(^|_)(ice_cream|gelato|sorbet|popsicle|frozen_yogurt)(_|$)/.test(s)) return "frozen_dessert";
  if (/(^|_)(jerky|beef_stick|biltong|pepperoni_stick|slim_jim|salami_stick)(_|$)/.test(s)) return "jerky";
  if (/(^|_)(pickle|kimchi|sauerkraut|olives|capers|miso|natto)(_|$)/.test(s)) return "pickle_ferment";
  // ── baking adjuncts ──
  if (/(^|_)(baking_powder|baking_soda|yeast|cornstarch|arrowroot|tapioca|xanthan_gum|cream_of_tartar|cocoa_powder|chocolate_chips|sprinkles|food_coloring|frosting|icing|fondant)(_|$)/.test(s)) return "baking";
  if (/_extract(_|$)/.test(s)) return "baking";   // vanilla_extract, almond_extract, etc.
  // ── nuts / seeds ──
  if (/(^|_)(almonds?|walnuts?|pecans?|cashews?|pistachios?|hazelnuts?|macadamias?|peanuts?|brazil_nuts?|pine_nuts?|sunflower_seeds?|pumpkin_seeds?|chia_seeds?|flax_seeds?|hemp_seeds?|sesame_seeds?|nut_butter|peanut_butter|almond_butter)(_|$)/.test(s)) return "nut_seed";
  // ── leafy greens / specific produce ──
  if (/(^|_)(spinach|kale|arugula|romaine|iceberg|butter_lettuce|leaf_lettuce|swiss_chard|collards|bok_choy|escarole|frisee|radicchio|watercress|microgreens)(_|$)/.test(s)) return "leafy_green";
  // ── breads / bakery ──
  if (/(^|_)(bagel|bagels|baguette|croissant|english_muffin|muffin|brioche|sourdough|tortilla|tortillas|pita|naan|focaccia|ciabatta|challah|cornbread|biscuit)(_|$)/.test(s)) return "bread";
  // ── dried chilies (more variants) ──
  if (/(^|_)(ancho|guajillo|pasilla|chipotle|arbol|mulato|cascabel|new_mexico_chile|california_chile|morita)(_|$)/.test(s)) return "dried_chili";
  // Spice catch-net — fires only when name strongly implies a spice.
  // Common spices the AI may not have tagged with category=spice.
  if (/(^|_)(gochugaru|gochujang_powder|cumin|paprika|oregano|thyme|cinnamon|nutmeg|coriander|cardamom|turmeric|ginger_powder|garlic_powder|onion_powder|chili_powder|curry_powder|cayenne|sumac|saffron|fennel_seed|mustard_seed|fenugreek|asafoetida|peppercorn|allspice|clove|star_anise|bay_leaf|dill_seed|caraway|sesame_seed_toasted|nigella|amchur|zaatar|garam_masala|herbes_de_provence|italian_seasoning|seasoning|spice_blend|cajun_seasoning|old_bay|jerk_seasoning|berbere|ras_el_hanout|chinese_five_spice|pumpkin_pie_spice|apple_pie_spice|dill_weed|tarragon_dried|rosemary_dried|sage_dried|basil_dried|parsley_dried|mint_dried)(_|$)/.test(s)) return "spice";
  return null;
}

// Called by IngredientInfoProvider whenever ingredient_info's dbMap
// refreshes (initial load + every realtime update + admin approvals).
// Clears + repopulates rather than merging so a slug the admin
// deleted doesn't linger in search results.
//
// Skips slugs that already exist in the bundled INGREDIENTS registry —
// bundled entries are the source of truth for those ids and any DB
// enrichment on the same slug rides alongside via ingredient_info's
// info JSONB (not by shadowing the canonical object).
export function registerCanonicalsFromDb(dbMap) {
  dbCanonicals.clear();
  dbOverlay.clear();
  if (!dbMap || typeof dbMap !== "object") return;
  // Also reset the alias-map cache so inferCanonicalFromName picks
  // up newly-registered DB canonicals on the next lookup.
  _aliasMapCache = null;
  for (const [slug, info] of Object.entries(dbMap)) {
    if (!slug) continue;
    if (byId.has(slug)) {
      // Bundled canonical — extract gap-fill overlay (density,
      // count_weight_g, preferredUnit, measuredIn, prepTimings). The
      // bundled entry stays the source of truth; overlay only fills
      // fields the bundled record didn't author. Applied at
      // findIngredient read time via applyDbOverlay.
      const overlay = extractDbOverlayFields(info || {});
      if (overlay) dbOverlay.set(slug, overlay);
      continue;
    }
    const synthetic = syntheticFromInfo(slug, info || {});
    if (synthetic) dbCanonicals.set(slug, synthetic);
  }
}

// Pull just the fields the runtime resolver consults that the bundled
// record may not have authored. Returns null when info has nothing
// useful so the overlay map stays small.
function extractDbOverlayFields(info) {
  if (!info || typeof info !== "object") return null;
  const out = {};
  // Hub assignment from DB info.hub. When the bundled canonical has
  // no parentId of its own (gochugaru, paprika, oats — many older
  // seed() calls omit the extras object entirely), the runtime
  // resolver had no way to reach the hub chain. With this overlay
  // path, applyDbOverlay fills bundled.parentId from info.hub on
  // read, which means scan-time placement chips, the wizard's
  // tap-to-place classifier, and getCanonicalHome's Tier 4 walker
  // all see the same answer the migration table already wrote into
  // ingredient_info. Storage block carried alongside in the same
  // shape getCanonicalHome's Tier 1 expects.
  if (typeof info.hub === "string" && info.hub.trim()) {
    const raw = info.hub.trim();
    out.parentId = raw.endsWith("_hub") ? raw : `${raw}_hub`;
  }
  if (info.storage && typeof info.storage === "object") {
    const s = info.storage;
    if (s.location || s.tile) {
      out.storage = {
        ...(s.location ? { location: s.location } : {}),
        ...(s.tile ? { tile: s.tile } : {}),
      };
    }
  }
  // searchAliases — bare-token / alternate-spelling routes the bundled
  // record may not have stamped (the AI alias-generation deployment
  // populates these). Bundled aliases AND DB-tier aliases both feed
  // the alias map; getCanonicalAliasMap merges. Bundled wins on
  // collision via map insertion order.
  if (Array.isArray(info.searchAliases)) {
    const cleaned = info.searchAliases
      .filter(a => typeof a === "string")
      .map(a => a.trim().toLowerCase())
      .filter(a => a.length >= 3);
    if (cleaned.length > 0) out.searchAliases = cleaned;
  }
  if (info.density && typeof info.density === "object") {
    const d = info.density;
    const tsp  = Number(d.g_per_tsp);
    const tbsp = Number(d.g_per_tbsp);
    const cup  = Number(d.g_per_cup);
    const dn = {};
    if (Number.isFinite(tsp)  && tsp  > 0) dn.g_per_tsp  = tsp;
    if (Number.isFinite(tbsp) && tbsp > 0) dn.g_per_tbsp = tbsp;
    if (Number.isFinite(cup)  && cup  > 0) dn.g_per_cup  = cup;
    if (Object.keys(dn).length > 0) out.density = dn;
  }
  const validUnitPair = (p) =>
    p && typeof p === "object" &&
    typeof p.us === "string" && p.us.length > 0 &&
    typeof p.metric === "string" && p.metric.length > 0;
  if (validUnitPair(info.preferredUnit)) out.preferredUnit = { us: info.preferredUnit.us, metric: info.preferredUnit.metric };
  if (validUnitPair(info.measuredIn))    out.measuredIn    = { us: info.measuredIn.us,    metric: info.measuredIn.metric };
  const cwg = Number(info.count_weight_g);
  if (Number.isFinite(cwg) && cwg > 0) out.count_weight_g = cwg;
  if (info.prepTimings && typeof info.prepTimings === "object") {
    out.prepTimings = info.prepTimings;
  }

  // ── Round-2 expansion: Tier 1 fields ──────────────────────────
  // Internal-temperature targets (meats, breads, custards) drive
  // cook-mode probe prompts + safe-doneness pushes.
  if (info.internalTempF && typeof info.internalTempF === "object" && Object.keys(info.internalTempF).length > 0) {
    out.internalTempF = info.internalTempF;
  }
  // Yield ratios — whole→prepped, raw→cooked, post-trim. Drives
  // recipe scaling math without measuring (1 lemon = 3 tbsp juice).
  if (info.yields && typeof info.yields === "object" && Object.keys(info.yields).length > 0) {
    out.yields = info.yields;
  }
  // Smoke point for fats/oils. Drives recipe AI's oil selection
  // (high-heat sear → grapeseed; finishing drizzle → olive oil).
  const smokePoint = Number(info.smokePointF);
  if (Number.isFinite(smokePoint) && smokePoint > 0) out.smokePointF = smokePoint;
  // Heat-state flavor descriptors (raw / cooked / charred). Already
  // a real read path in IngredientCard.jsx for the flavor section.
  if (info.flavor && info.flavor.heatChange && typeof info.flavor.heatChange === "object") {
    out.flavor = { heatChange: info.flavor.heatChange };
  }
  // Volume-loss ratio when sautéed — spinach 0.1, mushroom 0.5,
  // onion 0.6. Drives pantry-decrement math + recipe scaling.
  if (info.reductionRatio && typeof info.reductionRatio === "object") {
    out.reductionRatio = info.reductionRatio;
  }

  // ── Round-2 expansion: Tier 2 fields ──────────────────────────
  // Recipe-suggestion array (already consumed by IngredientCard).
  if (Array.isArray(info.recipes) && info.recipes.length > 0) {
    out.recipes = info.recipes;
  }
  // Seasonality — yearRound flag or per-month windows.
  if (info.seasonality && typeof info.seasonality === "object") {
    out.seasonality = info.seasonality;
  }
  // Quality / sourcing tip (freetext, 1-2 sentences).
  if (typeof info.sourcing === "string" && info.sourcing.trim()) {
    out.sourcing = info.sourcing.trim();
  }
  // Skill-development context (skills array + difficulty + from-scratch hint).
  if (info.skillDev && typeof info.skillDev === "object") {
    out.skillDev = info.skillDev;
  }
  // Irreplaceable flag — substitutions UI shows "no equivalent" rather
  // than a weak guess. Pair with irreplaceableNote for explanation.
  if (typeof info.irreplaceable === "boolean") out.irreplaceable = info.irreplaceable;
  if (typeof info.irreplaceableNote === "string" && info.irreplaceableNote.trim()) {
    out.irreplaceableNote = info.irreplaceableNote.trim();
  }
  // Compound-blend breakdown — italian_seasoning → [oregano, basil, ...]
  if (Array.isArray(info.blendOf) && info.blendOf.length > 0) {
    out.blendOf = info.blendOf;
  }
  // Allergen specifics — tree_nuts: ["almond"] for almond_milk, etc.
  if (info.allergenDetail && typeof info.allergenDetail === "object" && Object.keys(info.allergenDetail).length > 0) {
    out.allergenDetail = info.allergenDetail;
  }
  // Kitchen equivalents — "1 stick butter = 8 tbsp" type approximations.
  if (Array.isArray(info.equivalents) && info.equivalents.length > 0) {
    out.equivalents = info.equivalents;
  }
  // Equipment requirements — mortar_pestle, candy_thermometer, etc.
  if (Array.isArray(info.equipment) && info.equipment.length > 0) {
    out.equipment = info.equipment;
  }
  return Object.keys(out).length > 0 ? out : null;
}

// Merge a bundled canonical with its DB overlay. Bundled wins when
// it has the value; overlay fills nulls / undefined / missing.
// Returns the bundled record unchanged when no overlay is registered
// for this id (the common path — most canonicals don't need an
// overlay because their bundled record already authors these fields).
function applyDbOverlay(bundled) {
  const overlay = dbOverlay.get(bundled.id);
  if (!overlay) return bundled;
  const out = { ...bundled };
  // Hub + storage gap-fill. Bundled wins; overlay only fills when
  // the bundled record didn't author these. This is what makes
  // gochugaru-class canonicals (bundled with no parentId, but
  // info.hub set in DB) resolve correctly at runtime — getCanonical
  // Home's Tier 1 reads out.storage, Tier 4 walks via out.parentId.
  if (!out.parentId && overlay.parentId) out.parentId = overlay.parentId;
  if (!out.storage && overlay.storage)   out.storage  = overlay.storage;
  // Density: bundled canonicals encode density via the units ladder
  // (each unit's `toBase` is grams). If the ladder is missing
  // tsp/tbsp/cup entries that the overlay carries, ADD them — never
  // override an existing entry (bundled wins).
  if (overlay.density && Array.isArray(out.units)) {
    const have = new Set(out.units.map(u => u.id));
    const additions = [];
    if (!have.has("tsp")  && overlay.density.g_per_tsp)  additions.push({ id: "tsp",  label: "tsp",  toBase: overlay.density.g_per_tsp });
    if (!have.has("tbsp") && overlay.density.g_per_tbsp) additions.push({ id: "tbsp", label: "tbsp", toBase: overlay.density.g_per_tbsp });
    if (!have.has("cup")  && overlay.density.g_per_cup)  additions.push({ id: "cup",  label: "cups", toBase: overlay.density.g_per_cup });
    if (additions.length > 0) out.units = [...out.units, ...additions];
  }
  if (!out.preferredUnit && overlay.preferredUnit) out.preferredUnit = overlay.preferredUnit;
  if (!out.measuredIn    && overlay.measuredIn)    out.measuredIn    = overlay.measuredIn;
  if (out.count_weight_g == null && overlay.count_weight_g != null) out.count_weight_g = overlay.count_weight_g;
  if (!out.prepTimings && overlay.prepTimings) out.prepTimings = overlay.prepTimings;
  // Round-2 fields. Bundled wins on conflict (these aren't authored
  // on bundled entries today, so the overlay path is the only filler).
  if (!out.internalTempF  && overlay.internalTempF)  out.internalTempF  = overlay.internalTempF;
  if (!out.yields         && overlay.yields)         out.yields         = overlay.yields;
  if (out.smokePointF == null && overlay.smokePointF != null) out.smokePointF = overlay.smokePointF;
  // flavor.heatChange merge — only fill the heatChange child, never
  // clobber an existing bundled `flavor` block.
  if (overlay.flavor?.heatChange) {
    out.flavor = {
      ...(out.flavor || {}),
      heatChange: out.flavor?.heatChange || overlay.flavor.heatChange,
    };
  }
  if (!out.reductionRatio && overlay.reductionRatio) out.reductionRatio = overlay.reductionRatio;
  if (!out.recipes        && overlay.recipes)        out.recipes        = overlay.recipes;
  if (!out.seasonality    && overlay.seasonality)    out.seasonality    = overlay.seasonality;
  if (!out.sourcing       && overlay.sourcing)       out.sourcing       = overlay.sourcing;
  if (!out.skillDev       && overlay.skillDev)       out.skillDev       = overlay.skillDev;
  if (out.irreplaceable == null && overlay.irreplaceable != null) out.irreplaceable = overlay.irreplaceable;
  if (!out.irreplaceableNote && overlay.irreplaceableNote) out.irreplaceableNote = overlay.irreplaceableNote;
  if (!out.blendOf        && overlay.blendOf)        out.blendOf        = overlay.blendOf;
  if (!out.allergenDetail && overlay.allergenDetail) out.allergenDetail = overlay.allergenDetail;
  if (!out.equivalents    && overlay.equivalents)    out.equivalents    = overlay.equivalents;
  if (!out.equipment      && overlay.equipment)      out.equipment      = overlay.equipment;
  return out;
}

// Read helper — lets callers enumerate the live DB canonicals
// alongside the bundled array. Returns a snapshot array; mutations
// to the return value don't affect the registry.
export function dbCanonicalsSnapshot() {
  return Array.from(dbCanonicals.values());
}

// SANCTIONED ENUMERATION ENTRY POINT — the ONLY way component code
// should iterate "every canonical." Bundled registry first, then the
// synthetic (user-minted / admin-promoted) canonicals from
// ingredient_info. dbCanonicals never holds a slug that exists in
// byId (registerCanonicalsFromDb routes those to the overlay), so
// the concat carries no duplicates.
//
// Any `for (const ing of INGREDIENTS)` in component code is the
// forbidden pattern that made synthetic slugs (spam, gjetost,
// rookworst…) invisible on whole surfaces — reach for this instead.
export function allCanonicals() {
  return [...INGREDIENTS, ...dbCanonicals.values()];
}

// SANCTIONED TYPEAHEAD ENTRY POINT — name/alias search over BOTH
// tiers. Every canonical search box (eat-this, scan-review pickers,
// swap search, …) routes through here so no surface can disagree
// with another about which ingredients exist.
//
// Scoring: exact name > name prefix > exact alias > shortName prefix
// > name substring > alias substring. Hub records and headerHidden
// rows are never returned — they aren't bindable identities.
export function searchCanonicals(query, limit = 6) {
  const q = String(query || "").trim().toLowerCase();
  if (q.length < 2) return [];
  const hits = [];
  for (const ing of allCanonicals()) {
    if (!ing || ing.headerHidden) continue;
    if (ing.parentId === undefined && String(ing.id).endsWith("_hub")) continue;
    const name = (ing.name || "").toLowerCase();
    const shortName = (ing.shortName || "").toLowerCase();
    const aliases = [
      ...(Array.isArray(ing.aliases) ? ing.aliases : []),
      ...(Array.isArray(ing.searchAliases) ? ing.searchAliases : []),
    ].map((s) => String(s).toLowerCase());
    const score = name === q ? 4
                : name.startsWith(q) ? 3
                : aliases.some((a) => a === q) ? 3
                : shortName.startsWith(q) ? 2
                : name.includes(q) ? 1
                : aliases.some((a) => a.includes(q)) ? 1
                : 0;
    if (score > 0) hits.push({ ing, score });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, Math.max(1, limit)).map((h) => h.ing);
}


// Canonical aliases — legacy slugs where STATE was baked into the
// canonical id ("ground_beef", "ground_pork", "ground_turkey").
// Per CLAUDE.md the identity hierarchy keeps state AND cut as
// separate axes from the canonical. These deprecated slugs resolve
// to the BASE canonical + a state or cut hint. findIngredient falls
// through so pantry_items rows written under an old slug still
// render correctly until migration 0060 / 0122 rewrites them.
//
// Shape: { [legacySlug]: { base: <baseSlug>, state?: <stateToken>, cut?: <cutToken> } }
// Exactly one of `state` or `cut` is typically set; they address
// different axes. "chicken_breast" is chicken + cut=breast; "ground_beef"
// is beef + state=ground.
export const CANONICAL_ALIASES = {
  // STATE-baked slugs — migration 0060 collapsed these.
  ground_beef:   { base: "beef",   state: "ground" },
  ground_pork:   { base: "pork",   state: "ground" },
  ground_turkey: { base: "turkey", state: "ground" },
  // CUT-baked slugs — migration 0122 collapsed these. Per user
  // principle: "chicken is canonical, cut is breast". Chicken breast
  // and chicken thigh are the SAME ingredient in different anatomies,
  // orthogonal to the state axis (cubed chicken breast and ground
  // chicken thigh are both legitimate combinations).
  chicken_breast:     { base: "chicken", cut: "breast" },
  chicken_thigh:      { base: "chicken", cut: "thigh" },
  chicken_leg:        { base: "chicken", cut: "leg" },
  chicken_wing:       { base: "chicken", cut: "wing" },
  chicken_tenderloin: { base: "chicken", cut: "tenderloin" },
  ribeye:             { base: "beef",    cut: "ribeye" },
  ny_strip:           { base: "beef",    cut: "ny_strip" },
  sirloin:            { base: "beef",    cut: "sirloin" },
  brisket:            { base: "beef",    cut: "brisket" },
  chuck_roast:        { base: "beef",    cut: "chuck" },
  pork_chop:          { base: "pork",    cut: "chop" },
  pork_loin:          { base: "pork",    cut: "loin" },
  pork_shoulder:      { base: "pork",    cut: "shoulder" },
  turkey_breast:      { base: "turkey",  cut: "breast" },
  // Sugar slug consolidation — the generic `sugar` and the duplicate
  // `white_sugar` both collapse into `cane_sugar`. Many sugar types
  // exist (brown, powdered, turbinado, etc.); the generic was a
  // mistake that this redirect retires for legacy pantry rows.
  sugar:              { base: "cane_sugar" },
  white_sugar:        { base: "cane_sugar" },
  // Butter slug variants — AI recipes emit `unsalted_butter` /
  // `salted_butter`, but the bundled canonical is `butter` (which IS
  // unsalted — its name is "Unsalted Butter"). Without these redirects
  // findIngredient returns null for the slug, so unit conversion can't
  // reach butter's correct ladder (stick = 113.4 g, tbsp = 14.175 g)
  // and falls back to generic handling with wrong factors — the
  // "1 stick → 113 sticks" inflation bug. Aliasing resolves every
  // read path to the real butter ladder.
  unsalted_butter:    { base: "butter" },
  salted_butter:      { base: "butter" },
  sweet_cream_butter: { base: "butter" },
  granulated_sugar:   { base: "cane_sugar" },
  // Salt slug rename — `salt_*` seeds (broken SOLID() ladders) were
  // removed 2026-05-02 in favor of `*_salt` from the spice factory
  // (proper tsp/tbsp/cup ladders + density). These aliases keep any
  // pre-existing pantry rows that wrote the old slugs from
  // orphaning — findIngredient walks here when the lookup misses.
  salt_kosher:        { base: "kosher_salt" },
  salt_sea:           { base: "sea_salt" },
  salt_table:         { base: "table_salt" },
};

/**
 * Resolve a slug to its canonical tuple: { ingredient, cut, state }.
 * Used by callers that need the axis hints carried on legacy slugs
 * — scan resolution, the pairing resolver, AI-recipe ingestion —
 * so a row read as "chicken_breast" surfaces as ingredient=Chicken,
 * cut=breast rather than silently losing the cut on the redirect.
 *
 * Returns { ingredient: null, cut: null, state: null } when the
 * slug matches neither an alias nor a registered canonical. When an
 * alias is hit the ingredient is its base (never the deprecated
 * leaf) and the cut/state field reflects whichever axis the legacy
 * slug baked in.
 */
export function resolveSlug(id) {
  if (!id) return { ingredient: null, cut: null, state: null };
  const alias = CANONICAL_ALIASES[id];
  if (alias) {
    return {
      ingredient: byId.get(alias.base) || null,
      cut:        alias.cut   || null,
      state:      alias.state || null,
    };
  }
  const ing = byId.get(id) || dbCanonicals.get(id) || null;
  return { ingredient: ing, cut: null, state: null };
}

/**
 * Look up an ingredient by slug. Transparently resolves aliased
 * legacy slugs (ground_beef → beef, chicken_breast → chicken).
 * Returns null when the slug doesn't match any bundled canonical
 * OR alias.
 *
 * Loses the cut/state axis info carried by the alias — callers that
 * need those hints must use resolveSlug(id) instead.
 *
 * NAMING: "ingredient" === "canonical" in this codebase. The arg is
 * a canonicalId (from pantry_items.canonical_id, scan results,
 * inferCanonicalFromName, etc.). See file-header doc.
 *
 * RETURNS structural identity ONLY (id, name, emoji, units, nutrition,
 * parentId, etc.). For storage / shelfLife / description /
 * substitutions / prepTips, route through getIngredientInfo(ing,
 * dbOverride) — those fields live on INGREDIENT_INFO, not on the
 * registry entry. Reading `.storage` directly off this return value
 * gives undefined.
 *
 * Kept for compatibility with every site that just wants "what's
 * this slug's ingredient object?" — the overwhelmingly common case.
 */
export function findIngredient(id) {
  if (!id) return null;
  // Bundled registry wins over CANONICAL_ALIASES. Cuts of meat are
  // first-class canonicals now (chicken_breast, ribeye, brisket etc.
  // each carry their own bundled row with proper name + shortName);
  // the alias redirect is a read-compat fallback for slugs that
  // exist ONLY as legacy data with no bundled entry. DB-registered
  // synthetics (admin-approved user canonicals) fall through last.
  // Each path layers dbOverlay on top so DB-side gap-fill (density,
  // count_weight_g, preferredUnit, measuredIn, prepTimings) reaches
  // every consumer.
  const bundled = byId.get(id);
  if (bundled) return applyDbOverlay(bundled);
  const alias = CANONICAL_ALIASES[id];
  if (alias) {
    const aliased = byId.get(alias.base);
    if (aliased) return applyDbOverlay(aliased);
  }
  return dbCanonicals.get(id) || null;
}

/**
 * Return every canonical that shares a parent hub with `id` —
 * including `id` itself. Used by the ItemCard CUT picker so the user
 * can swap between siblings of the same hub (chicken_breast ↔
 * chicken_thigh ↔ chicken_tenderloin, etc.) without retyping the
 * item from scratch. Sort is registry order so the picker reads
 * top-to-bottom the way the file was authored.
 *
 * Returns [] when the canonical has no parentId (a hub itself, or a
 * standalone ingredient like "flour"). Callers render the picker
 * only when the list has ≥2 entries — a single-sibling hub has no
 * cuts to switch between.
 */
export function siblingsInHub(id) {
  const self = findIngredient(id);
  if (!self) return [];
  const hubId = self.parentId;
  if (!hubId) return [];
  const out = [];
  for (const ing of INGREDIENTS) {
    if (ing.parentId === hubId) out.push(ing);
  }
  for (const ing of dbCanonicals.values()) {
    if (ing.parentId === hubId) out.push(ing);
  }
  return out;
}

/**
 * Resolve a canonical slug into its { canonical, state, cut } triple.
 * Callers that want the state or cut axis (pantry row hydration,
 * UI chips, scan correction) use this instead of findIngredient's
 * flattening lookup. Slug form — mirrors resolveSlug's shape but
 * returns slug strings instead of ingredient objects.
 *
 * Non-aliased slugs return the slug itself with state and cut both
 * null so callers can treat every identity uniformly.
 */
export function resolveCanonicalIdentity(id) {
  if (!id) return { canonical: null, state: null, cut: null };
  const alias = CANONICAL_ALIASES[id];
  if (alias) {
    return {
      canonical: alias.base,
      state:     alias.state || null,
      cut:       alias.cut   || null,
    };
  }
  return { canonical: id, state: null, cut: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// Ingredient hubs — group related ingredients under one searchable parent so
// the Add flow is "pick Chicken → pick cut" instead of scrolling 400 items,
// and so the Pantry list can collapse 20 kinds of cheese into one card.
//
// `aggregateUnit` is what we sum the group into for the pantry summary.
// (We can always convert each member to grams/ml via toBase, then back out.)
// ─────────────────────────────────────────────────────────────────────────────
export const HUBS = [
  // ── meat ────────────────────────────────────────────────────────────────
  // Each meat hub declares its natural home through `storage`. Children
  // of the hub (chicken_breast, ribeye, ground_pork, etc.) inherit this
  // home via `resolveStorageFromHubChain` — every consumer that needs to
  // know "where does this canonical live?" reads through that walker
  // instead of hardcoding hub-id checks.
  //
  // `storage.tile` is keyed by location so the same canonical routes
  // intelligently when a user moves it between fridge / freezer / pantry.
  {
    id: "chicken_hub",
    name: "Chicken",
    emoji: "🍗",
    category: "meat",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6, // grams per aggregate unit
    storage: {
      location: "fridge",
      tile: { fridge: "meat_poultry", freezer: "frozen_meat_poultry" },
    },
  },
  {
    id: "beef_hub",
    name: "Beef",
    emoji: "🥩",
    category: "meat",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "meat_poultry", freezer: "frozen_meat_poultry" },
    },
  },
  {
    id: "pork_hub",
    name: "Pork",
    emoji: "🥓",
    category: "meat",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "meat_poultry", freezer: "frozen_meat_poultry" },
    },
  },
  {
    id: "lamb_hub",
    name: "Lamb",
    emoji: "🥩",
    category: "meat",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "meat_poultry", freezer: "frozen_meat_poultry" },
    },
  },
  {
    id: "turkey_hub",
    name: "Turkey",
    emoji: "🦃",
    category: "meat",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "meat_poultry", freezer: "frozen_meat_poultry" },
    },
  },
  {
    id: "seafood_hub",
    name: "Seafood",
    emoji: "🐟",
    category: "meat",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "seafood", freezer: "frozen_seafood" },
    },
  },

  // ── dairy ───────────────────────────────────────────────────────────────
  {
    id: "cheese_hub",
    name: "Cheese",
    emoji: "🧀",
    category: "dairy",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "fridge",
      tile: { fridge: "dairy", freezer: "frozen_butter_dairy" },
    },
    // Fallback unit ladder used when a hub member (or a synthetic
    // canonical that points here via parentId) has no `units` array
    // of its own. Covers every common way cheese gets measured —
    // weight units (the truth), shop units (wedge / block / ball /
    // slice), and grated cup/tbsp for hard-aged cheeses (Parmigiano-
    // Reggiano, Pecorino, Asiago, etc) so a recipe asking for "1/2
    // cup grated parm" converts even when the specific canonical
    // doesn't enumerate that unit. findLadderEntry walks here when
    // the ingredient's own ladder misses the asked-for unit.
    defaultUnits: [
      { id: "g",     label: "g",            toBase: 1 },
      { id: "oz",    label: "oz",           toBase: 28.35 },
      { id: "lb",    label: "lb",           toBase: 453.6 },
      { id: "kg",    label: "kg",           toBase: 1000 },
      { id: "wedge", label: "wedges",       toBase: 226 },   // ~8oz wedge
      { id: "block", label: "blocks",       toBase: 226 },
      { id: "ball",  label: "balls",        toBase: 226 },   // mozzarella ball
      { id: "slice", label: "slices",       toBase: 21 },    // ~3/4oz slice
      { id: "cup",   label: "cups grated",  toBase: 90 },    // hard-cheese loose-grated
      { id: "tbsp",  label: "tbsp grated",  toBase: 5 },     // 1 tbsp ≈ 5g grated
      { id: "tsp",   label: "tsp grated",   toBase: 1.7 },
    ],
  },
  {
    id: "milk_hub",
    name: "Milk",
    emoji: "🥛",
    category: "dairy",
    aggregateUnit: "gallon",
    aggregateLabel: "gal",
    aggregateBase: 3785,
    storage: {
      location: "fridge",
      tile: { fridge: "dairy", freezer: "frozen_butter_dairy" },
    },
  },
  {
    id: "yogurt_hub",
    name: "Yogurt",
    emoji: "🥛",
    category: "dairy",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "fridge",
      tile: { fridge: "dairy", freezer: "frozen_butter_dairy" },
    },
  },

  // ── pantry ──────────────────────────────────────────────────────────────
  {
    id: "bread_hub",
    name: "Bread",
    emoji: "🍞",
    category: "pantry",
    aggregateUnit: "loaf",
    aggregateLabel: "loaves",
    aggregateBase: 20, // slices per loaf (base unit is slices)
    storage: {
      location: "pantry",
      tile: { pantry: "bread", fridge: "bread_baked", freezer: "frozen_bread_dough" },
    },
  },
  {
    id: "pasta_hub",
    name: "Pasta",
    emoji: "🍝",
    category: "pantry",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "pantry",
      tile: { pantry: "pasta_grains" },
    },
  },
  {
    // Flour hub (chunk 13c) — protein content varies dramatically
    // across types (all-purpose ~11%, bread ~13%, cake ~8%, 00 varies),
    // and they are NOT 1:1 interchangeable in baking. Grouping them
    // under a hub keeps the picker manageable while preserving each
    // as a first-class canonical for recipe matching.
    id: "flour_hub",
    name: "Flour",
    emoji: "🌾",
    category: "pantry",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "pantry",
      tile: { pantry: "baking" },
    },
  },
  {
    id: "rice_hub",
    name: "Rice",
    emoji: "🍚",
    category: "pantry",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "pantry",
      tile: { pantry: "pasta_grains" },
    },
  },
  {
    id: "bean_hub",
    name: "Beans",
    emoji: "🫘",
    category: "pantry",
    aggregateUnit: "can",
    aggregateLabel: "cans",
    aggregateBase: 425,
    storage: {
      location: "pantry",
      tile: { pantry: "beans_legumes" },
    },
  },

  // ── produce ─────────────────────────────────────────────────────────────
  // Sub-divide the Produce shelf so the picker doesn't dump 60+ items
  // into one drilldown. Each hub groups canonicals by intuitive family
  // (peppers, legumes, root vegetables) instead of forcing one giant
  // "Vegetables" list. Hubs render side-by-side in the Produce tile's
  // group step; users tap one to see its members.
  //
  // Sub-hubs (pepper_hub, root_vegetable_hub, leafy_green_hub) inherit
  // storage from their `parentHub` via `resolveStorageFromHubChain`, so
  // they don't need to redeclare it. Only the top-level produce hubs
  // carry the storage block.
  {
    id: "vegetable_hub",
    name: "Vegetables",
    emoji: "🥦",
    category: "produce",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "produce", freezer: "frozen_veg" },
    },
  },
  {
    id: "fruit_hub",
    name: "Fruit",
    emoji: "🍎",
    category: "produce",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "produce", freezer: "frozen_fruit" },
    },
  },
  {
    id: "pepper_hub",
    name: "Peppers & Chiles",
    emoji: "🫑",
    category: "produce",
    // parentHub nests this hub UNDER vegetable_hub in the wizard's
    // drilldown. Top of Produce shows "Vegetables" once; tapping it
    // reveals members + Peppers/Roots cards. Tapping Peppers drills
    // one more level. Storage inherits via resolveStorageFromHubChain.
    parentHub: "vegetable_hub",
    aggregateUnit: "piece",
    aggregateLabel: "pieces",
    aggregateBase: 100,
  },
  {
    id: "legume_hub",
    name: "Legumes & Soy",
    emoji: "🫘",
    category: "produce",
    aggregateUnit: "package",
    aggregateLabel: "packages",
    aggregateBase: 397,
    storage: {
      location: "fridge",
      tile: { fridge: "produce", freezer: "frozen_veg" },
    },
  },
  {
    id: "root_vegetable_hub",
    name: "Root Vegetables",
    emoji: "🥕",
    category: "produce",
    parentHub: "vegetable_hub",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
  },
  {
    id: "leafy_green_hub",
    name: "Leafy Greens",
    emoji: "🥬",
    category: "produce",
    parentHub: "vegetable_hub",
    aggregateUnit: "head",
    aggregateLabel: "heads",
    aggregateBase: 450,
  },
  {
    // Fungi sit at the TOP of Produce (no parentHub) per user spec —
    // mushrooms aren't really vegetables; they're their own kingdom.
    // Keeping them as a peer of Vegetables / Fruit / Legumes makes
    // the picker honest about that.
    id: "fungi_hub",
    name: "Mushrooms",
    emoji: "🍄",
    category: "produce",
    aggregateUnit: "lb",
    aggregateLabel: "lb",
    aggregateBase: 453.6,
    storage: {
      location: "fridge",
      tile: { fridge: "produce", freezer: "frozen_veg" },
    },
  },
  {
    id: "frozen_fruit_hub",
    name: "Frozen Fruit",
    emoji: "🍓",
    category: "produce",
    aggregateUnit: "package",
    aggregateLabel: "packages",
    aggregateBase: 454,
    storage: {
      location: "freezer",
      tile: { freezer: "frozen_fruit" },
    },
  },

  // ── pantry hubs ────────────────────────────────────────────────────────
  // Long-tail hubs added 2026-05-10 to close the "Gochugaru shows MISSING
  // for tile/hub on scan" gap. Until now, synthetic canonicals minted in
  // these categories had no `parentId` to inherit storage from — Tier 4
  // of getCanonicalHome's cascade returned null and the resolver fell
  // through to defaults. Each new hub declares storage so any canonical
  // pointing to it (bundled or DB-resident) inherits a sensible home.
  // Tile ids are pulled verbatim from the registered tile vocab in
  // src/lib/{pantry,fridge,freezer}Tiles.js so the wizard's drilldown
  // can map them without translation.
  {
    id: "spice_hub",
    name: "Spices & Dried Herbs",
    emoji: "🧂",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "pantry",
      tile: { pantry: "spices_dried_herbs" },
    },
  },
  {
    id: "condiment_hub",
    name: "Condiments & Sauces",
    emoji: "🫙",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    // Sealed = pantry, opened-and-fridged = condiments tile in fridge.
    // Wizard / scan can route per the user's actual storage choice.
    storage: {
      location: "pantry",
      tile: { pantry: "condiments_sauces", fridge: "condiments" },
    },
  },
  {
    id: "vinegar_hub",
    name: "Vinegars",
    emoji: "🍶",
    category: "pantry",
    parentHub: "condiment_hub",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
  },
  {
    id: "oil_hub",
    name: "Oils & Fats",
    emoji: "🫒",
    category: "pantry",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    storage: {
      location: "pantry",
      tile: { pantry: "oils_fats" },
    },
  },
  {
    id: "baking_hub",
    name: "Baking",
    emoji: "🌾",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "pantry",
      tile: { pantry: "baking" },
    },
  },
  {
    id: "sweetener_hub",
    name: "Sweeteners",
    emoji: "🍯",
    category: "pantry",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    storage: {
      location: "pantry",
      tile: { pantry: "sweeteners" },
    },
  },
  {
    id: "nut_seed_hub",
    name: "Nuts & Seeds",
    emoji: "🥜",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "pantry",
      tile: { pantry: "nuts_seeds" },
    },
  },
  {
    id: "dried_chili_hub",
    name: "Dried Chilies",
    emoji: "🌶️",
    category: "pantry",
    parentHub: "spice_hub",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    // Override parent's spices_dried_herbs tile with dried_chilies —
    // the chip user puts ancho / guajillo in their own corner.
    storage: {
      location: "pantry",
      tile: { pantry: "dried_chilies" },
    },
  },
  {
    id: "dried_fruit_hub",
    name: "Dried Fruit & Trail Mix",
    emoji: "🍇",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "pantry",
      tile: { pantry: "dried_fruit" },
    },
  },
  {
    id: "snack_hub",
    name: "Cookies, Crackers & Snacks",
    emoji: "🍪",
    category: "pantry",
    aggregateUnit: "package",
    aggregateLabel: "packages",
    aggregateBase: 200,
    storage: {
      location: "pantry",
      tile: { pantry: "snacks" },
    },
  },
  {
    id: "candy_hub",
    name: "Candy & Chocolate",
    emoji: "🍬",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "pantry",
      tile: { pantry: "candy_chocolate" },
    },
  },
  {
    id: "jerky_hub",
    name: "Jerky & Snack Meats",
    emoji: "🥩",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    // Sealed jerky = pantry; opened high-moisture sticks → fridge
    // meat tile when the user prefers cold storage.
    storage: {
      location: "pantry",
      tile: { pantry: "jerky_snacks", fridge: "meat_poultry" },
    },
  },
  {
    id: "pickle_ferment_hub",
    name: "Pickles & Ferments",
    emoji: "🥒",
    category: "pantry",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    // Sealed jar = pantry; opened jar = fridge condiments shelf.
    storage: {
      location: "pantry",
      tile: { pantry: "pickles_ferments", fridge: "condiments" },
    },
  },
  {
    id: "canned_jarred_hub",
    name: "Canned & Jarred",
    emoji: "🥫",
    category: "pantry",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    // Generic canned/jarred bucket — tomato paste, tuna, coconut milk,
    // stock cubes, etc. Domain-specific hubs (bean_hub for beans,
    // sauce/condiment hubs for sauces) win when the canonical fits
    // them more precisely; this is the fallback for everything else
    // shelf-stable in a can or jar.
    storage: {
      location: "pantry",
      tile: { pantry: "canned_jarred" },
    },
  },
  {
    id: "stock_hub",
    name: "Stocks & Broths",
    emoji: "🥣",
    category: "pantry",
    parentHub: "canned_jarred_hub",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    storage: {
      location: "pantry",
      tile: {
        pantry: "canned_jarred",
        fridge: "leftovers",
        freezer: "frozen_stocks_sauces",
      },
    },
  },
  {
    id: "beverage_hub",
    name: "Beverages",
    emoji: "🥤",
    category: "pantry",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    // Sealed shelf-stable = pantry; opened or naturally cold = fridge.
    storage: {
      location: "pantry",
      tile: { pantry: "pantry_drinks", fridge: "drinks" },
    },
  },
  {
    id: "cooking_alcohol_hub",
    name: "Cooking Alcohol",
    emoji: "🍷",
    category: "pantry",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    storage: {
      location: "pantry",
      tile: { pantry: "cooking_alcohol" },
    },
  },
  {
    id: "shelf_stable_dairy_hub",
    name: "Shelf-Stable Dairy",
    emoji: "🥛",
    category: "pantry",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    storage: {
      location: "pantry",
      tile: { pantry: "shelf_stable_dairy" },
    },
  },

  // ── fridge / freezer hubs ───────────────────────────────────────────
  {
    id: "egg_hub",
    name: "Eggs",
    emoji: "🥚",
    category: "dairy",
    aggregateUnit: "ct",
    aggregateLabel: "count",
    aggregateBase: 50,
    storage: {
      location: "fridge",
      tile: { fridge: "dairy" },
    },
  },
  {
    id: "butter_hub",
    name: "Butter & Spreads",
    emoji: "🧈",
    category: "dairy",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "fridge",
      tile: {
        fridge: "dairy",
        pantry: "oils_fats",
        freezer: "frozen_butter_dairy",
      },
    },
  },
  {
    id: "fresh_herb_hub",
    name: "Fresh Herbs",
    emoji: "🌿",
    category: "produce",
    aggregateUnit: "oz",
    aggregateLabel: "oz",
    aggregateBase: 28.35,
    storage: {
      location: "fridge",
      tile: { fridge: "fresh_herbs", freezer: "frozen_herbs" },
    },
  },
  {
    id: "frozen_meal_hub",
    name: "Frozen Meals",
    emoji: "🍱",
    category: "frozen",
    aggregateUnit: "package",
    aggregateLabel: "packages",
    aggregateBase: 340,
    storage: {
      location: "freezer",
      tile: { freezer: "frozen_meal_prep" },
    },
  },
  {
    id: "frozen_dessert_hub",
    name: "Frozen Desserts",
    emoji: "🧁",
    category: "frozen",
    aggregateUnit: "fl oz",
    aggregateLabel: "fl oz",
    aggregateBase: 29.57,
    storage: {
      location: "freezer",
      tile: { freezer: "frozen_desserts" },
    },
  },
  {
    id: "sea_vegetable_hub",
    name: "Sea Vegetables",
    storage: { location: "pantry", tile: { pantry: "spices_dried_herbs" } },
  },
  {
    id: "dough_hub",
    name: "Doughs & Pastry",
    storage: { location: "fridge", tile: { fridge: "bread_baked", freezer: "frozen_bread_dough", pantry: "baking" } },
  },
  {
    id: "salt_hub",
    name: "Salt",
    parentHub: "spice_hub",
  },
  // ── Deep spice sub-hubs (Phase 2 of the hub-tree migration —
  //    tmp/hub-tree-design.md). Each inherits storage from spice_hub
  //    (pantry / spices_dried_herbs tile). Drilldown order matches
  //    the design markdown so the wizard reads as a real menu
  //    instead of a flat 88-canonical list.
  //
  //    Note: the design called one of these "pepper_hub" but that id
  //    is taken by the vegetable bell-pepper hub. Renamed to
  //    peppercorn_hub for the spice tree. Same disambiguation applied
  //    to "salts_hub" → "seasoning_salts_hub" so it doesn't read as a
  //    typo of salt_hub. Mexican dried chiles fold into the existing
  //    dried_chili_hub rather than its own hub. The design's
  //    "salt_seasonings_hub" (single member: salt) collapses into
  //    salt_hub since they're functionally identical.
  {
    id: "peppercorn_hub",
    name: "Peppercorns",
    parentHub: "spice_hub",
  },
  {
    id: "paprika_chili_hub",
    name: "Paprika & Chili Spices",
    parentHub: "spice_hub",
  },
  {
    id: "indian_spices_hub",
    name: "Indian Spices & Blends",
    parentHub: "spice_hub",
  },
  {
    id: "warm_spices_hub",
    name: "Warm Spices",
    parentHub: "spice_hub",
  },
  {
    id: "seed_spices_hub",
    name: "Seeds & Aromatics",
    parentHub: "spice_hub",
  },
  {
    id: "dried_herbs_hub",
    name: "Dried Herbs",
    parentHub: "spice_hub",
  },
  {
    id: "powder_seasonings_hub",
    name: "Powder Seasonings",
    parentHub: "spice_hub",
  },
  {
    id: "seasoning_salts_hub",
    name: "Seasoning Salts",
    parentHub: "spice_hub",
  },
  {
    id: "seeds_hub",
    name: "Seeds",
    parentHub: "spice_hub",
  },
  {
    id: "global_blends_hub",
    name: "Global Spice Blends",
    parentHub: "spice_hub",
  },
  {
    id: "american_seasonings_hub",
    name: "American Seasonings",
    parentHub: "spice_hub",
  },
  {
    id: "baking_spices_hub",
    name: "Baking Spices",
    parentHub: "spice_hub",
  },
  {
    id: "korean_spices_hub",
    name: "Korean Spices",
    parentHub: "spice_hub",
  },
  {
    id: "mexican_spices_hub",
    name: "Mexican Spices",
    parentHub: "spice_hub",
  },
  // ── Vegetable sub-hubs (Phase 2 — tmp/hub-tree-design.md, with
  //    the audit fixes: herbs moved to fresh_herb_hub, all chiles
  //    under existing pepper_hub, roots merged into existing
  //    root_vegetable_hub, frozen variants under their own hub with
  //    a freezer-tile storage override so the breadcrumb resolves
  //    cleanly).
  {
    id: "alliums_hub",
    name: "Alliums",
    parentHub: "vegetable_hub",
  },
  {
    id: "cruciferous_hub",
    name: "Cruciferous Vegetables",
    parentHub: "vegetable_hub",
  },
  {
    id: "stalk_vegetable_hub",
    name: "Stalks & Stems",
    parentHub: "vegetable_hub",
  },
  {
    id: "fruiting_vegetable_hub",
    name: "Fruiting Vegetables",
    parentHub: "vegetable_hub",
  },
  {
    id: "squash_hub",
    name: "Winter Squash",
    parentHub: "vegetable_hub",
  },
  {
    id: "pea_pod_hub",
    name: "Pea Pods & Beans",
    parentHub: "vegetable_hub",
  },
  {
    id: "frozen_vegetable_hub",
    name: "Frozen Vegetables",
    parentHub: "vegetable_hub",
    storage: { location: "freezer", tile: { freezer: "frozen_veg" } },
  },
  {
    id: "raw_dough_hub",
    name: "Raw Doughs",
    parentHub: "dough_hub",
  },
  {
    id: "laminated_dough_hub",
    name: "Laminated & Pastry Doughs",
    parentHub: "dough_hub",
  },
  {
    id: "pastries_hub",
    name: "Finished Pastries",
    parentHub: "dough_hub",
  },
  {
    id: "tomato_hub",
    name: "Tomato Products",
    parentHub: "canned_jarred_hub",
  },
  {
    id: "fermented_vegetables_hub",
    name: "Fermented Vegetables",
    parentHub: "pickle_ferment_hub",
  },
  {
    id: "mediterranean_herbs_hub",
    name: "Mediterranean Herbs",
    parentHub: "fresh_herb_hub",
  },
  {
    id: "cream_hub",
    name: "Creams",
    parentHub: "milk_hub",
  },
  {
    id: "oats_hub",
    name: "Oats",
    parentHub: "rice_hub",
  },
];

const hubsById = new Map(HUBS.map(h => [h.id, h]));

export function findHub(id) {
  return id ? hubsById.get(id) || null : null;
}

// Return the hub an ingredient belongs to, or null if it's a standalone.
export function hubForIngredient(ingredientOrId) {
  const ing = typeof ingredientOrId === "string"
    ? findIngredient(ingredientOrId)
    : ingredientOrId;
  return ing?.parentId ? hubsById.get(ing.parentId) || null : null;
}

// Members (children) of a hub, in registry order.
export function membersOfHub(hubId) {
  return INGREDIENTS.filter(i => i.parentId === hubId);
}

// Ingredients that are NOT in any hub — shown alongside hub tiles in pickers.
export function standaloneIngredients() {
  return INGREDIENTS.filter(i => !i.parentId);
}

// resolveStorageFromHubChain — walks ingredient → hub → hub.parentHub
// looking for a `storage` block. Returns the first match. This is how
// children inherit a "natural home" from their hub:
//
//   findIngredient("arborio_rice") → ing { parentId: "rice_hub" }
//   rice_hub.storage = { location: "pantry", tile: { pantry: "pasta_grains" } }
//   → arborio_rice's home is pantry · pasta_grains.
//
// Sub-hubs (pepper_hub.parentHub = "vegetable_hub") let the walker
// drop one more level when the immediate hub doesn't declare a home.
//
// Implicit-parent fallback: when an ingredient has no `parentId` (the
// bundled `seed()` helper doesn't always set one — gochugaru, paprika,
// etc. were seeded with category="pantry" + subtype="spice" but no
// hub), infer the hub from category / subtype / slug via
// inferHubFromInfo. The walker then climbs from the inferred hub the
// same as if the ingredient had declared it explicitly. Without this,
// the bundled gochugaru row has no path to spice_hub even though
// every signal points there.
export function resolveStorageFromHubChain(ingredientOrId) {
  const ing = typeof ingredientOrId === "string"
    ? findIngredient(ingredientOrId)
    : ingredientOrId;
  if (!ing) return null;
  let parentId = ing.parentId || null;
  if (!parentId) {
    // Treat the bundled row as the `info` shape inferHubFromInfo
    // expects — category and subtype are the load-bearing fields,
    // and the slug fallback regex catches the rest.
    const inferred = inferHubFromInfo(ing, ing.id);
    if (inferred) {
      parentId = inferred.endsWith("_hub") ? inferred : `${inferred}_hub`;
    }
  }
  let cursor = parentId ? hubsById.get(parentId) : null;
  while (cursor) {
    if (cursor.storage) return cursor.storage;
    cursor = cursor.parentHub ? hubsById.get(cursor.parentHub) : null;
  }
  return null;
}

// getCanonicalHome — single read entry point for "where does this
// canonical live by default?" Cascade (most specific → most generic):
//
//   1. dbInfo.storage              (admin-approved DB enrichment)
//   2. INGREDIENT_INFO[id].storage  (curated bundled per-canonical block)
//   3. ing.storage                  (inline storage on the bundled row —
//                                    seed() canonicals put it here, e.g.
//                                    ice_cream / frozen_peas)
//   4. hub-chain storage            (declarative inheritance from parentId)
//
// Returns `{ location, tile }` where:
//   location: "fridge" | "pantry" | "freezer" | null
//   tile:     a string (preferred when caller knows location) OR an
//             object keyed by location ("fridge" | "pantry" | "freezer")
//             when the canonical's home varies by location. Caller
//             should index `tile?.[location]` when location is known.
//
// `dbInfo` is optional — pass the merged ingredient_info object from
// useIngredientInfo's getInfo when available so DB-side overrides take
// precedence over bundled. Bundled-only callers can omit it.
export function getCanonicalHome(canonicalId, dbInfo) {
  if (!canonicalId) return null;
  const ing = findIngredient(canonicalId);

  // The four tiers each contribute `location` and/or `tile` — neither
  // field is required to be present at any single tier. A higher tier
  // wins on conflict but does NOT short-circuit the lower tiers when
  // it only carries a partial answer. This matters for synthetic
  // canonicals whose admin enrichment landed `info.storage =
  // { location: "pantry" }` with no tile: tier 1 has location, tier 4
  // (hub chain) has the real tile, and the merged result is what the
  // wizard / scan flow / kitchen card all expect. Pre-merge versions
  // returned `{ location: "pantry", tile: null }` from tier 1 and
  // never asked tier 4 — the canonical landed in misc.
  let location = null;
  let tile     = null;
  const adopt = (storage) => {
    if (!storage) return;
    if (location == null && storage.location) location = storage.location;
    if (tile     == null && storage.tile)     tile     = storage.tile;
  };

  // Tier 1: DB-side ingredient_info storage (admin enrichment).
  adopt(dbInfo?.storage);

  // Tier 2: bundled INGREDIENT_INFO storage (curated per-canonical).
  if (location == null || tile == null) {
    const bundledInfo = ing ? INGREDIENT_INFO[ing.id] : null;
    adopt(bundledInfo?.storage);
  }

  // Tier 3: inline storage on the bundled INGREDIENTS row itself —
  // canonicals seeded via seed() with `{ storage: { location: "freezer" } }`
  // (ice_cream, frozen_peas, etc.) land here.
  if (location == null || tile == null) {
    adopt(ing?.storage);
  }

  // Tier 4: hub-chain inheritance.
  if (location == null || tile == null) {
    adopt(resolveStorageFromHubChain(ing));
  }

  if (location == null && tile == null) return null;
  return { location, tile };
}

// getCanonicalTile — convenience wrapper. Returns the resolved tile
// for `canonicalId` AT a given location. Handles both string and
// object shapes of `home.tile`:
//   home.tile === "pasta_grains"          → returns "pasta_grains" only when location matches home.location
//   home.tile === { pantry: "pasta_grains" } → returns the location-keyed value
export function getCanonicalTile(canonicalId, location, dbInfo) {
  const home = getCanonicalHome(canonicalId, dbInfo);
  if (!home || !home.tile) return null;
  if (typeof home.tile === "string") {
    // String tile — only valid for the canonical's primary location.
    return home.location && home.location === location ? home.tile : null;
  }
  if (typeof home.tile === "object") {
    return (location && home.tile[location]) || null;
  }
  return null;
}

// Lookup table for canonical inference: lowercase name/shortName →
// ingredient id. Built lazily so it's a one-time cost on first use.
// Cache is invalidated by registerCanonicalsFromDb so a freshly
// admin-approved canonical can show up in inferCanonicalFromName
// without a page reload. Only includes tokens ≥ 3 chars to avoid
// spurious matches ("a"/"i" would fire on almost anything).
let _aliasMapCache = null;
function getCanonicalAliasMap() {
  if (_aliasMapCache) return _aliasMapCache;
  const map = new Map();
  // Bundled entries first so their ids win on ties (registry order
  // is carefully curated, DB entries are user/admin-minted and may
  // overlap by name). "Tomato" in INGREDIENTS beats a DB-registered
  // "Heirloom Tomato" when matching just the word "tomato".
  for (const ing of INGREDIENTS) {
    // shortName intentionally NOT an alias source. Many shortNames
    // are pure modifiers ("Whole" for milk, "White" for rice, "Brown"
    // for brown_rice, "Ground" shared across ground_beef/pork/chicken)
    // that produce nonsense matches when they sneak into the alias
    // map: "egg white" → rice via the shortName "White"; "whole eggs"
    // → milk via "Whole". shortName stays as a UI display field
    // (ShopMode / ItemCard / CanonicalSuggestionCard etc.); aliases
    // come from ing.name and the explicit ing.searchAliases list,
    // which are author-controlled and tier-aware.
    const tokens = [ing.name].filter(Boolean);
    // searchAliases — opt-in array of additional substrings that
    // route to this canonical. Lets `soda` match "root beer", "cola",
    // "ginger ale" without needing separate canonicals for each
    // flavor family. Bundled aliases are authored inline; DB-tier
    // aliases come from ingredient_info.info.searchAliases via the
    // AI alias-generation deployment and merge on top here. Bundled
    // wins on collision because we walk bundled first and check
    // `!map.has(key)` before each insert.
    if (Array.isArray(ing.searchAliases)) {
      for (const a of ing.searchAliases) {
        if (typeof a === "string" && a.trim().length >= 3) {
          tokens.push(a);
        }
      }
    }
    // DB-tier searchAliases for THIS bundled id (via the same
    // ingredient_info row that drives applyDbOverlay). Bundled-author
    // aliases above already populated `tokens`; these are gap-fillers
    // that ride alongside the bundled entry.
    const overlay = dbOverlay.get(ing.id);
    if (overlay && Array.isArray(overlay.searchAliases)) {
      for (const a of overlay.searchAliases) tokens.push(a);
    }
    // Legacy `aliases` field — historically used as slug-form
    // synonyms for CANONICAL_ALIASES legacy-redirect lookup
    // (cane_sugar.aliases = ["granulated_sugar","white_sugar"];
    // heavy_cream.aliases = ["heavy_whipping_cream"]; etc.). These
    // are real English-language synonyms too, just written in
    // slug-form (underscores). Convert _ → space so they show up
    // in inferCanonicalFromName as "granulated sugar" / "heavy
    // whipping cream" / "soda pop" / "chicken thighs" rather than
    // sitting unused outside the legacy-redirect path. Closes a
    // real gap: pre-this, "granulated sugar" in a recipe row
    // never bound to cane_sugar because the alias map only knew
    // "cane sugar" + (nothing for the variants).
    if (Array.isArray(ing.aliases)) {
      for (const a of ing.aliases) {
        if (typeof a !== "string") continue;
        const spaced = a.replace(/_/g, " ").trim();
        if (spaced.length >= 3) tokens.push(spaced);
      }
    }
    for (const t of tokens) {
      const key = t.toLowerCase().trim();
      if (key.length < 3) continue;
      if (!map.has(key)) map.set(key, ing.id);
    }
  }
  // DB-registered synthetics fill gaps the bundled registry doesn't
  // cover. Admin-approved "pepperoni" gets its slug registered so
  // typing "pepperoni" in the typeahead or scanning a pepperoni
  // product with no bundled match lands on the DB entry. Synthetics
  // also carry searchAliases (set by syntheticFromInfo from the
  // AI deployment).
  for (const ing of dbCanonicals.values()) {
    const tokens = [ing.name].filter(Boolean);
    if (Array.isArray(ing.searchAliases)) {
      for (const a of ing.searchAliases) tokens.push(a);
    }
    for (const t of tokens) {
      const key = String(t).toLowerCase().trim();
      if (key.length < 3) continue;
      if (!map.has(key)) map.set(key, ing.id);
    }
  }
  _aliasMapCache = map;
  return map;
}

/**
 * Infer the most-specific canonical ingredient id from a free-text
 * name. Returns the id or null. Longest-matching alias wins; ties
 * break on registry order.
 *
 * Example:
 *   "Oscar Mayer Bratwurst"        → 'sausage' (no bratwurst canonical;
 *                                   falls through to type default)
 *   "Mama Bear's Green Onion"      → 'green_onion'
 *   "Franks Best Cheese Dogs"      → 'hot_dog'
 *   "Home Run Inn Pizza"           → null  (no pizza canonical yet)
 *   "random text"                  → null
 *
 * Pair with canonicalIdForType (foodTypes.js) — name-based wins when
 * it fires, type default fires otherwise.
 */
// COMPOSITIONAL TIER — used by inferCanonicalFromName when multiple
// canonicals match a productName. The architectural rule:
//
//   "Inserting cheese into meat does not make meat cheese.
//    Sugar Cookie is not Sugar — it's a Cookie that contains sugar."
//
// Higher tier wins. Lower-tier matches are NOT the canonical; they
// are claims / ingredients / inclusions stuck inside the higher-tier
// product.
//
//   tier 1 — primitive / raw ingredient (sugar, salt, flour, eggs,
//            milk, butter, oil, water, vinegar, baking soda, honey,
//            sour cream). Almost never the actual product the user
//            scanned; almost always a flavor or compositional
//            ingredient inside something else.
//   tier 2 — sub-product / assembled-but-still-an-ingredient
//            (cheese, yogurt, sausage, dough, jam, sauce). Can be
//            the product (a block of cheddar) OR an ingredient
//            inside a higher-tier product (cheese in a cheese
//            frank). Default when not specified.
//   tier 3 — pre-built assembled product (cookie, candy_bar,
//            beef_stick, frank, hot_dogs, ice_cream, pretzels,
//            chips, mac_and_cheese, lunchables). What the user
//            actually bought as a SKU. Wins over tiers 1 and 2.
//
// Default tier when not declared on the ingredient is 2 — most of
// the registry is in the middle ground. The lookup below only needs
// to set tier on canonicals that frequently collide with others
// (the over-matched primitives like sugar/salt/honey, the sub-
// product cheeses/yogurts that get stuffed into other products,
// and the assembled snacks/cookies/etc. that should always win).
const COMPOSITIONAL_TIER_OVERRIDES = {
  // tier 1 — primitives that should NEVER win when something else
  // matches. "Sugar Cookie" → cookie, "Honey Mustard Pretzels" →
  // pretzels (then mustard demoted further), "Salt & Vinegar Chips"
  // → chips.
  sugar: 1, brown_sugar: 1, powdered_sugar: 1,
  salt: 1, kosher_salt: 1, sea_salt: 1, table_salt: 1,
  flour: 1, bread_flour: 1, cake_flour: 1, pastry_flour: 1,
  eggs: 1, milk: 1, butter: 1,
  honey: 1, maple_syrup: 1, molasses: 1,
  baking_soda: 1, baking_powder: 1, yeast: 1, cornstarch: 1,
  cocoa: 1, cocoa_powder: 1, chocolate_chips: 1,
  vanilla: 1, vanilla_extract: 1,
  sour_cream: 1,
  // Spices and seasonings
  black_pepper: 1, white_pepper: 1, cinnamon: 1, paprika: 1,
  cumin: 1, ginger: 1, garlic: 1, onion: 1,
  // Oils + vinegars
  olive_oil: 1, vegetable_oil: 1, canola_oil: 1, sesame_oil: 1,
  vinegar: 1, rice_vinegar: 1, balsamic_vinegar: 1, apple_cider_vinegar: 1,

  // tier 3 — assembled products that should ALWAYS win when
  // matched. "Cheese Frank" → frank (cheese demotes), "Strawberry
  // Greek Yogurt" → greek_yogurt (strawberry demotes).
  cookie: 3, chocolate_chip_cookie: 3, sandwich_cookie: 3, cookies_and_cream: 3,
  candy: 3, candy_bar: 3, chocolate_candy: 3,
  peanut_butter_candy: 3, peanut_butter_cup: 3,
  cracker: 3, goldfish: 3,
  beef_stick: 3, snack_stick: 3, beef_jerky: 3,
  pork_stick: 3, pork_jerky: 3, turkey_jerky: 3,
  hot_dogs: 3, frank: 3,
  ice_cream: 3, ice_cream_bar: 3, frozen_yogurt: 3,
  greek_yogurt: 3, yogurt_drink: 3,
  mac_and_cheese: 3, lunchables: 3, cereal: 3,
  bologna: 3, pastrami: 3, roast_beef: 3, mortadella: 3,
  capicola: 3, kielbasa: 3, smoked_sausage: 3,
  string_cheese: 3, shredded_cheese: 3, sliced_cheese: 3, cheese_singles: 3,
  chicken_nuggets: 3, chicken_wings: 3,
};

export function tierForCanonical(canonicalId) {
  if (!canonicalId) return 2;
  return COMPOSITIONAL_TIER_OVERRIDES[canonicalId] ?? 2;
}

// Longest-match canonical resolver, NOT tier-aware. Use for token-
// level lookups inside an ingredient declaration where each token
// identifies itself ("BAKING SODA" → baking_soda, not the soda
// beverage). Tier preference is the WRONG default for ingredient-
// list parsing — "baking soda" SHOULD resolve to baking_soda even
// though `soda` is at a higher tier.
export function inferCanonicalFromNameLongestMatch(name) {
  const lower = (name || "").toLowerCase().trim();
  if (lower.length < 3) return null;
  const map = getCanonicalAliasMap();
  let bestId = null;
  let bestLen = 0;
  for (const [alias, id] of map) {
    if (alias.length < 3) continue;
    if (!lower.includes(alias)) continue;
    if (alias.length > bestLen) {
      bestId = id;
      bestLen = alias.length;
    }
  }
  return bestId;
}

// Tier-aware canonical inference. Walks the alias map, collects ALL
// matches, then picks the best from the HIGHEST TIER GROUP. Within a
// tier, longest match wins (the existing rule). Returns just the id
// for back-compat with existing callers; resolveCanonicalWithClaims
// below returns id + claims if the caller wants both.
// Word-boundary match — substring won't false-match "cod" inside
// "code". Hyphens count as boundaries so "Coca-Cola" matches "cola"
// AND "Lemon-Lime" matches "lemon-lime" (the latter because the
// alias itself contains the hyphen, so regex compares whole token).
// Apostrophes are NOT boundaries — keeps "Fitz's" intact as one
// token so "fitz's" alias matches without breaking on apostrophe.
function aliasMatches(haystack, alias) {
  const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Boundary class includes hyphen so "coca-cola" → "cola" matches.
  const re = new RegExp(`(^|[^a-z0-9'])${escaped}([^a-z0-9']|$)`);
  return re.test(haystack);
}

export function inferCanonicalFromName(name) {
  const lower = (name || "").toLowerCase().trim();
  if (lower.length < 3) return null;
  const map = getCanonicalAliasMap();
  // Collect every match, grouped by tier.
  const byTier = { 1: [], 2: [], 3: [] };
  for (const [alias, id] of map) {
    if (alias.length < 3) continue;
    if (!aliasMatches(lower, alias)) continue;
    const tier = tierForCanonical(id);
    byTier[tier].push({ alias, id, len: alias.length });
  }
  // Highest tier with matches wins. Within tier, longest alias wins.
  for (const t of [3, 2, 1]) {
    if (byTier[t].length === 0) continue;
    byTier[t].sort((a, b) => b.len - a.len);
    return byTier[t][0].id;
  }
  return null;
}

// Same resolution as inferCanonicalFromName, but returns the
// extracted claims (lower-tier matches that demoted out of the
// canonical slot). Use this when the caller wants both the canonical
// AND the flavor/inclusion tokens to surface as chips. "Cheese
// Frank" → { canonicalId: "frank", claims: ["Cheese"] }.
export function resolveCanonicalWithClaims(name) {
  const lower = (name || "").toLowerCase().trim();
  if (lower.length < 3) return { canonicalId: null, claims: [] };
  const map = getCanonicalAliasMap();
  const byTier = { 1: [], 2: [], 3: [] };
  for (const [alias, id] of map) {
    if (alias.length < 3) continue;
    if (!aliasMatches(lower, alias)) continue;
    const tier = tierForCanonical(id);
    byTier[tier].push({ alias, id, len: alias.length });
  }
  let canonicalId = null;
  let winningTier = 0;
  for (const t of [3, 2, 1]) {
    if (byTier[t].length === 0) continue;
    byTier[t].sort((a, b) => b.len - a.len);
    canonicalId = byTier[t][0].id;
    winningTier = t;
    break;
  }
  // Claims = matches at LOWER tiers than the winner. Same-tier
  // runner-ups don't auto-demote (cheddar + cheese both at tier 2
  // for example — only one wins the canonical slot, the other isn't
  // a "claim" by virtue of losing alphabetically).
  //
  // Redundancy filter: if a lower-tier canonical's name is already
  // contained in the winner's canonical name OR slug, skip it. "Beef
  // Stick" canonical implicitly contains "beef" — surfacing "Beef"
  // as a claim would be redundant. "Sliced Cheese" implicitly
  // contains "cheese". Same idea for "Greek Yogurt" + "Yogurt".
  const winningIng = canonicalId ? findIngredient(canonicalId) : null;
  const winningTokens = new Set();
  const collectTokens = (s) => {
    if (!s) return;
    String(s).toLowerCase().split(/[\s_\-,&'/]+/).forEach(t => {
      if (t.length >= 3) winningTokens.add(t);
    });
  };
  collectTokens(canonicalId);
  collectTokens(winningIng?.name);
  collectTokens(winningIng?.shortName);

  const claims = [];
  const seen = new Set();
  for (const t of [3, 2, 1]) {
    if (t >= winningTier) continue;   // skip winning + higher-tier (none higher exists)
    for (const m of byTier[t]) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      // Use the canonical's display name as the claim label so a
      // claim chip reads "Sugar" not "sugar" / "honey_mustard".
      const ing = findIngredient(m.id);
      const label = ing?.name || m.alias.replace(/_/g, " ");
      // Skip redundant claims — words already represented in the
      // winning canonical's name/slug.
      const labelTokens = label.toLowerCase().split(/[\s_\-,&'/]+/).filter(t => t.length >= 3);
      const allTokensRedundant = labelTokens.length > 0 && labelTokens.every(t => winningTokens.has(t));
      if (allTokensRedundant) continue;
      claims.push(label);
    }
  }
  // Map each surfaced claim back to a canonical id when the claim
  // text resolves through the alias map. "Cheese" claim → cheese
  // canonical id. The resulting claimCanonicalIds list lets callers
  // populate `pantry_items.ingredient_ids` so diet filters
  // (isCompatibleWithDiet) can walk the row's actual composition —
  // a Cheese Frank scan ends up with ingredient_ids=["cheese"] and
  // a dairy-free user gets a warning chip on the row.
  const claimCanonicalIds = [];
  const claimSeen = new Set();
  for (const t of [3, 2, 1]) {
    if (t >= winningTier) continue;
    for (const m of byTier[t]) {
      if (claimSeen.has(m.id) || m.id === canonicalId) continue;
      claimSeen.add(m.id);
      // Reuse the redundancy filter from above — same rule, just
      // applied to canonical ids instead of label strings.
      const ing = findIngredient(m.id);
      const label = ing?.name || m.alias.replace(/_/g, " ");
      const labelTokens = label.toLowerCase().split(/[\s_\-,&'/]+/).filter(t => t.length >= 3);
      const allTokensRedundant = labelTokens.length > 0 && labelTokens.every(t => winningTokens.has(t));
      if (allTokensRedundant) continue;
      claimCanonicalIds.push(m.id);
    }
  }

  return { canonicalId, claims, claimCanonicalIds };
}

// Common freshness / prep / form modifiers the AI tends to prepend
// to canonical-looking slugs ("fresh_tortillas", "canned_tomatoes",
// "ground_cinnamon" when it means "cinnamon"). Stripped one at a
// time from the leading position when a direct lookup misses, so
// the coercer falls back to the underlying canonical rather than
// silently dropping the reference. Order doesn't matter — the
// coerce pass tries each prefix independently.
const AI_MODIFIER_PREFIXES = [
  "fresh", "dried", "dry", "canned", "jarred", "pickled",
  "raw", "frozen", "cooked", "leftover",
  "grilled", "roasted", "boiled", "steamed", "sauteed", "seared",
  "pan_fried", "deep_fried", "baked",
  "whole", "halved", "quartered",
  "diced", "minced", "chopped", "sliced", "julienned",
  "grated", "shredded", "crumbled",
  "powdered", "ground",
  "organic", "local",
  // Dietary / macro modifiers — Claude often emits these as leading
  // slug tokens ("low_carb_tortillas", "gluten_free_pasta"). Strip to
  // land on the base canonical; the modifier itself is preserved as
  // a dietaryClaim on the recipe's ideal slot (AIRecipe).
  "low_carb", "lowcarb", "low_sugar", "lowsugar", "low_sodium", "lowsodium",
  "low_fat", "lowfat", "fat_free", "fatfree", "sugar_free", "sugarfree",
  "zero_carb", "zerocarb", "zero_sugar", "zerosugar",
  "gluten_free", "glutenfree", "dairy_free", "dairyfree",
  "grain_free", "grainfree",
  "keto", "paleo", "whole30", "vegan", "vegetarian",
  "whole_wheat", "wholewheat", "whole_grain", "wholegrain",
  "multigrain", "high_protein", "highprotein",
];

/**
 * Coerce a free-text or slug-ish ingredient reference into a known
 * canonical id. Built to be robust against the ways AI-generated
 * recipes drift from our registry:
 *
 *   "Tortillas"            → "tortillas"  (case)
 *   "tortilla-wraps"       → null         (unless registered)
 *   "fresh_tortillas"      → "tortillas"  (modifier strip)
 *   "fresh tortillas"      → "tortillas"  (space→underscore, modifier strip)
 *   "corn_tortillas"       → "tortillas"  (plural + lead-word strip)
 *   "Ground Cinnamon"      → "ground_cinnamon" (direct hit — we have that slug)
 *   "heirloom tomatoes"    → "tomato"     (infer-by-name on stripped form)
 *   "pepperoni"            → db-canonical if admin-minted, else null
 *
 * Returns the canonical id when resolvable, or null when the input
 * truly doesn't match. Never persists a non-canonical string as if
 * it were a canonical — callers should drop the field on null
 * rather than storing the raw AI guess.
 */
export function coerceToCanonicalId(raw) {
  if (!raw || typeof raw !== "string") return null;
  // Discriminator: was the input PROSE (free text with spaces) or
  // a SLUG (snake_case / kebab-case identifier)? Free-text inputs
  // get the prose-fallback at step 4 (inferCanonicalFromName).
  // Slug-form inputs (e.g. "reese-s-peanut-butter-chips" — a
  // synthetic pending-canonical slug Claude proposed) must NOT
  // fall through to that path — it substring-matches alias tokens
  // and silently binds the synthetic slug to whatever registered
  // canonical happens to share a word. That's how "Reese's Peanut
  // Butter Chips" wrongly bound to peanut_butter: the slug
  // contained "peanut butter" so the alias resolver picked it up.
  const wasProse = /\s/.test(raw);
  // Strip surrounding noise → lowercase → spaces/hyphens become
  // underscores → drop anything outside [a-z0-9_] → trim underscores.
  const norm = raw.toLowerCase().trim()
    .replace(/[\s\-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/^_+|_+$/g, "");
  if (!norm) return null;

  const direct = (id) => (byId.has(id) || dbCanonicals.has(id)) ? id : null;
  const viaAlias = (id) => {
    const alias = CANONICAL_ALIASES[id];
    return (alias?.base && byId.has(alias.base)) ? alias.base : null;
  };

  // 1. Direct hit — most AI outputs land here when the prompt is
  //    well-behaved and the model echoes pantry ids verbatim.
  if (direct(norm)) return norm;
  if (viaAlias(norm)) return viaAlias(norm);

  // 2. Plural nudge. "tortilla" → "tortillas" and vice versa.
  if (norm.endsWith("s")) {
    const sing = norm.slice(0, -1);
    if (direct(sing)) return sing;
    if (viaAlias(sing)) return viaAlias(sing);
  } else {
    const plur = norm + "s";
    if (direct(plur)) return plur;
    if (viaAlias(plur)) return viaAlias(plur);
  }

  // 3. Strip lead modifier and retry (direct + plural nudge + alias).
  //    "fresh_tortillas" → "tortillas" (plural direct hit).
  //    "canned_tomato"   → "tomatoes" → "tomato" (plural nudge lands).
  for (const mod of AI_MODIFIER_PREFIXES) {
    const prefix = mod + "_";
    if (!norm.startsWith(prefix)) continue;
    const stripped = norm.slice(prefix.length);
    if (!stripped) continue;
    if (direct(stripped)) return stripped;
    if (viaAlias(stripped)) return viaAlias(stripped);
    if (stripped.endsWith("s")) {
      const sing = stripped.slice(0, -1);
      if (direct(sing)) return sing;
      if (viaAlias(sing)) return viaAlias(sing);
    } else {
      const plur = stripped + "s";
      if (direct(plur)) return plur;
      if (viaAlias(plur)) return viaAlias(plur);
    }
  }

  // 4. Last resort — treat the normalized string as prose and run the
  //    full name-inference pass. Handles descriptors we haven't
  //    enumerated as modifiers ("heirloom tomatoes" → tomato) via
  //    longest-alias match across the whole registry.
  //
  //    GATED ON wasProse: only run for free-text inputs. Slug-form
  //    inputs that fell through steps 1-3 are unregistered synthetic
  //    slugs (proposed canonicals not yet promoted, or genuinely
  //    unknown ids). Substring-matching a multi-token slug against
  //    the alias map is silently wrong — "reese-s-peanut-butter-
  //    chips" → "peanut_butter" via the "peanut butter" alias
  //    substring is the canonical example. Return null instead;
  //    callers like coerceRecipeCanonicalIds will then fall back to
  //    inferring from the row's free-text `item` field, which is the
  //    correct prose source.
  if (wasProse) {
    const inferred = inferCanonicalFromName(norm.replace(/_/g, " "));
    if (inferred) return inferred;
  }

  return null;
}

/**
 * Walk every ingredient reference in a recipe object and rewrite
 * `ingredientId` through `coerceToCanonicalId`. Unresolved ids get
 * set to null — we never persist an AI hallucination as if it were
 * a canonical. Returns a new recipe object; input is not mutated.
 *
 * Covers:
 *   - recipe.ingredients[]  (canonical list)
 *   - recipe.steps[].uses[] (per-step measurement subset)
 *   - recipe.ideal[]        (sketch mode IDEAL column)
 *   - recipe.pantry[]       (sketch mode PANTRY column)
 */
export function coerceRecipeCanonicalIds(recipe) {
  if (!recipe || typeof recipe !== "object") return recipe;
  const coerceRow = (row) => {
    if (!row || typeof row !== "object") return row;
    // Prefer ingredientId; some sketch shapes use `id` instead.
    const raw = row.ingredientId ?? row.id ?? null;
    let coerced = coerceToCanonicalId(raw);
    // Name-fallback resolution. When the recipe row has no
    // ingredientId (Claude returned null OR the field wasn't set),
    // try to infer one from the free-text name. The bundled alias
    // map has every common ingredient stamped — "butter" / "eggs"
    // / "instant yeast" / "buttermilk" etc. — and Tier 1 in
    // inferCanonicalFromName is a tier-aware alias lookup, so this
    // is conservative (false positives extremely rare). Without
    // this fallback, every downstream consumer that depends on
    // ingredientId (pantry pairing, nutrition, baking validator,
    // substitution suggestions) silently degrades on AI-generated
    // recipes where Claude only bound the canonicals it was 100%
    // sure of and left the rest null. Validators-over-prompts: the
    // generation prompt asks Claude to bind, this is the contract.
    if (!coerced) {
      const nameSrc = (typeof row.item === "string" && row.item)
        || (typeof row.name === "string" && row.name)
        || null;
      if (nameSrc) {
        const inferred = inferCanonicalFromName(nameSrc);
        if (inferred) coerced = inferred;
      }
    }
    return { ...row, ingredientId: coerced };
  };
  const next = { ...recipe };
  if (Array.isArray(recipe.ingredients)) next.ingredients = recipe.ingredients.map(coerceRow);
  if (Array.isArray(recipe.ideal))       next.ideal       = recipe.ideal.map(coerceRow);
  if (Array.isArray(recipe.pantry))      next.pantry      = recipe.pantry.map(coerceRow);
  if (Array.isArray(recipe.steps)) {
    next.steps = recipe.steps.map(step => {
      if (!step || typeof step !== "object") return step;
      if (!Array.isArray(step.uses)) return step;
      return { ...step, uses: step.uses.map(coerceRow) };
    });
  }
  return next;
}

export function unitLabel(ingredient, unitId) {
  if (!ingredient) return unitId || "";
  return ingredient.units.find(u => u.id === unitId)?.label || unitId;
}

// Convert a { amount, unit } pair to the ingredient's base unit (g / ml / count).
// Returns NaN if either the ingredient or unit is unknown.
export function toBase(qty, ingredient) {
  if (!qty || !ingredient) return NaN;
  const u = ingredient.units.find(x => x.id === qty.unit);
  if (!u) return NaN;
  return Number(qty.amount) * u.toBase;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fallback unit inference for scanned items that don't match the registry.
//
// The scanner sometimes returns ingredientId: null with a sensible emoji but a
// nonsense unit like "count" (e.g. "Alouette" cheese as 1 count). We use the
// emoji + category to pick a reasonable unit list so the user can actually
// edit the thing with a real dropdown instead of being stuck at "1 count".
// ─────────────────────────────────────────────────────────────────────────────

// Unit presets, so we don't repeat the same weight/volume ladders over and over.
const UNITS_CHEESE = [
  { id: "oz",    label: "oz",     toBase: 28.35 },
  { id: "lb",    label: "lb",     toBase: 453.6 },
  { id: "block", label: "blocks", toBase: 226 },
  { id: "tub",   label: "tubs",   toBase: 198 },
  { id: "g",     label: "g",      toBase: 1 },
];
const UNITS_DAIRY_LIQUID = [
  { id: "gallon",      label: "gallons",      toBase: 3785 },
  { id: "half_gallon", label: "half gallons", toBase: 1893 },
  { id: "quart",       label: "quarts",       toBase: 946 },
  { id: "pint",        label: "pints",        toBase: 473 },
  { id: "cup",         label: "cups",         toBase: 240 },
  { id: "fl_oz",       label: "fl oz",        toBase: 29.57 },
];
const UNITS_MEAT = [
  { id: "lb", label: "lb", toBase: 453.6 },
  { id: "oz", label: "oz", toBase: 28.35 },
  { id: "kg", label: "kg", toBase: 1000 },
];
// "bag" deliberately excluded from the default ladder — a bag IS
// the package (i.e. pantry_items.max is the bag's content). Using
// "bag" as the unit makes "1 bag out of 16oz package" tautological.
// Per-ingredient unit lists may still declare "bag" explicitly when
// the curator wants it as an option (e.g. chips, where "0.25 bag"
// is natural); this generic fallback sticks to weight/volume.
const UNITS_DRY_WEIGHT = [
  { id: "oz",  label: "oz",  toBase: 28.35 },
  { id: "lb",  label: "lb",  toBase: 453.6 },
  { id: "cup", label: "cups",toBase: 150 },
  { id: "g",   label: "g",   toBase: 1 },
];
const UNITS_PRODUCE_COUNT = [
  { id: "count", label: "count", toBase: 1 },
  { id: "lb",    label: "lb",    toBase: 453.6 },
  { id: "bunch", label: "bunches", toBase: 5 },
];
const UNITS_BREAD = [
  { id: "slice", label: "slices", toBase: 1 },
  { id: "loaf",  label: "loaves", toBase: 20 },
  { id: "count", label: "count",  toBase: 1 },
];
const UNITS_GENERIC = [
  { id: "count", label: "count", toBase: 1 },
  { id: "oz",    label: "oz",    toBase: 28.35 },
  { id: "lb",    label: "lb",    toBase: 453.6 },
];

// Given a scanned (non-canonical) item's emoji + category, pick a sensible
// unit list + default unit. Used in the scanner confirm screen so the user
// gets a real dropdown (oz, lb, fl oz) instead of being locked into "count".
export function inferUnitsForScanned({ emoji, category, unit }) {
  // Cheese — emoji is the strongest signal, category "dairy" often misleads.
  if (emoji === "🧀") {
    return { units: UNITS_CHEESE, defaultUnit: "oz" };
  }
  // Liquid dairy: milk glass, juice box.
  if (emoji === "🥛" || emoji === "🧃") {
    return { units: UNITS_DAIRY_LIQUID, defaultUnit: "half_gallon" };
  }
  // Egg — only case "count" is actually right.
  if (emoji === "🥚") {
    return {
      units: [
        { id: "count", label: "eggs",  toBase: 1 },
        { id: "dozen", label: "dozen", toBase: 12 },
      ],
      defaultUnit: "count",
    };
  }
  // Meats
  if (emoji === "🥩" || emoji === "🍗" || emoji === "🥓" || emoji === "🦃" ||
      emoji === "🐟" || emoji === "🍤" || category === "meat") {
    return { units: UNITS_MEAT, defaultUnit: "lb" };
  }
  // Bread-ish
  if (emoji === "🍞" || emoji === "🥖" || emoji === "🥐" || emoji === "🥯") {
    return { units: UNITS_BREAD, defaultUnit: "loaf" };
  }
  // Dry/pantry grains & similar
  if (emoji === "🍚" || emoji === "🌾" || emoji === "🥜" || category === "dry") {
    return { units: UNITS_DRY_WEIGHT, defaultUnit: "lb" };
  }
  // Produce fallback — usually legitimately counted (apples, peppers).
  if (category === "produce") {
    return { units: UNITS_PRODUCE_COUNT, defaultUnit: "count" };
  }
  // Dairy fallback without a more specific emoji — probably a tub (yogurt etc.).
  if (category === "dairy") {
    return {
      units: [
        { id: "oz",  label: "oz",  toBase: 28.35 },
        { id: "tub", label: "tubs",toBase: 454 },
        { id: "cup", label: "cups",toBase: 240 },
        { id: "lb",  label: "lb",  toBase: 453.6 },
      ],
      defaultUnit: "oz",
    };
  }
  // Last ditch. Preserve whatever unit Claude guessed (might be "bottle", "jar", etc.).
  return { units: UNITS_GENERIC, defaultUnit: unit || "count" };
}

// Compare pantry "have" vs recipe "need". Both are { amount, unit }.
// Returns one of "ok" | "low" | "missing" | "unknown" (if conversion fails).
//   - missing: no pantry item OR have < need after conversion
//   - low:     have ≥ need, but leftover would be below the pantry item's
//              lowThreshold (also converted to base units)
//   - ok:      plenty
// ─────────────────────────────────────────────────────────────────────────────
// Ingredient info — description, flavor profile, wine pairings, suggested
// recipes. Drives the detail sheet the user sees when they tap an
// ingredient row in the drill-down. We seed rich content for a couple
// dozen popular items (cheeses + meats mostly) and fall back to a
// subcategory-level blurb for everything else so nothing feels empty.
// ─────────────────────────────────────────────────────────────────────────────

// Per-cheese-subcategory defaults. Written so that even an obscure entry
// like Piave or Stinking Bishop has a sensible profile until we curate it.
const SUBCATEGORY_INFO = {
  // Cheese subcategories
  "Fresh / Unaged": {
    description: "Young cheeses with high moisture and no rind. Milky and soft, meant to be eaten within days of production.",
    flavorProfile: "Mild, milky, clean; light acidity",
    winePairings: ["Prosecco", "Sauvignon Blanc", "Pinot Grigio", "Dry Rosé"],
    recipes: ["Caprese salad", "Ricotta toast", "Stuffed pastas", "Fresh tomato & basil"],
  },
  "Soft Ripened": {
    description: "Bloomy-rind cheeses ripened from the outside in. Soft, sometimes oozy paste under a velvety white rind.",
    flavorProfile: "Buttery, mushroomy, creamy; earthy rind",
    winePairings: ["Champagne", "Chardonnay", "Pinot Noir", "Beaujolais"],
    recipes: ["Baked brie with honey", "Cheese boards", "Melted on crusty bread", "Fig & jam pairings"],
  },
  "Semi-Soft": {
    description: "Cheeses with a pliable, sliceable paste — softer than a Gouda, firmer than a Brie. Great melters.",
    flavorProfile: "Mild to savory; creamy, sometimes tangy",
    winePairings: ["Riesling", "Pinot Gris", "Chardonnay", "Light reds"],
    recipes: ["Grilled cheese", "Raclette night", "Quesadillas", "Pasta bakes"],
  },
  "Washed Rind": {
    description: "Cheeses washed with brine, wine, or beer during aging. Sticky orange rinds, bold barnyard aromas.",
    flavorProfile: "Pungent, meaty, funky; softer interior than the smell suggests",
    winePairings: ["Gewürztraminer", "Alsatian Riesling", "Trappist ale", "Sauternes"],
    recipes: ["Served at room temp on bread", "Melted into pasta", "Tartiflette", "With charcuterie"],
  },
  "Semi-Hard": {
    description: "Pressed cheeses aged a few months — firm, sliceable, reliably melty. The workhorses of the cheese drawer.",
    flavorProfile: "Nutty, mellow, balanced; slight sweetness",
    winePairings: ["Cabernet Sauvignon", "Merlot", "Chardonnay", "Tempranillo"],
    recipes: ["Grilled cheese", "Mac & cheese", "Cheeseburgers", "Fondue"],
  },
  "Hard / Aged": {
    description: "Long-aged cheeses with low moisture and concentrated flavor. Shave, grate, or snack in thin slivers.",
    flavorProfile: "Nutty, savory, umami; crystalline crunch",
    winePairings: ["Barolo", "Chianti Classico", "Sangiovese", "Aged Amarone"],
    recipes: ["Pasta finishing", "Risotto", "Caesar salad", "Shaved over roasted vegetables"],
  },
  "Blue": {
    description: "Cheeses inoculated with Penicillium mold, producing blue-green veins throughout the paste.",
    flavorProfile: "Salty, spicy, sharp; creamy or crumbly depending on age",
    winePairings: ["Port", "Sauternes", "Late-harvest Riesling", "Zinfandel"],
    recipes: ["Blue cheese burger", "Wedge salad", "Steak topping", "Pear & walnut salads"],
  },
  "Smoked": {
    description: "Cheeses cold- or hot-smoked over wood to impart a distinct savory layer on top of the base cheese.",
    flavorProfile: "Smoky, savory, often salty; base cheese profile still shows through",
    winePairings: ["Zinfandel", "Syrah/Shiraz", "Smoky Rioja", "Amber ales"],
    recipes: ["Smoked cheese board", "Stuffed burgers", "Mac & cheese", "Breakfast sandwiches"],
  },
  "Alpine": {
    description: "Mountain cheeses from the Swiss/French Alps. Dense, nutty pastes made for melting in fondue or raclette.",
    flavorProfile: "Nutty, brothy, slightly sweet; savory long finish",
    winePairings: ["Savoie whites", "Chasselas", "Dry Riesling", "Light Pinot Noir"],
    recipes: ["Classic fondue", "Raclette", "Croque monsieur", "French onion soup topping"],
  },
  "American Originals": {
    description: "Cheeses developed in the United States — from factory classics to Pacific Northwest curios.",
    flavorProfile: "Varies widely; generally approachable and melty",
    winePairings: ["Zinfandel", "Cabernet Sauvignon", "Chardonnay", "Craft lagers"],
    recipes: ["Grilled cheese", "Nachos", "Burgers", "Mac & cheese"],
  },
};

// ╭──────────────────────────────────────────────────────────────────────╮
// │ INGREDIENT_INFO — JS-side mirror of the Supabase ingredient_info     │
// │ table. Editorial / storage / substitutions / etc. prose. Used as the │
// │ OFFLINE FALLBACK when the DB row hasn't loaded yet.                  │
// │                                                                      │
// │ DO NOT read off this directly from feature code — go through         │
// │ getIngredientInfo(ingredient, dbOverride) below so DB-approved data  │
// │ from useIngredientInfo() wins. See file-header doc for the full      │
// │ rationale on why this is split from INGREDIENTS.                     │
// ╰──────────────────────────────────────────────────────────────────────╯
//
// Rich per-ingredient info. Anything on the ingredient overrides the
// subcategory fallback in `getIngredientInfo` below.
const INGREDIENT_INFO = {
  // ── dairy staples ──────────────────────────────────────────────────
  butter: {
    description: "Churned cream concentrated to ~80% fat. The indispensable cooking fat — conducts heat evenly, browns beautifully, emulsifies sauces, and enriches anything it touches.",
    flavorProfile: "Rich, creamy, faintly sweet; browns into toasted-nut territory",
    prepTips: "For a golden sear: melt butter into hot oil so the oil's smoke point protects the milk solids from burning. For sauces: finish off-heat, whisking cold cubes in a little at a time (monter au beurre).",
    storage: {
      location: "fridge",
      shelfLifeDays: 90,
      shelfLife: { fridge: 90, freezer: 365, pantry: null },
      // Opened (post-seal-broken) freshness window. Butter holds
      // up well in the fridge after opening — 30 days is a
      // conservative consumer-safety estimate; food-science
      // sources cite up to 60 days for plain unsalted. PantryCard
      // re-anchors expiresAt off "now" the moment the user
      // drags the fill gauge below 100%.
      shelfLifeOpened: { fridge: 30, freezer: 365, pantry: null },
      tips: "Wrap tightly — butter absorbs fridge odors. Freezes beautifully for up to a year in its original wrap plus a zip-top bag.",
      spoilageSigns: "Yellow waxy surface, off smell, rancid-nut aroma. Mold along an exposed edge — trim an inch off or toss.",
      freezable: true,
      freezeNotes: "In its original wrapper inside a zip-top bag. 1 year; no quality loss.",
    },
    substitutions: [
      { id: "ghee",      tier: "direct",    note: "Pure butterfat — no milk solids. Higher smoke point, no burning." },
      { id: "olive_oil", tier: "emergency", note: "For savory sautés; loses the browning and enrichment." },
    ],
    pairs: ["garlic", "parsley", "lemon", "sage"],
    flavor: {
      primary: ["fat", "salt"],
      intensity: "moderate",
      heatChange: {
        raw: "cool, creamy, barely-there sweetness",
        cooked: "rich, silky, aromatic — brown-butter notes develop around 150°C",
        charred: "burned milk solids turn bitter fast; black specks = start over",
      },
    },
    nutrition: { per: "100g", kcal: 717, protein_g: 1, fat_g: 81, carb_g: 0, sodium_mg: 11 },
    origin: "Europe; churned butter dates to at least 4,500 BCE in cooler dairy-herding cultures.",
    culturalNotes: "French cuisine built its reputation on butter; Mediterranean cuisines lean on olive oil — the same dish (fish, vegetables, pasta) tells a different story depending which fat carries it.",
    allergens: ["dairy"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: true, kosher: "dairy", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "European-style (≥82% fat) makes richer sauces and flakier pastry; American-style is fine for everyday cooking and has more water for stovetop sautés.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Kerrygold, Plugra, or any European-style cultured butter makes a visible, tasteable difference in pastry and finishing sauces. For scrambling eggs, store brand is fine.",
    },
    skillDev: { skills: ["sauce", "heat"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },

  eggs: {
    description: "Chicken eggs — the single most versatile ingredient in the kitchen. Emulsify sauces, leaven cakes, bind meatballs, thicken custards, or stand alone as the meal.",
    flavorProfile: "Rich, mild, buttery yolk; clean, neutral white that picks up anything you add",
    prepTips: "Cold eggs crack cleanly, room-temp eggs whip to more volume. For even cooking, start hard-boiled eggs in simmering water and shock in ice at 9 minutes. For silky scrambled: low heat, constant stirring, pull off just before they look done.",
    storage: {
      location: "fridge",
      shelfLifeDays: 35,
      shelfLife: { fridge: 35, freezer: 365, pantry: null },
      tips: "Store in the carton (not the door) — temperature's more stable on the main shelf. A fresh egg sinks; a bad one floats.",
      spoilageSigns: "Cracked egg smells sulfurous or ammonia-like — toss. Float-test: raises to vertical = past prime but OK cooked; floats flat = done.",
      freezable: true,
      freezeNotes: "Crack, whisk, portion into ice-cube trays or 2-egg zip-top bags. 1 year. Never freeze in shell.",
      prepYield: { whole: "1 large egg", yields: "~3 tbsp / 50g whisked" },
    },
    substitutions: [
      { id: "flax_egg", tier: "dietary", note: "1 tbsp ground flax + 3 tbsp water for baking. Works as a binder — not for scrambles, custards, or anything where the egg is the star." },
    ],
    pairs: ["butter", "cream", "parmesan", "chive", "tarragon"],
    flavor: {
      primary: ["umami", "fat"],
      intensity: "moderate",
      heatChange: {
        raw: "viscous, slightly salty, neutral",
        cooked: "savory, mellow, buttery — 63°C yolk is peak silky",
        charred: "sulfurous and chewy past 75°C; overcooked whites squeak",
      },
    },
    nutrition: { per: "count", kcal: 72, protein_g: 6, fat_g: 5, carb_g: 0.4, sodium_mg: 71 },
    origin: "Domesticated from the red junglefowl in Southeast Asia ~8,000 years ago; now the world's most-eaten animal protein.",
    culturalNotes: "Escoffier said 'the cuisine of a nation can be judged by its egg cookery' — which is why French apprentices start with the omelette.",
    allergens: ["egg"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Pasture-raised yolks are deeper orange and richer in omega-3s. 'Cage-free' is a minimum bar, not a gold standard. Freshness beats provenance — a farm-stand egg laid yesterday outcooks a week-old organic.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "In a simple fried egg or custard, you taste the difference between supermarket cage-free and pasture-raised. In a quiche or scrambled eggs, less so.",
    },
    skillDev: { skills: ["egg", "timing", "heat"], difficulty: "moderate", proFromScratch: false, fromScratchRecipeId: null },
  },

  milk: {
    description: "Whole cow's milk, ~3.25% fat. The liquid foundation of custards, béchamels, soaked croutons, and a thousand breakfasts.",
    flavorProfile: "Clean, sweet, faintly grassy; cream on top if unhomogenized",
    prepTips: "Always scald (heat to just below simmer) before adding to eggs for custards — it shortens cook time and prevents curdling. Milk burns fast; use a heavy pot and don't walk away.",
    storage: {
      location: "fridge",
      shelfLifeDays: 10,
      shelfLife: { fridge: 10, freezer: 90, pantry: null },
      tips: "Coldest shelf, not the door. Ultra-pasteurized lasts much longer; traditional pasteurized tastes better but spoils sooner.",
      spoilageSigns: "Sour smell, chunky when poured, yellow tinge. If it curdles on pour into hot coffee, it's gone.",
      freezable: true,
      freezeNotes: "Freezes fine but fat separates on thaw — shake vigorously. Best for cooking post-thaw, not drinking. 3 months.",
    },
    substitutions: [
      { id: "half_and_half", tier: "direct",  note: "Richer — cut 1:1 with water to mimic whole milk in recipes." },
      { id: "oat_milk",      tier: "dietary", note: "Best non-dairy swap in savory cooking. Avoid for custards that need egg-proteins to set against milk solids." },
    ],
    pairs: ["butter", "vanilla", "cinnamon", "honey"],
    flavor: {
      primary: ["fat", "sweet"],
      intensity: "mild",
      heatChange: {
        raw: "clean, slightly sweet, cooling",
        cooked: "mellow, rounded, faintly caramelized when scalded",
        charred: "scorched milk solids taste of burnt popcorn — ruined in seconds",
      },
    },
    nutrition: { per: "100g", kcal: 61, protein_g: 3.2, fat_g: 3.3, carb_g: 4.8, sodium_mg: 43 },
    origin: "Cattle domesticated ~10,000 years ago in the Fertile Crescent; Northern European populations evolved lactose tolerance within the last 7,500.",
    allergens: ["dairy"],
    diet: { vegan: false, vegetarian: true, keto: false, halal: true, kosher: "dairy", fodmap: "moderate", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Grass-fed milk has higher omega-3 and conjugated linoleic acid. Small-dairy glass bottles are the pinnacle — worth it for drinking, overkill for cooking.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "For cooking: barely matters. For drinking: the jump from supermarket to small-dairy glass-bottle is real.",
    },
    skillDev: { skills: ["sauce", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  // ── popular cheeses ────────────────────────────────────────────────
  parmesan: {
    description: "Italian hard cheese made from cow's milk and aged 12-36 months. Granular, crystalline texture from tyrosine protein clusters.",
    flavorProfile: "Nutty, savory, sharp; intense umami finish with hints of pineapple",
    prepTips: "Grate on a microplane just before using — pre-grated dries out and loses bloom. Save rinds to simmer into stocks and bean pots (they melt into the liquid).",
    storage: {
      location: "fridge",
      shelfLifeDays: 180,
      shelfLife: { fridge: 180, freezer: 365, pantry: null },
      tips: "Wrap in parchment, then loosely in foil — the cheese needs to breathe a little. A sweating wedge means you've wrapped it too tight.",
      spoilageSigns: "Fuzzy mold ≠ the rind. Surface mold on a wedge: scrape ½-inch deep; anything fuzzy past that, toss. Ammonia smell = gone.",
      freezable: true,
      freezeNotes: "Best grated into a zip-top bag, frozen flat, broken off as needed. Wedges freeze but get crumbly.",
    },
    substitutions: [
      { id: "pecorino",     tier: "direct", note: "Sharper, saltier. Cut the dish's added salt by a pinch when swapping." },
      { id: "grana_padano", tier: "direct", note: "Softer, younger cousin. Milder but interchangeable in most uses." },
    ],
    pairs: ["butter", "black_pepper", "garlic", "basil"],
    flavor: {
      primary: ["umami", "salt", "fat"],
      intensity: "strong",
      heatChange: {
        raw: "granular crunch, intense savory-sweet",
        cooked: "melts into a silken umami layer — builds pan sauces' body",
        charred: "caramelizes into a crisp frico; past that, acrid and dry",
      },
    },
    nutrition: { per: "100g", kcal: 431, protein_g: 38, fat_g: 29, carb_g: 4, sodium_mg: 1529 },
    winePairings: ["Barolo", "Chianti Classico", "Sangiovese", "Aged Chardonnay"],
    recipes: ["Pasta carbonara", "Risotto alla Milanese", "Caesar salad", "Parmigiana"],
    origin: "Emilia-Romagna, Italy — the true 'Parmigiano-Reggiano' is PDO-protected and made only in a specific zone.",
    culturalNotes: "Italian banks have accepted Parmigiano wheels as loan collateral since the 1950s — they age for years and only appreciate in value.",
    allergens: ["dairy"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: false, kosher: "dairy", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Look for the dot-matrix rind stamp on true Parmigiano-Reggiano. 'Parmesan' with no origin guarantee is fine for everyday grating; save the real thing for finishing.",
    market: {
      priceTier: "premium",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "The gulf between supermarket-can 'parm' and real Parmigiano-Reggiano DOP is enormous. For cacio e pepe or anything where the cheese IS the dish, the real stuff is non-negotiable.",
    },
    skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },
  parmigiano: {
    description: "The original 'Parmigiano-Reggiano' — a protected Italian cheese aged 24+ months in the Parma/Reggio region.",
    flavorProfile: "Deeply nutty, fruity, salty; tyrosine crystals give it crunch",
    winePairings: ["Barolo", "Brunello di Montalcino", "Lambrusco", "Vintage Champagne"],
    recipes: ["Finish any pasta", "Risotto", "Shaved on prosciutto", "Eaten in chunks with balsamic"],
  },
  pecorino: {
    description: "Italian sheep's milk cheese, saltier and sharper than Parmesan. Aged firm for grating (Pecorino Romano) or young for eating (Pecorino Toscano).",
    flavorProfile: "Sharp, salty, tangy; pronounced sheep-milk funk that parmesan lacks",
    prepTips: "Grate fresh and pile loose — don't pack it. For cacio e pepe, the fineness of the grate matters more than any other ingredient; use a microplane, not a box grater.",
    storage: {
      location: "fridge",
      shelfLifeDays: 150,
      shelfLife: { fridge: 150, freezer: 365, pantry: null },
      tips: "Parchment + foil. Don't let it touch other cheeses in the drawer — the sheep-milk aroma bleeds.",
      spoilageSigns: "Sheep-milk funk is normal; ammonia is not. Surface mold: scrape. Deep mold or sour smell: toss.",
      freezable: true,
      freezeNotes: "Grate first, freeze flat in a zip-top. Use within 6 months for peak flavor.",
    },
    substitutions: [
      { id: "parmesan", tier: "direct", note: "Milder and less salty. Cut the recipe's salt by a pinch if substituting." },
    ],
    pairs: ["black_pepper", "spaghetti", "honey", "fava"],
    flavor: {
      primary: ["salt", "umami"],
      intensity: "strong",
      heatChange: {
        raw: "sharp, tangy, mouth-puckering",
        cooked: "melts slower than parm but emulsifies famously in cacio e pepe",
        charred: "crisps into salty shards; sharper than parm frico",
      },
    },
    nutrition: { per: "100g", kcal: 419, protein_g: 28, fat_g: 33, carb_g: 2, sodium_mg: 1800 },
    winePairings: ["Chianti", "Vermentino", "Nero d'Avola", "Dry Rosé"],
    recipes: ["Cacio e pepe", "Pasta alla gricia", "Shaved on fava beans", "With honey & pears"],
    origin: "Lazio and Sardinia, Italy — Pecorino Romano is the oldest documented Italian cheese, fed to Roman legions.",
    culturalNotes: "'Pecorino' literally means 'from sheep' (pecora). The 'Romano' was added centuries after Roman shepherds brought the craft to Sardinia, where most of it is actually made today.",
    allergens: ["dairy"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: false, kosher: "dairy", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Pecorino Romano DOP is the reference for cacio e pepe and pasta alla gricia. For table eating, a younger Pecorino Toscano is mellower.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "For cacio e pepe, it has to be real Pecorino Romano DOP — anything labeled 'Romano cheese' (often cow's milk) won't emulsify the sauce correctly.",
    },
    skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },
  mozzarella: {
    description: "Fresh Italian cheese, traditionally made from water buffalo milk (Mozzarella di Bufala) or cow's milk (fior di latte). Soft, stretchy, sold in balls packed in water.",
    flavorProfile: "Milky, clean, lightly sweet; tender bite that squeaks briefly against the teeth",
    prepTips: "Pull from the fridge 30 minutes before serving — cold mozz is rubbery and mute. Tear rather than cut for salads so the craggy edges catch oil and salt.",
    storage: {
      location: "fridge",
      shelfLifeDays: 7,
      shelfLife: { fridge: 7, freezer: 30, pantry: null },
      tips: "Keep it submerged in its brine. Once opened, use within 2–3 days; it sours quickly.",
      spoilageSigns: "Cloudy or sour brine, yellow tint on the ball, or a sharp tang — fresh mozz should smell like cream, not cheese.",
      freezable: true,
      freezeNotes: "OK for cooking (pizza, pasta bakes) — texture suffers badly on thaw, so never freeze for caprese.",
    },
    substitutions: [
      { id: "burrata", tier: "pro",       note: "Richer, creamier center. Upgrade for the same dishes when mozz is the star." },
      { id: "ricotta", tier: "emergency", note: "For cooked dishes (pizza, pasta bakes) when fresh mozz isn't available." },
    ],
    pairs: ["tomato", "basil", "olive_oil", "balsamic"],
    flavor: {
      primary: ["fat", "sweet"],
      intensity: "mild",
      heatChange: {
        raw: "milky, cool, faintly tangy",
        cooked: "melts into a pillowy stretch; sweetens as the water cooks off",
        charred: "blisters into leopard spots on pizza — prized browning that goes bitter past char-black",
      },
    },
    nutrition: { per: "100g", kcal: 280, protein_g: 18, fat_g: 22, carb_g: 3, sodium_mg: 627 },
    winePairings: ["Prosecco", "Pinot Grigio", "Falanghina", "Rosé"],
    recipes: ["Caprese", "Pizza Margherita", "Fresh pasta", "Grilled on tomato toast"],
    origin: "Campania, Italy — the bufala version is PDO-protected and comes from a specific zone around Naples.",
    culturalNotes: "'Mozzarella' comes from 'mozzare' (to cut by hand) — traditional makers literally pinch off balls from a rope of stretched curd.",
    allergens: ["dairy"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: true, kosher: "dairy", fodmap: "moderate", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "For caprese and pizza Margherita, splurge on bufala at least once and taste the difference. For everything else, good fior di latte is excellent.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Supermarket vacuum-packed 'low-moisture' mozz is rubbery and bland. Get water-packed balls for anything where the cheese is the star; save the shredded bag for quick weeknight pizzas.",
    },
    skillDev: { skills: [], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },
  burrata: {
    description: "A pouch of fresh mozzarella filled with stracciatella and cream. Cuts open to an oozing center.",
    flavorProfile: "Rich, buttery, milky; silkier than mozzarella",
    winePairings: ["Champagne", "Vermentino", "Gavi", "Chenin Blanc"],
    recipes: ["On toast with olive oil & sea salt", "With ripe peaches & prosciutto", "On pizza after baking", "Over roasted tomatoes"],
  },
  cheddar: {
    description: "English-origin pressed cow's milk cheese, 'cheddared' in slabs during production. Ranges from mild to extra sharp.",
    flavorProfile: "Creamy, tangy, sharp; increases in complexity with age",
    winePairings: ["Cabernet Sauvignon", "Zinfandel", "Aged Bordeaux", "Stout"],
    recipes: ["Grilled cheese", "Mac & cheese", "Cheeseburger", "Ploughman's lunch"],
  },
  aged_cheddar: {
    description: "Cheddar aged 2+ years. Drier, crumblier, and dotted with tyrosine crystals.",
    flavorProfile: "Sharp, nutty, slightly sweet; crystalline crunch",
    winePairings: ["Cabernet Sauvignon", "Vintage Port", "Amarone", "Barrel-aged stout"],
    recipes: ["Cheese board with chutney", "Apple & cheddar pie", "Shaved on soups", "Paired with dark chocolate"],
  },
  gruyere: {
    description: "Swiss Alpine cheese from the Gruyères region, pressed and aged 5-12 months. The quintessential melter — fondue, gratin, croque monsieur.",
    flavorProfile: "Nutty, brothy, earthy; savory long finish with a slight sweetness from the Alpine grasses",
    prepTips: "Shred just before cooking — pre-shredded bags are coated with anti-caking starch that muddies the melt. For fondue, toss the shreds with a spoon of cornstarch to prevent the fat from breaking.",
    storage: {
      location: "fridge",
      shelfLifeDays: 120,
      shelfLife: { fridge: 120, freezer: 365, pantry: null },
      tips: "Parchment + foil in the cheese drawer. A little surface mold on an aged Gruyère is normal — scrape it off, not a disaster.",
      spoilageSigns: "Ammonia sting in the nose, deep blue-green mold through the paste (not surface), cracked rind oozing — past its prime.",
      freezable: true,
      freezeNotes: "Grate first, freeze in a zip-top. Whole wedges get crumbly on thaw.",
    },
    substitutions: [
      { id: "comte",    tier: "direct",    note: "French cousin from the Jura. More nuanced, interchangeable in most applications." },
      { id: "emmental", tier: "emergency", note: "Milder, holier. Melts well but lacks Gruyère's depth." },
    ],
    pairs: ["ham", "white_wine", "potato", "yellow_onion"],
    flavor: {
      primary: ["umami", "fat", "sweet"],
      intensity: "strong",
      heatChange: {
        raw: "firm, deeply nutty, savory-sweet",
        cooked: "melts into an elastic, stretchy pull — the fondue standard for a reason",
        charred: "browns on a gratin into a deep-amber crust with caramelized edges",
      },
    },
    nutrition: { per: "100g", kcal: 413, protein_g: 30, fat_g: 32, carb_g: 0.4, sodium_mg: 714 },
    winePairings: ["Chasselas", "Dry Riesling", "Chardonnay", "Pinot Noir"],
    recipes: ["Fondue", "French onion soup", "Croque monsieur", "Quiche Lorraine"],
    origin: "Canton of Fribourg, Switzerland — named for the town of Gruyères, made since at least 1115 CE.",
    culturalNotes: "Swiss soldiers in the Vatican Guard were once paid partly in Gruyère rations. The cheese travels well and doesn't spoil fast — a medieval protein bar.",
    allergens: ["dairy"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: false, kosher: "dairy", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "'Gruyère AOP' (Swiss) is the real one; 'Le Gruyère' alone can be French-style. Both are excellent — AOP is deeper.",
    market: {
      priceTier: "premium",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Cave-aged Gruyère AOP is on a different planet from the grocery-aisle stuff sold as 'gruyere'. For fondue or French onion soup, the real one pays for itself.",
    },
    skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },
  comte: {
    description: "French Alpine cheese from the Jura mountains, aged 6-24 months. Cousin to Gruyère with a longer finish.",
    flavorProfile: "Nutty, fruity, caramelized; brown butter notes",
    winePairings: ["Jura Chardonnay", "Vin Jaune", "Savagnin", "Pinot Noir"],
    recipes: ["Cheese board", "Gougères", "Melted on baguette", "Grated over gratins"],
  },
  brie: {
    description: "French bloomy-rind cheese, soft and oozy at room temperature. Most common cheese-board cheese.",
    flavorProfile: "Buttery, mushroomy, mild; earthy rind",
    winePairings: ["Champagne", "Chardonnay", "Pinot Noir", "Beaujolais"],
    recipes: ["Baked brie with honey", "Brie + apple + prosciutto sandwich", "Cheese boards", "Melted into pasta"],
  },
  camembert: {
    description: "Normandy cheese, a smaller and more assertive cousin of Brie. Sold in its iconic wooden box.",
    flavorProfile: "Earthy, mushroomy, barnyard; softer paste than Brie",
    winePairings: ["Normandy cider", "Chablis", "Pinot Noir", "Champagne"],
    recipes: ["Baked in its box", "On crusty baguette", "With apple slices", "Cheese boards"],
  },
  feta: {
    description: "Greek brine-cured cheese, traditionally made from sheep and goat milk. Crumbly and bright.",
    flavorProfile: "Salty, tangy, sharp; crumbly texture",
    winePairings: ["Assyrtiko", "Sauvignon Blanc", "Retsina", "Rosé"],
    recipes: ["Greek salad", "Spanakopita", "Crumbled on watermelon", "Roasted with tomatoes & olives"],
  },
  goat_cheese: {
    description: "Fresh chèvre — soft, spreadable cheese made from goat's milk. Often sold in small logs.",
    flavorProfile: "Tangy, grassy, clean; slightly lemony",
    winePairings: ["Sancerre", "Sauvignon Blanc", "Chenin Blanc", "Pouilly-Fumé"],
    recipes: ["Beet & goat cheese salad", "Goat cheese tart", "Warm crostini with honey", "Stuffed dates"],
  },
  cream_cheese: {
    description: "Fresh, soft American cheese made from cream + milk. The bagel cheese.",
    flavorProfile: "Mild, tangy, rich; very smooth",
    winePairings: ["Mimosas", "Off-dry Riesling", "Prosecco"],
    recipes: ["Bagel & lox", "Cheesecake", "Cream-cheese frosting", "Rangoons & dips"],
  },
  gouda: {
    description: "Dutch cow's milk cheese. Young gouda is mild and supple; aged versions turn butterscotchy.",
    flavorProfile: "Buttery, slightly sweet; caramelly when aged",
    winePairings: ["Chardonnay", "Merlot", "Pinot Noir", "Belgian ale"],
    recipes: ["Grilled cheese", "Cheese board", "Melted in toasties", "Shaved on apples"],
  },
  aged_gouda: {
    description: "Gouda aged 1-5 years. Dry, crystalline, deeply sweet-savory.",
    flavorProfile: "Butterscotch, caramel, umami; snappy crystals",
    winePairings: ["Vintage Port", "Madeira", "Amarone", "Old Ale"],
    recipes: ["Cheese plates", "With dark chocolate", "Shaved on charcuterie", "In stroopwafel-cheese pairings"],
  },
  manchego: {
    description: "Spanish sheep's milk cheese from La Mancha, pressed and aged 3-12+ months. Recognizable basket-weave rind.",
    flavorProfile: "Nutty, buttery, grassy; tangy with age",
    winePairings: ["Tempranillo", "Rioja Reserva", "Sherry (Amontillado)", "Cava"],
    recipes: ["With membrillo (quince paste)", "Tapas boards", "Shaved on jamón", "Grilled on bread"],
  },
  mascarpone: {
    description: "Italian triple-cream cheese. Thicker than crème fraîche, essentially a pourable cream cheese.",
    flavorProfile: "Sweet, buttery, very rich; pillowy texture",
    winePairings: ["Moscato d'Asti", "Vin Santo", "Prosecco", "Late-harvest Riesling"],
    recipes: ["Tiramisu", "Pasta alla norcina", "Folded into risotto", "On toast with berries"],
  },
  ricotta: {
    description: "Italian whey cheese, cooked and drained. Soft, pillowy, lightly sweet — not a true cheese by European rules.",
    flavorProfile: "Mild, milky, subtly sweet; fluffy curds",
    winePairings: ["Prosecco", "Pinot Grigio", "Orvieto", "Dry Rosé"],
    recipes: ["Lasagna", "Stuffed shells", "Ricotta pancakes", "On toast with honey + lemon"],
  },
  gorgonzola: {
    description: "Italian blue cheese. Dolce is young and creamy; piccante is aged and sharper.",
    flavorProfile: "Creamy, tangy, spicy-sharp; buttery mouthfeel",
    winePairings: ["Recioto", "Port", "Moscato d'Asti", "Amarone"],
    recipes: ["Gorgonzola gnocchi", "On pear + walnut salad", "Risotto", "Pizza quattro formaggi"],
  },
  roquefort: {
    description: "French sheep's milk blue, aged in the caves of Combalou. The original blue cheese.",
    flavorProfile: "Intense, salty, sharp; creamy yet crumbly",
    winePairings: ["Sauternes", "Tawny Port", "Monbazillac", "Gewürztraminer"],
    recipes: ["Roquefort butter on steak", "Endive salad", "On crusty bread with honey", "Steak sauce"],
  },
  stilton: {
    description: "English blue cheese from the Midlands, firm and crumbly with bold blue veining.",
    flavorProfile: "Rich, tangy, savory; mellow compared to Roquefort",
    winePairings: ["Vintage Port", "Madeira", "Late Bottled Port", "Stout"],
    recipes: ["Christmas cheese board", "Stilton & pear tart", "Crumbled in soup", "With walnuts"],
  },
  humboldt_fog: {
    description: "American artisan goat cheese (Cypress Grove, California) with a central ash line and bloomy rind.",
    flavorProfile: "Tangy, lemony, floral; creamy-to-chalky texture",
    winePairings: ["Sauvignon Blanc", "Sancerre", "Prosecco", "Dry Rosé"],
    recipes: ["Cheese board centerpiece", "On beet salad", "With fig jam", "Crostini with honey"],
  },
  epoisses: {
    description: "French Burgundy washed-rind cheese, washed with Marc de Bourgogne. Pungent, sticky, unforgettable.",
    flavorProfile: "Barnyard, boozy, meaty; creamy spoon-able interior",
    winePairings: ["Marc de Bourgogne", "Gewürztraminer", "Burgundy", "Trappist ale"],
    recipes: ["Spooned onto toast", "Melted on baked potato", "With charcuterie", "Cheese course"],
  },

  // ── meats ──────────────────────────────────────────────────────────
  chicken_breast: {
    description: "Lean, boneless/skinless cut from the pectoral muscle. Quick to cook, easy to overcook — the window between juicy and shoe-leather is maybe 30 seconds.",
    flavorProfile: "Mild, lightly savory; needs seasoning and fat to come alive",
    prepTips: "Pound to even thickness (½-inch) for uniform cooking. Brine 30 min in 1 tbsp salt + 1 cup water before cooking for noticeably juicier results. Pull at 160°F — carryover brings it to 165°F safe temp.",
    storage: {
      location: "fridge",
      shelfLifeDays: 2,
      shelfLife: { fridge: 2, freezer: 120, pantry: null },
      tips: "On the bottom shelf so drips don't cross-contaminate. Freeze within 1–2 days if you're not using — freezes well up to 4 months in a zip-top bag.",
      spoilageSigns: "Grey tint, slime on the surface, sour or sulfurous smell — toss. Fresh chicken should smell like basically nothing.",
      freezable: true,
      freezeNotes: "Individually wrapped then bagged so you can defrost a single breast. 4 months.",
    },
    substitutions: [
      { id: "chicken_thigh", tier: "direct",    note: "Darker, fattier, more forgiving. Better for braises and slow cooks." },
      { id: "turkey_breast", tier: "direct",    note: "1:1 swap; adjust cooking time down slightly for even thinner cuts." },
    ],
    pairs: ["lemon", "garlic", "rosemary", "butter"],
    flavor: {
      primary: ["umami", "salt"],
      intensity: "mild",
      heatChange: {
        raw: "virtually flavorless — a blank protein canvas",
        cooked: "gently savory; picks up whatever you cooked it with (butter, wine, lemon, herbs)",
        charred: "sear marks add roasted depth; past that, dry and chewy fast",
      },
    },
    nutrition: { per: "100g", kcal: 165, protein_g: 31, fat_g: 3.6, carb_g: 0, sodium_mg: 74 },
    winePairings: ["Chardonnay", "Sauvignon Blanc", "Pinot Noir", "Dry Riesling"],
    recipes: ["Chicken piccata", "Chicken parmesan", "Grilled chicken salad", "Chicken marsala"],
    origin: "Domesticated ~8,000 years ago in Southeast Asia; the breast was historically the less-prized cut — it was the thigh meat that was eaten first.",
    culturalNotes: "The US consumes ~65 billion chicken breasts annually. American breeding has selected for oversized breasts since the 1950s — they're roughly 4× their natural size.",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: true, kosher: "meat", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Pasture-raised or heritage breeds (Freedom Ranger, Bresse) have better flavor and firmer texture. 'Air-chilled' is a genuine quality upgrade over standard water-chilled.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Air-chilled, pasture-raised chicken breast is a different animal than factory-farmed. Worth the 2× price for dishes where chicken IS the meal; for chicken salad or soup, conventional is fine.",
    },
    skillDev: { skills: ["heat", "timing"], difficulty: "moderate", proFromScratch: false, fromScratchRecipeId: null },
  },
  chicken_thigh: {
    description: "Dark-meat cut from the upper leg. More forgiving than breast, richer flavor.",
    flavorProfile: "Rich, juicy, deeply savory; handles high heat",
    winePairings: ["Pinot Noir", "Grenache", "Chardonnay", "Zinfandel"],
    recipes: ["Chicken thighs with lemon & olives", "Chicken adobo", "Thai basil chicken", "Roasted with potatoes"],
  },
  chicken: {
    description: "Whole bird, 3–5 lb. The most versatile protein in the kitchen — roast it, spatchcock it, break it down for parts, simmer the carcass into stock.",
    flavorProfile: "Balanced light + dark meat; crispy skin is the whole game",
    prepTips: "Dry-brine 24 hours ahead (salt + pepper + whatever herbs, uncovered in fridge). This does more for crispy skin + seasoned meat than any marinade. Truss only if the legs splay wildly; otherwise let the bird breathe.",
    storage: {
      location: "fridge",
      shelfLifeDays: 2,
      shelfLife: { fridge: 2, freezer: 365, pantry: null },
      tips: "Dry-brine = uncovered on a rack for 24 hours. Cover only if storing longer than that. Stock from the carcass freezes beautifully for 6 months.",
      spoilageSigns: "Slimy, grey, or green tint; sour or sulfurous smell. Raw chicken should look fresh-pink and smell neutral.",
      freezable: true,
      freezeNotes: "Vacuum-seal if you can; zip-top is fine for up to 12 months. Defrost in the fridge, never at room temp.",
    },
    substitutions: [
      { id: "cornish_hen", tier: "direct", note: "Smaller (~1 lb) versions of the same bird. Cook in 45 min instead of 90. Great for individual portions." },
    ],
    pairs: ["lemon", "thyme", "garlic", "butter", "white_wine"],
    flavor: {
      primary: ["umami", "fat"],
      intensity: "moderate",
      heatChange: {
        raw: "neutral, faintly iron",
        cooked: "savory, clean; roasted skin adds deeply caramelized Maillard depth",
        charred: "grilled-skin char is prized; burnt flesh turns acrid",
      },
    },
    nutrition: { per: "100g", kcal: 239, protein_g: 27, fat_g: 14, carb_g: 0, sodium_mg: 82 },
    winePairings: ["Chardonnay", "Pinot Noir", "Côtes du Rhône", "Beaujolais"],
    recipes: ["Classic roast chicken", "Spatchcocked + grilled", "Chicken stock", "Coq au vin"],
    origin: "Descended from the red junglefowl of Southeast Asia. Gallus gallus domesticus is now the most numerous bird on Earth by roughly an order of magnitude.",
    culturalNotes: "A whole roast chicken is the French 'poulet rôti' — the benchmark dish any aspiring cook is measured against. If you can nail a roast chicken, you can cook.",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: true, kosher: "meat", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Pasture-raised > organic > 'free range' > conventional. An air-chilled, pasture-raised bird tastes like a different animal than a factory-farmed one.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "For a roast chicken dinner, get a pasture-raised bird if it's available. The deeper flavor rewards the technique you're about to put in.",
    },
    skillDev: { skills: ["heat", "seasoning", "timing"], difficulty: "moderate", proFromScratch: false, fromScratchRecipeId: null },
  },
  ribeye: {
    description: "Fatty, well-marbled steak cut from the rib primal. Considered the most flavorful beef cut.",
    flavorProfile: "Rich, beefy, buttery; intense marbling renders into the meat",
    winePairings: ["Cabernet Sauvignon", "Malbec", "Syrah/Shiraz", "Zinfandel"],
    recipes: ["Cast-iron ribeye", "Grilled with compound butter", "Tomahawk for sharing", "Steak frites"],
  },
  ny_strip: {
    description: "Tender cut from the short loin — firmer and less fatty than a ribeye, with a beefy chew.",
    flavorProfile: "Beefy, moderately marbled; clean finish",
    winePairings: ["Cabernet Sauvignon", "Bordeaux", "Tempranillo", "Chianti Classico"],
    recipes: ["Pan-seared with rosemary butter", "Grilled NY strip", "Steak au poivre", "Steakhouse dinner"],
  },
  sirloin: {
    description: "Leaner, firmer cut from the back of the cow. Great value for everyday steak dinners and the workhorse cut for fajitas, stir-fries, and kebabs.",
    flavorProfile: "Beefy, firm; less marbling than ribeye or strip — flavor comes from technique, not fat",
    prepTips: "Slice against the grain after cooking, always. Rest 5 minutes before slicing. A high-heat sear (cast-iron, ripping hot, no oil) gives a Maillard crust leaner cuts desperately need.",
    storage: {
      location: "fridge",
      shelfLifeDays: 3,
      shelfLife: { fridge: 3, freezer: 180, pantry: null },
      tips: "Room-temp 30 minutes before cooking — cold meat hits the pan and steams instead of sears. Freeze in flat packs, not chunks, for quick defrost.",
      spoilageSigns: "Grey-brown dullness through the whole cut (surface browning alone is fine), sticky film, sour smell.",
      freezable: true,
      freezeNotes: "Vacuum-seal or tight-wrap in butcher paper + foil. 6 months for steaks.",
    },
    substitutions: [
      { id: "ribeye",      tier: "pro",       note: "Fattier, more forgiving. The upgrade for special occasions." },
      { id: "flank_steak", tier: "direct",    note: "Thinner, more grain. Also great for fajitas and steak tacos." },
    ],
    pairs: ["garlic", "rosemary", "butter", "red_wine"],
    flavor: {
      primary: ["umami", "salt"],
      intensity: "moderate",
      heatChange: {
        raw: "mineral, iron, fresh-meat coolness",
        cooked: "beefy, clean; the crust does most of the talking",
        charred: "crust reads as caramelized-savory; past medium-well it dries out fast",
      },
    },
    nutrition: { per: "100g", kcal: 183, protein_g: 25, fat_g: 8.7, carb_g: 0, sodium_mg: 53 },
    winePairings: ["Malbec", "Cabernet Sauvignon", "Syrah", "Côtes du Rhône"],
    recipes: ["Beef stir-fry", "Steak sandwiches", "Steak salad", "Pan-seared with chimichurri"],
    origin: "'Sirloin' is from the French 'surlonge' — above the loin. Large working-muscle area, which is why it's leaner.",
    culturalNotes: "English legend says King James I was so impressed by a beef dinner he knighted the cut: 'Arise, Sir Loin.' Charming, apocryphal — the name actually predates him.",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: true, kosher: "meat", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Grass-fed has a firmer texture and more mineral flavor; grain-finished is more buttery. Dry-aged (if you can find it) concentrates flavor and tenderizes the muscle.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Sirloin's job is value — it's the utility steak. For everyday stir-fries and fajitas, regular supermarket cuts are fine. Splurge on ribeye instead.",
    },
    skillDev: { skills: ["heat", "knife"], difficulty: "moderate", proFromScratch: false, fromScratchRecipeId: null },
  },
  brisket: {
    description: "Tough, fatty cut from the breast. Needs long, slow cooking to break down into tender layers.",
    flavorProfile: "Deeply beefy, fatty, smoky when BBQ'd",
    winePairings: ["Zinfandel", "Malbec", "Syrah/Shiraz", "Barbera"],
    recipes: ["Texas BBQ brisket", "Jewish braised brisket", "Beef pho", "Pastrami"],
  },
  ground_beef: {
    description: "Coarsely or finely ground beef, usually 80/20 fat-to-lean for burgers and sauces.",
    flavorProfile: "Savory, rich; texture depends on grind & fat ratio",
    winePairings: ["Zinfandel", "Chianti", "Malbec", "Merlot"],
    recipes: ["Cheeseburgers", "Bolognese", "Chili", "Meatballs"],
  },
  salmon: {
    description: "Fatty, rich fish with bright pink-orange flesh. Wild or farmed, fillet or whole.",
    flavorProfile: "Rich, buttery, clean; stands up to big flavors",
    winePairings: ["Pinot Noir", "Chardonnay", "Dry Rosé", "Grüner Veltliner"],
    recipes: ["Pan-seared with crispy skin", "Cedar-plank grilled", "Poached with herbs", "Lox/gravlax"],
  },
  shrimp: {
    description: "Sweet, briny crustaceans sold by size (count per lb). Cook in 2-3 minutes — overcooking makes them rubbery.",
    flavorProfile: "Sweet, briny, snappy; takes on surrounding flavors",
    winePairings: ["Sauvignon Blanc", "Albariño", "Dry Rosé", "Grüner Veltliner"],
    recipes: ["Shrimp scampi", "Shrimp & grits", "Garlic shrimp tapas", "Gambas al ajillo"],
  },
  bacon: {
    description: "Cured and smoked pork belly, sliced thin. America's breakfast meat; Italy's pancetta is the unsmoked cousin, France's lardon the thicker chunk.",
    flavorProfile: "Salty, smoky, fatty; savory long finish with a sweet edge from any maple or brown-sugar cure",
    prepTips: "Start bacon in a COLD pan and heat slowly — the fat renders fully and the strips end up evenly crisp instead of curled. Save the grease (strain into a jar, fridge it) — it's the best cooking fat you're not using.",
    storage: {
      location: "fridge",
      shelfLifeDays: 7,
      shelfLife: { fridge: 7, freezer: 120, pantry: null },
      tips: "Once opened, roll unused strips in parchment and freeze — snap off what you need. Unopened packs keep 2+ weeks past the date if the bag is unpunctured.",
      spoilageSigns: "Green or gray tint, slimy feel, sour smell. Cured pork goes off slowly but unmistakably.",
      freezable: true,
      freezeNotes: "Roll strips in parchment, stack in a zip-top. Snap off individual strips frozen straight into a cold pan.",
    },
    substitutions: [
      { id: "pancetta",   tier: "direct", note: "Unsmoked Italian cure. Use 1:1 where the smoke isn't the point (carbonara, pasta dishes)." },
      { id: "guanciale",  tier: "pro",    note: "Cured pork jowl. The authentic upgrade for Roman pastas — richer, more intense than bacon." },
    ],
    pairs: ["eggs", "tomato", "maple_syrup", "black_pepper"],
    flavor: {
      primary: ["salt", "fat", "umami"],
      intensity: "strong",
      heatChange: {
        raw: "salty-fatty, smoky, slightly sweet if maple-cured",
        cooked: "crisps into the platonic breakfast smell — the fat carries the whole kitchen",
        charred: "burnt sugar in the cure turns bitter; aim for crisp-brown, not black",
      },
    },
    nutrition: { per: "100g", kcal: 541, protein_g: 37, fat_g: 42, carb_g: 1.4, sodium_mg: 1717 },
    winePairings: ["Pinot Noir", "Zinfandel", "Sparkling wine with brunch", "Chardonnay"],
    recipes: ["BLT", "Carbonara", "Bacon-wrapped anything", "Breakfast hash"],
    origin: "Cured pork techniques date to ancient China and Rome; 'bacon' as a word is Old French (bacö, 'back'), referring to the cured back cuts.",
    culturalNotes: "American bacon is belly cut — most of the rest of the world's 'bacon' is the leaner back cut ('rashers' in the UK, 'peameal' in Canada). The US-centric belly version is the outlier, not the norm.",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: false, kosher: "nonkosher", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Look for 'uncured' (nitrate-free) bacons that use celery powder instead of sodium nitrate if you care — they taste the same. Thick-cut holds up in pasta; thin-cut crisps faster for BLTs.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Butcher-shop dry-cured bacon is distinctly better than supermarket wet-cured — less water weight, cleaner smoke, crisps into shatter-crisp instead of rubbery chew.",
    },
    skillDev: { skills: ["heat", "timing"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },
  guanciale: {
    description: "Cured pork jowl — fattier, richer, and more intensely flavored than pancetta or bacon. The authentic fat for Roman pasta: carbonara, gricia, amatriciana.",
    flavorProfile: "Deeply porky, rich, faintly funky; pepper rim adds warmth. Renders to a puddle of amber fat that perfumes the pasta.",
    prepTips: "Start in a cold pan on medium-low so the fat renders slowly and evenly. Pull the crisped pieces out when the outside is browned and the centers are still tender — over-rendering turns them into jerky.",
    storage: {
      location: "fridge",
      shelfLifeDays: 90,
      shelfLife: { fridge: 90, freezer: 365, pantry: null },
      tips: "Wrapped in parchment, sealed in a zip-top — it keeps for months. The cure is its preservation. Slice as needed rather than pre-dicing.",
      spoilageSigns: "Gray mold through the flesh (surface white bloom is fine — just trim). Rancid-nut smell from the fat = past its prime.",
      freezable: true,
      freezeNotes: "Cut into recipe-size chunks before freezing. Wrap tight. 1 year easy.",
    },
    substitutions: [
      { id: "pancetta", tier: "direct",    note: "Most common swap. Leaner, less funky — acceptable but noticeably different in a gricia." },
      { id: "bacon",    tier: "emergency", note: "Last resort. Smoke changes the dish's character. Skip the smoke by blanching 60 seconds first." },
    ],
    pairs: ["spaghetti", "pecorino", "black_pepper", "eggs"],
    flavor: {
      primary: ["fat", "salt", "umami"],
      intensity: "strong",
      heatChange: {
        raw: "salty, fatty, intensely porky",
        cooked: "amber fat renders into a glossy sauce base; crisped edges turn caramel-nutty",
        charred: "black spots go bitter fast; stop at deep amber",
      },
    },
    nutrition: { per: "100g", kcal: 660, protein_g: 7, fat_g: 69, carb_g: 0, sodium_mg: 1350 },
    winePairings: ["Frascati", "Cesanese", "Lambrusco", "Chianti"],
    recipes: ["Carbonara", "Pasta alla gricia", "Amatriciana", "Tossed through bitter greens"],
    origin: "Lazio and Umbria, Italy. 'Guancia' means cheek — cured with salt, black pepper, and sometimes wine, then aged 2–3 months.",
    culturalNotes: "Purists insist Roman pastas must use guanciale — not pancetta, not bacon. The fat content and texture are genuinely different, and you can taste it.",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: false, kosher: "nonkosher", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Italian imports are best. A good specialty grocer or Italian deli will have it; online specialty meat shops ship it cured. Domestic producers are catching up fast.",
    market: {
      priceTier: "premium",
      availability: "specialty",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Guanciale IS the dish in Roman pastas. A mass-market bacon swap tastes like a different recipe — the jowl's fat content is part of the emulsification.",
    },
    skillDev: { skills: ["heat", "timing"], difficulty: "moderate", proFromScratch: true, fromScratchRecipeId: null },
  },
  ham: {
    description: "Cured pork leg. Ranges from deli slices to bone-in whole hams to dry-cured Iberian specialties. Versatile cooked-meat staple.",
    flavorProfile: "Salty, savory, lightly sweet; smoke varies by style",
    prepTips: "Deli slices: keep flat-packed in the fridge, use within a week. Whole hams: glaze with brown sugar + mustard + a splash of bourbon in the last 30 min of baking. Slice bone-in hams thinly against the grain.",
    storage: {
      location: "fridge",
      shelfLifeDays: 7,
      shelfLife: { fridge: 7, freezer: 60, pantry: null },
      tips: "Once opened, deli ham loses quality fast — plan to use within 5 days. Bone-in ham lasts 7–10 days if well-wrapped.",
      spoilageSigns: "Slimy, tacky surface; sour or ammonia-like smell; any green or gray tint.",
      freezable: true,
      freezeNotes: "Deli slices freeze poorly — texture suffers. Whole or bone-in ham freezes well; 2 months.",
    },
    substitutions: [
      { id: "prosciutto", tier: "pro",       note: "Dry-cured, eaten raw. Upgrade for cold applications; not a sub for sandwiches or gratins." },
      { id: "turkey",     tier: "dietary",   note: "Leaner deli alternative. Mild; needs more seasoning in cooked applications." },
    ],
    pairs: ["gruyere", "mustard", "butter", "bread"],
    flavor: {
      primary: ["salt", "umami", "sweet"],
      intensity: "moderate",
      heatChange: {
        raw: "salty-sweet, smoky, moist — eats fine straight from the package",
        cooked: "glazed edges caramelize; interior stays juicy if you don't overbake",
        charred: "burnt sugar in the glaze goes bitter past deep amber",
      },
    },
    nutrition: { per: "100g", kcal: 145, protein_g: 21, fat_g: 6, carb_g: 2, sodium_mg: 1203 },
    winePairings: ["Riesling", "Gewürztraminer", "Pinot Noir", "Rosé"],
    recipes: ["Croque monsieur", "Ham & cheese omelette", "Holiday baked ham", "Split pea soup"],
    origin: "Cured pork legs appear across world cuisines — China (jinhua ham), Spain (jamón ibérico), Italy (prosciutto), Germany (Schwarzwälder Schinken). Every pork-eating culture has one.",
    culturalNotes: "Easter ham is a Christian tradition rooted in spring pig slaughters — hams cured in fall were ready to eat by spring.",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: false, kosher: "nonkosher", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "For a sandwich: a specialty-shop cured ham will taste noticeably better than pre-packaged. For holidays: bone-in is worth the extra fuss — you get stock from the bone.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Deli-counter ham is distinctly better than packaged. For a holiday centerpiece, a heritage-breed bone-in ham rewards the roasting time.",
    },
    skillDev: { skills: ["heat", "timing"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },
  prosciutto: {
    description: "Italian dry-cured ham, aged 12-24 months. Sliced paper-thin, eaten without cooking.",
    flavorProfile: "Salty, sweet, buttery; melts on the tongue",
    winePairings: ["Prosecco", "Franciacorta", "Lambrusco", "Chianti"],
    recipes: ["Prosciutto + melon", "Wrapped around asparagus", "On pizza after baking", "Antipasto platters"],
  },

  // ── produce: alliums + herbs ──────────────────────────────────────
  garlic: {
    description: "Bulb of the allium family, broken into cloves. The foundational aromatic in most of the world's cuisines — on every inhabited continent, some version of garlic-in-oil starts dinner.",
    flavorProfile: "Sharp and pungent raw; sweet, mellow, and nutty when cooked; deeply caramelized and jammy when roasted whole",
    prepTips: "Smash cloves with the flat of a knife to loosen the papery skin. Chop just before using — pre-chopped garlic oxidizes within an hour. Never burn it; brown garlic is bitter garlic.",
    storage: {
      location: "pantry",
      shelfLifeDays: 90,
      shelfLife: { fridge: null, freezer: 180, pantry: 90 },
      tips: "Cool, dry, airy spot — not the fridge (goes moldy). If you see a green sprout inside, it's safe but bitter; halve and remove the germ.",
      spoilageSigns: "Soft spots, brown bruising, visible mold on the paper, or a sprouting stem that took over the clove.",
      freezable: true,
      freezeNotes: "Peel, mince, pack into ice-cube trays with olive oil. Pop out a cube per sauté.",
      prepYield: { whole: "1 medium head", yields: "~10 cloves / 2–3 tbsp minced" },
    },
    substitutions: [
      { id: "shallot", tier: "direct",  note: "Sweeter, subtler. 1 clove ≈ ½ small shallot, minced." },
    ],
    pairs: ["olive_oil", "butter", "parsley", "lemon"],
    flavor: {
      primary: ["umami", "heat"],
      intensity: "strong",
      heatChange: {
        raw: "pungent, mouth-tingling, almost sulfurous",
        cooked: "sweet, nutty, mellow — the caramelized state you actually want",
        charred: "bitter and acrid in seconds — the #1 reason home sautés fail",
      },
    },
    nutrition: { per: "100g", kcal: 149, protein_g: 6.4, fat_g: 0.5, carb_g: 33, fiber_g: 2.1, sodium_mg: 17 },
    origin: "Central Asia — domesticated over 5,000 years ago. Fed to the pyramid builders as a stimulant and antibiotic.",
    culturalNotes: "Every Mediterranean grandmother has an opinion about pre-peeled garlic (generally: don't). The difference between fresh and jarred is audible in a quiet kitchen.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: true },
    seasonality: { peakMonthsN: [7, 8, 9], peakMonthsS: [1, 2, 3], yearRound: true },
    sourcing: "Hardneck varieties (Rocambole, Porcelain) have deeper flavor and easier-to-peel cloves. California-grown softneck is the everyday workhorse.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Chinese-import garlic is cheaper but milder and faster to sprout. Locally grown hardneck from a farmer's market tastes like a different food.",
    },
    skillDev: { skills: ["knife", "heat", "seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  yellow_onion: {
    description: "The default onion. Thick, papery skin over a sharp, pungent flesh that mellows and sweetens when cooked. The base of roughly every soup, sauté, and stew in Western cooking.",
    flavorProfile: "Sharp and pungent raw; sweet and savory when cooked; deeply caramelized when slow-cooked 30+ minutes in butter",
    prepTips: "Chill 15 minutes before chopping to reduce the tear-inducing vapor. For uniform dice: halve through the root, peel, make horizontal cuts toward (not through) the root, then vertical cuts, then slice across.",
    storage: {
      location: "pantry",
      shelfLifeDays: 60,
      shelfLife: { fridge: null, freezer: 240, pantry: 60 },
      tips: "Cool, dry, DARK spot in a mesh bag or open basket. Never next to potatoes — both emit ethylene gas that ripens the other.",
      spoilageSigns: "Soft spots, dark liquid at the root end, green mold, or that distinctive funky onion-rot smell.",
      freezable: true,
      freezeNotes: "Dice and freeze raw on a sheet tray, then bag. Better yet: pre-caramelize a batch, freeze in tablespoon dollops.",
      prepYield: { whole: "1 medium onion (~170g)", yields: "~1 cup diced / ~1½ cups sliced" },
    },
    substitutions: [
      { id: "shallot",     tier: "pro",    note: "Subtler, sweeter. The upgrade for finer sauces and vinaigrettes." },
      { id: "pearl_onion", tier: "direct", note: "Whole-form variety for braises and stews." },
    ],
    pairs: ["garlic", "butter", "olive_oil", "carrot"],
    flavor: {
      primary: ["umami", "sweet", "heat"],
      intensity: "moderate",
      heatChange: {
        raw: "sharp, watery-pungent, tear-inducing",
        cooked: "sweet and savory; surrenders sugars into anything fatty",
        charred: "deeply caramelized at 45 min+; blackened past that = scrape and start over",
      },
    },
    nutrition: { per: "100g", kcal: 40, protein_g: 1.1, fat_g: 0.1, carb_g: 9.3, fiber_g: 1.7, sodium_mg: 4 },
    origin: "Central Asia, cultivated ~7,000 years ago. 'The onion is the poor man's food' — Egyptian proverb.",
    culturalNotes: "Deeply caramelized onions (45 min+ in butter, constant stirring) are the secret foundation of French onion soup, coq au vin, and half the soups that ever tasted like 'mom made this.'",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: true },
    seasonality: { peakMonthsN: [8, 9, 10], peakMonthsS: [2, 3, 4], yearRound: true },
    sourcing: "Look for firm bulbs with dry, papery skin — soft spots mean rot underneath. Storage onions (yellow) are what you want; sweet onions (Vidalia, Walla Walla) are specialty.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Basic yellow onion is a commodity. The exception: Vidalia / Walla Walla sweet onions for raw salads or caramelized-onion dips where the sweetness matters.",
    },
    skillDev: { skills: ["knife", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  shallot: {
    description: "Small, elongated allium — milder, sweeter, and more refined than an onion. The go-to for vinaigrettes, pan sauces, and anywhere you want onion character without the bite.",
    flavorProfile: "Delicate, sweet, faintly garlicky; no bitter aftertaste raw",
    prepTips: "Mince fine for vinaigrettes — bigger pieces stay crunchy in the mouth. For pan sauces, sweat in butter before adding wine so they surrender their sugars.",
    storage: {
      location: "pantry",
      shelfLifeDays: 45,
      shelfLife: { fridge: null, freezer: 180, pantry: 45 },
      tips: "Same as yellow onions — cool, dark, airy. Once cut, refrigerate and use within 2 days; the delicate flavor fades fast.",
      spoilageSigns: "Soft spots, green shoots, sour smell. Tight, papery bulbs last; the moment they soften they go fast.",
      freezable: true,
      freezeNotes: "Mince, freeze in oil cubes like garlic. Whole bulbs don't freeze well.",
      prepYield: { whole: "1 medium shallot", yields: "~2 tbsp minced" },
    },
    substitutions: [
      { id: "yellow_onion", tier: "emergency", note: "Use half the amount; the bite is stronger and less refined." },
      { id: "garlic",       tier: "emergency", note: "Same amount for pan sauces; accept that the result will be more assertive." },
    ],
    pairs: ["butter", "white_wine", "dijon", "parsley"],
    flavor: {
      primary: ["sweet", "umami"],
      intensity: "moderate",
      heatChange: {
        raw: "delicate, sweet, barely-there garlic hint",
        cooked: "melts into pan sauces — disappears visually but rounds out everything",
        charred: "bitter fast, just like garlic — keep the heat moderate",
      },
    },
    nutrition: { per: "100g", kcal: 72, protein_g: 2.5, fat_g: 0.1, carb_g: 17, fiber_g: 3.2, sodium_mg: 12 },
    origin: "Named for the ancient city of Ashkelon (Ascalon) in modern Israel, where crusaders first encountered them.",
    culturalNotes: "French cooks consider shallots the 'onion for sauces' — onion is too bold, garlic too assertive; shallot is the neutral. Eschalot / échalote in French.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: true },
    seasonality: { peakMonthsN: [7, 8, 9], peakMonthsS: [1, 2, 3], yearRound: true },
    sourcing: "French gray shallots (échalote grise) have deeper flavor but are hard to find. Jersey (banana) shallots are the usual supermarket option — smaller, milder, still good.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: false,
      qualityNote: "Supermarket Jersey shallots are fine for 95% of uses. Splurge on French grey shallots only when the shallot is the entire character of the dish (mignonette, classical mother sauces).",
    },
    skillDev: { skills: ["knife", "sauce"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  pearl_onion: {
    description: "Miniature onions ~½-inch across. Sold in mesh bags (fresh) or jars (pickled). The classic garnish for braises, casseroles, and a proper gin martini.",
    flavorProfile: "Mild, sweet, delicate; less aggressive than a full onion",
    prepTips: "To peel fresh ones: blanch 60 seconds, shock in ice water, squeeze the root end — the onion slips out of its skin. Doing 30 of them individually is the only thing worse than crying through yellow-onion dice.",
    storage: {
      location: "pantry",
      shelfLifeDays: 30,
      shelfLife: { fridge: 14, freezer: 365, pantry: 30 },
      tips: "Fresh: cool dry mesh bag, same as yellow onions. Frozen pearl onions (pre-peeled) are a legitimate shortcut for braises — nobody will taste the difference.",
      spoilageSigns: "Soft skin-slip, green shoots, sour bog-smell at the mesh-bag bottom.",
      freezable: true,
      freezeNotes: "Frozen pre-peeled pearl onions from the supermarket are the move for braises. 1 year easy.",
    },
    substitutions: [
      { id: "yellow_onion", tier: "emergency", note: "Quartered chunks work for the flavor; you lose the whole-onion visual moment." },
    ],
    pairs: ["bacon", "red_wine", "thyme", "mushroom"],
    flavor: {
      primary: ["sweet", "umami"],
      intensity: "mild",
      heatChange: {
        raw: "crisp, sweet, mild",
        cooked: "picks up whatever wine and fat you braise them in — sweet and glossy",
        charred: "bitter if dry-roasted too long; pearls want braising liquid or a buttery glaze",
      },
    },
    nutrition: { per: "100g", kcal: 40, protein_g: 1.1, fat_g: 0.1, carb_g: 9.3, fiber_g: 1.7, sodium_mg: 4 },
    origin: "Bred from regular onions for size and uniformity; Dutch and French cooks made them famous as garnish onions.",
    culturalNotes: "The bourguignon question: traditional recipe calls for ~20 pearl onions per serving, each individually peeled. Frozen pre-peeled is the modern grace.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: true },
    seasonality: { peakMonthsN: [8, 9, 10], peakMonthsS: [2, 3, 4], yearRound: true },
    sourcing: "Frozen pearl onions are the move for braises — already peeled, consistent sizing, half the price of fresh. Fresh for spring peas + new pearl onions is worth the peeling penance.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: false,
      qualityNote: "Frozen pre-peeled is the practical answer 90% of the time. Fresh is for when the peeling is part of the ritual.",
    },
    skillDev: { skills: ["knife", "timing"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  basil: {
    description: "Sweet Italian herb with large, tender leaves. The summer-tomato herb, the pesto herb, the herb you add last so it doesn't blacken.",
    flavorProfile: "Sweet, peppery, aniseed; clove-ish on the back end; turns soapy and thin if heated too long",
    prepTips: "Tear, don't chop — oxidation blackens cut edges. Add at the very end or scatter raw on top. For pesto, blanch 10 seconds + ice-shock to lock the color in.",
    storage: {
      location: "pantry",
      shelfLifeDays: 7,
      shelfLife: { fridge: null, freezer: 90, pantry: 7 },
      tips: "Never refrigerate — basil blackens in the cold. Stand stems in a glass of water on the counter like cut flowers; covers loose with a bag over the leaves.",
      spoilageSigns: "Black spots on leaves, slimy stems, sour smell — the whole bunch blackens within hours once it starts.",
      freezable: true,
      freezeNotes: "Purée with olive oil into ice-cube trays; 3 months. Dried basil is a different ingredient; not a swap.",
    },
    substitutions: [
      { id: "parsley",    tier: "emergency", note: "Different flavor entirely but works in a pinch as a soft green garnish." },
      { id: "thai_basil", tier: "dietary",   note: "For Asian dishes — stronger, spicier, anise-forward. Not a sub for pesto." },
    ],
    pairs: ["tomato", "mozzarella", "olive_oil", "garlic"],
    flavor: {
      primary: ["sweet", "heat", "umami"],
      intensity: "moderate",
      heatChange: {
        raw: "bright, sweet-peppery, aromatic oils popping",
        cooked: "flavor thins fast above 60°C — always add last",
        charred: "blackens in seconds; the aroma escapes before char sets in",
      },
    },
    nutrition: { per: "100g", kcal: 23, protein_g: 3.2, fat_g: 0.6, carb_g: 2.7, fiber_g: 1.6, sodium_mg: 4 },
    origin: "India and Southeast Asia originally; the Italian Genovese cultivar is what pasta built its reputation on.",
    culturalNotes: "Pesto alla Genovese is protected DOP — real basilico Genovese grown near the coast has smaller leaves and less mint character than what most of us grow.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [6, 7, 8, 9], peakMonthsS: [12, 1, 2, 3], yearRound: false, preservedAvailable: true },
    sourcing: "Greenhouse basil year-round is fine; summer's farmers-market basil is incomparably better. A pot on the windowsill is almost always worth it.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "For pesto, fresh basil is non-negotiable. For tomato sauce, you can get away with the packaged hydroponic stuff in January.",
    },
    skillDev: { skills: ["seasoning", "knife"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  parsley: {
    description: "Flat-leaf (Italian) parsley is the workhorse herb in Western kitchens — brighter than you remember, and not the garnish you scraped off your diner plate.",
    flavorProfile: "Clean, grassy, peppery; refreshing bitter edge that cuts through fat",
    prepTips: "Chop LESS than you think — over-chopped parsley is mushy and bruised. A quick mince with a sharp knife is better than a rough chop. Use stems in stocks and dressings; they're more flavorful than the leaves.",
    storage: {
      location: "fridge",
      shelfLifeDays: 10,
      shelfLife: { fridge: 10, freezer: 180, pantry: null },
      tips: "Wrap stems in a damp paper towel inside a zip-top bag, or stand in a glass of water with the leaves covered loosely. Refreshes wilted parsley in 30 minutes.",
      spoilageSigns: "Yellow leaves, slimy stems, sour smell at the base of the bunch.",
      freezable: true,
      freezeNotes: "Chop, spread on a sheet tray, freeze solid, transfer to a zip-top. 6 months for cooking; texture doesn't work for raw garnish.",
    },
    substitutions: [
      { id: "cilantro", tier: "dietary",   note: "Different profile (citrusy, divisive) but similar green-brightener role." },
      { id: "chervil",  tier: "pro",       note: "Finer, aniseed-tinged. Use in French preparations where parsley was already the subtle choice." },
    ],
    pairs: ["garlic", "lemon", "olive_oil", "butter"],
    flavor: {
      primary: ["bitter", "umami"],
      intensity: "mild",
      heatChange: {
        raw: "grassy, peppery, refreshing",
        cooked: "loses the bright edge fast; use stems in slow cooking, leaves at the end",
        charred: "burns to acrid green char — add off-heat only",
      },
    },
    nutrition: { per: "100g", kcal: 36, protein_g: 3, fat_g: 0.8, carb_g: 6.3, fiber_g: 3.3, sodium_mg: 56 },
    origin: "Mediterranean. The ancient Greeks made parsley wreaths for athletic victors; 'requiring parsley' was a Greek euphemism for the gravely ill (parsley grew on graves).",
    culturalNotes: "Curly-leaf parsley is what ended up on every steakhouse plate in the 1970s. Flat-leaf (Italian) is for actual cooking — stronger flavor, easier to chop.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [5, 6, 7, 8, 9], peakMonthsS: [11, 12, 1, 2, 3], yearRound: true },
    sourcing: "Flat-leaf for cooking, always. Curly is fine for decoration. Bunches should be perky, not slimy or yellowing at the base.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Parsley is parsley. Freshness matters way more than sourcing.",
    },
    skillDev: { skills: ["knife", "seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  // ── produce: fruits + vegetables ──────────────────────────────────
  tomato: {
    description: "Fresh tomato — the one ingredient that deserves actual seasonal respect. August's vine-ripened tomato and January's grocery-store tomato are barely the same food.",
    flavorProfile: "Sweet, acidic, umami-rich; depth of flavor tracks directly with ripeness",
    prepTips: "Never refrigerate a ripe tomato — cold destroys the aromatic compounds and mealies the texture. Salt tomatoes 15 minutes before serving raw; it draws out juice and concentrates flavor.",
    storage: {
      location: "pantry",
      shelfLifeDays: 7,
      shelfLife: { fridge: 5, freezer: 240, pantry: 7 },
      tips: "Counter, stem-end DOWN, out of direct sun. If overripe and you can't eat them in time: quarter and freeze for future sauce.",
      spoilageSigns: "Soft dark bruising, shriveled skin, mold at the stem scar, or a sour-fermented smell.",
      freezable: true,
      freezeNotes: "Whole tomatoes freeze beautifully for future sauce — the skins slip off like magic after a 2-minute thaw under warm water.",
      prepYield: { whole: "1 medium tomato (~150g)", yields: "~1 cup diced" },
    },
    substitutions: [
      { id: "tomato_paste",    tier: "emergency", note: "For cooked dishes; concentrated flavor but no water. Use 1 tbsp paste per fresh tomato." },
      { id: "sundried_tomato", tier: "pro",       note: "For flavor, not volume. A tablespoon adds what a cup of fresh would — upgrade for intensity." },
      { id: "canned_tomatoes", tier: "direct",    note: "Winter winner. Canned San Marzanos beat out-of-season fresh tomatoes for cooking every time." },
    ],
    pairs: ["basil", "mozzarella", "olive_oil", "balsamic"],
    flavor: {
      primary: ["umami", "sour", "sweet"],
      intensity: "moderate",
      heatChange: {
        raw: "bright, acidic, sweet — summer-ripe versions taste like nothing else",
        cooked: "concentrates into deep savory-sweetness as water cooks off",
        charred: "blistered skins add smoky-sweet depth — the base of good salsa asada",
      },
    },
    nutrition: { per: "100g", kcal: 18, protein_g: 0.9, fat_g: 0.2, carb_g: 3.9, fiber_g: 1.2, sodium_mg: 5 },
    origin: "Western South America (Andes) — domesticated in Mexico by the Aztecs. Brought to Europe in the 1500s; long thought poisonous because the leaves and stems are.",
    culturalNotes: "Tomatoes in Italian cooking only date to the 1800s. Caprese, marinara, pasta al pomodoro — all 'traditional' dishes younger than the USA.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: true, allium: false },
    seasonality: { peakMonthsN: [7, 8, 9], peakMonthsS: [1, 2, 3], yearRound: false, preservedAvailable: true },
    sourcing: "Heirloom varieties (Brandywine, Cherokee Purple, Green Zebra) taste like tomato; uniform red supermarket tomatoes are bred for shipping, not flavor. Even in winter, canned San Marzanos beat fresh supermarket tomatoes for cooking.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Farmer's-market summer tomatoes are in a different universe from supermarket tomatoes. If the tomato is the dish (caprese, BLT), the sourcing IS the recipe.",
    },
    skillDev: { skills: ["knife", "seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  bell_pepper: {
    description: "Sweet, crunchy, hollow-fleshed peppers. Red, yellow, and orange are ripe; green is an unripe red. Raw or cooked, they're mild and family-friendly.",
    flavorProfile: "Sweet, grassy raw (green more so); smoky and deep when charred",
    prepTips: "Char directly over a gas flame or under a broiler until black, then steam in a bowl covered with plastic wrap for 10 minutes — skin slips off by hand. Cut lengthwise for fajitas, rings for sandwiches.",
    storage: {
      location: "fridge",
      shelfLifeDays: 14,
      shelfLife: { fridge: 14, freezer: 240, pantry: null },
      tips: "Crisper drawer, unwashed. Once cut, wrap tightly — exposed flesh dries fast. Freezes well for cooking: dice, freeze on a sheet tray, transfer to a bag.",
      spoilageSigns: "Soft wrinkled skin, dark spots, white mold in the core around the seeds.",
      freezable: true,
      freezeNotes: "Dice raw, sheet-freeze, bag. 8 months for cooking use — texture kills raw applications but fine for sauté, stew, stir-fry.",
      prepYield: { whole: "1 medium pepper", yields: "~1 cup sliced / ~¾ cup diced" },
    },
    substitutions: [
      { id: "poblano", tier: "pro",       note: "Mild heat + more character. Upgrade for fajitas and stuffed peppers." },
      { id: "pimento", tier: "emergency", note: "Sweeter, smaller, usually jarred. Substitute in sauces and dressings where texture doesn't matter." },
    ],
    pairs: ["yellow_onion", "olive_oil", "garlic", "chicken_breast"],
    flavor: {
      primary: ["sweet", "bitter"],
      intensity: "mild",
      heatChange: {
        raw: "crisp, grassy, sweet — green peppers carry more bitter edge than red",
        cooked: "softens, sweetens, loses some of the watery freshness",
        charred: "blistered skin peeled off reveals silken smoky-sweet flesh — the upgrade play",
      },
    },
    nutrition: { per: "100g", kcal: 31, protein_g: 1, fat_g: 0.3, carb_g: 6, fiber_g: 2.1, sodium_mg: 4 },
    origin: "Central and South America — domesticated over 9,000 years ago. The 'bell' shape was developed in the 1920s in Hungary from sweeter paprika peppers.",
    culturalNotes: "Red, yellow, and orange peppers are 2–3× the price of green because they're on the vine longer. Green ones are picked early and sold to keep the crop profitable while the rest ripen.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: true, allium: false },
    seasonality: { peakMonthsN: [7, 8, 9, 10], peakMonthsS: [1, 2, 3, 4], yearRound: true },
    sourcing: "Heavy for their size = more flesh. Wrinkled or soft spots = past prime. For fajitas, mix colors for visual interest; flavor differences are subtle.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Commodity vegetable. Green is half the price of colored — use green for cooked applications, colored for raw.",
    },
    skillDev: { skills: ["knife", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  carrot: {
    description: "Root vegetable — sweet, crunchy raw, transformed to deep caramelized sweetness when roasted or braised. A mirepoix staple along with onion and celery.",
    flavorProfile: "Sweet, earthy; intensifies when cooked. Raw carrots are crisp and grassy; roasted carrots are candy.",
    prepTips: "Peel only if the skin's tough or dirty — young carrots don't need it. For stocks and braises, leave chunky; for raw salads, match the cut (ribbons, julienne, grated) to the dressing.",
    storage: {
      location: "fridge",
      shelfLifeDays: 30,
      shelfLife: { fridge: 30, freezer: 300, pantry: null },
      tips: "Remove any leafy tops — they pull moisture from the root. Store in a zip-top bag in the crisper; they keep for weeks. Limp carrots can be revived 30 min in ice water.",
      spoilageSigns: "Slimy film, white mold, black soft spots, or dried-out cracked skin that won't revive in water.",
      freezable: true,
      freezeNotes: "Blanch 3 minutes, shock in ice water, bag. 10 months. Raw-frozen carrots get texturally weird but work for purées.",
      prepYield: { whole: "1 medium carrot (~60g)", yields: "~½ cup diced / ~⅓ cup grated" },
    },
    substitutions: [
      { id: "parsnip", tier: "direct", note: "Similar cooking behavior; sweeter and more aromatic when roasted." },
    ],
    pairs: ["yellow_onion", "butter", "thyme", "honey"],
    flavor: {
      primary: ["sweet", "bitter"],
      intensity: "mild",
      heatChange: {
        raw: "crisp, grassy, clean sweet",
        cooked: "softens, caramelizes, and turns genuinely candy-like at the edges",
        charred: "deep roasted sweetness with bitter smoky edge — roasting at 425°F is the play",
      },
    },
    nutrition: { per: "100g", kcal: 41, protein_g: 0.9, fat_g: 0.2, carb_g: 10, fiber_g: 2.8, sodium_mg: 69 },
    origin: "Central Asia (Afghanistan) — originally purple. The orange variety was developed in 17th-century Netherlands, possibly as a tribute to William of Orange.",
    culturalNotes: "Rainbow / heirloom carrots (purple, yellow, white, red) are closer to the original species than the uniform orange we grew up with. Purple ones stain everything they touch bright pink.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [9, 10, 11], peakMonthsS: [3, 4, 5], yearRound: true },
    sourcing: "Loose carrots with greens attached are fresher than bagged. 'Baby' carrots are usually whittled-down large carrots; real baby carrots from a farmer's market are another category.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Commodity vegetable. A fresh farmer's-market bunch tastes better raw but disappears into mirepoix. Bagged baby carrots are a huge step down in flavor.",
    },
    skillDev: { skills: ["knife", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  mushroom: {
    description: "The catch-all category — button, cremini, portobello, shiitake, oyster, chanterelle, morel. Meaty, umami-rich, the vegetarian kitchen's secret weapon.",
    flavorProfile: "Earthy, savory, umami; dried mushrooms concentrate all three to a different dimension",
    prepTips: "Don't wash — wipe with a damp paper towel. Mushrooms absorb water and steam instead of browning. For crispy sear, crowd-free hot pan, dry mushrooms, high heat, salt AFTER they've browned.",
    storage: {
      location: "fridge",
      shelfLifeDays: 7,
      shelfLife: { fridge: 7, freezer: 300, pantry: null },
      tips: "In their original packaging or a paper bag (not plastic — traps moisture, speeds rot). Wrinkled caps mean they're past prime but still fine for soups and stocks.",
      spoilageSigns: "Slimy film on the cap, black spots, sour or ammonia smell. Wrinkled-but-firm is still usable for soup.",
      freezable: true,
      freezeNotes: "Sauté first, then freeze. Raw-frozen mushrooms weep into mush on thaw — pre-cooked ones retain texture better.",
      prepYield: { whole: "8 oz / 227g", yields: "~3 cups sliced raw / ~1 cup cooked" },
    },
    substitutions: [
      { id: "dried_porcini", tier: "pro", note: "Rehydrate in warm water; the soaking liquid becomes an umami bomb for sauces and risottos. Use 1 oz dried per 8 oz fresh." },
    ],
    pairs: ["butter", "garlic", "thyme", "red_wine"],
    flavor: {
      primary: ["umami", "bitter"],
      intensity: "moderate",
      heatChange: {
        raw: "mild, earthy, watery — not great eating on its own",
        cooked: "deeply savory; Maillard browning is the whole reason to cook them hot and dry",
        charred: "meaty-smoky — grilled portobellos are a steak substitute for good reason",
      },
    },
    nutrition: { per: "100g", kcal: 22, protein_g: 3.1, fat_g: 0.3, carb_g: 3.3, fiber_g: 1, sodium_mg: 5 },
    reductionRatio: { sauteFactor: 0.3, note: "Loses ~70% volume on sauté — water-rich. 3 cups raw collapses to ~1 cup cooked." },
    origin: "Mushroom cultivation dates to Imperial China ~1,500 years ago. Agaricus bisporus (white button, cremini, portobello — all same species, different maturities) was first cultivated in 17th-century France.",
    culturalNotes: "Cremini and portobello are just older button mushrooms. Same species, picked at different ages. The 'portobello' name was invented in the 1980s to sell what producers had been throwing away.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [9, 10, 11], peakMonthsS: [3, 4, 5], yearRound: true },
    sourcing: "Button and cremini year-round; specialty (shiitake, oyster, trumpet) at better groceries; wild (morel, chanterelle) at farmer's markets in season. Dried porcini is worth keeping in the pantry permanently.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Cremini taste dramatically better than white button — same species, older, more flavorful. Specialty mushrooms (shiitake, oyster) multiply the effect. Wild seasonal ones change the game.",
    },
    skillDev: { skills: ["heat", "knife"], difficulty: "moderate", proFromScratch: false, fromScratchRecipeId: null },
  },

  spinach: {
    description: "Soft-leaved dark green — wilts in seconds, filling when raw, vanishes into a cup of cooked volume. The kitchen's most reliable green.",
    flavorProfile: "Mild, slightly mineral, faintly sweet; tangy edge with long cooking from oxalic acid",
    prepTips: "Buy in bulk; cooks down to ~10% of raw volume. Wash even 'pre-washed' bags — spinach holds grit. Sauté in olive oil with garlic and a pinch of salt for 90 seconds, pull when just wilted — longer goes slimy.",
    storage: {
      location: "fridge",
      shelfLifeDays: 7,
      shelfLife: { fridge: 7, freezer: 300, pantry: null },
      tips: "Crisper drawer in its original bag or a zip-top with a paper towel to absorb moisture. Wilted spinach can be cooked — slimy spinach cannot.",
      spoilageSigns: "Dark slimy patches, rotten-swamp smell, yellowing across the bunch. Goes from perfect to slime in 24 hours.",
      freezable: true,
      freezeNotes: "Blanch 30 sec, shock, squeeze bone-dry, portion, freeze. 10 months. Frozen commercial spinach is legitimate for soups/fillings.",
      prepYield: { whole: "10 oz / 284g raw", yields: "~1 cup cooked (shrinks to ~10%)" },
    },
    substitutions: [
      { id: "kale",        tier: "direct", note: "Hardier; needs longer cooking or a massage with oil + salt to soften raw." },
      { id: "swiss_chard", tier: "direct", note: "Bigger leaves, earthier flavor. Stems cook separately — dice and sauté with aromatics first." },
    ],
    pairs: ["garlic", "olive_oil", "lemon", "parmesan"],
    flavor: {
      primary: ["bitter", "umami"],
      intensity: "mild",
      heatChange: {
        raw: "tender, mildly mineral, barely-sweet",
        cooked: "softens and concentrates — the oxalic-acid tang rises the longer you cook it",
        charred: "wilts too fast to char meaningfully — not a high-heat green",
      },
    },
    nutrition: { per: "100g", kcal: 23, protein_g: 2.9, fat_g: 0.4, carb_g: 3.6, fiber_g: 2.2, sodium_mg: 79 },
    origin: "Persia (modern Iran) — came to Europe via Moorish Spain in the 11th century. 'Spanish vegetable' in many European languages for centuries.",
    culturalNotes: "Popeye's strength came from spinach's iron content, which turns out to be overstated — an early 1870s study mis-placed a decimal point by 10×, and the error wasn't caught until the 1930s.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [3, 4, 5, 10, 11], peakMonthsS: [4, 5, 9, 10, 11], yearRound: true, preservedAvailable: true },
    sourcing: "Baby spinach for salads, mature (bunched) spinach for cooking. Bunched is cheaper and more flavorful but needs washing. Frozen spinach is legitimate for soups and fillings — squeeze out the water.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Bagged baby spinach is fine. Bunched mature spinach from a farmer's market has deeper flavor but needs a real wash.",
    },
    skillDev: { skills: ["knife", "heat", "timing"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  broccoli: {
    description: "Tight green florets on edible stalks. Steamed, roasted, stir-fried, or shaved raw — a year-round workhorse with a mild brassica bite.",
    flavorProfile: "Grassy, faintly sweet when fresh; sulfurous when overcooked; deeply nutty and caramelized when roasted hard",
    prepTips: "For roasting: cut florets, dry thoroughly, toss in oil + salt, single layer on a 425°F sheet pan, do not stir for 18–22 min. Crispy edges are the whole point. For steaming: bright-green at 4 minutes, army-green at 8 — pull early.",
    storage: {
      location: "fridge",
      shelfLifeDays: 8,
      shelfLife: { fridge: 8, freezer: 300, pantry: null },
      tips: "Crisper drawer in a loosely-closed plastic bag. Do not wash until use — moisture accelerates yellowing on the florets.",
      spoilageSigns: "Yellowing florets (florets yellow before the stalk goes bad — still safe but flavor's tipping), slimy patches on the stem cut, sulfur-bomb smell.",
      freezable: true,
      freezeNotes: "Blanch florets 2 min, shock in ice water, dry, freeze in a single layer then bag. 10 months. Frozen broccoli is legitimate for stir-fries and soups; loses crunch for raw use.",
      prepYield: { whole: "1 head (~450g with stalk)", yields: "~4 cups florets / ~3 cups florets + 1 cup peeled stalk dice" },
    },
    substitutions: [
      { id: "cauliflower",      tier: "direct", note: "Same family, similar cook times. Milder flavor; takes browning even better than broccoli." },
      { id: "broccolini",       tier: "direct", note: "Thinner stalks, milder, faster-cooking. Roast or sauté — don't bother steaming." },
      { id: "broccoli_rabe",    tier: "close",  note: "Bitterer cousin (it's actually a turnip relative). Blanch first to tame, then sauté with garlic and chili." },
    ],
    pairs: ["garlic", "lemon", "parmesan", "olive_oil", "soy_sauce", "chili_flakes"],
    flavor: {
      primary: ["bitter", "umami"],
      intensity: "moderate",
      heatChange: {
        raw: "crunchy, mineral, faintly sweet — good shaved into slaws",
        cooked: "softens and sweetens; steamed it stays grassy, roasted it goes nutty-caramelized",
        charred: "the best version — Maillard runs hard on the floret tips, edges go crisp, flavor concentrates",
      },
    },
    nutrition: { per: "100g", kcal: 34, protein_g: 2.8, fat_g: 0.4, carb_g: 6.6, fiber_g: 2.6, sodium_mg: 33 },
    origin: "Domesticated from wild cabbage (Brassica oleracea) in the northern Mediterranean — Italy specifically. Roman writers describe it ~2,000 years ago. Came to American kitchens via Italian immigrants in the early 20th century.",
    culturalNotes: "Broccoli, cauliflower, kale, Brussels sprouts, cabbage, kohlrabi, and collard greens are all the same species — Brassica oleracea — bred over centuries to emphasize different parts of the plant. Florets, leaves, buds, stems, roots — all one ancestral cabbage.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [9, 10, 11, 3, 4, 5], peakMonthsS: [3, 4, 5, 9, 10, 11], yearRound: true },
    sourcing: "Tight, dark-green florets with a firm stalk are the freshness markers. Yellowing florets or limp stalks mean it's been on the shelf too long. Stalks are food too — peel the woody outside, dice the tender core.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Supermarket broccoli is fine. Farmer's-market heads in fall and spring taste meaningfully sweeter, especially after a frost.",
    },
    skillDev: { skills: ["heat", "knife"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  avocado: {
    description: "Buttery, green-fleshed fruit of the Persea americana tree. Eaten raw — guacamole, smashed on toast, diced in salads, sliced in tacos.",
    flavorProfile: "Rich, buttery, mildly grassy; creamy texture from ~15% fat content (mostly monounsaturated)",
    prepTips: "Ripe when it yields to gentle squeeze (not thumb-piercing pressure). Rock-hard? Bag with an apple or banana for 24 hours — ethylene accelerates ripening. Halve around the pit, twist, pop the pit with a knife tap.",
    storage: {
      location: "pantry",
      shelfLifeDays: 5,
      shelfLife: { fridge: 7, freezer: 150, pantry: 5 },
      tips: "Unripe on the counter. Ripe ones move to the fridge (slows further ripening by ~2–3 days). Cut avocado: cling wrap against the flesh + squeeze of lemon juice keeps it green overnight.",
      spoilageSigns: "Dark stringy flesh inside, sour or fermented smell, brown mushy spots that don't slice away cleanly.",
      freezable: true,
      freezeNotes: "Purée with lime juice, freeze in small portions. 5 months. Whole or sliced avocado does NOT freeze well — texture turns to paste on thaw.",
      prepYield: { whole: "1 large Hass (~200g with pit/skin)", yields: "~¾ cup mashed / ~1 cup diced" },
    },
    substitutions: [],
    pairs: ["lime", "cilantro", "tortillas", "tomato"],
    flavor: {
      primary: ["fat", "sweet"],
      intensity: "mild",
      heatChange: {
        raw: "creamy, buttery, barely-there grassiness",
        cooked: "mostly cosmetic — light warming on toast is fine; hard cooking turns it bitter and gray",
        charred: "grilled avocado halves is a real technique — smoky-sweet, briefly",
      },
    },
    nutrition: { per: "count", kcal: 322, protein_g: 4, fat_g: 29, carb_g: 17, fiber_g: 14, sodium_mg: 14 },
    origin: "Mexico and Central America — cultivated by the Mesoamerican civilizations for 10,000+ years. The Aztec word 'ahuacatl' (from which 'avocado' derives) also meant 'testicle.'",
    culturalNotes: "~90% of the Hass avocados sold in the US come from a single tree bred by Rudolph Hass in 1935. Every store-bought Hass is a clone of that tree.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [5, 6, 7, 8, 9], peakMonthsS: [11, 12, 1, 2, 3], yearRound: true },
    sourcing: "Hass (knobby, darkens when ripe) is the default; Fuerte is a smoother, greener variety. Buy unripe, ripen on the counter — it gives you control.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Hass is Hass — clones of a single 1935 tree. Ripeness matters infinitely more than sourcing.",
    },
    skillDev: { skills: ["knife"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  lime: {
    description: "Small, green citrus. Sharper and more aromatic than lemon; the citrus of choice for Mexican, Thai, and Caribbean cooking.",
    flavorProfile: "Sharp, floral, tart; slightly bitter in the pith and zest",
    prepTips: "Roll on the counter pressing firmly before juicing — breaks the internal membranes, doubles the juice yield. Microwaving 10 seconds also works. Zest before juicing; it's impossible the other way around.",
    storage: {
      location: "fridge",
      shelfLifeDays: 21,
      shelfLife: { fridge: 21, freezer: 120, pantry: 7 },
      tips: "Crisper drawer, not the door. On the counter they'll last a week; in the fridge up to 3. Juice freezes well in ice-cube trays for later use.",
      spoilageSigns: "Hard shriveled skin, brown spots, white/gray mold at the stem end.",
      freezable: true,
      freezeNotes: "Juice into ice-cube trays (1 lime ≈ 2 tbsp), pop out when frozen, bag. 4 months. Whole limes also freeze but lose some aromatic lift.",
      prepYield: { whole: "1 Persian lime", yields: "~2 tbsp juice / 1 tsp zest" },
    },
    substitutions: [
      { id: "lemon", tier: "direct", note: "Similar brightness, different profile. Lemon is more floral-sweet; lime is sharper and more tropical." },
    ],
    pairs: ["cilantro", "avocado", "chicken_breast", "tortillas"],
    flavor: {
      primary: ["sour", "bitter"],
      intensity: "strong",
      heatChange: {
        raw: "sharp, floral, nose-tingling",
        cooked: "acid stays; aromatic oils dissipate — always add at the end",
        charred: "grilled lime halves squeeze better and add smoky sweetness to tacos/salsa",
      },
    },
    nutrition: { per: "count", kcal: 20, protein_g: 0.5, fat_g: 0.1, carb_g: 7, fiber_g: 1.9, sodium_mg: 1 },
    origin: "Southeast Asia — spread through Persian and Arab trade routes. Key limes (the small, aromatic originals) come from the Florida Keys and are used for the namesake pie.",
    culturalNotes: "18th-century British sailors were issued lime juice to prevent scurvy — hence 'limey.' Ironically, Key limes worked far better than the Persian limes the navy eventually substituted.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [5, 6, 7, 8, 9], peakMonthsS: [11, 12, 1, 2, 3], yearRound: true },
    sourcing: "Persian limes (the usual supermarket kind) are larger and juicier. Key limes are tiny, intensely aromatic, usually only available bagged. Bottled 'lime juice' is an insult — always fresh.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Fresh limes are non-negotiable — bottled juice tastes harsh and chemical. Soft, slightly wrinkled limes yield more juice than firm ones.",
    },
    skillDev: { skills: ["knife", "seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  cilantro: {
    description: "Fresh coriander leaves — the herb that Mexican, Thai, Vietnamese, and North African cuisines lean on hardest. Divisive: a small fraction of people taste soap (a genetic variant of the OR6A2 olfactory receptor).",
    flavorProfile: "Bright, citrusy, soapy-to-some, fresh; cools and lifts fatty or spicy dishes",
    prepTips: "Chop stems and leaves — stems carry more flavor than the leaves and are common in Thai/Mexican cooking. Add at the very end; cilantro wilts to mush with any real heat.",
    storage: {
      location: "fridge",
      shelfLifeDays: 10,
      shelfLife: { fridge: 10, freezer: 180, pantry: null },
      tips: "Stand stems in a glass of water, loose bag over the top. Or wrap in slightly damp paper towel inside a zip-top. Slimy cilantro is a total loss — no rescue.",
      spoilageSigns: "Yellow or translucent leaves, slimy stems, sour smell — rots faster than parsley, so check often.",
      freezable: true,
      freezeNotes: "Purée with lime juice + neutral oil into cubes. 6 months. Dried cilantro is essentially flavorless; don't bother.",
    },
    substitutions: [
      { id: "parsley", tier: "dietary", note: "Closest visual match; entirely different flavor. Use if cilantro-aversion is in play (the soap-gene crowd is real)." },
    ],
    pairs: ["lime", "garlic", "jalapeno", "avocado"],
    flavor: {
      primary: ["sour", "heat", "bitter"],
      intensity: "strong",
      heatChange: {
        raw: "bright, citrusy, nose-tingling",
        cooked: "flavor fades fast; stems hold up better than leaves",
        charred: "wilts to slime with any real heat; purely a finishing herb",
      },
    },
    nutrition: { per: "100g", kcal: 23, protein_g: 2.1, fat_g: 0.5, carb_g: 3.7, fiber_g: 2.8, sodium_mg: 46 },
    origin: "Eastern Mediterranean and Central Asia. One of the oldest cultivated herbs — seeds (coriander) found in Egyptian tombs.",
    culturalNotes: "The soap-taste haters are genuine — roughly 4–14% of people depending on heritage, due to a gene variant. Not picky eaters; literally a different sensory experience.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { peakMonthsN: [5, 6, 7, 8], peakMonthsS: [11, 12, 1, 2], yearRound: true },
    sourcing: "Bunches should be vibrant green with no slime at the base. 'Culantro' (recao) is a stronger, sturdier cousin used in Caribbean cooking — a legit substitute if you can find it.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Cheap bunches are fine. Freshness is everything — buy small, buy often.",
    },
    skillDev: { skills: ["knife", "seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  // ── pantry: grains + flours ──────────────────────────────────────
  spaghetti: {
    description: "Long, thin dried wheat pasta. The default noodle of the American Italian kitchen — the shape for carbonara, aglio e olio, meatballs, and a thousand weeknight dinners.",
    flavorProfile: "Clean, wheaty, faintly nutty; takes on whatever sauce it meets",
    prepTips: "Salt the water like the sea (1 tbsp per quart). Cook 1 minute less than the box for al dente, then finish in the pan with the sauce — the last minute of starch-release is what makes sauce cling. Save a cup of pasta water before draining.",
    storage: {
      location: "pantry",
      shelfLifeDays: 730,
      shelfLife: { fridge: null, freezer: null, pantry: 730 },
      tips: "Sealed in a dry cabinet. Dried pasta is basically immortal — 'best by' dates are conservative. An opened box keeps 1–2 years.",
      spoilageSigns: "Moth eggs or webs (pantry moths love pasta), musty smell, or oily spots indicating rancidity. Extremely rare.",
      freezable: false,
      freezeNotes: "No point — dried pasta stores indefinitely in the pantry.",
    },
    substitutions: [
      { id: "linguine", tier: "direct", note: "Flatter, similar thickness. Works in most spaghetti recipes." },
      { id: "bucatini", tier: "pro",    note: "Hollow core; heartier. Classic for amatriciana — upgrade for Roman pastas." },
    ],
    pairs: ["pecorino", "guanciale", "black_pepper", "garlic"],
    flavor: {
      primary: ["umami", "sweet"],
      intensity: "mild",
      heatChange: {
        raw: "firm, chalky, brittle",
        cooked: "tender with a slight chew (al dente); carries whatever sauce it meets",
        charred: "toasted-pasta technique (brown the dry noodles in oil before boiling) adds a nutty depth",
      },
    },
    nutrition: { per: "100g", kcal: 371, protein_g: 13, fat_g: 1.5, carb_g: 75, fiber_g: 3.2, sodium_mg: 6 },
    origin: "Wheat pasta dates to ancient Persia/China; Italian tradition codified around Naples and Genoa in the 1200s. The American diet's obsession with spaghetti specifically came post-WWII.",
    culturalNotes: "'Al dente' literally means 'to the tooth' — the pasta should have a tiny chalky core visible when you bite one in half. Mushy pasta is an American invention.",
    allergens: ["gluten"],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Bronze-die (trafilato al bronzo) pasta has a rougher surface that holds sauce better. Italian imports (De Cecco, Rustichella, Setaro) are worth the markup over American mass-market brands.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Bronze-die Italian pasta is noticeably better than standard teflon-extruded. The rough surface holds sauce; the texture is chewier. Worth 2–3× for the dishes where pasta is the star.",
    },
    skillDev: { skills: ["timing", "heat"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },

  bread: {
    description: "Generic loaf — the thing you actually have in the kitchen when a recipe says 'a slice of bread.' Sandwich loaves, sourdough boules, multigrain, whatever the house keeps.",
    flavorProfile: "Wheaty, mildly sweet; sourdough adds tang, rye adds earth, whole wheat adds grass",
    prepTips: "Stale bread > fresh bread for nearly every cooking application: breadcrumbs, strata, panzanella, French toast. Fresh bread is for eating, not cooking.",
    storage: {
      location: "pantry",
      shelfLifeDays: 5,
      shelfLife: { fridge: 10, freezer: 90, pantry: 5 },
      tips: "Paper bag (breathable) keeps the crust crisp. Plastic bags keep the crumb soft but soften the crust. Freeze halved loaves if you're not eating them fast — toasts from frozen perfectly.",
      spoilageSigns: "Any visible mold — the rest of the loaf is also contaminated, don't 'cut off' and eat. Sour-fermented smell beyond a sourdough's natural tang.",
      freezable: true,
      freezeNotes: "Halve, wrap in foil + zip-top. Toast directly from frozen in a toaster — perfect slices, no defrost needed. 3 months.",
    },
    substitutions: [
      { id: "baguette",  tier: "direct", note: "Same family; crustier. Use for tartines and crusty-bread applications." },
      { id: "ciabatta",  tier: "direct", note: "Holier crumb, better for sandwiches." },
    ],
    pairs: ["butter", "olive_oil", "cheese", "tomato"],
    flavor: {
      primary: ["sweet", "umami"],
      intensity: "mild",
      heatChange: {
        raw: "soft, yeasty, mildly sweet",
        cooked: "toasts into Maillard-rich crisp-chewy perfection",
        charred: "burnt toast is acrid; pulled-back char adds bitter-sweet caramelization",
      },
    },
    nutrition: { per: "100g", kcal: 265, protein_g: 9, fat_g: 3.2, carb_g: 49, fiber_g: 2.7, sodium_mg: 491 },
    origin: "Leavened bread dates to at least ancient Egypt (4,000+ years ago). Almost every agricultural civilization developed some form independently.",
    allergens: ["gluten"],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "A real bakery loaf tastes like a different food than sliced supermarket bread. If you have one nearby, worth the 10-minute detour. Frozen half-loaves from a good bakery outperform fresh from a mediocre one.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Bakery bread is transformative. Supermarket soft sliced bread has its place (school lunches, French toast) but for toast, cheese boards, and dipping — spend on the good stuff.",
    },
    skillDev: { skills: ["dough", "heat"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },

  rye_bread: {
    description: "Loaf made with rye flour — denser, darker, and earthier than wheat bread. Often laced with caraway. The backbone of Reuben sandwiches, pastrami on rye, and Northern European open-faced sandwiches (smørrebrød).",
    flavorProfile: "Earthy, faintly sour, mineral; caraway adds warm anise-citrus notes when present",
    prepTips: "Rye toasts darker and faster than wheat — pull it a notch earlier than your usual setting. For Reubens, butter the outside and griddle low-and-slow until the crust is mahogany.",
    storage: {
      location: "pantry",
      shelfLifeDays: 6,
      shelfLife: { fridge: 12, freezer: 90, pantry: 6 },
      tips: "Rye stays moist longer than white bread — its higher pentosan content holds water. Paper bag at room temp; freeze halves you won't finish in a week.",
      spoilageSigns: "Visible mold (toss the whole loaf — don't cut around it), sour-funky smell beyond rye's natural tang, dried-out bricky crumb.",
      freezable: true,
      freezeNotes: "Slice before freezing so you can pull single slices straight to the toaster. 3 months keeps quality.",
    },
    substitutions: [
      { id: "sourdough", tier: "direct",    note: "Closest tang and density. Loses the rye-specific earthiness but works for most sandwich applications." },
      { id: "bread",     tier: "emergency", note: "Plain sandwich bread is a last resort — fundamentally different character." },
    ],
    pairs: ["caraway", "butter", "swiss_cheese", "pastrami", "mustard", "dill", "smoked_salmon"],
    flavor: {
      primary: ["sweet", "umami", "sour"],
      intensity: "moderate",
      heatChange: {
        raw: "dense, slightly tangy, faintly sweet",
        cooked: "toasts to a deep mahogany — Maillard runs hard with rye's sugars",
        charred: "burns to bitter quickly thanks to the dark crust; pull early",
      },
    },
    nutrition: { per: "100g", kcal: 259, protein_g: 8.5, fat_g: 3.3, carb_g: 48.3, fiber_g: 5.8, sodium_mg: 660 },
    origin: "Northern and Eastern European staple — Germany, Scandinavia, the Baltics, Russia, Poland. Rye thrives in cold, poor soil where wheat struggles, so it became the daily bread across that whole climate band.",
    culturalNotes: "German pumpernickel is essentially 100% rye, fermented and steamed for hours. Scandinavian rye is the canvas for smørrebrød. Jewish deli rye (with caraway) is the third lineage Americans know best — the bread of Reubens and pastrami sandwiches.",
    allergens: ["gluten"],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Real bakery rye — especially marble rye or pumpernickel — is meaningfully better than supermarket sliced. Look for visible caraway seeds and a dense, slightly moist crumb. Light, fluffy 'rye' from the soft-bread aisle is mostly white bread with caramel coloring.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Deli or bakery rye, dense and dark, is the version worth eating. Supermarket sliced rye is fine for everyday sandwiches but the textural and flavor depth comes from a real loaf.",
    },
    skillDev: { skills: ["dough", "fermentation"], difficulty: "technical", proFromScratch: true, fromScratchRecipeId: null },
  },

  baguette: {
    description: "The long, slender French loaf. Crackling golden crust, airy interior, ready for butter, cheese, or the classic ham-and-butter sandwich (jambon-beurre).",
    flavorProfile: "Wheaty, faintly sour, clean; crust carries caramelized notes from the high-heat bake",
    prepTips: "Best within 4–6 hours of baking. Day-old baguette: refresh with a 30-second splash under running water, then 5 minutes in a 350°F oven — returns the crust to shatter-crisp.",
    storage: {
      location: "pantry",
      shelfLifeDays: 2,
      shelfLife: { fridge: 5, freezer: 60, pantry: 2 },
      tips: "Stand it upright in a paper bag; this slows the crust-softening. Freeze the second half the day you buy it.",
      spoilageSigns: "Visible mold (especially in humid climates within 48h), sour-funky smell, crust gone rock-hard and crumb turned stale-gray.",
      freezable: true,
      freezeNotes: "Slice and freeze for toast-ready rounds. Whole half-baguettes freeze fine but need refresh in the oven on thaw.",
    },
    substitutions: [
      { id: "ciabatta", tier: "direct",    note: "Italian cousin; holier crumb, wider shape. Similar crust play." },
      { id: "bread",    tier: "emergency", note: "Emergency fallback — results noticeably different." },
    ],
    pairs: ["butter", "ham", "brie", "olive_oil"],
    flavor: {
      primary: ["sweet", "umami"],
      intensity: "mild",
      heatChange: {
        raw: "crackling crust, airy crumb, barely-there sweetness",
        cooked: "re-crisps beautifully at 350°F for 5 min — the day-old baguette save",
        charred: "bruschetta territory — grilled slices with olive oil are prized; past that it's toast-char",
      },
    },
    nutrition: { per: "100g", kcal: 274, protein_g: 9.8, fat_g: 2.6, carb_g: 52, fiber_g: 2.5, sodium_mg: 570 },
    origin: "Paris, early 20th century — invented as a quick-to-bake loaf after labor laws banned early-morning baker shifts. The thin shape cooks in 20 minutes instead of an hour.",
    culturalNotes: "UNESCO recognized French baguette craftsmanship as an 'intangible cultural heritage' in 2022. By law, a real Parisian baguette tradition contains only flour, water, salt, and yeast — no additives.",
    allergens: ["gluten"],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "A real bakery baguette at ~$3 is one of the best deals in food. Supermarket baguettes are an entirely different product — same shape, different soul.",
    market: {
      priceTier: "budget",
      availability: "specialty",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "This is THE ingredient to source carefully. Supermarket baguettes are factory bread shaped like a baguette; the real thing from a bakery is a completely different food.",
    },
    skillDev: { skills: ["dough", "timing"], difficulty: "technical", proFromScratch: true, fromScratchRecipeId: null },
  },

  ciabatta: {
    description: "Italian rustic bread — holey, airy crumb; floury, crackling crust; wide flat loaf shape. The sandwich bread for when you want structure and chew.",
    flavorProfile: "Mild, yeasty, faintly sour; open crumb soaks up oil and vinegar beautifully",
    prepTips: "For panini and grilled sandwiches, ciabatta is unbeatable — the holes trap fillings, the crust crisps gorgeously under the press. For bruschetta, toast cut slices in a hot dry pan.",
    storage: {
      location: "pantry",
      shelfLifeDays: 3,
      shelfLife: { fridge: 7, freezer: 60, pantry: 3 },
      tips: "Paper bag or bread box. Reheats beautifully: 5 min in a 350°F oven wakes the crust back up. Freezes well in halves.",
      spoilageSigns: "Visible mold, rock-hard crust plus stale crumb, or sour-funky smell beyond a hint of sourdough tang.",
      freezable: true,
      freezeNotes: "Halve before freezing. Revive in a 350°F oven for 8 min straight from frozen — crust shatters back to crisp.",
    },
    substitutions: [
      { id: "baguette",  tier: "direct", note: "Finer crumb; different sandwich style (tartine vs panino)." },
      { id: "focaccia",  tier: "pro",    note: "Same region, richer with olive oil. Upgrade for sandwiches where oil-absorption is the point." },
    ],
    pairs: ["tomato", "mozzarella", "prosciutto", "olive_oil"],
    flavor: {
      primary: ["sweet", "umami"],
      intensity: "mild",
      heatChange: {
        raw: "airy, slightly chewy, faintly yeasty",
        cooked: "panini-press territory — crust goldens and the crumb soaks up the filling's juices",
        charred: "grilled-bruschetta char adds toasty-bitter edge; past that it's burnt toast",
      },
    },
    nutrition: { per: "100g", kcal: 271, protein_g: 9, fat_g: 3, carb_g: 52, fiber_g: 2.5, sodium_mg: 540 },
    origin: "Invented in 1982 in Veneto, Italy. Actually recent — an Italian baker (Arnaldo Cavallari) developed it as a response to the French baguette's popularity in sandwiches.",
    culturalNotes: "The name means 'slipper' in Italian — the wide, flat loaf shape. For all its rustic appearance, ciabatta is essentially a modern invention younger than the microwave.",
    allergens: ["gluten"],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "A bakery ciabatta is meaningfully better than supermarket. The hydration (water in the dough) is very high — commercial bakers often cheat with less, and you can taste it.",
    market: {
      priceTier: "moderate",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "High-hydration dough is what makes the holes — commercial supermarket ciabatta often cheats and is denser. Bakery versions have the real open crumb.",
    },
    skillDev: { skills: ["dough", "timing"], difficulty: "technical", proFromScratch: true, fromScratchRecipeId: null },
  },

  flour: {
    description: "All-purpose wheat flour — the default. A ~10–12% protein content splits the difference between soft cake flour and high-gluten bread flour. Works for most baking and all thickening.",
    flavorProfile: "Clean, faintly sweet; takes on whatever flavors surround it",
    prepTips: "Weigh, don't scoop — a packed cup can vary by 40g. For gravies and roux: whisk 1 tbsp flour + 1 tbsp fat for 1 minute (blonde) or 3 minutes (caramel) to cook out the raw-flour taste before adding liquid.",
    storage: {
      location: "pantry",
      shelfLifeDays: 240,
      shelfLife: { fridge: 365, freezer: 730, pantry: 240 },
      tips: "Cool, dry cabinet in an airtight container — the original paper bag invites weevils. Whole-wheat flour goes rancid faster (oils in the germ); store in the fridge if you keep any.",
      spoilageSigns: "Musty or sour smell, visible weevils/moths, yellowed color, or rancid oil scent (especially whole-wheat). Clumping alone isn't spoilage — just humidity; sift and use.",
      freezable: true,
      freezeNotes: "Freeze in an airtight bag to kill any weevil eggs from the mill — standard practice. Thaw to room temp before baking so the flour behaves predictably in recipes.",
      prepYield: "1 cup AP flour ≈ 120g (spooned and leveled). A \"packed\" cup can be 160g+ — why weighing matters.",
    },
    substitutions: [
      { id: "bread_flour",   tier: "pro",       note: "Higher protein (~13%). For yeasted breads and pizza dough — chewier crumb." },
      { id: "cake_flour",    tier: "direct",    note: "Lower protein (~8%). For tender cakes and biscuits — different job, not a 1:1 swap." },
    ],
    pairs: ["butter", "sugar", "eggs", "salt"],
    flavor: {
      primary: ["sweet"],
      intensity: "mild",
      heatChange: {
        raw: "chalky, faintly sweet — never eat raw (food-safety) and tastes pasty anyway",
        cooked: "develops nutty, bready notes; roux goes blonde → peanut → chocolate as it browns",
        charred: "burnt flour is acrid and bitter; past dark-chocolate roux it's ruined",
      },
    },
    nutrition: { per: "100g", kcal: 364, protein_g: 10, fat_g: 1, carb_g: 76, fiber_g: 2.7, sodium_mg: 2 },
    origin: "Milled wheat dates to the dawn of agriculture (~10,000 years). Modern roller-milled white flour is a 19th-century invention; before that, all flour was stoneground and coarser.",
    allergens: ["gluten"],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "high", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "King Arthur's AP flour has a consistent 11.7% protein content — the most reliable American AP flour. European '00' flour is finer and softer, great for pasta and pizza but not interchangeable for most baking.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "King Arthur's consistent protein spec actually matters for bread baking — cheaper bags vary batch-to-batch and your dough behaves differently. For cakes and roux, any AP flour is fine.",
    },
    skillDev: { skills: ["dough", "sauce"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  sugar: {
    description: "White granulated sugar — refined sucrose. The universal sweetener in Western baking, key to Maillard browning, structure in meringues, balance in savory sauces.",
    flavorProfile: "Clean, sweet; no flavor beyond sweetness",
    prepTips: "For caramel: dry method (sugar alone in a pan) is faster but risks burning — stay vigilant. Wet method (sugar + water) is forgiving but slower. Don't stir once it starts to color; swirl the pan.",
    storage: {
      location: "pantry",
      shelfLifeDays: 1825,
      shelfLife: { fridge: null, freezer: null, pantry: 1825 },
      tips: "Airtight container. White sugar is functionally immortal — it doesn't spoil; just hardens if exposed to humidity. A slice of bread in the container softens hardened sugar overnight.",
      spoilageSigns: "Ants, weevils, or mold (only if water got in). Hardening is not spoilage — break up and use. Yellowing suggests contamination; toss.",
      freezable: false,
      freezeNotes: "No benefit — pantry storage is essentially forever.",
      prepYield: "1 cup white sugar ≈ 200g. 1 cup packed brown sugar ≈ 220g.",
    },
    substitutions: [
      { id: "brown_sugar", tier: "direct",    note: "Molasses content adds warmth and moisture. Packed cup for cup in most recipes — different color and slight flavor." },
      { id: "honey",       tier: "dietary",   note: "Sweeter, more moisture. Reduce other liquid by 25%, reduce oven temp by 25°F." },
    ],
    pairs: ["butter", "cinnamon", "vanilla", "flour"],
    flavor: {
      primary: ["sweet"],
      intensity: "mild",
      heatChange: {
        raw: "pure sweetness, no other notes",
        cooked: "caramelizes to nutty, buttery, complex flavors as it passes through amber to mahogany",
        charred: "burnt sugar is acrid and bitter — past dark-amber it's lost; start over",
      },
    },
    nutrition: { per: "100g", kcal: 387, protein_g: 0, fat_g: 0, carb_g: 100, sodium_mg: 1 },
    origin: "Sugarcane originated in New Guinea ~10,000 years ago; sugar refining came via India to the Arab world, to Europe, and eventually to the colonial Americas — with all the dark history that implies.",
    culturalNotes: "Pre-industrial, sugar was a luxury spice stored in locked boxes. Average per-capita consumption has increased roughly 30× since 1800 — industrial processing made it the cheap default it is today.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "For baking, white granulated is the standard. Specialty sugars (muscovado, demerara, turbinado) have moisture and molasses notes that change the behavior — not 1:1 swaps.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Commodity ingredient — any brand of white granulated is indistinguishable in baking. Specialty sugars (muscovado, demerara) are where quality starts to matter.",
    },
    skillDev: { skills: ["heat", "timing"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  tortillas: {
    description: "Flatbreads from Mexican cuisine — corn (yellow or white masa) or flour (wheat), varying diameters. Tortillas are the plate, the spoon, and the context in countless dishes.",
    flavorProfile: "Corn: earthy, slightly sweet, toasty when warm. Flour: mild, wheaty, tender.",
    prepTips: "ALWAYS warm before serving — cold tortillas crack. Gas flame: 10 seconds per side directly on the burner (tongs required). No gas? Dry pan over medium-high, 20 seconds per side. Stack wrapped in foil to keep warm.",
    storage: {
      location: "fridge",
      shelfLifeDays: 14,
      shelfLife: { fridge: 14, freezer: 180, pantry: 3 },
      tips: "In their original bag in the fridge. Freeze in flat packs for longer storage. Room temp: 2–3 days max; they dry out fast.",
      spoilageSigns: "Visible mold on any one — toss the stack (spores spread fast in a stacked bag). Sour smell, stiffness past rehydration, dark spots.",
      freezable: true,
      freezeNotes: "Freeze in flat stacks with parchment between every 3–4 tortillas so you can pull what you need. Thaw at room temp 20 min or microwave wrapped in damp paper towel.",
      prepYield: "A standard 10-count pack of 6\" corn tortillas serves 4 as taco base (2–3 per person). Flour 10\" burrito-size: 1 per person.",
    },
    substitutions: [
      { id: "bread",   tier: "emergency", note: "Wrap-style sandwiches in a pinch — loses the whole Mexican context but works for hand-held." },
    ],
    pairs: ["lime", "cilantro", "avocado", "chicken_breast"],
    flavor: {
      primary: ["sweet", "umami"],
      intensity: "mild",
      heatChange: {
        raw: "packaged tortillas are pliable but flavorless cold — always warm",
        cooked: "toasted corn tortillas gain nutty, popcorn-adjacent depth; flour tortillas blister and get buttery",
        charred: "blackened char-spots (not the whole tortilla) are the goal for carne asada — adds smoky bitterness that balances the fat",
      },
    },
    nutrition: { per: "count", kcal: 96, protein_g: 2.6, fat_g: 2.3, carb_g: 16, fiber_g: 1.1, sodium_mg: 204 },
    origin: "Mesoamerica — domesticated from teosinte corn ~9,000 years ago. Corn tortillas are the older tradition; wheat tortillas came with the Spanish in the 1500s and settled in Northern Mexico.",
    culturalNotes: "Real Mexican tortillerías use nixtamalized masa (corn cooked in alkaline water) — the process releases nutrients and creates the distinctive aroma. Supermarket corn tortillas skip this step, which is why they taste different.",
    allergens: ["gluten"],  // flour tortillas; corn tortillas are gluten-free
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "For tacos al pastor / carne asada: handmade corn tortillas from a local tortillería (or make your own from masa harina). For burritos and quesadillas: large flour. Freshness matters more than brand.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Handmade corn tortillas from a tortillería are a different food than shelf-stable supermarket ones — nixtamalized masa, proper aroma, pliability. If there's one in your area, worth the detour.",
    },
    skillDev: { skills: ["heat", "timing"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },

  // ── pantry: oils + wines + stocks ────────────────────────────────
  olive_oil: {
    description: "Pressed juice of olives — extra-virgin (EVOO) is cold-pressed first-press from a single harvest, unrefined; regular 'olive oil' is refined and blended. The base of Mediterranean cooking and one of the most versatile fats in the kitchen.",
    flavorProfile: "Grassy, peppery, fruity; a good EVOO finishes with a bitter throat-tickle that means polyphenols are present",
    prepTips: "Two oils, two jobs: cheap blended olive oil for hot pans (sautéing, roasting); expensive EVOO for finishing (drizzling on burrata, dressing salads, dipping bread). Heating great EVOO over 350°F destroys the polyphenols you paid for.",
    storage: {
      location: "pantry",
      shelfLifeDays: 540,
      shelfLife: { fridge: null, freezer: null, pantry: 540 },
      tips: "Cool, dark cabinet — heat and light go after the flavor compounds. A dark glass bottle beats clear. Once opened, use within 6 months for peak flavor; rancid oil smells like crayons or putty.",
      spoilageSigns: "Crayon, putty, or \"old paint\" smell = rancid — toss. Cloudy/solid at cold temps is normal (olive oil solidifies in the fridge); warms back clear. Metallic or musty off-notes mean it's been exposed to heat/light too long.",
      freezable: false,
      freezeNotes: "Don't freeze — no benefit, and the texture breaks when thawed. Cool pantry is the right home.",
      prepYield: "1 cup = ~215g. A \"glug\" (home-cook pour) ≈ 1 tbsp.",
    },
    substitutions: [
      { id: "butter",      tier: "direct",    note: "For finishing pasta or vegetables; richer, less herbaceous. 1:1 by volume." },
      { id: "ghee",        tier: "pro",       note: "For high-heat sautés where olive oil's smoke point is the issue — clarified butter handles 450°F+ without breaking." },
    ],
    pairs: ["garlic", "basil", "lemon", "tomato", "balsamic"],
    flavor: {
      primary: ["bitter", "sour"],
      intensity: "medium",
      heatChange: {
        raw: "grassy, peppery, fruity; polyphenols bite the throat — the signature of a fresh EVOO",
        cooked: "mellows; the assertive grassy notes fade under heat — save good EVOO for finishing",
        charred: "past ~410°F (smoke point) it breaks down to acrid, burnt-rubber notes — pull the pan",
      },
    },
    nutrition: { per: "100g", kcal: 884, protein_g: 0, fat_g: 100, carb_g: 0, sodium_mg: 2 },
    origin: "Mediterranean basin — domesticated olives date to ~6,000 BCE in the Levant. Spain, Italy, and Greece account for ~70% of global production today.",
    culturalNotes: "The 'extra-virgin' label is widely abused. Italian-bottled doesn't mean Italian-grown — much of what's sold as Italian EVOO is blended from Spain, Greece, and Tunisia. The 2007 'extra-virgin olive oil scandal' (chronicled in Tom Mueller's 'Extra Virginity') showed most US-supermarket EVOO failed lab tests for purity.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true, preservedAvailable: true },
    sourcing: "Look for a harvest date on the bottle (not just 'best by') and a single country of origin. California Olive Ranch, Cobram Estate, and any oil with a recent (within 12 months) harvest date is a safer bet than the cheap big-name imports.",
    market: {
      priceTier: "mid",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "One of the most fraudulent categories in food — supermarket \"Italian EVOO\" routinely fails purity tests. A single-origin bottle with a harvest date within 12 months is night-and-day different from a cheap blended import.",
    },
    skillDev: { skills: ["heat", "sauce"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  balsamic: {
    description: "Italian vinegar from Modena and Reggio Emilia, made from cooked grape must (mosto cotto), aged in a battery of progressively smaller wooden casks. True 'tradizionale' is aged 12–25+ years; commercial 'Aceto Balsamico di Modena IGP' is younger and blended with wine vinegar.",
    flavorProfile: "Sweet-tart, syrupy, deeply fruity; aged versions taste like fig jam with bright acidity",
    prepTips: "Cheap supermarket balsamic ($5–10): reduce in a pan with a splash of honey to fake the syrupy intensity of an aged bottle. Real tradizionale ($80+ for 100ml): never cook it. Drop it onto Parmigiano shards, vanilla ice cream, or strawberries.",
    storage: {
      location: "pantry",
      shelfLifeDays: 1825,
      shelfLife: { fridge: null, freezer: null, pantry: 1825 },
      tips: "Tightly capped in a cool cabinet. Vinegar's acidity makes it essentially immortal — it'll outlive the bottle. No need to refrigerate.",
      spoilageSigns: "Essentially never spoils. A \"mother\" (gelatinous mass) can form — harmless, strain if bothered. Sediment at the bottom is fine.",
      freezable: false,
      freezeNotes: "No benefit — pantry storage outlasts most kitchens.",
    },
    substitutions: [
      { id: "vinegar",  tier: "emergency", note: "Plain vinegar lacks the sweetness; add a pinch of brown sugar or a drop of honey to mimic." },
    ],
    pairs: ["olive_oil", "tomato", "mozzarella", "basil", "parmesan"],
    flavor: {
      primary: ["sour", "sweet"],
      intensity: "strong",
      heatChange: {
        raw: "sharp vinegary punch softened by grape-must sweetness; complex in aged bottles",
        cooked: "reduces to syrup that concentrates the fruit and caramelizes the sugars — the cheap-balsamic upgrade move",
        charred: "only happens if the pan scorches — burnt sugar bitterness ruins it",
      },
    },
    nutrition: { per: "100g", kcal: 88, protein_g: 0.5, fat_g: 0, carb_g: 17, sodium_mg: 23 },
    origin: "Modena and Reggio Emilia, Emilia-Romagna, Italy. The tradition dates to the Middle Ages; the first written reference is from 1046, when Holy Roman Emperor Henry III was gifted a bottle.",
    culturalNotes: "True 'Aceto Balsamico Tradizionale di Modena DOP' must be aged at least 12 years and is graded by a panel of judges who score it from 0–400 points. The bottle, the cork, and even the shape are protected by law. 99% of supermarket 'balsamic' is the IGP version — younger, blended, and a different product.",
    allergens: ["sulfites"],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "For everyday cooking, 'Aceto Balsamico di Modena IGP' from a brand like Giuseppe Giusti or Mussini is excellent at $15–25. For drizzling: spend $40+ on a labeled aged bottle. Avoid anything labeled just 'balsamic vinegar' with no IGP/DOP designation — usually wine vinegar with caramel coloring.",
    market: {
      priceTier: "mid",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "IGP and DOP designations are the quality tiers — without them it's wine vinegar + caramel coloring. Worth paying for a real IGP bottle; tradizionale DOP is a different beast (and budget).",
    },
    skillDev: { skills: ["sauce", "seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  dijon: {
    description: "French mustard made from brown/black mustard seeds and white wine or verjuice (tart unripe-grape juice). Sharper, smoother, and more wine-forward than American yellow mustard.",
    flavorProfile: "Sharp, tangy, sinus-tingling; creamy texture, complex finish from the wine",
    prepTips: "Whisk a teaspoon into vinaigrettes — it's a natural emulsifier and the dressing won't separate. Stir into pan sauces off the heat (boiling kills the volatile mustard oils that make it sharp). For honey-mustard glaze: 2 parts dijon, 1 part honey, brushed on chicken or salmon.",
    storage: {
      location: "fridge",
      shelfLifeDays: 365,
      shelfLife: { fridge: 365, freezer: null, pantry: 30 },
      tips: "Refrigerate after opening — keeps for a year, though the heat (the sinus burn) fades after 6 months. If a brown crust forms on top, scrape it off; the mustard underneath is fine.",
      spoilageSigns: "Sharp sinus-bite fading is the first sign (not spoilage, just age — still usable as milder mustard). Actual spoilage: mold, off smell beyond mustard's normal pungency, or separation that doesn't stir back together.",
      freezable: false,
      freezeNotes: "Freezing breaks the emulsion — it separates and the heat dulls. Fridge-only.",
    },
    substitutions: [
      { id: "vinegar",  tier: "emergency", note: "Loses the body and pungency. Add a pinch of mustard powder if you have it." },
    ],
    pairs: ["honey", "olive_oil", "balsamic", "chicken_breast"],
    flavor: {
      primary: ["sour", "bitter"],
      intensity: "strong",
      heatChange: {
        raw: "sharp sinus-tingling heat from volatile mustard oils; creamy wine body",
        cooked: "heat mellows fast (the pungent oils are heat-sensitive) — stir into sauces off the heat to keep the kick",
        charred: "mustard past a hard boil goes flat and slightly bitter — one-note",
      },
    },
    nutrition: { per: "100g", kcal: 60, protein_g: 4, fat_g: 3, carb_g: 5, fiber_g: 3, sodium_mg: 1100 },
    origin: "Dijon, Burgundy, France. The city has been a mustard-making center since the 1300s; the formula was codified in 1856 when Jean Naigeon substituted verjuice for the vinegar in the local recipe.",
    culturalNotes: "Despite the name, 'Dijon mustard' is not a protected designation — it can be made anywhere. Most 'Dijon' on US shelves is made in the United States or by Maille (now owned by Unilever) outside of Burgundy. The actual brown mustard seed used today is largely grown in Canada.",
    allergens: ["mustard"],
    diet: { vegan: true, vegetarian: true, keto: true, halal: false, kosher: "nonkosher", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Maille and Edmond Fallot are the standard-bearers. Fallot still stone-grinds the seeds and is the only major Dijon producer based in Burgundy. Grey Poupon (Kraft) is a fine cheap default.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Fallot (stone-ground, real Burgundy) is noticeably sharper and cleaner-tasting than supermarket Dijon. Maille is the reliable default. Grey Poupon is fine for a quick vinaigrette.",
    },
    skillDev: { skills: ["sauce", "seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  beef_stock: {
    description: "Long-simmered broth from roasted beef bones, mirepoix (carrot/onion/celery), and aromatics. Roasting the bones first is what gives it the deep brown color and meaty depth — unroasted bones make a much paler, less intense stock.",
    flavorProfile: "Deep, meaty, roasted; rich, gelatinous mouthfeel from the collagen",
    prepTips: "Reduce by half before using in pan sauces — boxed stock is engineered for safety, not concentration. To deepen any boxed stock: simmer with a splash of soy sauce, a halved onion, and a bay leaf for 20 minutes before using. Bone broth (longer simmer, more collagen) is the same idea taken to its logical end.",
    storage: {
      location: "pantry",
      shelfLifeDays: 365,
      shelfLife: { fridge: 5, freezer: 180, pantry: 365 },
      tips: "Boxed/canned: pantry until opened, then 4–5 days in the fridge. Homemade: 5 days in the fridge or 6 months in the freezer (ice-cube trays for measured small portions, quart bags lying flat for braises).",
      spoilageSigns: "Cloudy when it was clear, sour smell, slime on the surface, any mold on homemade — toss. Opened boxed stock past 5 fridge days is a coin flip; don't risk it.",
      freezable: true,
      freezeNotes: "Freeze in ice-cube trays (2 tbsp portions for pan sauces) then bag, or lay quart bags flat for full-braise portions. Thaws in minutes for cubes, overnight for bags.",
      prepYield: "One 32oz box = 4 cups. Better Than Bouillon: 1 tsp + 1 cup boiling water ≈ 1 cup stock.",
    },
    substitutions: [
      { id: "chicken_stock", tier: "direct",    note: "Lighter and less beefy. Add a splash of soy sauce + tomato paste to deepen." },
    ],
    pairs: ["red_wine", "tomato_paste", "yellow_onion", "carrot", "garlic"],
    flavor: {
      primary: ["umami", "sweet"],
      intensity: "medium",
      heatChange: {
        raw: "cold/room-temp stock is gelatinous (good — means real collagen); thin watery stock is a red flag",
        cooked: "simmered and reduced it concentrates to a glossy, meaty jus — pan sauce magic",
        charred: "a demi-glace reduction can catch on the pan — scorch = bitter; pull before it goes mahogany-dark",
      },
    },
    nutrition: { per: "100g", kcal: 7, protein_g: 1.3, fat_g: 0.2, carb_g: 0.1, sodium_mg: 363 },
    origin: "Stock-making is universal in cuisines that butcher whole animals. The French codified 'fond brun' (brown stock) in the 1700s; Escoffier's 'Le Guide Culinaire' (1903) made it the foundation of Western fine dining.",
    culturalNotes: "Real homemade beef stock is rare in modern home kitchens — it takes 6–8 hours and a stockpot of bones. Boxed stock is what most weeknight cooks actually use, and it's a perfectly respectable shortcut. The 'better than bouillon' style concentrated paste is often a better deal than boxed (more depth per dollar, longer shelf life).",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: true, kosher: "meat", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Better Than Bouillon's Roasted Beef Base is the home cook's best deal — one 8oz jar makes 9 quarts. For boxed: Kettle & Fire (more concentrated) or Swanson's organic beef stock. Avoid 'beef broth' which is weaker than stock by definition.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Better Than Bouillon concentrated paste blows most boxed stocks out of the water for depth per dollar. Homemade is the pro-tier move when you've got bones from a roast anyway.",
    },
    skillDev: { skills: ["sauce", "timing"], difficulty: "moderate", proFromScratch: true, fromScratchRecipeId: null },
  },

  chicken_stock: {
    description: "Simmered broth from chicken bones (carcasses, backs, wings), mirepoix, and aromatics. The all-purpose cooking liquid — lighter than beef stock, more savory than vegetable, the foundation of risotto, soups, braises, and pan sauces across most cuisines.",
    flavorProfile: "Clean, savory, gently sweet; silky mouthfeel when well-made (collagen from joints does the work)",
    prepTips: "For risotto, warm the stock before adding to the rice — cold stock shocks the grains and slows the starch release. Save chicken carcasses in the freezer; once you have 2–3, simmer them with carrots/celery/onion/parsley stems for 4 hours to make stock that's better than anything boxed.",
    storage: {
      location: "pantry",
      shelfLifeDays: 365,
      shelfLife: { fridge: 5, freezer: 180, pantry: 365 },
      tips: "Boxed/canned: pantry until opened, then 4–5 days in the fridge. Homemade: 5 days fridge / 6 months freezer. Frozen in ice-cube trays = perfect 2-tbsp portions for finishing pan sauces.",
      spoilageSigns: "Cloudy when it was clear, sour smell, surface slime or film, any mold — toss. Opened boxed past day 5 fridge is risky.",
      freezable: true,
      freezeNotes: "Ice-cube trays for pan-sauce portions (2 tbsp each), quart bags laid flat for risotto/braise batches. Frozen cubes melt in 60 seconds over low heat — perfect for deglazing.",
      prepYield: "One 32oz box = 4 cups. Better Than Bouillon: 1 tsp + 1 cup boiling water ≈ 1 cup stock. A standard chicken carcass + mirepoix makes ~2 quarts homemade.",
    },
    substitutions: [
      { id: "beef_stock", tier: "direct",    note: "Heavier and more assertive. Use half + half water if you only have beef stock." },
      { id: "white_wine", tier: "emergency", note: "For deglazing or risotto in a pinch — different flavor but works for the liquid role." },
    ],
    pairs: ["yellow_onion", "carrot", "garlic", "parsley", "butter"],
    flavor: {
      primary: ["umami", "sweet"],
      intensity: "mild",
      heatChange: {
        raw: "gelatinous when cold (good) — the jiggle is the collagen that silkens sauces",
        cooked: "simmered it turns silky; reduced by half it carries pan sauces that wouldn't need butter",
        charred: "stuck-to-the-pan reduction at high heat goes bitter fast — pull before it darkens past caramel",
      },
    },
    nutrition: { per: "100g", kcal: 6, protein_g: 0.9, fat_g: 0.2, carb_g: 0.5, sodium_mg: 343 },
    origin: "Universal — every cuisine that cooks chicken makes some form of chicken stock. The French 'fond blanc' (white stock, no roasting) is the version that became the Western culinary standard.",
    culturalNotes: "Chicken stock was the first 'restaurant' food — the word 'restaurant' originally referred to the restorative meat broths sold from 18th-century Parisian shops. The cuisine took its name from the soup, not the other way around.",
    allergens: [],
    diet: { vegan: false, vegetarian: false, keto: true, halal: true, kosher: "meat", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Better Than Bouillon Roasted Chicken Base is an excellent default. For boxed: Swanson's organic, Kettle & Fire bone broth, or any brand where 'chicken' (not 'chicken broth' or 'chicken flavor') is the first ingredient. Low-sodium versions give you more control.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Homemade from saved carcasses is dramatically better — and free. For shortcuts: Better Than Bouillon concentrate > most boxed stocks. Avoid generic \"chicken broth\" which is watered-down by definition.",
    },
    skillDev: { skills: ["sauce", "timing"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: "homemade-chicken-stock" },
  },

  red_wine: {
    description: "Cooking red wine — use something you'd be willing to drink. Medium-bodied dry reds (Chianti, Côtes du Rhône, Cabernet, Merlot) are most versatile. Avoid 'cooking wine' from the supermarket aisle — it's loaded with salt and tastes like jet fuel.",
    flavorProfile: "Tannic, fruity, acidic; concentrates dramatically as it reduces — what was balanced wine becomes intense fruit-and-tannin syrup",
    prepTips: "For red wine pan sauces and braises: deglaze with the wine FIRST, then reduce by half before adding stock. Cooking the alcohol off (~5 minutes of vigorous bubbling) is what removes the harsh ethanol bite and lets the fruit flavors shine. Open a bottle for braising, drink the rest with dinner.",
    storage: {
      location: "pantry",
      shelfLifeDays: 730,
      shelfLife: { fridge: 21, freezer: null, pantry: 730 },
      tips: "Sealed bottle: years in a cool, dark cabinet. Once opened: 3–5 days with a stopper, longer if you transfer to a smaller bottle (less air contact). For cooking specifically: opened wine is fine for braises for 2–3 weeks if refrigerated.",
      spoilageSigns: "Vinegar smell and sharp sourness = oxidized past the \"cookable\" point. Cloudiness, odd funky smells, or brown color in a red mean it's gone. For drinking it's done; for cooking the vinegar-tinged can still sneak into braises.",
      freezable: false,
      freezeNotes: "Skip freezing full bottles. Trick: pour leftover wine into ice-cube trays (1 cube ≈ 1 oz) for easy pan-sauce portions — lasts 6 months.",
    },
    substitutions: [
      { id: "beef_stock",       tier: "direct",    note: "Loses the acidity and fruit. Add a splash of balsamic or red wine vinegar to compensate." },
      { id: "white_wine",       tier: "emergency", note: "Different flavor profile but works for deglazing in a pinch — lighter, brighter result." },
    ],
    pairs: ["beef_stock", "tomato_paste", "yellow_onion", "garlic", "brisket"],
    flavor: {
      primary: ["sour", "bitter"],
      intensity: "strong",
      heatChange: {
        raw: "tannic, acidic, fruity — drinking wine in the glass",
        cooked: "reduced by half the alcohol burns off; fruit and tannin concentrate to glossy, syrupy pan-sauce gold",
        charred: "sticks and burns if the pan goes dry — bitter, acrid, unsalvageable; keep stirring and add stock",
      },
    },
    nutrition: { per: "100g", kcal: 85, protein_g: 0.1, fat_g: 0, carb_g: 2.6, sodium_mg: 4 },
    origin: "Wine in cooking is as old as wine itself (~6,000 BCE). The French codified its role in haute cuisine — coq au vin, boeuf bourguignon, daube provençal — using local Burgundy and Rhône wines for both the dish and the table.",
    culturalNotes: "'Cooking wine' from the supermarket aisle is salted to make it non-potable (so it can be sold in dry counties / outside of liquor licenses). Salt content is so high it'll wreck a sauce — always cook with real wine you'd drink. Two-buck Chuck (Charles Shaw) is fine for most braises.",
    allergens: ["sulfites"],
    diet: { vegan: true, vegetarian: true, keto: true, halal: false, kosher: "nonkosher", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "For braising: a $10–12 Côtes du Rhône or basic Chianti is the value sweet spot. Don't waste expensive bottles on long cooks (the nuance disappears) but don't go cheaper than something you'd actually drink — the wine is the dominant flavor in red wine braises.",
    market: {
      priceTier: "mid",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Not about bottle price — about drinkability. A $10 bottle you'd pour at the table beats a $30 fancy one (nuance disappears in the pot) but both beat \"cooking wine\" aisle salt-bombs by a mile.",
    },
    skillDev: { skills: ["sauce", "heat"], difficulty: "moderate", proFromScratch: false, fromScratchRecipeId: null },
  },

  white_wine: {
    description: "Cooking white wine — dry, crisp, unoaked. Pinot Grigio, Sauvignon Blanc, Vermentino, or dry Vermouth all work. Avoid sweet wines (Moscato, Riesling) and heavily oaked Chardonnay — both turn cloying or bitter when reduced.",
    flavorProfile: "Crisp, acidic, aromatic; brightens and lifts sauces as it reduces (where red wine deepens, white wine clarifies)",
    prepTips: "Dry vermouth (Noilly Prat, Dolin) is the secret weapon — it keeps for months opened (it's fortified), and a bottle costs about the same as one bottle of cooking wine but lasts 10× as long. Use it 1:1 anywhere a recipe calls for white wine.",
    storage: {
      location: "pantry",
      shelfLifeDays: 730,
      shelfLife: { fridge: 7, freezer: null, pantry: 730 },
      tips: "Sealed: years in a cool dark cabinet. Once opened: 2–3 days in the fridge with a stopper. Vermouth opened: 2–3 months refrigerated. For cooking, opened wine is usable for about a week.",
      spoilageSigns: "Sharp vinegar smell, brown tint (white wine should stay pale gold), or loss of all aroma = oxidized. For cooking, vermouth's longer window makes it the safer pantry bet.",
      freezable: false,
      freezeNotes: "Skip freezing bottles. Trick: ice-cube trays for leftover wine (1 cube ≈ 1 oz) for finishing pan sauces — 6 months in the freezer.",
    },
    substitutions: [
      // Cooking wines come FIRST — closest matches by flavor profile
      // (dry, acidic, aromatic). Chicken stock works but loses
      // brightness; it's a "savory cooked applications only" sub, not
      // the headline answer for "white wine called for in recipe."
      { id: "sake",            tier: "direct",    note: "Closest 1:1 match — also dry, aromatic, and reduces similarly. Best in Asian-leaning dishes; works fine in Western pan sauces too." },
      { id: "mirin",           tier: "caution",   note: "Sweeter than wine — use ⅔ the amount and skip any added sugar in the step. Excellent in glazes, stir-fries, teriyaki-style reductions." },
      { id: "chicken_stock",   tier: "caution",   note: "Loses brightness — add a splash of vinegar (white wine, champagne, or lemon juice) to compensate. Best for savory cooked applications (braises, risotto, pan sauces); skip for raw / dessert / fruit-forward dishes." },
      { id: "white_wine_vinegar", tier: "emergency", note: "Use ¼ the amount + ¾ water; the acidity is right but no body or aromatics. Last resort." },
    ],
    pairs: ["chicken_stock", "shallot", "butter", "lemon", "garlic"],
    flavor: {
      primary: ["sour"],
      intensity: "medium",
      heatChange: {
        raw: "crisp, acidic, aromatic — the glass pour",
        cooked: "reduces to a bright, wine-forward concentrate that brightens cream and butter sauces (where red deepens, white clarifies)",
        charred: "pans that run dry scorch fast — wine sugars go from bright to bitter in seconds; add stock or water before it burns",
      },
    },
    nutrition: { per: "100g", kcal: 82, protein_g: 0.1, fat_g: 0, carb_g: 2.6, sodium_mg: 5 },
    origin: "Wine in cooking is universal in wine-producing cultures. The French use of white wine for fish and chicken sauces (beurre blanc, chicken in white wine) is the technique most Western cuisines have inherited.",
    culturalNotes: "Risotto al vino bianco — adding white wine to risotto right after the toasting step — is non-negotiable in Italian technique. The acid balances the richness of the cheese and butter at the end; without it, the dish tastes flat.",
    allergens: ["sulfites"],
    diet: { vegan: true, vegetarian: true, keto: true, halal: false, kosher: "nonkosher", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "For cooking: any dry $8–12 Pinot Grigio or Sauvignon Blanc. Better answer: keep a bottle of dry vermouth on hand. For mussels in white wine: a Muscadet from the Loire is the classic match.",
    market: {
      priceTier: "mid",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Dry vermouth is the smart buy — lasts 2-3 months opened (fortified) vs. 2-3 days for regular white wine. One bottle does the work of ten for cooking.",
    },
    skillDev: { skills: ["sauce", "heat"], difficulty: "moderate", proFromScratch: false, fromScratchRecipeId: null },
  },

  tomato_paste: {
    description: "Concentrated tomato purée, cooked and reduced until it's a brick-red paste. Adds depth, color, and umami to anything tomato-adjacent — and to plenty of things that aren't (chili, curry, French onion soup, beef stews).",
    flavorProfile: "Concentrated tomato umami; sweet-tart, slightly bitter; almost meaty when caramelized",
    prepTips: "ALWAYS bloom it in hot fat for 1–2 minutes before adding liquid — the color goes from bright red to brick red, the raw acidity mellows, and the umami compounds (glutamates) get released. This single step is what separates restaurant tomato sauces from home cooking.",
    storage: {
      location: "pantry",
      shelfLifeDays: 730,
      shelfLife: { fridge: 42, freezer: 180, pantry: 730 },
      tips: "Sealed can/tube: 2 years pantry. Once opened: tubes keep 6 weeks in the fridge; opened cans should be transferred to a jar or freezer (freeze in 1-tbsp dollops on parchment, then bag — perfect portions for any recipe).",
      spoilageSigns: "Dark discoloration (past brick-red to brown-black), mold anywhere on the surface, bulging can = botulism risk — toss without tasting. A natural dark skin on opened paste just needs scraping; deeper off-color means gone.",
      freezable: true,
      freezeNotes: "The portion-saver trick: scoop 1-tbsp dollops onto parchment, freeze solid, transfer to a zip-top bag. Pull the exact number of tbsp you need straight into a hot pan; no thaw needed.",
      prepYield: "1 standard 6oz can ≈ 12 tbsp (perfect for the dollop-freeze portioning). A good tube delivers ~1 tbsp per 2-inch squeeze.",
    },
    substitutions: [
      { id: "canned_tomatoes", tier: "emergency", note: "Use 3× the volume and reduce in the pan to drive off the water. Fresher flavor, less concentrated." },
    ],
    pairs: ["olive_oil", "garlic", "yellow_onion", "red_wine", "ground_beef"],
    flavor: {
      primary: ["umami", "sweet"],
      intensity: "strong",
      heatChange: {
        raw: "bright red, tart, slightly bitter — unpleasant straight from the can",
        cooked: "blooming in hot fat turns it brick-red; glutamates release, acidity mellows, depth blooms — the single move that separates restaurant sauce from home cooking",
        charred: "over-blooming scorches it bitter and one-note; pull before it goes past deep mahogany",
      },
    },
    nutrition: { per: "100g", kcal: 82, protein_g: 4.3, fat_g: 0.5, carb_g: 19, fiber_g: 4.1, sodium_mg: 59 },
    origin: "Italy and the United States — the canning of tomato concentrate scaled commercially in the late 1800s. Mutti (Parma, Italy, est. 1899) is generally considered the original modern producer.",
    culturalNotes: "European cooks (especially Italian and French) reach for tubed tomato paste — easier portioning, no half-can waste. American cooks default to the 6oz can, then throw out 5oz unused. The tube is one of the great kitchen upgrades for $4.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: true, allium: false },
    seasonality: { yearRound: true, preservedAvailable: true },
    sourcing: "Mutti double-concentrate (tube or can) is the gold standard — Italian-grown tomatoes, no additives, distinctly brighter flavor than American mass-market brands. Cento is a respectable American option. Avoid pastes with 'tomato puree' as the first ingredient (it's diluted) — real paste lists 'tomatoes' first.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Mutti double-concentrate in a tube is transformative — brighter, cleaner, and the tube kills the half-can-waste problem. Cento is the respectable American fallback.",
    },
    skillDev: { skills: ["sauce", "heat"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  // ── compounds with working scratch recipes (Phase 1 of the compound-
  // ingredient work). Each of these is BOTH a pantry ingredient AND a
  // thing you can make yourself — fromScratchRecipeId points at the
  // recipe; IngredientCard renders a "🔪 make from scratch" link.
  sriracha: {
    description: "Bright red, garlicky chili sauce — originally from Si Racha, Thailand, but now a global condiment. Huy Fong's rooster bottle is the iconic American version; a hundred imitators and regional takes. Heat + garlic + sugar + vinegar in one bottle.",
    flavorProfile: "Hot, garlicky, tangy, slightly sweet; more fragrant than pure heat — capsaicin plays second fiddle to the fermented-chili-and-garlic combo",
    prepTips: "Squirt into mayo (1:4) for the sriracha-mayo every sandwich should have. Whisk into honey for a wings glaze. Stir into soup noodles or scrambled eggs. Cook it briefly and the flavors marry; leave it raw and it punches.",
    storage: {
      location: "pantry",
      shelfLifeDays: 730, // unopened commercial; opened drops to ~365
      shelfLife: { fridge: 365, freezer: null, pantry: 730 },
      tips: "Unopened commercial: pantry for years. Once opened: most people leave it in the pantry and it's fine; fridge extends freshness but changes the texture (gets thicker). Homemade: always fridge, ~3 months.",
      spoilageSigns: "Color shift from bright red to brown, loss of aroma, mold at the bottle neck — toss. Vinegar separation alone is fine, just shake.",
      freezable: false,
      freezeNotes: "No benefit — the texture breaks on thaw.",
    },
    substitutions: [
      { id: "hot_sauce", tier: "direct",    note: "Any vinegar-based hot sauce works for heat, though you lose the garlic punch." },
      { id: "gochujang", tier: "pro",       note: "Korean chili paste — thicker, fermented, less sweet. Different flavor but a pro swap in marinades." },
    ],
    pairs: ["mayo", "honey", "lime", "eggs", "soy_sauce"],
    flavor: {
      primary: ["umami", "sour", "sweet"],
      intensity: "strong",
      heatChange: {
        raw: "the signature raw condiment — fragrant garlic and fermented-chili edge",
        cooked: "simmered into a sauce it mellows and the garlic rounds out; great glaze base",
        charred: "direct flame scorches the sugars and goes bitter — brush on at the end, not the start",
      },
    },
    nutrition: { per: "1 tsp", kcal: 5, protein_g: 0.1, fat_g: 0, carb_g: 1, sodium_mg: 90 },
    origin: "Si Racha, Thailand, where coastal cooks made versions for a local seafood restaurant from the 1930s onward. Huy Fong Foods (founded by David Tran, a Vietnamese refugee) launched the rooster bottle in the US in 1980 — American sriracha diverged from Thai sriracha immediately.",
    culturalNotes: "The Huy Fong rooster bottle is NOT what most Thai people think of as sriracha — their local sauces (like Sriraja Panich) are sweeter, thinner, and less garlicky. American sriracha is its own genre at this point.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "high", nightshade: true, allium: true },
    seasonality: { yearRound: true, preservedAvailable: true },
    sourcing: "Huy Fong is the default and has improved again since their 2022 supply shortage. Lee Kum Kee and Shark brand are decent alternatives. Homemade is transformative if you can get fresh red Fresno chilies.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Homemade sriracha from fresh Fresnos is a different food than the bottle — brighter, more garlic, less cloying. If you cook enough to justify the 30-min batch, do it.",
    },
    skillDev: { skills: ["sauce", "seasoning"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: "sriracha" },
  },

  pesto: {
    description: "Ligurian uncooked sauce — basil, pine nuts, Parmigiano, Pecorino, garlic, olive oil, salt. The scratch version in the fridge is a different food than the shelf-stable jar; both have their place.",
    flavorProfile: "Bright, herbaceous, nutty, salty; high-quality pesto has a grassy pepperiness from good EVOO and a buttery richness from pine nuts",
    prepTips: "Toss with hot pasta OFF the heat (direct contact with a boiling pot breaks the sauce). Loosen with pasta water, never more oil. On toast under eggs. On caprese. On roast chicken. On pizza after it's out of the oven.",
    storage: {
      location: "fridge",
      shelfLifeDays: 7, // homemade; shelf-stable jar is pantry for months
      shelfLife: { fridge: 7, freezer: 90, pantry: 180 },
      tips: "Homemade: fridge 1 week under a film of olive oil (the film blocks oxygen, keeps the surface green). Shelf-stable jar: pantry until opened, then 2–3 weeks fridge. Freezes beautifully in ice-cube trays.",
      spoilageSigns: "Dark olive-drab or brown color instead of bright green = oxidized (still edible but sad). White fuzzy mold on the surface = toss.",
      freezable: true,
      freezeNotes: "The save-my-basil-glut move. Freeze in ice-cube trays, transfer cubes to a bag. 2 cubes = enough for a pasta portion. Thaws in 30 seconds tossed into hot pasta.",
      prepYield: "One batch of the scratch recipe yields ~1 cup (240g) — enough to sauce pasta for 4 generously.",
    },
    substitutions: [
      { id: "basil",       tier: "emergency", note: "Raw basil + good olive oil + cheese is a minute-to-make rough approximation when you're out of pesto." },
    ],
    pairs: ["spaghetti", "mozzarella", "tomato", "chicken_breast", "bread"],
    flavor: {
      primary: ["umami", "bitter", "sweet"],
      intensity: "medium",
      heatChange: {
        raw: "the point of pesto — bright, herbaceous, never cooked",
        cooked: "direct heat dulls the basil instantly; toss with hot pasta OFF the heat",
        charred: "don't. Past 150°F the sauce breaks and goes bitter; brushed on pizza post-bake is the limit.",
      },
    },
    nutrition: { per: "2 tbsp", kcal: 180, protein_g: 3, fat_g: 18, carb_g: 2, sodium_mg: 210 },
    origin: "Genoa, Liguria, Italy. The tradition is mortar-and-pestle (hence the name 'pestare' = to pound) using the small-leaf Genovese basil variety. 'Pesto alla Genovese' is a DOP designation with specific requirements.",
    culturalNotes: "Real Genovese pesto is made with a marble mortar and wooden pestle at a specific rotation speed — bruising not grinding. Food processor pesto is faster but a different texture. Both are defensible; pretend otherwise at your peril.",
    allergens: ["tree_nuts", "dairy"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: true, kosher: "dairy", fodmap: "moderate", nightshade: false, allium: true },
    seasonality: { yearRound: true, preservedAvailable: true },
    sourcing: "For jarred: Barilla is fine, Rana is better, anything labeled 'Genovese DOP' is the real thing (at a real price). Homemade with good basil and real Parmigiano-Reggiano beats any jar.",
    market: {
      priceTier: "mid",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Supermarket pesto is almost always stabilized with other oils and less cheese than the name implies. A scratch batch from one $3 bunch of basil is both cheaper and better.",
    },
    skillDev: { skills: ["sauce", "seasoning", "knife"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: "pesto" },
  },

  // ── condiments: American staples (batch 1) ─────────────────────────
  mayo: {
    description: "Egg-yolk emulsion of oil, acid (lemon or vinegar), mustard, salt. Hellmann's (east of the Rockies) and Best Foods (same jar, west of the Rockies) are the American benchmark. Kewpie (Japan) is eggier and sweeter. Duke's (the South) uses more yolk and no sugar. Homemade is a 90-second job with a stick blender.",
    flavorProfile: "Creamy, tangy, faintly sweet; a good mayo tastes of egg and oil first, acid second. Bad mayo tastes of plastic and sugar.",
    prepTips: "Whisk with lemon juice + pepper + a dab of dijon for the best sandwich spread there is. Mix 3:1 with sriracha for spicy mayo (every Japanese-steakhouse trick). Fold into canned tuna or chicken with mustard + relish — a salad in 2 minutes.",
    storage: {
      location: "fridge",
      shelfLifeDays: 90, // opened; sealed commercial keeps 3–4 months pantry and then fridge after opening
      shelfLife: { fridge: 90, freezer: null, pantry: 180 },
      tips: "Commercial mayo is shelf-stable sealed; refrigerate after opening. Homemade is ALWAYS fridge-only, 5–7 days tops. The jar lid and the tool you scoop with are the infection vectors — clean spoon, always.",
      spoilageSigns: "Separation that doesn't stir back in, sour/off smell, yellowing at the top surface, any pink or black spots. Opened jar past 2–3 months in the fridge is a coin flip; sniff before using.",
      freezable: false,
      freezeNotes: "Don't. Mayo freezes but the emulsion breaks irrevocably on thaw — you'll get oil-slicked water and sad egg solids.",
    },
    substitutions: [
      { id: "sour_cream", tier: "direct",    note: "For dips and dressings; tangier, less fat. 1:1 swap." },
      { id: "avocado",    tier: "dietary",   note: "Mashed avocado + lemon is a vegan sandwich spread that plays a similar role." },
    ],
    pairs: ["dijon", "sriracha", "lemon", "tuna", "bread"],
    flavor: {
      primary: ["umami", "sour"],
      intensity: "medium",
      heatChange: {
        raw: "the condiment itself — creamy, tangy, binding",
        cooked: "stirred into pan sauces off-heat it smooths; direct heat breaks the emulsion — mayo-roasted potatoes use the film as an oil carrier, not a sauce",
        charred: "brushed on grilled sandwiches (Duke's melt, Cuban press) it crisps the bread beautifully — the mayo is the frying fat",
      },
    },
    nutrition: { per: "1 tbsp", kcal: 94, protein_g: 0.1, fat_g: 10, carb_g: 0.4, sodium_mg: 88 },
    origin: "Generally credited to 18th-century French cooks; the name may come from Mahón, Menorca (1756 French siege) where chef to the Duc de Richelieu supposedly improvised an egg-oil sauce. Modern jarred mayo is a 20th-century American invention — Hellmann's launched in 1905 from a NYC deli.",
    culturalNotes: "Mayo is one of the most regionally-loved condiments on earth — Japan's Kewpie (MSG + rice vinegar + yolks-only) inspires cult devotion, Belgium puts mayo on frites as the default (ketchup is for tourists), and the US South guards Duke's like a state secret. Kraft-brand Miracle Whip is NOT mayo — it's a \"dressing\" with sugar and spices. Don't confuse.",
    allergens: ["egg"],
    diet: { vegan: false, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Hellmann's/Best Foods for the default American. Duke's for the sweet-free South style. Kewpie for the eggy/umami Japanese style — worth owning alongside regular. Avoid anything calling itself \"salad dressing\" on the label.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: true,
      qualityNote: "Kewpie vs. Hellmann's vs. Duke's is a real flavor-difference question, not a snobbery one. Pick the one that matches what you're making — Kewpie for sushi-adjacent, Duke's for pimento cheese, Hellmann's for everything else.",
    },
    skillDev: { skills: ["sauce", "seasoning"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },

  ketchup: {
    description: "Tomato-vinegar-sugar-spice paste, a 400-year journey from a fermented fish sauce in SE Asia (kê-tsiap) through English walnut-and-mushroom versions to Heinz's 1876 bottled tomato version — the one that every American now means. Heinz's 57 is sweet-tart-salty-umami balance that other brands chase.",
    flavorProfile: "Sweet, tart, salty, faintly spiced (clove, allspice); tomato umami undergirds everything. A great ketchup is balanced; a bad one is cloying.",
    prepTips: "Whisk with soy sauce + garlic + a splash of vinegar for a 90-second BBQ-sauce base. Fold into ground beef for meatloaf binding (1 tbsp/lb). Drizzle + sriracha for spicy ketchup. Dip. Fry. Burger. Hash browns.",
    storage: {
      location: "fridge",
      shelfLifeDays: 180, // opened fridge; sealed keeps years pantry
      shelfLife: { fridge: 180, freezer: null, pantry: 365 },
      tips: "Shelf-stable sealed for a year or more. After opening: most people shelf it at room temp and it's fine for 4–6 weeks, but fridge extends to 6 months without flavor loss. Glass bottle + dark cabinet > plastic squeeze.",
      spoilageSigns: "Brown/maroon darkening (oxidation — still safe but flavor dulls), mold around the cap threads, any fizz or gas when opened. Crystallized sugar at the neck is normal and fine.",
      freezable: false,
      freezeNotes: "No benefit — the water separates and the texture goes grainy on thaw.",
    },
    substitutions: [
      { id: "tomato_paste", tier: "emergency", note: "Tomato paste + vinegar + sugar + salt in a pinch. Closer to homemade ketchup than you'd think." },
    ],
    pairs: ["mustard", "mayo", "hot_sauce", "worcestershire", "ground_beef"],
    flavor: {
      primary: ["sweet", "umami", "sour"],
      intensity: "medium",
      heatChange: {
        raw: "the condiment — cold, sharp, tangy-sweet",
        cooked: "reduces into a sticky glaze on burgers and wings; caramelizes on meatloaf tops",
        charred: "brushed on ribs and smoked over wood it lacquers; past deep mahogany the sugars scorch bitter",
      },
    },
    nutrition: { per: "1 tbsp", kcal: 17, protein_g: 0.2, fat_g: 0, carb_g: 4.5, sodium_mg: 154 },
    origin: "Kê-tsiap — 17th-century Fujianese fermented fish sauce — traveled on merchant ships to SE Asia and England. English cooks tried walnut and mushroom versions for 150 years before tomato became the default in 1812 (James Mease, Philadelphia). Heinz bottled it in 1876; added cider vinegar to replace coal-tar preservative in 1906 — the current formula.",
    culturalNotes: "Americans put ketchup on everything (eggs, hot dogs, hash browns); much of the rest of the world finds this alarming. The Filipino banana ketchup (sweeter, brighter, WWII improvisation) is a separate, delicious thing worth knowing. \"Catsup\" is the same product with a dying spelling.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: false, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: true, allium: false },
    seasonality: { yearRound: true },
    sourcing: "Heinz is the benchmark — their 57 blend is genuinely hard to beat. Sir Kensington's (organic, less sweet) is the upmarket alternative. Primal Kitchen does a sugar-free version for keto. Banana ketchup from the Philippines (Jufran, UFC) for tropical heat.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: true,
      qualityMatters: false,
      qualityNote: "Heinz is the gold standard and a nostalgia artifact — trying to \"upgrade\" ketchup often just makes it worse. Save fancy spending for mustard.",
    },
    skillDev: { skills: ["sauce"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },

  mustard: {
    description: "Yellow mustard — ground mustard seed + turmeric (the yellow) + vinegar + salt. French's is the American default and a 1904 St. Louis World's Fair debut. Sharper than Dijon but a one-note punch compared to the wine-forward complexity of the French version.",
    flavorProfile: "Bright yellow, sharp, vinegar-forward, mildly pungent; the sinus-tingle is milder than Dijon's because ground yellow mustard seed has less allyl isothiocyanate than brown.",
    prepTips: "The right mustard on a hot dog and the wrong one on a ham sandwich — yellow for ballpark food, Dijon for vinaigrettes and pan sauces, whole grain for pretzels and charcuterie. Mix 1:1 with mayo for a sandwich spread. Blend into deviled eggs.",
    storage: {
      location: "fridge",
      shelfLifeDays: 365,
      shelfLife: { fridge: 365, freezer: null, pantry: 60 },
      tips: "Refrigerate after opening. Keeps nearly a year in the fridge; flavor dulls after 6 months but still safe. Pantry storage is fine for a couple months unopened, but opened-at-room-temp drops to 4–6 weeks before the heat fades noticeably.",
      spoilageSigns: "Dark crust on top (scrape and use), actual mold, separation that doesn't stir, sour smell beyond mustard's normal sharpness. Darkening alone is not spoilage.",
      freezable: false,
      freezeNotes: "Freezing breaks the emulsion and the heat dulls further. Fridge only.",
    },
    substitutions: [
      { id: "dijon",   tier: "pro",       note: "Upgrade pick — wine-based, smoother, more complex. Better in vinaigrettes and sauces." },
    ],
    pairs: ["ketchup", "mayo", "honey", "sausage", "bread"],
    flavor: {
      primary: ["sour", "bitter"],
      intensity: "medium",
      heatChange: {
        raw: "sharp, vinegar-forward, mild sinus tingle",
        cooked: "heat dulls the pungency fast — add late to pan sauces, never at the start",
        charred: "mustard past a hard boil goes flat and slightly bitter; glazes on ham bake well because of the sugar, not the mustard",
      },
    },
    nutrition: { per: "1 tsp", kcal: 3, protein_g: 0.2, fat_g: 0.2, carb_g: 0.3, sodium_mg: 57 },
    origin: "St. Louis, 1904 — French's debuted at the World's Fair as \"cream salad mustard\" and rode the hot-dog boom. Turmeric was the coloring because ground yellow mustard seed alone is beige. The name \"French's\" has nothing to do with France; it's the founding family's name.",
    culturalNotes: "Yellow mustard on ballpark hot dogs is an American identity artifact. Most of Europe thinks it's too mild; Eastern Europe prefers brown Düsseldorf-style; Japan's karashi is a horseradish-fueled different thing. English mustard (Colman's) is the nuclear version.",
    allergens: ["mustard"],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "low", nightshade: false, allium: false },
    seasonality: { yearRound: true },
    sourcing: "French's is the benchmark. Gulden's is a slightly spicier Brooklyn version. Plochman's (Midwest) is the underdog pick. Colman's English mustard (yellow, loose-powder) for serious heat.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: false,
      qualityNote: "Yellow mustard is commodity. If you're cooking where mustard matters, reach for Dijon or whole-grain — those brand differences are real.",
    },
    skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: false, fromScratchRecipeId: null },
  },

  hot_sauce: {
    description: "Vinegar-forward cayenne-pepper sauce — the Tabasco family (fermented Louisiana), the Frank's family (Buffalo wing origin), the Crystal/Louisiana family (thin, sharp, not sweet). Distinct from sriracha (thick, garlicky, sweet) and chili oil (oil-based). Every major style has a role.",
    flavorProfile: "Sharp vinegar, cayenne heat, salt; Louisiana-style leans thin and pure, Frank's adds garlic, Tabasco adds a light wood-barrel ferment funk.",
    prepTips: "Shake into eggs, soups, beans, chili, anything braised. Mix 1:1 with melted butter for Buffalo sauce (this is literally the recipe). A dash into mayo or aioli instantly lifts a sandwich. Don't boil — the volatile heat compounds flash off at high heat.",
    storage: {
      location: "pantry",
      shelfLifeDays: 730,
      shelfLife: { fridge: 1095, freezer: null, pantry: 730 },
      tips: "Vinegar + salt + ferment = effectively immortal. Pantry unopened indefinitely; opened works fine pantry for 1–2 years (you'll use it well before it matters). Fridge extends color and heat for 3+ years but most people don't bother.",
      spoilageSigns: "Color shift from bright red to brown (oxidation, still safe), loss of kick, separation that doesn't shake back. Actual spoilage (mold, fizz) is essentially never — the vinegar and salt are intense preservatives.",
      freezable: false,
      freezeNotes: "Pointless — it's already more shelf-stable than your freezer.",
    },
    substitutions: [
      { id: "sriracha",  tier: "direct",    note: "Thicker, garlicky, sweeter; works for most applications but changes the profile meaningfully." },
      { id: "gochujang", tier: "pro",       note: "Korean fermented chili paste — thicker, umami-forward, less vinegar. Different idiom, excellent pick in marinades and stews." },
    ],
    pairs: ["butter", "eggs", "mayo", "ketchup", "chicken_wing"],
    flavor: {
      primary: ["sour", "bitter"],
      intensity: "strong",
      heatChange: {
        raw: "the point — raw vinegar heat, immediate capsaicin kick",
        cooked: "reduces into glazes and braises nicely; half the heat carries through, the other half flashes off",
        charred: "brushed on meat over direct flame and the vinegar scorches fast — brush in the last 2 minutes, not at the start",
      },
    },
    nutrition: { per: "1 tsp", kcal: 1, protein_g: 0.1, fat_g: 0, carb_g: 0.2, sodium_mg: 130 },
    origin: "Louisiana, 1860s–1870s — Tabasco (Edmund McIlhenny, Avery Island, 1868) is the oldest commercial American. Frank's RedHot (Cincinnati, 1920) invented Buffalo wings via Anchor Bar NY in 1964. Crystal (New Orleans, 1923) is the thin working-class benchmark most Southerners prefer for gumbo and eggs.",
    culturalNotes: "The \"hot sauce\" aisle has exploded from 3 bottles in 1990 to hundreds today — a lot of them are marketing, not better sauce. The Louisiana trinity (Tabasco/Frank's/Crystal) plus a good habanero bottle covers 90% of cooking. Everything else is novelty until you find one you actually love.",
    allergens: [],
    diet: { vegan: true, vegetarian: true, keto: true, halal: true, kosher: "pareve", fodmap: "moderate", nightshade: true, allium: false },
    seasonality: { yearRound: true, preservedAvailable: true },
    sourcing: "Tabasco for classic fermented vinegar-forward. Frank's for Buffalo wings (it's literally the original recipe). Crystal for Louisiana cooking. Yucateco (green habanero, yellow habanero) for heat with actual flavor. Cholula if you want a medium everyday bottle.",
    market: {
      priceTier: "budget",
      availability: "supermarket",
      organicCommon: false,
      qualityMatters: true,
      qualityNote: "Different sauces have different jobs — Tabasco is not a Buffalo sauce, Frank's is not Louisiana gumbo hot sauce. Own 2–3 styles; skip the boutique $15 bottles with death-metal labels.",
    },
    skillDev: { skills: ["seasoning"], difficulty: "easy", proFromScratch: true, fromScratchRecipeId: null },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Technique hints — keyed object on INGREDIENT_INFO[id].technique.
//
// Hand-curated, ≤ 1 sentence per technique. Time + doneness signal +
// critical detail. The recipe generator's FINAL pass injects these into
// the prompt so Claude can write prose grounded in real timings ("35-40 min
// caramelize") instead of generic timings ("until soft, ~10 min").
//
// Top-50 staples — the canonicals that show up in most recipes. Keep
// this list opinionated and tight; bloat = prompt cost. Add new entries
// only when the field would actually shape AI output (i.e. the AI gets
// it wrong without the hint).
//
// Defined as a separate block then merged into INGREDIENT_INFO below so
// the entries can be added/removed without scrolling through 2,400 lines
// of ingredient definitions.
// ─────────────────────────────────────────────────────────────────────────────
const TECHNIQUES = {
  yellow_onion: {
    caramelize: "35-40 min in butter + pinch salt over med-low, stir every 5 min until deep amber. Splash of water if sticking. NOT 10 min — true caramelization is slow.",
    sauté:      "5-7 min in fat over medium, translucent and softened, no browning.",
    sweat:      "8-10 min over low, covered, until limp; no color.",
    brown:      "12-15 min over med-high, stir less to develop fond, golden edges.",
    raw:        "Slice paper-thin, soak in cold water 10 min to mellow bite.",
  },
  garlic: {
    sauté:  "30-60 sec in fat over medium until fragrant and pale gold — burns fast. Add AFTER onions, not with.",
    roast:  "Whole head, top trimmed, drizzle oil, 400°F 40 min wrapped in foil; squeeze cloves out.",
    raw:    "Microplane or paste with salt for vinaigrettes; mellows over hours in acid.",
    bloom:  "Sliced into cold oil heating gently, 5-7 min until just sizzling — infuses without burning.",
    confit: "Whole peeled cloves submerged in oil at 200°F, 1-2 hr until soft and spreadable.",
  },
  shallot: {
    mince:  "Fine for vinaigrettes (bigger pieces stay crunchy); brunoise for refined sauces.",
    sauté:  "2-3 min in butter over medium until softened and translucent — faster than onion, watch closely.",
    fry:    "Crispy fried shallots: thin-sliced, low oil, 5-8 min until golden brown; drain on paper.",
    pickle: "10 min in seasoned vinegar — pink, tangy, ready for tacos / grain bowls.",
    raw:    "Soak 10 min in cold water or vinegar to take edge off for salads.",
  },
  green_onion: {
    raw:          "Slice thin on the bias; treat green tops as a fresh herb, not background flavor.",
    grill:        "Whole stalks, hot grill 3-4 min total, char on outside until limp.",
    sauté:        "Whites first 1-2 min; greens at the end for color.",
    scallion_oil: "Sliced greens steeped in hot oil 30 sec off heat — neutral, brilliant green base for fish or noodles.",
  },
  ginger: {
    mince:   "Peel with a spoon edge (less waste); microplane for pastes, fine mince for stir-fry.",
    sauté:   "30-60 sec with garlic in hot oil to release oils — past 90 sec it gets bitter.",
    infuse:  "Steep coins in simmering broth 10-15 min; remove before serving.",
    pickle:  "Paper-thin slices in seasoned rice vinegar 1 hr — gari for sushi or grain bowls.",
    candied: "Slow simmer in sugar syrup 45 min, then coat — concentrated heat + sweetness.",
  },
  chicken_breast: {
    sear:  "Pat DRY, salt, hot pan med-high in oil; 4-5 min presentation side undisturbed, flip 3-4 min more. Pull at 160°F + rest to 165°F.",
    poach: "Submerge in barely simmering liquid (170-180°F), 15-18 min for a 6 oz breast.",
    grill: "Pound to even thickness first; med-high direct heat 5-6 min per side, internal 160°F.",
    roast: "Boneless: 400°F 18-22 min. Bone-in: 425°F 30-35 min. Rest 5 min before slicing.",
    brine: "30 min in 5% salt water transforms texture; pat dry before searing.",
  },
  chicken_thigh: {
    sear:   "Skin-on bone-in: skin-side down in COLD pan, med heat 10-12 min to render fat; flip 8-10 min more. Crisp skin payoff.",
    braise: "Brown both sides hard, then 35-45 min covered in liquid at 300°F or low simmer. More forgiving than breast.",
    grill:  "Boneless: 5-6 min per side, char marks fine — thighs tolerate higher heat than breast.",
    roast:  "Bone-in skin-on: 425°F 35-40 min until skin shatters. Boneless: 400°F 22-25 min.",
    confit: "Submerged in fat at 200°F, 2-3 hr until tender; chill, then crisp skin in hot pan.",
  },
  ground_beef: {
    sear:        "Hot pan med-high, DON'T crowd or stir — let it brown 3-4 min before breaking up. Crowding = steam = grey meat.",
    brown:       "Pat dry first, sear in batches if needed; deglaze with wine/stock to lift fond into sauce.",
    smashburger: "Loose 3 oz ball on screaming-hot griddle, smash hard 30 sec, flip after crust forms 90 sec more.",
    meatball:    "Mix gently — overworking = rubbery. Brown all sides, then finish in sauce 15-20 min.",
  },
  ground_chicken: {
    sauté:    "Less fat than beef — start in oil, hot pan, break into small bits and cook through 5-7 min. Easy to overcook to dry.",
    meatball: "Add fat (oil, mayo, ricotta) and breadcrumbs — leaner than beef, needs help with moisture.",
    larb:     "Cook in dry pan until just opaque + slightly browned, ~5 min; finish off heat with lime + fish sauce.",
  },
  salmon: {
    sear:  "Skin-on, pat dry, salt, hot pan med-high in oil; skin-side down 4-5 min undisturbed for crisp skin, flip 1-2 min. Internal 125°F for med-rare.",
    roast: "400°F 10-12 min per inch of thickness; pull at 125°F internal and rest.",
    poach: "Submerge in 170-180°F broth or court bouillon, 8-10 min for a 6 oz fillet.",
    cure:  "Equal parts salt + sugar, plus dill; 24-36 hr in fridge for gravlax.",
    raw:   "Sashimi-grade only; freeze 48 hr at -4°F first to kill parasites.",
  },
  shrimp: {
    sauté: "1-2 min per side in hot oil — they turn opaque and curl. Past 4 min total = rubber.",
    grill: "Skewered, med-high direct heat 2-3 min per side; shells on for more flavor.",
    poach: "Drop into barely simmering seasoned water, off heat 2-3 min until pink and curled.",
    brine: "15 min in 5% salt water + pinch baking soda = snap and seasoning.",
  },
  eggs: {
    scramble: "Low heat, stir constantly with rubber spatula 3-4 min, pull off heat while still wet — carryover sets them.",
    fry:      "Med-low in butter/oil 2-3 min for sunny-side, baste with hot fat for over-easy. Crisp edges = med-high 90 sec.",
    softboil: "Place in already-boiling water, 6.5 min, ice bath stops the cook — set white + jammy yolk.",
    hardboil: "Cold start, bring to boil, off heat, cover 10-12 min, ice bath. Easier to peel than start-boiling.",
    poach:    "Simmering water + splash vinegar, swirl, slide egg in, 3 min for soft yolk.",
    bake:     "Cracked into ramekin or hollowed bread, 400°F 8-12 min for set white runny yolk.",
  },
  bacon: {
    pan:     "Cold pan, lay strips flat, med heat 8-10 min, flip once; renders gently for even crisp without burning.",
    oven:    "400°F on sheet pan with rack, 15-20 min — hands-off and even.",
    candied: "Brown sugar + cayenne, 350°F 25 min flipping once; cool on rack to crisp.",
    lardons: "Diced thick-cut, render slowly in skillet 10-12 min until brown and crisp — flavor base for vinaigrettes, soups.",
  },
  carrot: {
    roast: "Halved or in chunks, oil + salt, 425°F 25-30 min until caramelized edges, soft center.",
    sauté: "Diced ¼\" pieces, 6-8 min in butter over medium until softened.",
    boil:  "Whole baby carrots 10-12 min, sliced 5-7 min — pierce with knife tip easily.",
    glaze: "Cook in butter + honey + small water, lid on 8 min, lid off until syrupy.",
    raw:   "Shave with peeler for ribbons, julienne for slaws — soak in ice water 15 min to curl.",
  },
  celery: {
    sauté:  "Diced fine for mirepoix, 5-7 min in fat — until softened and aromatic.",
    braise: "Hearts halved, 30 min in stock + butter at 325°F covered — tender and silky.",
    raw:    "Strings: peel with vegetable peeler. Crisper after 30 min in ice water.",
    blanch: "2 min in salted boiling water for soups/cold salads — keeps color, kills harshness.",
  },
  potato: {
    roast:   "Cut even chunks, parboil 8 min then drain and shake to rough up edges; 425°F in hot fat 35-40 min flipping once. The roughed edges are the crisp.",
    boil:    "Cold-start in salted water, simmer 15-20 min until knife slides easily.",
    mash:    "Steam or boil, pass through ricer (not blender — gluey), fold in warm milk + butter off heat.",
    fry:     "Double-fry: 325°F 5-7 min to cook through, rest, 375°F 3-4 min to crisp.",
    bake:    "425°F 50-60 min, prick first, rub skin with oil + salt for crackling skin.",
    smashed: "Boil whole baby potatoes 15 min, smash flat, drizzle oil, 425°F 20 min to crisp edges.",
  },
  sweet_potato: {
    roast: "Cubed, oil + salt, 425°F 25-30 min — caramelizes faster than regular potato due to sugars.",
    bake:  "Whole, pricked, 400°F 50-60 min until soft inside, sugar-syrup leaks through skin.",
    mash:  "Boil 15-20 min, mash with butter + cinnamon or coconut milk + chili.",
    fry:   "Lower than regular fries — 300°F first fry 4 min, 375°F second 2-3 min. Wet inside, soft.",
  },
  bell_pepper: {
    roast: "Whole over open flame or 450°F oven 20-25 min until skin blackens; steam in covered bowl 10 min, slip skins off.",
    sauté: "Sliced ¼\" thick, 5-7 min over med-high; don't crowd or they steam to limp.",
    raw:   "Crisp and sweet — best in slaws and crudités, deseed and remove ribs for less bitterness.",
    grill: "Quartered or whole, char marks on all sides 8-10 min total — smoky-sweet.",
  },
  tomato: {
    roast:  "Halved, cut-side up, oil + salt, 300°F 1.5-2 hr — concentrates sugars, near-confit.",
    sauté:  "Diced, 5-7 min with garlic + oil until they break down into pulpy sauce.",
    raw:    "Salt 10 min before serving to draw water, drain — fixes watery salad.",
    blanch: "X-cut bottom, 30 sec in boiling water, ice bath — skins slip off for sauces.",
    confit: "Whole or halved, submerged in oil + garlic + herbs, 250°F 1.5 hr.",
  },
  mushroom: {
    sauté: "DRY pan first, med-high, no oil — sweat out water 4-5 min, THEN add fat + salt for browning 3-4 min more. Salt early = leathery.",
    roast: "Whole or quartered, oil + salt + thyme, 425°F 20-25 min until edges crisp.",
    grill: "Whole portobellos, marinate 30 min, grill 4-5 min per side until juicy.",
    raw:   "Thin-shaved over salads — earthy and mild, dress lightly.",
  },
  broccoli: {
    roast:  "Florets + thinly-sliced stems, oil + salt, 425°F 18-22 min until charred edges. Charring = the point, not a mistake.",
    blanch: "2-3 min in heavily-salted boiling water, ice bath — bright green, slight crunch for salads.",
    steam:  "5-6 min over boiling water until knife pierces stem — softer than blanched, less seasoning.",
    sauté:  "Blanched florets, 3-4 min in hot oil + garlic; finish with squeeze of lemon.",
  },
  spinach: {
    wilt:    "10 oz raw → 1 cup cooked. Add to hot pan with a splash of water, 1-2 min until limp; squeeze excess water out.",
    sauté:   "Garlic in oil first 30 sec, then spinach handful at a time, 2-3 min total.",
    raw:     "Baby spinach holds up better than mature; dress just before serving — wilts under acid.",
    creamed: "Cooked spinach, drained hard, into béchamel — finish with parmesan + nutmeg.",
  },
  kale: {
    massage: "Tear stems off, drizzle oil + salt, scrunch with hands 60-90 sec until volume halves — fixes the chew for salads.",
    sauté:   "Stems and ribs removed, ribbons, 4-5 min in oil + garlic, splash of water at end.",
    roast:   "Chip-style: torn pieces, light oil, 300°F 12-15 min until crisp but green.",
    blanch:  "1-2 min for braising bases, ice bath, squeeze and chop.",
  },
  zucchini: {
    sauté: "Hot pan, oil, slices ½\" thick, 3-4 min per side — don't stir constantly or they steam.",
    grill: "Lengthwise planks, oil + salt, 3-4 min per side over med-high direct heat.",
    raw:   "Shave with peeler or spiralize for noodles; salt 10 min and drain to remove water before dressing.",
    roast: "Halved or chunks, oil + salt, 425°F 18-20 min until tender and browned.",
  },
  cauliflower: {
    roast: "Florets, oil + salt, 425°F 25-30 min until charred edges, tender center. Don't undercook — bitter.",
    steam: "8-10 min until knife pierces — soft enough for mash or purée.",
    whole: "Brush with oil + spices, 425°F 45-60 min for whole head; tender all the way through.",
    rice:  "Pulse florets in food processor to grain size; sauté 5-7 min in dry pan to lose moisture, then season.",
    raw:   "Shaved thin or in florets for crudité; slightly bitter but crisp.",
  },
  avocado: {
    ripen: "Brown bag with apple/banana 2-3 days; firm-to-soft when ready. Slight give at stem end.",
    cut:   "Halve around pit, twist, scoop with spoon. Pit removal: tap with knife heel, twist out.",
    raw:   "Acid (lime/lemon) on cut surface delays browning; keep pit in mashed avocado for storage.",
    mash:  "Fork-mash for guacamole — chunky beats smooth. Salt + lime first, mix-ins after.",
    grill: "Halved, pit out, cut-side down on hot grill 3-4 min for char marks.",
  },
  olive_oil: {
    finish:   "Cold-pressed extra-virgin OFF heat — drizzled at the end for grassy fruity flavor. Heat kills it.",
    sauté:    "Mid-grade EVOO or regular olive oil for med heat under 350°F. Smoke point ~410°F.",
    confit:   "Submerge garlic/herbs/fish at 200°F to infuse — low-and-slow extraction.",
    emulsify: "Slowly whisk into vinegar/mustard or stream into yolk for vinaigrette/aioli.",
  },
  butter: {
    brown:   "Melt over med, swirl occasionally, 5-7 min until milk solids brown and nutty aroma — pull off heat IMMEDIATELY when amber, carryover burns.",
    melt:    "Low heat, no stirring, 2-3 min — for finishing or as cooking fat.",
    foam:    "Med-high, foam subsides ~90 sec = ready to sear. Past foam = burning.",
    cream:   "Room temp butter + sugar, paddle attachment 3-5 min until pale and fluffy — incorporates air for baked goods.",
    mount:   "Cold cubes whisked into sauce off heat — glossy, emulsified finish for pan sauces.",
    clarify: "Melt slowly, skim foam, decant clear butterfat from milk solids — ghee variant cooks solids longer for nutty flavor.",
  },
  kosher_salt: {
    season:    "Salt early and in layers — proteins before cooking, vegetables as they hit heat, finish with flake at end.",
    brine:     "5% by weight (50g salt per 1L water) for poultry/pork 30 min – 12 hr depending on size.",
    dry_brine: "Salt directly on meat, uncovered in fridge, 1 hr per pound up to 24 hr — draws out moisture, re-absorbs seasoned.",
    cure:      "Equal parts salt + sugar for short cures; 1-3 days for gravlax, longer for hams.",
    cookwater: "Salt pasta/blanching water until 'sea-salty' (~2% by weight) — seasons from inside.",
  },
  black_pepper: {
    grind:  "Always FRESHLY ground — pre-ground loses essential oils within weeks. Coarse for steak, fine for dressings.",
    bloom:  "Add early to hot fat in tempering — releases pepperonin and warmth into the oil.",
    finish: "Crack at the table for raw flavor; cooks down to muted background fast.",
    cacio:  "Toast cracked pepper in dry pan 30 sec before adding fat for cacio e pepe — releases volatile oils.",
  },
  rice: {
    steam:   "1.5:1 water-to-rice (or 1:1 short-grain), cold start, simmer covered 18 min, off heat covered 10 min more. NO PEEKING.",
    pilaf:   "Toast dry rice in fat 2-3 min before liquid — separates grains, adds nutty flavor.",
    risotto: "Toast in butter, deglaze with wine, ladle hot stock 1 cup at a time stirring 18-22 min until creamy.",
    fried:   "DAY-OLD rice only — fresh rice gums up. High heat, oil, push to side, scramble egg, toss all together.",
    wash:    "Rinse 3-4 times until water runs nearly clear to remove surface starch — fluffier separated grains.",
  },
  spaghetti: {
    cook:       "Heavily salted boiling water (~2% by weight), stir first 60 sec to prevent sticking. Cook 1 min LESS than package; finish in sauce.",
    aldente:    "Bite a strand — a thin pale-white ring in the center means al dente. No ring = overcooked.",
    pastaWater: "Reserve 1 cup starchy cooking water before draining; it's the emulsifier for any pan sauce.",
    sauce:      "Finish pasta IN the sauce 1-2 min with splash of pasta water — sauce coats, doesn't pool.",
    bake:       "Pre-cook 2 min less than al dente, layer in sauce, 400°F 25 min until bubbling.",
  },
  flour: {
    measure: "Spoon-and-level into cup, don't scoop (packs 25%+ extra). 125g per cup is the gold standard.",
    sift:    "Lightens texture for cakes and crepes; necessary when recipes specify 'sifted'.",
    bloom:   "Toast dry in pan 3-5 min for nuttier flavor in roux — cuts raw flour taste.",
    dust:    "Light coating on dough, work surface, or proteins (dredge) to prevent sticking and aid browning.",
    roux:    "Equal parts butter + flour, cook 2-15 min depending on color desired (blonde → brick).",
  },
  sugar: {
    cream:    "With butter, paddle 3-5 min until pale and fluffy — sugar crystals cut air pockets into fat.",
    caramel:  "Dry: heat in pan over med, swirl no stir, 5-8 min to amber. Wet: add water, simmer 10-12 min; watch closely — burns fast.",
    syrup:    "1:1 simple syrup, simmer until dissolved 2-3 min. Richer ratios for stickier glazes.",
    macerate: "Sprinkled on fruit 15-30 min draws out juice — basis for compotes, pavlovas.",
    bloom:    "Sprinkled over yeast in warm water 5-10 min to confirm activity (bubbles = alive).",
  },
  brown_sugar: {
    pack:  "Always measured PACKED — fills cracks. 1 cup packed = ~220g.",
    cream: "With butter for chewier cookies — molasses adds moisture vs white sugar.",
    glaze: "Bacon, ham, salmon — melts into syrup at 300°F, caramelizes by 350°F.",
    store: "Airtight + slice of bread or terracotta disk to keep soft; rock-hard sugar steams back to soft in microwave 20 sec.",
  },
  soy_sauce: {
    marinate: "10-30 min for tender proteins (shrimp, tofu); 1-4 hr for tougher (chicken thigh, beef). Past that = salty mush.",
    finish:   "Splash off heat to season stir-fries, broths, dressings — heat reduces complexity.",
    glaze:    "Reduce with sugar + mirin/sake/ginger 5-7 min until syrupy — teriyaki base.",
    season:   "Replace some salt in soups, braises, broths — 1 tbsp soy ≈ 1 tsp salt salinity, plus umami depth.",
  },
  vinegar: {
    deglaze:  "Splash into hot pan after sauté to lift fond and brighten the sauce.",
    dressing: "3:1 oil:acid baseline; balance with mustard or honey for emulsifying body.",
    pickle:   "Equal parts vinegar:water + 1 tbsp salt + 1 tbsp sugar per cup, hot over crisp veg; ready 1 hr.",
    finish:   "Drop into braises and stews right at the end — wakes up the flavors.",
    reduce:   "Balsamic: simmer 10-15 min to ⅓ volume for a glaze (don't reduce cheap balsamic past syrupy).",
  },
  parmesan: {
    grate:  "Microplane for finishing, box grater for melting into sauces. Pre-grated is dry — buy a wedge.",
    melt:   "Stir into hot pasta off heat with butter + pasta water — emulsifies into creamy coating. Heat kills the smoothness.",
    rind:   "Save rinds for soup/risotto — drop into simmering broth, fish out before serving.",
    finish: "Snowed over finished dishes — best in last 30 sec to preserve aroma.",
  },
  cheddar: {
    melt:  "Shred FRESH (pre-shredded has anti-caking agents that break melt); add to hot pan off heat, stir until smooth.",
    sharp: "Aged sharp cheddar bites stronger raw; melts grainier than mild — split half mild for mac and cheese smoothness.",
    grate: "Box grater for sandwiches and mac; microplane for fluffy fine snow on tacos.",
    grill: "Slices in cold pan med-low until just melted, lid on — protects bread from burning.",
  },
  mozzarella: {
    melt:   "Fresh: tear by hand, drain on paper first or pizza goes soggy. Low-moisture: shred and melt evenly. 400°F+ for bubbly char.",
    raw:    "Fresh in water — slice ¼\" thick, salt + drizzle oil + basil, serve at room temp.",
    fryup:  "Breaded sticks: panko + egg + flour dredge, fry 350°F 90 sec until golden.",
    salad:  "Caprese: alternating slices, salt, basil, oil; rest 5 min before serving to draw flavor.",
  },
  heavy_cream: {
    whip:    "Cold bowl + cold whisks; 2-3 min by hand or 60-90 sec on stand mixer. Soft peaks for folding, medium for piping, firm for stable topping.",
    reduce:  "Simmer 10-15 min to half volume for sauces — coats the back of a spoon when ready.",
    ganache: "1:1 cream:chocolate by weight, scald cream, pour over chocolate, rest 2 min, stir from center.",
    sauce:   "Stir into warm pan sauces off heat — won't break like milk.",
  },
  sour_cream: {
    finish: "Stir in OFF heat (curdles above 180°F); thins and brightens soups, stews, chilis.",
    bake:   "Replace ½ butter in cakes for tang and moisture; key in Eastern European baking.",
    crema:  "Whisk with lime juice + salt for tacos; thins for drizzling.",
    dip:    "Equal parts mayo, add herbs or alliums, rest 30 min for flavors to bloom.",
  },
  milk: {
    scald:    "Heat to 180°F (steam, no boil) — denatures whey for smoother custards and yeast doughs.",
    foam:     "Steam wand or French press: hold spout barely under surface 30 sec for stretch, then dive deeper 30 sec to texturize.",
    bechamel: "Whisk warm milk SLOWLY into blonde roux, no lumps; simmer 10 min stirring to cook out flour taste.",
    sub:      "1 cup milk ≈ ½ cup cream + ½ cup water for emergencies (or vice versa).",
  },
  basil: {
    raw:        "TEAR don't cut — bruising oxidizes and blackens edges. Add at the end of cooking.",
    pesto:      "Blanch leaves 5 sec + ice bath FIRST to preserve color (lasts a week instead of a day).",
    chiffonade: "Stack leaves, roll into cigar, slice thin ribbons — for garnishes.",
    infuse:     "Steep in warm oil 1 hr or simmer in cream 5 min for ice cream/panna cotta.",
  },
  parsley: {
    chop:      "Pluck leaves, pile, rock the knife — minced parsley is dryer and brighter than coarse-chopped.",
    finish:    "Stir in at the end of soups/stews for color and freshness — cooked parsley goes muddy fast.",
    stems:     "DON'T discard — finely minced into chimichurris, herb sauces, stocks.",
    gremolata: "Equal parts chopped parsley + lemon zest + minced garlic — finishes osso buco, roasted vegetables.",
  },
  cilantro: {
    chop:   "Use both leaves AND tender stems — most flavor lives in the stems. Discard only thick bottom inches.",
    finish: "Off-heat addition only — heat kills it and turns the flavor soapy/dull.",
    raw:    "Mince fine for salsa, tear for tacos. Pairs with lime, chili, fish sauce, coconut.",
    stems:  "Pound in mortar with garlic + peppercorns for Thai curry pastes — the umami base.",
  },
  thyme: {
    strip:   "Run fingers backward down the stem — leaves pop off. Whole sprigs for braises (pull stem out before serving).",
    bouquet: "Tie 4-6 sprigs with twine, drop into stocks/braises/soups, retrieve before serving.",
    sauté:   "Add early to oil with onions — woody and slow-releasing, needs heat time to bloom.",
    finish:  "Fresh leaves at the end for green color + brighter flavor — dried form is muddier.",
  },
  lemon: {
    zest:     "Microplane the yellow only — white pith is bitter. Always zest BEFORE juicing.",
    juice:    "Room-temp lemon, roll under palm hard to break cells, halve, squeeze with fork tines for max yield.",
    finish:   "Acid at the end of any dish brightens — taste, salt, then add a squeeze if it tastes flat.",
    preserve: "Quarter lemons, pack in salt + their juice, refrigerate 1-2 weeks until rinds soft and translucent.",
    confit:   "Slices in oil with garlic + thyme, 250°F 1 hr — silky and sweet.",
  },
  lime: {
    zest:   "Microplane the green only — pith is bitter. Use sparingly, lime zest is more aggressive than lemon.",
    juice:  "Same as lemon — room temp, roll, halve, squeeze. Lime juice oxidizes within hours, juice to order.",
    finish: "Squeeze over Mexican / SE-Asian food just before serving; fish sauce and lime are the salt-and-acid backbone.",
    leaf:   "Kaffir lime leaves: tear or chiffonade, into curries — perfumed citrus without the acid.",
  },
  tofu: {
    press:    "Drain, wrap in towel, weight with plate + can 15-30 min to expel water — firmer texture, better browning.",
    pan_fry:  "Pressed cubes, hot oil med-high, 3-4 min per side undisturbed for golden crust. Cornstarch dust = crispier.",
    deep_fry: "Cubed firm tofu in 350°F oil 4-5 min until golden — puffs slightly, crisp shell.",
    silken:   "Don't press — silken is for soups, sauces, custards. Slide into broth gently or blend smooth.",
    marinate: "1 hr minimum after pressing — porous and thirsty for sauces (soy, miso, gochujang).",
    raw:      "Silken with soy + ginger + sesame oil as agedashi-style cold dish; firm shaved on salads.",
  },
};

// Attach techniques to INGREDIENT_INFO at module load. Creates stub
// entries for canonicals that don't otherwise have an INGREDIENT_INFO
// row (e.g. potato, kale, kosher_salt) — getIngredientInfo returns
// null-safe defaults for missing fields, so a `{ technique: {…} }`-only
// entry still works correctly.
for (const [id, technique] of Object.entries(TECHNIQUES)) {
  if (!INGREDIENT_INFO[id]) INGREDIENT_INFO[id] = {};
  INGREDIENT_INFO[id].technique = technique;
}

// Look up display info for an ingredient. Three-layer merge:
//   1. dbOverride (from Supabase ingredient_info table) — wins everything
//   2. INGREDIENT_INFO (the JS object in this file) — code-side fallback
//   3. SUBCATEGORY_INFO (cheese subcategories etc.) — last resort
//
// Pass `dbOverride` as the optional second arg when you have a row from
// useIngredientInfo(). Without it, the lookup is purely code-side and
// works offline / pre-load — so existing callers keep working as-is.
//
// Returns null-safe defaults for every schema field so the UI can safely
// read `info.storage?.location` without per-key existence checks.
export function getIngredientInfo(ingredient, dbOverride) {
  // User-created canonicals (admin-approved, not bundled) show up
  // here with ingredient=null because findIngredient only knows the
  // bundled registry. We used to early-return null, which silently
  // wiped out the admin-approved enrichment JSON on the canonical
  // card. Fall through to the merge — dbOverride still contains the
  // real enriched info; the JS fallback + subcategory just aren't
  // available, which is fine for user-created slugs.
  if (!ingredient && !dbOverride) return null;
  const sub = ingredient?.subcategory ? SUBCATEGORY_INFO[ingredient.subcategory] : null;
  const ing = ingredient ? (INGREDIENT_INFO[ingredient.id] || null) : null;
  const db  = dbOverride || null;
  return {
    // ── cooking-centric ────────────────────────────────────────────────
    description:    db?.description    || ing?.description    || sub?.description    || null,
    flavorProfile:  db?.flavorProfile  || ing?.flavorProfile  || sub?.flavorProfile  || null,
    prepTips:       db?.prepTips       || ing?.prepTips       || sub?.prepTips       || null,
    storage:        db?.storage        || ing?.storage        || sub?.storage        || null,
    substitutions:  db?.substitutions  || ing?.substitutions  || sub?.substitutions  || [],
    irreplaceable:       db?.irreplaceable ?? ing?.irreplaceable ?? sub?.irreplaceable ?? false,
    irreplaceableNote:   db?.irreplaceableNote || ing?.irreplaceableNote || sub?.irreplaceableNote || null,
    pairs:          db?.pairs          || ing?.pairs          || sub?.pairs          || [],
    clashes:        db?.clashes        || ing?.clashes        || [],
    // ── flavor (structured v2; freeform flavorProfile above) ───────────
    flavor:         db?.flavor         || ing?.flavor         || sub?.flavor         || null,
    // ── nutrition ──────────────────────────────────────────────────────
    nutrition:      db?.nutrition      || ing?.nutrition      || sub?.nutrition      || null,
    // ── social / cultural ──────────────────────────────────────────────
    origin:         db?.origin         || ing?.origin         || sub?.origin         || null,
    culturalNotes:  db?.culturalNotes  || ing?.culturalNotes  || sub?.culturalNotes  || null,
    winePairings:   db?.winePairings   || ing?.winePairings   || sub?.winePairings   || [],
    recipes:        db?.recipes        || ing?.recipes        || sub?.recipes        || [],
    // ── sourcing / allergens / seasonality ─────────────────────────────
    allergens:      db?.allergens      || ing?.allergens      || sub?.allergens      || [],
    allergenDetail: db?.allergenDetail || ing?.allergenDetail || sub?.allergenDetail || null,
    seasonality:    db?.seasonality    || ing?.seasonality    || sub?.seasonality    || null,
    sourcing:       db?.sourcing       || ing?.sourcing       || sub?.sourcing       || null,
    // ── dietary / lifestyle flags (v2) ─────────────────────────────────
    diet:           db?.diet           || ing?.diet           || sub?.diet           || null,
    // ── market intelligence (structured v2) ────────────────────────────
    market:         db?.market         || ing?.market         || sub?.market         || null,
    // ── skill + course links (v2) ──────────────────────────────────────
    skillDev:       db?.skillDev       || ing?.skillDev       || sub?.skillDev       || null,
    // ── blend composition (new for JSONB-era spice metadata) ──────────
    blendOf:        db?.blendOf        || ing?.blendOf        || null,
    // ── AI meal-planning primitives (v3; emitted by the enrich-ingredient
    // edge function). All nullable — missing on older rows, the meal
    // planner should treat absence as "no signal" rather than error. ──
    flavor_profile: db?.flavor_profile || ing?.flavor_profile || null,
    aromatic_category:
      db?.aromatic_category || ing?.aromatic_category || null,
    cooking_behaviors:
      db?.cooking_behaviors || ing?.cooking_behaviors || [],
    role_tendencies:
      db?.role_tendencies || ing?.role_tendencies || null,
    heat_stability:
      db?.heat_stability || ing?.heat_stability || null,
    // Functional vs flavor substitution split (v3). Falls back to the
    // legacy flat `substitutions` array above when absent, so the 32
    // seeded rows keep rendering correctly.
    substitutions_functional:
      db?.substitutions_functional || ing?.substitutions_functional || null,
    substitutions_flavor:
      db?.substitutions_flavor || ing?.substitutions_flavor || null,
    // Provenance block stamped by the enrichment edge function. UI can
    // show "AI-generated, pending review" badges when _meta.reviewed is
    // false; null for canonical rows authored pre-v3.
    _meta: db?._meta || ing?._meta || null,
    // ── Round 2 metadata expansion ─────────────────────────────────
    // Added to the runtime overlay via the Phase 3 AI enrichment pass.
    // Bundled JS rarely sets these (they're AI-fillable); DB layer
    // wins consistently. Cards + audit + recipe matching read through
    // here, so without these passthroughs the data lands in
    // ingredient_info but is invisible to every consumer.
    //
    // density: AI-written shape `{g_per_tsp, g_per_tbsp, g_per_cup}`.
    // Bundled rows historically used a flat `density_g_per_ml` number;
    // both shapes are valid — unitConvert.js handles the dispatch.
    density:        db?.density        || ing?.density        || null,
    density_g_per_ml: db?.density_g_per_ml ?? ing?.density_g_per_ml ?? null,
    // count_weight_g: grams per "1 count" unit. AI-fills for canonicals
    // sold by count (eggs, lemons, garlic cloves, sandwich slices).
    count_weight_g: db?.count_weight_g ?? ing?.count_weight_g ?? null,
    // Display-preference + measurement-axis fields used by the unit
    // converter to pick the right ladder for a row's amount/unit.
    preferredUnit:  db?.preferredUnit  || ing?.preferredUnit  || null,
    measuredIn:     db?.measuredIn     || ing?.measuredIn     || null,
    // Prep timings — open-ended map of cooking-method → timing object
    // (saute_stovetop, roast_oven, thaw_fridge, etc.). Cook-mode reads
    // this for default timer durations + done cues.
    prepTimings:    db?.prepTimings    || ing?.prepTimings    || null,
    // Technique hints — hand-curated map of `{ techniqueVerb: "≤1-sentence
    // hint" }`. Top-50 staples carry these via the TECHNIQUES block above.
    // Consumed by generate-recipe's final pass so prose is grounded in
    // real timings (caramelize = 35-40 min, not "until soft, ~10 min").
    // Future: a richer per-technique catalog (techniques table) will
    // carry full doneness cues + animation_id; this stays as the
    // per-ingredient pointer/shortcut.
    technique:      db?.technique      || ing?.technique      || null,
    // Internal temperature targets. Probe prompt + cook-mode safety
    // surface this. Object shape: {rare, mediumRare, medium, ...,
    // safeMin} for meats; {done} for breads; {set} for custards.
    internalTempF:  db?.internalTempF  || ing?.internalTempF  || null,
    // Recipe-scaling yields. {wholeToJuice, wholeToZest, rawToCooked,
    // postTrim} — emitted for citrus, garlic, rice, mushrooms, etc.
    yields:         db?.yields         || ing?.yields         || null,
    // Smoke point in °F — oils/fats only. Cook-mode "high heat?" warning.
    smokePointF:    db?.smokePointF    ?? ing?.smokePointF    ?? null,
    // Reduction ratio — produce that shrinks meaningfully when sauteed.
    // {sauteFactor, note}. Pantry decrement math + AI scaling.
    reductionRatio: db?.reductionRatio || ing?.reductionRatio || null,
    // Equivalents — kitchen approximations like "1 stick butter = 8 tbsp".
    // Array of {from, to} pairs.
    equivalents:    db?.equivalents    || ing?.equivalents    || [],
    // Equipment — special tools recommended ("candy_thermometer",
    // "stand_mixer", "mortar_pestle"). Empty array when standard
    // kitchen tools suffice.
    equipment:      db?.equipment      || ing?.equipment      || [],
    // Package metadata — shape varies by source. AI emits typicalSizes
    // arrays; admin curation may add manufacturer-stated container
    // info. Whole object passes through.
    package:        db?.package        || ing?.package        || null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// v2 metadata helpers. These operate on the shape getIngredientInfo returns,
// so callers can pass the result straight in — no re-lookup required.
// ─────────────────────────────────────────────────────────────────────────────

// Returns true if the ingredient is compatible with a user's active dietary
// preferences. `userDiet` is a partial shape mirroring INGREDIENT_INFO's
// `diet` object — only the flags the user cares about need to be set.
//
// Example:
//   isCompatibleWithDiet(getIngredientInfo(canon), { vegan: true })
//   → false for parmesan, true for olive oil
//
// Rules:
//   - vegan/vegetarian/keto/halal/nightshade/allium: user's truthy flag
//     requires ingredient's matching flag to be truthy (or falsey-absent
//     for the inverse, e.g. user says "no nightshades" ⇒ ingredient must
//     not be flagged nightshade).
//   - kosher: user passes "meat" | "dairy" | "pareve" — ingredient must
//     not be "nonkosher"; pareve is compatible with anything.
//   - fodmap: user passes "low" or "moderate" — ingredient's fodmap level
//     must be at or below the user's threshold.
//
// Missing info defaults to "compatible" (don't hide ingredients just
// because we haven't backfilled their flags yet).
export function isCompatibleWithDiet(info, userDiet) {
  if (!userDiet || !info) return true;
  const diet = info.diet;
  if (!diet) return true;

  if (userDiet.vegan      && !diet.vegan)      return false;
  if (userDiet.vegetarian && !diet.vegetarian) return false;
  if (userDiet.keto       && !diet.keto)       return false;
  if (userDiet.halal      && diet.halal === false) return false;
  if (userDiet.kosher && userDiet.kosher !== "nonkosher") {
    if (diet.kosher === "nonkosher") return false;
  }
  if (userDiet.noNightshade && diet.nightshade) return false;
  if (userDiet.noAllium     && diet.allium)     return false;

  if (userDiet.fodmap) {
    const order = { low: 0, moderate: 1, high: 2 };
    const cap = order[userDiet.fodmap] ?? 2;
    const got = order[diet.fodmap] ?? 0;
    if (got > cap) return false;
  }
  return true;
}

// Returns true if the ingredient is in season right now for the given
// hemisphere. `month` is 1..12; defaults to current month.
//
// yearRound overrides — always in season.
// No peakMonths for the hemisphere → defaults to true (don't hide produce
// we haven't tagged).
export function isInSeason(seasonality, hemisphere = "N", month = new Date().getMonth() + 1) {
  if (!seasonality) return true;
  if (seasonality.yearRound) return true;
  const peak = hemisphere === "S"
    ? (seasonality.peakMonthsS || seasonality.peakMonths)
    : (seasonality.peakMonthsN || seasonality.peakMonths);
  if (!peak || !peak.length) return true;
  return peak.includes(month);
}

// ─────────────────────────────────────────────────────────────────────────────
// Fuzzy ingredient matching.
//
// Use cases:
//   1. Receipt scans where Claude returned `ingredientId: null` — client-side
//      second-pass fallback so obvious matches don't escape as free text.
//   2. The "Link to canonical" action on a free-text pantry row — surface
//      likely candidates so the user picks with one tap.
//   3. Autocomplete / "did you mean" in the manual-add flow (future).
//
// Algorithm: normalize both sides (lowercase, strip plurals, strip packaging
// descriptors and sizes, strip punctuation), then score.
//   100  → exact normalized match
//    80  → one fully contains the other
//   0–60 → Jaccard token overlap
//   +20  → Levenshtein closeness bonus (short edit distance)
//
// Returns [{ ingredient, score }] sorted highest first, up to `limit`.
// Callers can apply their own threshold; 30+ is a reasonable "likely match".
// ─────────────────────────────────────────────────────────────────────────────

// Words that add no signal — sizes, descriptors, packaging. Strip these so
// "Organic EVOO 500ml Extra Virgin" compares cleanly against "olive oil".
// Dietary / macro modifier tokens are stripped so "low-carb tortilla"
// scores against the base tortilla canonical. The modifiers themselves
// are the pantry row's attributes.claims — matching and claim-tracking
// are separate axes on purpose (see AIRecipe extractDietaryClaims).
// Packaging / size / descriptor noise — not state, not identity.
const FUZZY_NOISE_WORDS_BASE = new Set([
  "organic","grass","fed","free","range","local","fresh","natural",
  "reduced","fat","lowfat","nonfat","unsalted","salted","sweet",
  "large","medium","small","xl","xxl","jumbo","extra","virgin","premium",
  "select","prime","pack","packed","value","family","size","pcs","piece",
  "pieces","ea","each","ct","count","bunch","bag","jar","tin","can","tub",
  "carton","bottle","box","pouch",
  // dietary / macro modifiers — stripped here, captured as claims by
  // AIRecipe's extractDietaryClaims before the name hits normalization.
  "lowcarb","lowsugar","lowsodium","zerocarb","zerosugar","carb","carbs",
  "sugarfree","glutenfree","dairyfree","grainfree","fatfree","nofat",
  "keto","paleo","whole30","vegan","vegetarian",
  "multigrain","wholegrain","wholewheat","highprotein","protein",
]);
// Full noise set = packaging/descriptor noise UNION every state token
// the registry recognizes. Derived at module-init from ALL_STATE_TOKENS
// so adding a new state in INGREDIENT_STATES automatically updates
// every free-text matcher — no second list to maintain.
// Note the module initialization order: ALL_STATE_TOKENS is defined
// further down the file, so we lazy-merge on first use.
let _fuzzyNoise = null;
function fuzzyNoiseSet() {
  if (_fuzzyNoise) return _fuzzyNoise;
  _fuzzyNoise = new Set(FUZZY_NOISE_WORDS_BASE);
  for (const t of ALL_STATE_TOKENS) _fuzzyNoise.add(t);
  return _fuzzyNoise;
}
const FUZZY_UNIT_WORDS = new Set([
  "oz","lb","lbs","g","kg","ml","l","liter","liters","gal","gallon","gallons",
  "qt","quart","pt","pint","cup","cups","tbsp","tsp","fl",
]);

function fuzzyNormalize(s) {
  const noise = fuzzyNoiseSet();
  return String(s || "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")     // strip parenthetical asides
    .replace(/[^a-z0-9 ]+/g, " ")   // punctuation → space
    .replace(/\b\d+(\.\d+)?\b/g, " ") // strip numbers (sizes, counts)
    .split(/\s+/)
    .filter(t => t && !noise.has(t) && !FUZZY_UNIT_WORDS.has(t))
    // Cheap pluralization strip: "eggs" → "egg", "berries" → "berrie" (good
    // enough — fuzzy scoring tolerates the residual mismatch).
    .map(t => t.length > 3 && t.endsWith("s") ? t.slice(0, -1) : t)
    .join(" ")
    .trim();
}

// Classic iterative-DP Levenshtein — small arrays, fast enough for a
// 500-ingredient scan without memoization.
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

// Ingredients that LOOK like they share a head noun but are
// semantically different products. "peanut butter" ⊇ "butter" would
// otherwise match under head+subset logic (heads both = "butter",
// {butter} ⊆ {peanut, butter}) and the fuzzy matcher would happily
// resolve "butter" to peanut_butter in a household that doesn't have
// a plain butter row. Same footgun for coconut milk / almond milk on
// "milk", for sesame oil / truffle oil on "oil", for cream cheese on
// "cheese", etc.
//
// Policy: a modifier token from this list in ONE side and NOT the
// other disqualifies the head-noun match. The bare canonical
// ("butter") can still pair with "salted butter" / "unsalted butter"
// because those modifiers aren't in the set — the exclusion only
// kicks in for modifiers that FLIP the ingredient's identity.
//
// Format: Map<headNoun, Set<modifierToken>>, all lowercase, tokens
// compared post-normalize. Exported so recipePairing shares the same
// guard — a drift between the two matchers is how this class of bug
// hides.
export const HEAD_NOUN_MODIFIER_EXCLUSIONS = new Map([
  ["butter", new Set(["peanut", "almond", "cashew", "sunflower", "apple", "cocoa"])],
  ["milk",   new Set(["coconut", "almond", "soy", "oat", "rice", "cashew", "hemp", "pea"])],
  ["cream",  new Set(["sour", "ice", "whipped"])],
  ["cheese", new Set(["cream"])],
  ["oil",    new Set(["sesame", "truffle", "chili", "essential"])],
  ["sauce",  new Set(["soy", "fish", "hot", "bbq", "barbecue", "tartar", "tomato", "pasta", "pizza", "steak", "worcestershire"])],
  ["powder", new Set(["baking", "curry", "cocoa", "chili", "garlic", "onion", "protein", "espresso"])],
  ["salt",   new Set(["garlic", "onion", "celery", "seasoning"])],
  ["sugar",  new Set(["brown", "powdered", "confectioners", "coconut"])],
  ["flour",  new Set(["almond", "coconut", "rice", "oat", "tapioca", "corn"])],
  ["water",  new Set(["rose", "coconut", "sparkling", "tonic"])],
  ["juice",  new Set(["pickle"])],
]);

export function sharesDisqualifyingModifier(smallSet, bigSet, head) {
  const modifiers = HEAD_NOUN_MODIFIER_EXCLUSIONS.get(head);
  if (!modifiers) return false;
  // Fires when the bigger set carries a flip-modifier the smaller
  // set doesn't — i.e. one side is the bare head ("butter") and the
  // other is a compound product ("peanut butter"). Two compounds
  // with the same modifier (salted butter vs salted butter) never
  // trip it.
  for (const t of bigSet) {
    if (t === head) continue;
    if (modifiers.has(t) && !smallSet.has(t)) return true;
  }
  return false;
}

// Token-subset containment with head-noun equality. Replaces the
// bare `.includes()` substring check that was mis-resolving
// "tortillas" onto a `tortilla_chips` canonical (and similar) when
// the real tortillas slug wasn't available. For a candidate `c` and
// needle `n` (both post-fuzzyNormalize space-joined strings):
//   • heads must match (last token) — "tortilla chip" vs "tortilla"
//     have heads "chip" vs "tortilla", so they are NOT equivalent
//   • the shorter's token set must be fully contained in the longer's
//   • no disqualifying modifier (HEAD_NOUN_MODIFIER_EXCLUSIONS) —
//     kills the peanut-butter-as-butter class of false positive
// "flour tortilla" ⊇ "tortilla" still matches (both head=tortilla,
// {tortilla} ⊂ {flour, tortilla}). "Parm" still matches via the
// equal-string path above because candidates include shortName.
function headAndSubsetMatch(c, n) {
  if (!c || !n) return false;
  const tc = c.split(" ").filter(Boolean);
  const tn = n.split(" ").filter(Boolean);
  if (!tc.length || !tn.length) return false;
  const head = tc[tc.length - 1];
  if (head !== tn[tn.length - 1]) return false;
  const sc = new Set(tc);
  const sn = new Set(tn);
  const [small, big] = sc.size <= sn.size ? [sc, sn] : [sn, sc];
  for (const t of small) if (!big.has(t)) return false;
  if (sharesDisqualifyingModifier(small, big, head)) return false;
  return true;
}

// Score one ingredient against a needle string. Picks the best match across
// the ingredient's name, shortName, and id (underscores → spaces) — so
// "parm" matches "parmesan" via shortName even if the display name is longer.
function scoreIngredientMatch(needle, ing) {
  const n = fuzzyNormalize(needle);
  if (!n) return 0;
  const candidates = [ing.name, ing.shortName, ing.id.replace(/_/g, " ")]
    .filter(Boolean)
    .map(fuzzyNormalize)
    .filter(Boolean);
  if (!candidates.length) return 0;

  let best = 0;
  for (const c of candidates) {
    if (c === n)                           best = Math.max(best, 100);
    else if (headAndSubsetMatch(c, n))     best = Math.max(best, 80);
  }
  // Containment match — needle fully contains the candidate's tokens
  // regardless of word order. Handles modifier-at-end inputs like
  // "Sea Salt Coarse" → sea_salt that the head-anchored rule rejects
  // because the trailing token is the qualifier, not the noun.
  // Guarded by candidate.length >= 2 so single-token candidates
  // ("flour", "salt") can't sweep up multi-token needles
  // ("tortilla flour", "garlic salt") through this path — those
  // remain head-anchored. Disqualifying-modifier check still applies
  // so peanut/almond/etc. butter compounds don't bleed into butter.
  if (best === 0) {
    const nTokens = new Set(n.split(" "));
    for (const c of candidates) {
      const cTokens = c.split(" ").filter(Boolean);
      if (cTokens.length < 2) continue;
      const cSet = new Set(cTokens);
      let allIn = true;
      for (const t of cSet) { if (!nTokens.has(t)) { allIn = false; break; } }
      if (!allIn) continue;
      const head = cTokens[cTokens.length - 1];
      if (sharesDisqualifyingModifier(cSet, nTokens, head)) continue;
      best = Math.max(best, 75);
    }
  }
  if (best === 0) {
    const nTokens = new Set(n.split(" "));
    for (const c of candidates) {
      const cTokens = new Set(c.split(" "));
      if (!nTokens.size || !cTokens.size) continue;
      const overlap = [...nTokens].filter(t => cTokens.has(t)).length;
      const union   = new Set([...nTokens, ...cTokens]).size;
      best = Math.max(best, Math.round((overlap / union) * 60));
    }
  }
  // Levenshtein bonus — rewards near-misses on an already-matching candidate.
  if (best > 0) {
    const c = candidates[0];
    const d = levenshtein(n, c);
    const maxLen = Math.max(n.length, c.length);
    if (maxLen > 0) {
      const bonus = Math.max(0, 20 - Math.round((d / maxLen) * 20));
      best += bonus;
    }
  }
  return best;
}

// Public API: ranked matches for a free-text string. Returns at most `limit`
// candidates with a non-zero score. Caller decides the threshold for
// "confident enough to auto-link" (suggested: 70+).
//
// Optional `shoppingListItems` biases scoring toward items the user is
// actively buying. Rationale: if "ricotta" is on the shopping list for a
// recipe, a receipt line resembling ricotta (e.g. "RCTTA CHS") should
// preferentially bind to ricotta over any other canonical. Two tiers:
//
//   * +30 when the candidate canonical's id matches a list item's
//     ingredientId (strong signal — user already linked the list
//     entry to a canonical, so we're certain what they're buying).
//   * +20 when the candidate's name/shortName substring-matches a
//     free-text list item's name (weaker — the user typed a label
//     without linking, so noisier).
//
// Bonus only applies when the base score is already > 0 — we don't
// rescue a score of zero to avoid matching completely unrelated
// canonicals just because they're on the list.
export function fuzzyMatchIngredient(text, limit = 5, shoppingListItems = []) {
  if (!text || typeof text !== "string") return [];
  // Pre-compute the list-bias sets once so the per-ingredient loop is O(1)
  // lookups. Guarded for empty / missing input so existing call sites
  // (no third arg) keep working unchanged.
  const listIds = new Set(
    (shoppingListItems || [])
      .map(s => s?.ingredientId || s?.canonicalId)
      .filter(Boolean)
  );
  const listNames = (shoppingListItems || [])
    .map(s => (s?.name || "").toLowerCase().trim())
    .filter(n => n.length >= 3);

  const scored = [];
  // Iterate the bundled 400 FIRST. Bundled entries are the source of
  // truth for their slugs — full unit ladders, hub membership, real
  // categories, emoji curation.
  for (const ing of INGREDIENTS) {
    // Skip hubs — they're UI groupings, not real pantry items.
    if (ing.parentId === undefined && ing.id.endsWith("_hub")) continue;
    let score = scoreIngredientMatch(text, ing);
    if (score <= 0) continue;
    if (listIds.has(ing.id)) {
      score += 30;
    } else if (listNames.length > 0) {
      const ingName = (ing.name || "").toLowerCase();
      const ingShort = (ing.shortName || "").toLowerCase();
      const hit = listNames.some(n => ingName.includes(n) || ingShort.includes(n));
      if (hit) score += 20;
    }
    scored.push({ ingredient: ing, score });
  }
  // Then the DB-registered synthetics (admin-approved user canonicals
  // from ingredient_info, registered via registerCanonicalsFromDb).
  // Same scoring path — they're already normalized into a registry-
  // compatible shape by syntheticFromInfo. Bundled and synthetic
  // candidates compete for ranking on equal footing; the JS 400 still
  // typically wins for common terms because it has fuller metadata
  // (shortName, hub parent, etc.) feeding scoreIngredientMatch.
  for (const ing of dbCanonicals.values()) {
    let score = scoreIngredientMatch(text, ing);
    if (score <= 0) continue;
    if (listIds.has(ing.id)) {
      score += 30;
    } else if (listNames.length > 0) {
      const ingName = (ing.name || "").toLowerCase();
      const hit = listNames.some(n => ingName.includes(n));
      if (hit) score += 20;
    }
    scored.push({ ingredient: ing, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

// Estimates shelf life (in days) for an ingredient stored in a given
// location — used by receipt scans to compute a default expiration date
// when the receipt doesn't print one. Prefers the per-location shelfLife
// map when present; falls back to the flat shelfLifeDays if the requested
// location matches storage.location; returns null if no estimate is
// available (caller should leave expiration blank rather than guess).
export function estimateExpirationDays(storage, location) {
  if (!storage) return null;
  const loc = location || storage.location;
  if (!loc) return null;
  if (storage.shelfLife && storage.shelfLife[loc] != null) {
    return storage.shelfLife[loc];
  }
  if (storage.location === loc && storage.shelfLifeDays != null) {
    return storage.shelfLifeDays;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Manual-entry price estimation.
//
// When the user types "milk, 1 gallon" into the Add modal (no receipt in
// play), we still want a reasonable dollar amount so monthly spend and the
// pantry tile are useful. Each ingredient carries its own
// `estCentsPerBase` (cents per 1 unit of its base — g for mass, ml for
// volume, count for count). If an ingredient doesn't have one we return
// null rather than pretending: a nonsense estimate is worse than none.
// ─────────────────────────────────────────────────────────────────────────────

// Returns an estimated integer cents for { amount, unit } of `ingredient`,
// or null if the ingredient isn't priced yet or the inputs are nonsense.
export function estimatePriceCents({ amount, unit, ingredient }) {
  if (!ingredient || !amount) return null;
  const rate = ingredient.estCentsPerBase;
  if (rate == null) return null;
  const baseQty = toBase({ amount, unit }, ingredient);
  if (!Number.isFinite(baseQty) || baseQty <= 0) return null;
  return Math.max(1, Math.round(baseQty * rate));
}

export function compareQty({ have, need, lowThreshold, ingredient }) {
  const haveBase = toBase(have, ingredient);
  const needBase = toBase(need, ingredient);
  if (Number.isNaN(haveBase) || Number.isNaN(needBase)) return "unknown";
  if (haveBase < needBase) return "missing";
  const thresholdBase = lowThreshold != null
    ? toBase({ amount: lowThreshold, unit: have.unit }, ingredient)
    : 0;
  if (haveBase - needBase <= thresholdBase) return "low";
  return "ok";
}

// ─── compound vs. primitive kind ───────────────────────────────────────
//
// Primitive ingredients are terminal — basil is basil, you don't build it
// from simpler things. Compound ingredients (sriracha, pesto, stocks,
// mustards) can be purchased OR produced by a scratch recipe. The same
// id covers both cases — a bottle of Huy Fong sriracha and a jar of
// homemade sriracha both live as ingredientId === "sriracha" in the
// pantry, merge by id, match the same recipes.
//
// Source of truth is skillDev.fromScratchRecipeId — if a scratch recipe
// is linked, the ingredient is a compound. This set is a sugar helper
// for UI code that wants the primitive/compound split without reaching
// into skillDev.

export const COMPOUND_INGREDIENT_IDS = new Set([
  "sriracha",
  "pesto",
  "chicken_stock",
  // future: beef_stock, mayo, ketchup, dijon, tomato_paste, ricotta,
  // hot_sauce, peanut_butter, compound butter, vanilla extract, …
  // Each gets added here when its scratch recipe ships.
]);

export function ingredientKind(id) {
  return COMPOUND_INGREDIENT_IDS.has(id) ? "compound" : "primitive";
}

// ─── ingredient states ─────────────────────────────────────────────────
//
// Physical form an ingredient lives in on the shelf. Bread is the canonical
// example — one "bread" id, three rows: loaf, slices, crumbs. Recipes that
// care about state declare it; the matcher scopes accordingly. Users can
// convert between states via the "Make X from Y" flow (e.g., turn a loaf
// into crumbs, decrementing the loaf and creating a new crumbs row).
//
// Everything here is data-only — the convert UX, the state badges on
// pantry rows, and the state-aware recipe matcher live in their own
// commits and read from these maps.
//
//   INGREDIENT_STATES       — per-ingredient-id ordered state list. Order
//                             is "most raw → most processed" so the UI can
//                             render a sensible progression.
//   STATE_LABELS            — short human strings (singular). "grated"
//                             not "gratedcheese" — the ingredient name
//                             already tells the user which item is in
//                             that state.
//   DEFAULT_STATE_FOR       — preselected state when a user manually adds
//                             a new row for an ingredient that has states.
//                             Usually the least-processed / shelf-stable
//                             form ("loaf", "block", "whole") so a naive
//                             pantry entry lands sensibly.
//
// An ingredient without an entry here has no state distinction; pantry
// rows for it carry state=null and matching ignores state entirely.

// Meat state vocabulary — shared across every meat hub (chicken, beef,
// pork, turkey) so every canonical inheriting from those hubs gets the
// same list. Organized into four axes per the product's meat-state
// spec:
//
//   1. WHOLE / INTACT CUTS (default) — minimally altered structure.
//      whole, steak_cut, chop, fillet, tenderloin, rack.
//   2. CUT / REDUCED — geometry changed, composition intact.
//      cubed, diced, chunks, strips, sliced, shaved, shredded.
//   3. GROUND / COMMINUTED — structure destroyed.
//      ground (coarse), minced (finer), paste (uniform).
//   4. FORMED / RECONSTRUCTED — ground meat reshaped.
//      patty, meatball, sausage, loaf, nuggets.
//
// Plus cross-cutting cooking-stage flags: raw, cooked. Legacy compound
// states (shredded_cooked, diced_cooked) stay in the list so existing
// pantry rows with those values continue to render + remain editable
// in the state picker — don't remove them without a migration.
const MEAT_STATES = [
  // Whole / intact cuts — default band
  "whole", "steak_cut", "chop", "fillet", "tenderloin", "rack",
  // Cut / reduced forms — geometry only
  "cubed", "diced", "chunks", "strips", "sliced", "shaved", "shredded",
  // Ground / comminuted
  "ground", "minced", "paste",
  // Formed / reconstructed
  "patty", "meatball", "sausage", "links", "loaf", "nuggets",
  // Preserved / cured — structurally a separate axis but users
  // shop for jerky as "meat in a state", not a separate canonical.
  // All shelf-stable: jerky and cured (salami, prosciutto, pepperoni
  // in stick form) live at room temp sealed; "dried" covers air-dried
  // / dehydrated meats (biltong, dried beef strips); "canned" covers
  // tinned / pouched / shelf-stable retort packs (SPAM, Vienna
  // sausages, canned chicken / pork, deviled ham). Adding any of
  // these to a meat row forces the location cascade to pantry; jerky
  // / cured / dried route to the jerky_snacks tile, canned routes to
  // canned_jarred (see pantryTiles.js).
  "jerky", "cured", "dried", "canned",
  // Cooking stage
  "raw", "cooked",
  // Legacy compound — retained for backward compatibility
  "shredded_cooked", "diced_cooked",
];

// Ingredient densities (grams per millilitre) for mass ↔ volume
// bridging inside convertWithBridge. Without this, a recipe calling
// for "2 tbsp olive oil" against a pantry row in oz fails to convert
// because oil's mass (oz) and volume (tbsp) live in different unit
// families — the universal ladder only bridges within a family.
//
// Values from standard culinary references (USDA, King Arthur Baking,
// Cook's Illustrated). Close approximations; precision beyond one
// decimal isn't meaningful for cook-time estimation.
//
// CANONICAL DEFAULT when a slug isn't in this table: 1.0 g/ml (water-
// equivalent). Works for water-like liquids (broth, stock, most
// milks, juices, vinegars) within ~10% accuracy. Dense syrups and
// low-density fats need explicit entries to avoid drift.
export const INGREDIENT_DENSITY_G_PER_ML = {
  // Fats and oils
  olive_oil:     0.92,
  vegetable_oil: 0.92,
  canola_oil:    0.92,
  coconut_oil:   0.92,
  sesame_oil:    0.92,
  butter:        0.911,
  ghee:          0.91,
  // Dairy — close to water; entries here mostly for explicit
  // calibration, not correction
  milk:          1.03,
  milk_2pct:     1.03,
  milk_skim:     1.035,
  heavy_cream:   0.994,
  half_and_half: 1.01,
  buttermilk:    1.03,
  yogurt:        1.04,
  sour_cream:    0.994,
  // Sweeteners — dense
  honey:         1.42,
  maple_syrup:   1.32,
  corn_syrup:    1.38,
  molasses:      1.43,
  agave:         1.40,
  sugar:         0.85,  // granulated (bulk density — not true density)
  brown_sugar:   0.93,  // packed
  powdered_sugar: 0.56,
  // Flours & starches (bulk density)
  flour:         0.53,  // all-purpose, sifted
  bread_flour:   0.53,
  cake_flour:    0.45,
  whole_wheat_flour: 0.54,
  cornstarch:    0.51,
  cornmeal:      0.68,
  oats:          0.41,  // rolled
  // Salts
  salt:          1.20,  // table salt (fine)
  kosher_salt:   0.96,
  sea_salt:      1.04,
  // Other baking / pantry
  baking_powder: 0.90,
  baking_soda:   1.10,
  cocoa_powder:  0.41,
  // Condiments / sauces — most are water-adjacent
  soy_sauce:     1.18,
  fish_sauce:    1.20,
  vinegar:       1.01,
  balsamic_vinegar: 1.06,
  ketchup:       1.09,
  mustard:       1.05,
  mayonnaise:    0.91,
  // Pastes / concentrates
  tomato_paste:  1.04,
  tomato_sauce:  1.05,
  peanut_butter: 1.09,
  tahini:        1.07,
};

// CUT axis — anatomical / butchery slot. Orthogonal to STATE per
// CLAUDE.md; a given (canonical, cut) pair can take any state value.
// Chicken breast, cubed is chicken + cut=breast + state=cubed; ground
// chicken thigh is chicken + cut=thigh + state=ground.
//
// Scope is deliberately pragmatic: the cuts listed are what a typical
// grocery shopper encounters (Jewel, Kroger, Trader Joe's shelf cuts).
// Full-animal butchery yield trees (primals, subprimals, retail cuts)
// are out of scope — that's a pro-butcher workflow, not a pantry app.
//
// Keyed by BASE canonical slug. Other species (lamb) can be added as
// new base canonicals ship without touching existing entries.
export const CUTS_FOR = {
  chicken: ["breast","thigh","leg","drumstick","wing","tenderloin","back","whole_bird"],
  // Beef cuts in approximate grocery-counter encounter order: the
  // common steaks first (the ones a shopper would recognize on a
  // case label), then specialty cuts, then roasts and ribs. Ordering
  // matters because it drives the picker's display order; the
  // first-screen options should be the ones 80% of cooks reach for.
  beef: [
    // ── Steaks (popular)
    "ribeye","ny_strip","filet_mignon","t_bone","porterhouse",
    "sirloin","top_sirloin","tenderloin","tri_tip",
    // ── Steaks (specialty / butcher cuts)
    "flank","skirt","hanger","flat_iron","denver","chuck_eye","picanha",
    // ── Roasts
    "brisket","chuck","bottom_sirloin",
    "round","top_round","bottom_round","eye_of_round",
    // ── Ribs
    "short_rib",
  ],
  pork: [
    // ── Chops
    "chop","loin_chop","sirloin_chop",
    // ── Roasts
    "loin","shoulder","butt","belly","tenderloin","ham","shank",
    // ── Ribs
    "baby_back_rib","spare_rib","st_louis_rib","country_rib","rib",
  ],
  turkey:  ["breast","thigh","leg","wing","tenderloin","whole_bird"],
};

// Default gram weights per count for each meat cut. The "one piece"
// conversion — 1 chicken breast ≈ 200g, 1 thigh ≈ 120g, 1 ribeye ≈
// 340g. Keyed by BASE canonical, then cut slug. Used as a fallback
// in effectiveCountWeightG when the pantry row doesn't carry an
// explicit countWeightG and doesn't have a packageAmount-derivable
// value — "the user has Chicken + cut=breast, recipe wants 3 count,
// we know a breast is ~200g so 3 count ≈ 600g ≈ 1.32 lb."
//
// Users can still override per-row via pantry_items.count_weight_g
// ("my cloves are big — 6g each, not 4") and that wins over these
// defaults. These are rough averages good enough for the default
// prefill + conversion shadow; not claims of precision.
export const CUT_WEIGHTS_G = {
  chicken: {
    breast:      200,  // boneless-skinless, 170–225g USDA range
    thigh:       120,  // boneless-skinless
    leg:         300,  // whole leg quarter (drumstick + thigh, bone-in)
    drumstick:   100,  // bone-in drumstick alone
    wing:         80,
    tenderloin:   45,
    back:        300,
    whole_bird: 1800,  // ~4 lb whole chicken
  },
  beef: {
    ribeye:        340,  // 12 oz steak
    ny_strip:      340,
    filet_mignon:  225,  // 8 oz center-cut steak
    t_bone:        450,  // 16 oz bone-in
    porterhouse:   565,  // 20 oz bone-in (larger tenderloin section)
    sirloin:       225,  // 8 oz
    top_sirloin:   225,  // sirloin steak portion
    tenderloin:    900,  // ~2 lb whole roast (filet is the steak portion)
    tri_tip:      1135,  // 2.5 lb roast
    flank:         680,  // ~1.5 lb whole flank
    skirt:         340,  // ~12 oz portion
    hanger:        285,  // ~10 oz, single piece per cow
    flat_iron:     225,  // 8 oz portion
    denver:        200,  // 7 oz portion
    chuck_eye:     200,  // 7 oz, "poor man's ribeye"
    picanha:      1360,  // 3 lb whole picanha (sirloin cap)
    brisket:      2270,  // 5 lb point
    chuck:        1360,  // 3 lb roast
    bottom_sirloin: 1135, // 2.5 lb roast
    round:         680,
    top_round:    1820,  // 4 lb roast (London broil cut)
    bottom_round: 1820,
    eye_of_round: 1360,  // 3 lb roast
    short_rib:     170,
  },
  pork: {
    chop:           170,  // 6 oz bone-in
    loin_chop:      225,  // 8 oz center-cut
    sirloin_chop:   200,  // 7 oz
    loin:          1360,  // 3 lb roast
    shoulder:      1800,  // 4 lb (picnic / arm)
    butt:          1800,  // 4 lb Boston butt (synonym for shoulder cut, but shelf-distinct)
    belly:         1360,
    tenderloin:     340,  // small cut, ~12 oz
    ham:           3175,  // 7 lb bone-in
    shank:          680,
    baby_back_rib: 1135,  // ~2.5 lb rack
    spare_rib:     1820,  // ~4 lb rack
    st_louis_rib:  1820,  // ~4 lb trimmed rack
    country_rib:   1135,  // ~2.5 lb pack
    rib:            680,  // generic
  },
  turkey: {
    breast:     3175,  // 7 lb boneless
    thigh:       900,
    leg:        1360,
    wing:        500,
    tenderloin:  200,
    whole_bird: 6800,  // 15 lb whole turkey
  },
};

// Typical grams per count for canonicals whose ladder is count-only
// (no g:1 or ml:1 anchor). Brand nutrition on these items ships as
// per="100g" because that's how bakery / packaged-goods labels
// report it — but without a mass axis the resolver has nothing to
// scale along. This table supplies the missing grams-per-count as
// the last-resort fallback in effectiveCountWeightG, after explicit
// row override, package-derived, and cut-weight tiers.
//
// Keep entries conservative — one gram-weight per count, not a
// range. The pantry row can still override with a measured weight
// when the user wants precision. Only seed canonicals where the
// typical weight is well-characterized; otherwise leaving it null
// surfaces the "set each ~g" prompt, which is more honest than a
// guess.
export const COUNT_WEIGHTS_G = {
  tortillas: 49,  // standard 6-inch flour tortilla
};

// Per-cut nutrition for meat hub canonicals. Parallel to CUT_WEIGHTS_G —
// same hub → cut key shape, values are the nutrition block the resolver
// returns (per="100g" always, so the scaleFactor mass ladder on chicken /
// beef / pork / turkey picks it up directly). Sourced from USDA SR
// Legacy averages for raw, trimmed, boneless/skinless where applicable;
// legacy compound canonicals (chicken_breast, ribeye) pulled their
// inline values from here originally.
//
// The resolver (src/lib/nutrition.js) consults this table as the tier-4
// fallback when the pantry row is tagged with a cut but no brand /
// ingredient_info override exists. Explicit pantryRow.nutritionOverride
// and brand rows still win.
//
// Hub canonicals without a cut tag fall back to DEFAULT_CUT_PER_HUB
// below — the count.toBase convention picks the most-commonly-purchased
// cut (chicken count=200g=breast, beef count=340g=ribeye, etc.).
export const CUT_NUTRITION = {
  chicken: {
    breast:      { per: "100g", kcal: 165, protein_g: 31, fat_g: 3.6, carb_g: 0 },
    thigh:       { per: "100g", kcal: 209, protein_g: 19, fat_g: 15,  carb_g: 0 },
    leg:         { per: "100g", kcal: 184, protein_g: 19, fat_g: 12,  carb_g: 0 },
    drumstick:   { per: "100g", kcal: 172, protein_g: 18, fat_g: 11,  carb_g: 0 },
    wing:        { per: "100g", kcal: 222, protein_g: 21, fat_g: 15,  carb_g: 0 },
    tenderloin:  { per: "100g", kcal: 119, protein_g: 25, fat_g: 1.3, carb_g: 0 },
    back:        { per: "100g", kcal: 318, protein_g: 14, fat_g: 28,  carb_g: 0 },
    whole_bird:  { per: "100g", kcal: 215, protein_g: 18, fat_g: 15,  carb_g: 0 },
  },
  beef: {
    ribeye:        { per: "100g", kcal: 291, protein_g: 20, fat_g: 22,  carb_g: 0 },
    ny_strip:      { per: "100g", kcal: 200, protein_g: 23, fat_g: 11,  carb_g: 0 },
    filet_mignon:  { per: "100g", kcal: 219, protein_g: 22, fat_g: 14,  carb_g: 0 },  // tenderloin steak portion
    t_bone:        { per: "100g", kcal: 247, protein_g: 21, fat_g: 18,  carb_g: 0 },
    porterhouse:   { per: "100g", kcal: 247, protein_g: 21, fat_g: 18,  carb_g: 0 },
    sirloin:       { per: "100g", kcal: 158, protein_g: 21, fat_g: 7,   carb_g: 0 },
    top_sirloin:   { per: "100g", kcal: 158, protein_g: 22, fat_g: 7,   carb_g: 0 },
    tenderloin:    { per: "100g", kcal: 219, protein_g: 20, fat_g: 14,  carb_g: 0 },
    tri_tip:       { per: "100g", kcal: 192, protein_g: 22, fat_g: 11,  carb_g: 0 },
    flank:         { per: "100g", kcal: 192, protein_g: 22, fat_g: 11,  carb_g: 0 },
    skirt:         { per: "100g", kcal: 240, protein_g: 20, fat_g: 17,  carb_g: 0 },
    hanger:        { per: "100g", kcal: 220, protein_g: 21, fat_g: 14,  carb_g: 0 },
    flat_iron:     { per: "100g", kcal: 211, protein_g: 22, fat_g: 13,  carb_g: 0 },
    denver:        { per: "100g", kcal: 250, protein_g: 19, fat_g: 19,  carb_g: 0 },
    chuck_eye:     { per: "100g", kcal: 280, protein_g: 19, fat_g: 22,  carb_g: 0 },  // close to ribeye
    picanha:       { per: "100g", kcal: 219, protein_g: 22, fat_g: 14,  carb_g: 0 },
    brisket:       { per: "100g", kcal: 155, protein_g: 21, fat_g: 7,   carb_g: 0 },
    chuck:         { per: "100g", kcal: 240, protein_g: 17, fat_g: 19,  carb_g: 0 },
    bottom_sirloin:{ per: "100g", kcal: 200, protein_g: 21, fat_g: 11,  carb_g: 0 },
    round:         { per: "100g", kcal: 139, protein_g: 22, fat_g: 5,   carb_g: 0 },
    top_round:     { per: "100g", kcal: 137, protein_g: 23, fat_g: 4,   carb_g: 0 },
    bottom_round:  { per: "100g", kcal: 139, protein_g: 22, fat_g: 5,   carb_g: 0 },
    eye_of_round:  { per: "100g", kcal: 137, protein_g: 23, fat_g: 4,   carb_g: 0 },  // leanest cut
    short_rib:     { per: "100g", kcal: 303, protein_g: 18, fat_g: 25,  carb_g: 0 },
  },
  pork: {
    chop:           { per: "100g", kcal: 202, protein_g: 24, fat_g: 11,  carb_g: 0 },
    loin_chop:      { per: "100g", kcal: 195, protein_g: 25, fat_g: 10,  carb_g: 0 },
    sirloin_chop:   { per: "100g", kcal: 195, protein_g: 23, fat_g: 11,  carb_g: 0 },
    loin:           { per: "100g", kcal: 143, protein_g: 22, fat_g: 5,   carb_g: 0 },
    shoulder:       { per: "100g", kcal: 269, protein_g: 19, fat_g: 21,  carb_g: 0 },
    butt:           { per: "100g", kcal: 269, protein_g: 19, fat_g: 21,  carb_g: 0 },  // Boston butt; same anatomy as shoulder
    belly:          { per: "100g", kcal: 518, protein_g: 9,  fat_g: 53,  carb_g: 0 },
    tenderloin:     { per: "100g", kcal: 143, protein_g: 22, fat_g: 5,   carb_g: 0 },
    ham:            { per: "100g", kcal: 139, protein_g: 21, fat_g: 5,   carb_g: 0 },
    shank:          { per: "100g", kcal: 165, protein_g: 22, fat_g: 8,   carb_g: 0 },
    baby_back_rib:  { per: "100g", kcal: 277, protein_g: 19, fat_g: 22,  carb_g: 0 },
    spare_rib:      { per: "100g", kcal: 280, protein_g: 18, fat_g: 23,  carb_g: 0 },
    st_louis_rib:   { per: "100g", kcal: 280, protein_g: 18, fat_g: 23,  carb_g: 0 },
    country_rib:    { per: "100g", kcal: 250, protein_g: 21, fat_g: 18,  carb_g: 0 },
    rib:            { per: "100g", kcal: 277, protein_g: 19, fat_g: 22,  carb_g: 0 },
  },
  turkey: {
    breast:      { per: "100g", kcal: 120, protein_g: 26, fat_g: 1,   carb_g: 0 },
    thigh:       { per: "100g", kcal: 160, protein_g: 21, fat_g: 8,   carb_g: 0 },
    leg:         { per: "100g", kcal: 144, protein_g: 20, fat_g: 6,   carb_g: 0 },
    wing:        { per: "100g", kcal: 180, protein_g: 22, fat_g: 10,  carb_g: 0 },
    tenderloin:  { per: "100g", kcal: 109, protein_g: 23, fat_g: 1,   carb_g: 0 },
    whole_bird:  { per: "100g", kcal: 148, protein_g: 22, fat_g: 6,   carb_g: 0 },
  },
};

// Default cut when a hub-canonical pantry row isn't cut-tagged. Mirrors
// each hub's count.toBase convention (chicken count=200g=breast, beef
// count=340g=ribeye, pork count=170g=chop, turkey count=3175g=breast)
// so the nutrition fallback matches the gram fallback.
export const DEFAULT_CUT_PER_HUB = {
  chicken: "breast",
  beef:    "ribeye",
  pork:    "chop",
  turkey:  "breast",
};

// Per-state nutrition for meat hub canonicals. Grinding homogenizes
// the animal — ground beef from a mixed trim is nutritionally distinct
// from any one whole cut (typical 85/15 at ~250 kcal vs ribeye's 291,
// round's 139). The resolver consults this table BEFORE CUT_NUTRITION,
// so state=ground wins over any cut axis: grinding effectively erases
// the cut distinction.
//
// Seeded with "ground" for the four meat hubs using commonly-purchased
// fat ratios (85/15 for beef, default-blend for chicken/pork/turkey).
// Add new states (brined, cured, smoked, cooked) here if/when they
// surface as pantry tags.
export const STATE_NUTRITION = {
  beef:    { ground: { per: "100g", kcal: 250, protein_g: 18, fat_g: 20, carb_g: 0 } },  // 85/15 raw
  chicken: { ground: { per: "100g", kcal: 143, protein_g: 17, fat_g: 8,  carb_g: 0 } },  // blend raw
  pork:    { ground: { per: "100g", kcal: 263, protein_g: 17, fat_g: 22, carb_g: 0 } },  // blend raw
  turkey:  { ground: { per: "100g", kcal: 148, protein_g: 19, fat_g: 8,  carb_g: 0 } },  // 93/7 raw
};

export const CUT_LABELS = {
  // Chicken / turkey
  breast: "breast", thigh: "thigh", leg: "leg", drumstick: "drumstick",
  wing: "wing", tenderloin: "tenderloin", back: "back", whole_bird: "whole bird",
  // Beef
  ribeye: "ribeye",
  ny_strip: "NY strip",
  filet_mignon: "filet mignon",
  t_bone: "T-bone",
  porterhouse: "porterhouse",
  sirloin: "sirloin",
  top_sirloin: "top sirloin",
  bottom_sirloin: "bottom sirloin",
  tri_tip: "tri-tip",
  flank: "flank",
  skirt: "skirt",
  hanger: "hanger",
  flat_iron: "flat iron",
  denver: "Denver",
  chuck_eye: "chuck eye",
  picanha: "picanha",
  brisket: "brisket",
  chuck: "chuck",
  round: "round",
  top_round: "top round",
  bottom_round: "bottom round",
  eye_of_round: "eye of round",
  short_rib: "short rib",
  // Pork
  chop: "chop",
  loin_chop: "loin chop",
  sirloin_chop: "sirloin chop",
  loin: "loin",
  shoulder: "shoulder",
  butt: "Boston butt",
  belly: "belly",
  rib: "rib",
  baby_back_rib: "baby back ribs",
  spare_rib: "spare ribs",
  st_louis_rib: "St. Louis ribs",
  country_rib: "country-style ribs",
  ham: "ham",
  shank: "shank",
};

export function cutsForIngredient(ingredientOrId) {
  const id = typeof ingredientOrId === "string" ? ingredientOrId : ingredientOrId?.id;
  if (!id) return null;
  if (CUTS_FOR[id]) return CUTS_FOR[id];
  // Walk to base through aliases — "chicken_breast" → "chicken" → has cuts.
  const alias = CANONICAL_ALIASES[id];
  if (alias?.base && CUTS_FOR[alias.base]) return CUTS_FOR[alias.base];
  return null;
}

export function cutLabel(cut) {
  if (!cut) return null;
  return CUT_LABELS[cut] || String(cut).replace(/_/g, " ");
}

export const INGREDIENT_STATES = {
  bread:         ["loaf", "slices", "crumbs", "cubes", "toasted"],
  // Covers every specific cheese AND the generic cheese hub — all cheeses
  // share the same state vocabulary. The cheese-level registry entries
  // (parmesan, pecorino, mozzarella, etc.) inherit this list via their
  // parentId === "cheese_hub" link.
  cheese_hub:    ["block", "grated", "shredded", "sliced", "cubed", "crumbled"],
  // Every meat hub shares MEAT_STATES. Specific cuts (ribeye, pork_loin,
  // turkey_breast, chicken_breast, etc.) inherit via their parentId
  // link — no per-cut entry needed. Keeps the vocabulary consistent:
  // a ribeye can be "steak_cut" or "sliced" without us having to
  // enumerate every cut × state combination.
  beef_hub:      MEAT_STATES,
  chicken_hub:   MEAT_STATES,
  pork_hub:      MEAT_STATES,
  turkey_hub:    MEAT_STATES,
  // chicken (the whole-bird canonical) sits outside the hub-inherit
  // path — its parentId is null — so it needs its own entry. Same
  // list; the chicken "whole" default covers the intact bird case.
  chicken:       MEAT_STATES,
  // Seafood — own state vocabulary keyed off seafood_hub. Specific
  // fish canonicals (tuna / salmon / cod / etc.) inherit via
  // parentId. "canned" is what makes shelf-stable tuna / salmon /
  // sardines route to pantry.canned_jarred instead of fridge.seafood.
  // "smoked" covers cold/hot-smoked fish (still fridge-default in
  // the location cascade — only canned forces pantry — since most
  // smoked fish stays refrigerated until consumed).
  seafood_hub:   ["whole", "fillet", "steak_cut", "raw", "cooked", "canned", "smoked", "cured", "dried"],
  // Milk + dairy preservation. milk variants (whole / 2% / skim /
  // half-and-half / heavy cream / buttermilk) all inherit. "uht"
  // covers Parmalat-style aseptic packs; "evaporated" / "condensed"
  // are the canned forms; "powdered" covers shelf-stable milk
  // powder. Each forces pantry in the location cascade. Fresh /
  // raw stay on fridge.
  milk_hub:      ["uht", "evaporated", "condensed", "powdered"],
  salt:          ["fine", "coarse", "flaky"],
  kosher_salt:   ["coarse", "fine"],
  onion:         ["whole", "diced", "sliced", "minced"],
  garlic:        ["head", "cloves", "minced", "paste", "roasted"],
  lemon:         ["whole", "juiced", "zested"],
  lime:          ["whole", "juiced", "zested"],
  tomato:        ["whole", "diced", "sliced", "crushed"],
  carrot:        ["whole", "diced", "sliced", "shredded", "julienned"],
  potato:        ["whole", "diced", "sliced", "mashed", "shredded"],
};

export const STATE_LABELS = {
  loaf: "loaf",       slices: "sliced",    crumbs: "crumbs",    cubes: "cubed",    toasted: "toasted",
  block: "block",     grated: "grated",    shredded: "shredded",sliced: "sliced",  cubed: "cubed",    crumbled: "crumbled",
  raw: "raw",         cooked: "cooked",    ground: "ground",
  shredded_cooked: "shredded (cooked)", diced_cooked: "diced (cooked)",
  fine: "fine",       coarse: "coarse",    flaky: "flaky",
  whole: "whole",     diced: "diced",      minced: "minced",    julienned: "julienned",
  head: "head",       cloves: "cloves",    paste: "paste",      roasted: "roasted",
  juiced: "juiced",   zested: "zested",
  crushed: "crushed", mashed: "mashed",
  // Meat state labels — new taxonomy. Display text stays lowercase
  // to match the rest of the vocabulary; the UI uppercases at render.
  steak_cut: "steak cut", chop: "chop",       fillet: "fillet",
  tenderloin: "tenderloin", rack: "rack",
  chunks: "chunks",     strips: "strips",   shaved: "shaved",
  patty: "patty",       meatball: "meatball", sausage: "sausage",
  links: "links",       nuggets: "nuggets",  jerky: "jerky",
  cured: "cured",       dried: "dried",      canned: "canned",
  smoked: "smoked",
  // Shelf-stable dairy preservation states (uht / evaporated /
  // condensed / powdered) — see seafood_hub + cheese_hub vocab in
  // INGREDIENT_STATES. Force pantry routing.
  uht: "UHT",                evaporated: "evaporated",
  condensed: "condensed",    powdered: "powdered",
  // Pickled / fermented produce — sealed pantry, opened fridge.
  pickled: "pickled",        fermented: "fermented",
  // "loaf" already defined above (reused for meatloaf)
};

// Every state token the registry knows about, flattened into one set
// for free-text matchers that need to recognize a prep term wherever
// it shows up ("chicken breast, cubed", "ground fresh pork", "shredded
// cheese"). Derived from INGREDIENT_STATES + STATE_LABELS so adding a
// new state in the registry propagates to every matcher with no extra
// wiring. Multi-word labels ("steak cut") contribute each word
// separately since matchers operate token-by-token. Common prep
// descriptors that live outside the formal STATE axis but function
// identically in free-text ("boneless", "skinless", "chopped") are
// added as well.
// Tokens that would cause canonical collisions if stripped as state.
// Per the STATE-as-axis design (CLAUDE.md), ingredients like tomato
// paste, chicken sausage, beef jerky, and bread crumbs are NOT new
// canonicals — they're the base ingredient carrying a STATE value.
// So "paste", "sausage", "jerky", "crumbs", etc. all strip freely.
// The one exception is "steak" because "steak sauce" is a genuinely
// distinct condiment canonical; stripping would collide with other
// sauces at the head-noun level ("steak sauce" ↔ "soy sauce" both
// reduce to "sauce"). Keep this list minimal — add only on proven
// collision, not hypothetically.
const STATE_TOKEN_EXCLUSIONS = new Set(["steak"]);

export const ALL_STATE_TOKENS = (() => {
  const out = new Set();
  const add = (val) => {
    if (!val || typeof val !== "string") return;
    for (const tok of val.toLowerCase().split(/[\s_]+/)) {
      if (tok.length < 3) continue;
      if (STATE_TOKEN_EXCLUSIONS.has(tok)) continue;
      out.add(tok);
    }
  };
  for (const list of Object.values(INGREDIENT_STATES)) {
    if (Array.isArray(list)) for (const s of list) add(s);
  }
  for (const [slug, label] of Object.entries(STATE_LABELS)) {
    add(slug);
    add(label);
  }
  // Not formally STATE but functionally indistinguishable in free
  // text — AI and users write them interchangeably with state terms.
  for (const t of ["boneless", "skinless", "chopped"]) add(t);
  return out;
})();

export const DEFAULT_STATE_FOR = {
  bread: "loaf",
  cheese_hub: "block",
  // Meat hubs intentionally have NO default state. Prep is encoded in
  // the canonical itself in the new model (chicken_breast vs
  // ground_chicken vs sliced_chicken_breast are separate canonicals,
  // same way ground_cinnamon vs cinnamon_stick are separate). Setting
  // state="whole" by default produces nonsense like "Whole Ground
  // Chicken" once the canonical already encodes prep, and offers no
  // information when the canonical is genuinely whole. State stays
  // null until the user explicitly picks one.
  salt: "fine",
  kosher_salt: "coarse",
  onion: "whole",
  garlic: "head",
  lemon: "whole",
  lime: "whole",
  tomato: "whole",
  carrot: "whole",
  potato: "whole",
};

// Food-type → state-vocab hub mapping. Food category IS the source
// of state vocabulary — the specific canonical doesn't get to
// override. Burrata, mozzarella, and parmesan all share the cheese
// state vocab because they're all wweia_cheese. Ribeye, ground beef,
// and brisket all share MEAT_STATES because they're all wweia_beef.
//
// This keeps the state picker from being over-fitted to the
// canonical ("burrata comes in balls"? weird). The user's mental
// model is "I have a chunk of cheese in my fridge; what shape is
// it?" — a question that belongs to the category, not the specific
// cheese.
//
// Food types without a state vocabulary (produce, pantry staples,
// canned goods) stay unmapped so the STATE field doesn't render at
// all on those items.
const FOOD_TYPE_STATE_HUB = {
  // proteins — every meat food type to the shared MEAT_STATES
  wweia_beef:     "beef_hub",
  wweia_pork:     "pork_hub",
  wweia_poultry:  "chicken_hub",
  wweia_lamb:     "lamb_hub",
  wweia_hot_dogs: "pork_hub",
  wweia_sausages: "pork_hub",
  // dairy — cheese has a rich state vocabulary (block, grated,
  // shredded, sliced, cubed, crumbled) regardless of specific cheese.
  wweia_cheese:   "cheese_hub",
  // grains — bread has its own vocab (loaf, slices, crumbs, ...).
  wweia_bread:    "bread",
};

// Does this ingredient id have a meaningful state vocabulary? Walks the
// parent chain so a specific cheese (parmesan) inherits from cheese_hub
// without needing its own entry.
export function statesForIngredient(ingredientOrId) {
  const id = typeof ingredientOrId === "string" ? ingredientOrId : ingredientOrId?.id;
  if (!id) return null;
  if (INGREDIENT_STATES[id]) return INGREDIENT_STATES[id];
  const ing = typeof ingredientOrId === "object" ? ingredientOrId : findIngredient(id);
  const parentId = ing?.parentId;
  if (parentId && INGREDIENT_STATES[parentId]) return INGREDIENT_STATES[parentId];
  return null;
}

// Item-level state lookup. FOOD CATEGORY is the primary source of
// state vocabulary — the specific canonical doesn't drive it. The
// user's mental model: "it's cheese, what shape is it?" Not "it's
// burrata, what shape is it?". That means:
//
//   1. item.typeId → FOOD_TYPE_STATE_HUB → state vocab (WINS)
//   2. canonical's own states (legacy / typeId-less rows only)
//
// Rationale (user-stated): canonical-level state is "too drilled
// down." Burrata and parmesan are both wweia_cheese; both use the
// same state vocab (block/grated/shredded/sliced/cubed/crumbled).
// A ribeye, a brisket, and a pork chop are all MEAT_STATES. The
// canonical-first approach over-fit the state vocabulary to the
// specific ingredient; category-first keeps it consistent.
export function statesForItem(item) {
  if (!item) return null;
  // 1. Direct typeId → hub mapping. Food category drives state — the
  // primary path.
  const hubId = item.typeId ? FOOD_TYPE_STATE_HUB[item.typeId] : null;
  if (hubId && INGREDIENT_STATES[hubId]) return INGREDIENT_STATES[hubId];
  // 2. Canonical's own vocab via parent chain. Covers bundled
  // canonicals that inherit (hot_dog → pork_hub, ribeye → beef_hub,
  // parmesan → cheese_hub, etc.).
  const canonical = findIngredient(item.canonicalId || item.ingredientId);
  const fromCanon = statesForIngredient(canonical || item.canonicalId || item.ingredientId);
  if (fromCanon && fromCanon.length > 0) return fromCanon;
  // 3. Proxy-canonical fallback. If item.typeId is a bundled WWEIA
  // type that points at a canonical via canonicalIdForType (e.g.
  // wweia_sausages → "sausage"), walk THAT canonical's parent chain.
  // Catches cases where FOOD_TYPE_STATE_HUB is missing a mapping or
  // the item's own canonical/ingredient is unlinked but the type is
  // still set. Late-import-safe: uses the same INGREDIENTS array and
  // statesForIngredient helper we already have.
  if (item.typeId) {
    const proxyId = proxyCanonicalForFoodType(item.typeId);
    if (proxyId) {
      const proxy = findIngredient(proxyId);
      const fromProxy = statesForIngredient(proxy || proxyId);
      if (fromProxy && fromProxy.length > 0) return fromProxy;
    }
  }
  return null;
}

// Locally-resolved mirror of the FOOD_TYPES canonicalId bridge —
// avoids importing from foodTypes.js (which imports from us, circular).
// Keeps the small subset we actually need for state lookup. Expand if
// FOOD_TYPES grows new WWEIA types with canonical bridges.
function proxyCanonicalForFoodType(typeId) {
  const BRIDGE = {
    wweia_sausages:  "sausage",
    wweia_hot_dogs:  "hot_dog",
    wweia_bacon:     "bacon",
    wweia_beef:      "beef_hub",
    wweia_pork:      "pork_hub",
    wweia_poultry:   "chicken_hub",
    wweia_lamb:      "lamb_hub",
    wweia_cheese:    "cheese_hub",
    wweia_bread:     "bread",
  };
  return BRIDGE[typeId] || null;
}

// Default-state counterpart to statesForItem. Same priority:
// category wins, canonical fallback. Every meat category defaults
// to "whole"; cheese to "block"; bread to "loaf".
export function defaultStateForItem(item) {
  if (!item) return null;
  const hubId = item.typeId ? FOOD_TYPE_STATE_HUB[item.typeId] : null;
  if (hubId && DEFAULT_STATE_FOR[hubId]) return DEFAULT_STATE_FOR[hubId];
  const canonical = findIngredient(item.canonicalId || item.ingredientId);
  return defaultStateFor(canonical || item.canonicalId || item.ingredientId);
}

export function defaultStateFor(ingredientOrId) {
  const id = typeof ingredientOrId === "string" ? ingredientOrId : ingredientOrId?.id;
  if (!id) return null;
  if (DEFAULT_STATE_FOR[id]) return DEFAULT_STATE_FOR[id];
  const ing = typeof ingredientOrId === "object" ? ingredientOrId : findIngredient(id);
  const parentId = ing?.parentId;
  if (parentId && DEFAULT_STATE_FOR[parentId]) return DEFAULT_STATE_FOR[parentId];
  return null;
}

export function stateLabel(state) {
  if (!state) return "";
  return STATE_LABELS[state] || state;
}

// Receipt-code → canonical-state map. Grocery POS systems abbreviate
// aggressively (SHRD MOZZ, SLCD PROV, GRT PARM). This folds the common
// shorthand plus spelled-out forms to the controlled state vocabulary
// used by INGREDIENT_STATES.
//
// Keyed lowercase. Earlier entries win over later ones if patterns
// overlap (e.g., "shredded_cooked" should NOT match "shredded" alone —
// but the list below is for RAW codes on produce/cheese and doesn't
// touch the cooked-meat vocabulary, so there's no collision today).
//
// Exported so parseIdentity (and any other caller that wants the
// same raw-text → state vocabulary) can reuse this table without
// re-duplicating the patterns. Shape is stable: array of
// { pattern: RegExp, state: stateId } entries, iterated top-down.
export const STATE_ALIASES = [
  // dairy / cheese
  // Patterns include plural-noun forms ("Slices" / "Cubes" /
  // "Dices") because brands label pre-prepped cheese / produce
  // that way ("Sargento Cheddar Slices", "Diced Tomatoes" but
  // also "Tomato Dices"). Original `\bsliced?\b` only matched
  // slice / sliced — not slices — so "Cheddar Slices" fell
  // through state inference entirely.
  { pattern: /\bshrd\b|\bshreds?\b|\bshredded\b/i, state: "shredded" },
  { pattern: /\bslcd\b|\bslic(?:e|es|ed)\b/i,      state: "sliced"   },
  { pattern: /\bgrt\b|\bgrat(?:e|es|ed)\b/i,       state: "grated"   },
  { pattern: /\bblk\b|\bblocks?\b/i,               state: "block"    },
  { pattern: /\bcrmb\b|\bcrumbl(?:e|es|ed)\b/i,    state: "crumbled" },
  { pattern: /\bcub(?:e|es|ed)\b/i,                state: "cubed"    },
  // bread
  { pattern: /\bloaf\b/i,                        state: "loaf"     },
  { pattern: /\bcrumbs?\b|\bbrdcrmb\b/i,         state: "crumbs"   },
  { pattern: /\btoasted?\b/i,                    state: "toasted"  },
  // produce / aromatics
  { pattern: /\bwhl\b|\bwhole\b/i,               state: "whole"    },
  { pattern: /\bdced\b|\bdic(?:e|es|ed)\b/i,       state: "diced"    },
  { pattern: /\bmnc\b|\bminc(?:e|es|ed)\b/i,       state: "minced"   },
  { pattern: /\bjulienn(?:e|es|ed)\b/i,            state: "julienned" },
  { pattern: /\bmash(?:e[ds])?\b/i,                state: "mashed"   },
  { pattern: /\bcrush(?:e[ds])?\b/i,               state: "crushed"  },
  { pattern: /\bjuic(?:e|es|ed)\b/i,               state: "juiced"   },
  { pattern: /\bzest(?:e|es|ed)\b/i,               state: "zested"   },
  // meat — ordered by SPECIFICITY of the physical form. The
  // detector returns the FIRST in-vocab match, so this list is
  // the precedence ladder. Three tiers:
  //   1. Most-specific FORMED shapes (meatball / loaf / nuggets /
  //      patty / links). When the package literally says "ground
  //      beef patty", patty is the right read — the formed shape
  //      is more identity-defining than the source-meat process.
  //   2. `ground` — beats the more general `sausage` below. A
  //      package that says "Mild Italian Ground Sausage" is bulk
  //      ground meat in a tub, not link sausages; the user's
  //      mental model of the product is "ground" with sausage
  //      seasoning. Without this ordering, the loop hits
  //      `sausage` first (which is also in vocab) and the ground
  //      signal is dropped.
  //   3. `sausage` and below — general form-words that should only
  //      win when no more specific descriptor is present.
  { pattern: /\bmeatball(?:s)?\b|\bmtbl\b/i,     state: "meatball" },
  { pattern: /\bmeatloaf\b|\bmtlf\b/i,           state: "loaf"     },
  { pattern: /\bnuggets?\b|\bnggt\b/i,           state: "nuggets"  },
  { pattern: /\bpatt(?:y|ies)\b|\bptty\b/i,      state: "patty"    },
  { pattern: /\blinks?\b/i,                      state: "links"    },
  { pattern: /\bgrnd\b|\bground\b/i,             state: "ground"   },
  { pattern: /\bsausages?\b|\bssg\b/i,           state: "sausage"  },
  // Preservation states. "stick" / "snack stick" / "beef stick"
  // catches Slim Jim, Jack Link's stick-form products, etc. that
  // never use the word "jerky" in the productName but are
  // structurally the same shelf-stable form. "biltong" / "salami
  // stick" / "pepperoni stick" all reduce to cured. "canned" / "tin"
  // / branded shelf-stable retort packs (SPAM, Vienna sausages,
  // potted meat, deviled ham) reduce to canned. Ordering matters:
  // most-specific first so "biltong" doesn't accidentally swallow
  // a future "biltong-flavored" non-meat product, and SPAM-style
  // brand names (single-word) don't gobble unrelated tokens.
  { pattern: /\bjerky\b|\bjrky\b/i,              state: "jerky"    },
  { pattern: /\bbiltong\b/i,                     state: "dried"    },
  { pattern: /\bsnack\s+sticks?\b|\bbeef\s+sticks?\b|\bmeat\s+sticks?\b/i, state: "jerky" },
  { pattern: /\bsalami\s+sticks?\b|\bpepperoni\s+sticks?\b|\bdry\s+sausage\b|\bdried?\s+sausage\b/i, state: "cured" },
  { pattern: /\bcanned?\b|\btinned?\b|\bin\s+(oil|water|brine|sauce)\b/i, state: "canned" },
  { pattern: /\bspam\b|\bvienna\s+sausages?\b|\bpotted\s+meat\b|\bdeviled\s+ham\b/i, state: "canned" },
  { pattern: /\bsmoked\b|\bsmkd\b/i,             state: "smoked"   },
  // Shelf-stable dairy preservation — keyword-driven so a Parmalat
  // / evaporated milk / powdered milk scan picks up the right
  // pantry-forcing state even when OFF tags are sparse.
  { pattern: /\bsweetened\s+condensed\b|\bcondensed\s+milk\b/i, state: "condensed"  },
  { pattern: /\bevaporated\s+milk\b|\bevap\s+milk\b/i,          state: "evaporated" },
  { pattern: /\buht\b|\baseptic\b|\bshelf[- ]stable\s+milk\b/i, state: "uht"        },
  { pattern: /\bpowdered\s+milk\b|\bdry\s+milk\b|\bmilk\s+powder\b/i, state: "powdered" },
  // Pickled / fermented produce — sealed pantry, opened fridge.
  { pattern: /\bpickled\b|\bpckld\b/i,           state: "pickled"   },
  { pattern: /\bfermented\b|\bsauerkraut\b|\bkimchi\b/i, state: "fermented" },
  { pattern: /\btenderloin\b|\btndrln\b|\btndr\b/i, state: "tenderloin" },
  { pattern: /\bfil(?:l)?ets?\b|\bfil\b/i,       state: "fillet"   },
  { pattern: /\bsteak(?:s)?\b|\bstk\b/i,         state: "steak_cut" },
  { pattern: /\brack\b/i,                        state: "rack"     },
  { pattern: /\bchops?\b|\bchp\b/i,              state: "chop"     },
  { pattern: /\bshaved\b|\bshvd\b/i,             state: "shaved"   },
  { pattern: /\bstrips?\b|\bstrp\b/i,            state: "strips"   },
  { pattern: /\bchunks?\b|\bchnk\b/i,            state: "chunks"   },
  { pattern: /\bckd\b|\bcooked?\b/i,             state: "cooked"   },
  { pattern: /\braw\b|\bfresh\b/i,               state: "raw"      },
  // garlic
  { pattern: /\bhead\b/i,                        state: "head"     },
  { pattern: /\bcloves?\b/i,                     state: "cloves"   },
  { pattern: /\bpaste\b/i,                       state: "paste"    },
  { pattern: /\broasted\b/i,                     state: "roasted"  },
  // salt
  { pattern: /\bflaky\b|\bflaked\b/i,            state: "flaky"    },
  { pattern: /\bcoarse\b/i,                      state: "coarse"   },
  { pattern: /\bfine\b/i,                        state: "fine"     },
];

// Detect an ingredient state from free-text scan output. Returns the
// canonical state id when found AND that state exists in the
// ingredient's vocabulary. Returns null otherwise.
//
//   detectStateFromText("SHRD MOZZ", mozzarella_ingredient)
//     → "shredded"
//
//   detectStateFromText("WHL MILK", milk_ingredient)
//     → null   (milk has no state vocabulary — scan text is noise)
//
//   detectStateFromText("BREAD", bread_ingredient)
//     → null   (no state keyword matched)
export function detectStateFromText(text, ingredient) {
  if (!text) return null;
  const vocab = statesForIngredient(ingredient);
  if (!vocab || vocab.length === 0) return null;
  const vocabSet = new Set(vocab);
  for (const { pattern, state } of STATE_ALIASES) {
    if (pattern.test(text) && vocabSet.has(state)) return state;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// BRAND axis — grocery manufacturer labels that ride along with the
// free-text name ("KG UNSLT BTR", "TYSN CHKN TNDRLN"). Pulled onto
// a separate column via migration 0061 so parseIdentity can strip
// brand tokens BEFORE running state / canonical detection —
// otherwise brand abbreviations drown out the canonical keyword
// match and leak into inference as noise.
//
// Shape mirrors STATE_ALIASES: array of { pattern, brand } with a
// display-cased `brand` (the receipt abbreviation gets folded back
// to the brand's canonical capitalization so "GV" and "GREAT VALUE"
// both render as "Great Value" in the UI).
//
// THIS IS A FALLBACK ONLY. The primary ingestion paths are:
//   1. scan-receipt Edge Fn — Claude sees the full receipt line
//      with context (price, unit, store header) and returns brand
//      per item. Handles the long tail.
//   2. user_scan_corrections (migration 0062) — household taps-to-
//      correct the brand chip on a scan row and the mapping sticks
//      family-wide. Teaches regional / idiosyncratic abbreviations
//      Claude missed on.
//   3. This table — what the client-side parseIdentity() uses for
//      manual-add (AddItemModal) where no scan happened, and as a
//      safety net when Tier 1 + Tier 2 both miss.
//
// Grow this list sparingly. A new abbreviation usually belongs in
// the correction table (Tier 2), not here. Patterns are abbreviation-
// first — receipts almost never print the brand in full. Each entry
// anchors with `\b` word boundaries so short tokens ("GV", "KG")
// don't match inside longer words ("GIVEN", "KEG").
// ─────────────────────────────────────────────────────────────────────────────
export const BRAND_ALIASES = [
  // Store brands — highest-frequency on receipts because every
  // household buys them. Abbreviations lead; full names listed as
  // alternatives so manually-typed entries in AddItemModal also
  // resolve.
  { pattern: /\bgv\b|\bgrt\s+value\b|\bgreat\s+value\b/i,  brand: "Great Value" },
  { pattern: /\bkrk\b|\bkirkland\b/i,                       brand: "Kirkland" },
  { pattern: /\btj\b|\btrdr\s+joe\b|\btrader\s+joe'?s\b/i,  brand: "Trader Joe's" },
  { pattern: /\b365\b|\bwhole\s+foods\b/i,                  brand: "365" },
  { pattern: /\bgood\s+(?:&|and)?\s*gather\b/i,             brand: "Good & Gather" },
  { pattern: /\bsig\s+sel\b|\bsignature\s+select\b/i,       brand: "Signature Select" },
  // Dairy
  { pattern: /\bkg\b|\bkerrygold\b/i,                       brand: "Kerrygold" },
  { pattern: /\bplugr[aá]\b/i,                              brand: "Plugrá" },
  { pattern: /\borg\s+valley\b|\borganic\s+valley\b/i,      brand: "Organic Valley" },
  { pattern: /\bdaisy\b/i,                                  brand: "Daisy" },
  { pattern: /\bchob\b|\bchobani\b/i,                       brand: "Chobani" },
  { pattern: /\bfage\b/i,                                   brand: "Fage" },
  { pattern: /\byop\b|\byoplait\b/i,                        brand: "Yoplait" },
  { pattern: /\bdannon\b/i,                                 brand: "Dannon" },
  { pattern: /\bphlly\b|\bphilly\s+crm\b|\bphiladelphia\b/i, brand: "Philadelphia" },
  { pattern: /\bboursin\b/i,                                brand: "Boursin" },
  { pattern: /\bsilk\b/i,                                   brand: "Silk" },
  { pattern: /\boatly\b/i,                                  brand: "Oatly" },
  // Meat / poultry
  { pattern: /\btysn\b|\btyson\b/i,                         brand: "Tyson" },
  { pattern: /\bprde\b|\bperdue\b/i,                        brand: "Perdue" },
  { pattern: /\bbtrbll\b|\bbutterball\b/i,                  brand: "Butterball" },
  { pattern: /\bjd\b|\bjimmy\s+dean\b/i,                    brand: "Jimmy Dean" },
  { pattern: /\bom\b|\boscr\s+myr\b|\boscar\s+mayer\b/i,    brand: "Oscar Mayer" },
  { pattern: /\bhrml\b|\bhormel\b/i,                        brand: "Hormel" },
  { pattern: /\bapplegate\b/i,                              brand: "Applegate" },
  { pattern: /\bboar'?s?\s+head\b/i,                        brand: "Boar's Head" },
  // Pantry
  { pattern: /\bhz\b|\bhnz\b|\bheinz\b/i,                   brand: "Heinz" },
  { pattern: /\bkft\b|\bkraft\b/i,                          brand: "Kraft" },
  { pattern: /\bhell\b|\bhlmn\b|\bhellmann'?s\b/i,          brand: "Hellmann's" },
  { pattern: /\bduke'?s\b/i,                                brand: "Duke's" },
  { pattern: /\bjif\b/i,                                    brand: "Jif" },
  { pattern: /\bskippy\b/i,                                 brand: "Skippy" },
  { pattern: /\bsmkr\b|\bsmucker'?s\b/i,                    brand: "Smucker's" },
  { pattern: /\bcmpbll\b|\bcampbell'?s\b/i,                 brand: "Campbell's" },
  { pattern: /\bswn\b|\bswnsn\b|\bswanson\b/i,              brand: "Swanson" },
  { pattern: /\bprgrs\b|\bprogresso\b/i,                    brand: "Progresso" },
  { pattern: /\bmutti\b/i,                                  brand: "Mutti" },
  { pattern: /\bcento\b/i,                                  brand: "Cento" },
  { pattern: /\bde\s+cecco\b/i,                             brand: "De Cecco" },
  { pattern: /\bbrlla\b|\bbarilla\b/i,                      brand: "Barilla" },
  { pattern: /\brao'?s\b/i,                                 brand: "Rao's" },
  { pattern: /\bkkmn\b|\bkikkoman\b/i,                      brand: "Kikkoman" },
  { pattern: /\bhuy\s+fng\b|\bhuy\s+fong\b/i,               brand: "Huy Fong" },
  { pattern: /\bchol\b|\bcholula\b/i,                       brand: "Cholula" },
  { pattern: /\btabasco\b/i,                                brand: "Tabasco" },
  { pattern: /\bfrnks\s+rh\b|\bfrank'?s\s+redhot\b/i,       brand: "Frank's RedHot" },
];

// Strip every occurrence of `pattern` from `text`, collapse resulting
// whitespace, and return the cleaned string. Used by parseIdentity to
// subtract matched brand/state tokens before the next layer runs —
// "KERRYGOLD SHRD MOZZ" → (strip KERRYGOLD) → "SHRD MOZZ" → (strip SHRD)
// → "MOZZ" → canonical lookup finds mozzarella cleanly.
function stripPattern(text, pattern) {
  if (!text) return "";
  // Build a global version of the pattern so replaceAll-ish behavior
  // works even when the incoming pattern uses a single-match flag set.
  const flags = pattern.flags.includes("g") ? pattern.flags : pattern.flags + "g";
  const global = new RegExp(pattern.source, flags);
  return text.replace(global, " ").replace(/\s+/g, " ").trim();
}

/**
 * Three-layer BRAND → STATE → CANONICAL parser. Run on any raw
 * grocery-receipt / pantry-scan / user-typed name to peel the
 * identity stack apart into its axes.
 *
 *   parseIdentity("KERRYGOLD SHRD MOZZ")
 *     → { brand: "Kerrygold", state: "shredded", canonical: "mozzarella",
 *         remainder: "mozz" }
 *
 *   parseIdentity("TYSON CHKN TNDRLN")
 *     → { brand: "Tyson", state: "tenderloin", canonical: "chicken",
 *         remainder: "chkn" }
 *
 *   parseIdentity("bananas")
 *     → { brand: null, state: null, canonical: "banana", remainder: "bananas" }
 *
 * Each layer runs against the text AFTER earlier layers have stripped
 * their matched tokens — that's what keeps brand tokens from drowning
 * out canonical matches and what prevents the state-vocabulary from
 * matching against a brand substring (no current brand collides with
 * a state keyword, but the order insulates us from future additions).
 *
 * Returns null-valued axes when no layer fires. The STATE layer here
 * is vocabulary-agnostic — it returns whatever STATE_ALIASES matches
 * since we don't know the canonical yet. Callers that need to validate
 * the state against an ingredient's state vocabulary should re-run
 * detectStateFromText with the resolved canonical, which applies the
 * per-ingredient filter.
 */
export function parseIdentity(rawText) {
  const empty = { brand: null, state: null, canonical: null, remainder: "" };
  if (!rawText || typeof rawText !== "string") return empty;
  let remainder = rawText.trim();
  if (!remainder) return empty;

  // ── 1. BRAND — peel manufacturer labels first. Name-brand receipts
  //    lead with "KERRYGOLD" / "TYSON"; stripping these up-front keeps
  //    their tokens from biasing the later state + canonical layers.
  let brand = null;
  for (const { pattern, brand: label } of BRAND_ALIASES) {
    if (pattern.test(remainder)) {
      brand = label;
      remainder = stripPattern(remainder, pattern);
      break;
    }
  }

  // ── 2. STATE — grocery POS abbreviations (SHRD/SLCD/WHL/GRND). First
  //    match wins, mirroring detectStateFromText's order. We don't
  //    gate on the ingredient's vocabulary here because the canonical
  //    isn't resolved yet — callers that need the vocab check should
  //    re-run detectStateFromText after resolving the canonical.
  let state = null;
  for (const { pattern, state: stateId } of STATE_ALIASES) {
    if (pattern.test(remainder)) {
      state = stateId;
      remainder = stripPattern(remainder, pattern);
      break;
    }
  }

  // ── 3. CANONICAL — substring-match against the bundled alias map.
  //    Longest match wins (same ordering discipline as
  //    inferCanonicalFromName). The remainder drives the lookup so
  //    brand/state tokens don't bias the match.
  const canonical = inferCanonicalFromName(remainder) || null;

  return { brand, state, canonical, remainder };
}
